// ============================================================
// TERROR TCG — MASTER DEBUGGER  (clause pass, 2026-08-25)
// ============================================================
// An in-app rules-compliance checker that is allowed to be WRONG.
//
// ── WHY IT WAS REBUILT ──────────────────────────────────────
// The cut before last claimed 44% coverage and proved 13%. The audit
// found ~36 checks that could not fail: tautologies (`assert(x===x)`),
// checks placed AFTER the `Math.min` clamp that made them true,
// unreachable branches (`defenders.length===0 && !onlyEsclings(field)`
// is a contradiction), and a hand-typed `SIMULATED_RULES` array that
// listed 20 codes with no `viol()` call anywhere in the file.
//
// Worse, the simulator DIVERGED from the engine in ways that made a
// green report a lie: `fallen` was set and never cleared (the engine
// clears it at the owner's turn start), Fracture was fused with Ruin
// (R22 says the being SURVIVES the first hit), descent targets were
// invented out of the global card pool instead of read from the slot's
// SOUL, chip damage was never applied so R27/R28 had no state to see,
// and R10 discarded BEFORE drawing when the rule is draw-then-discard.
//
// ── WHY IT WAS REBUILT AGAIN (this pass) ────────────────────
// The rebuild reported two numbers honestly — 98 rules REACHED, 36
// MUTATION-PROVEN — and the 62-rule gap between them was not an
// accident of effort. It was the SHAPE OF THE MUTATION VOCABULARY. A
// mutation perturbed a CONSTANT, so it could only ever reach the rules
// that ARE constants: prices, caps, counts, clocks. R25 has no number
// in it. Neither does R31, R16, R22, R49 or R69. Sixty rules were on
// the honour system inside a file whose entire purpose is refusing to
// take checks on the honour system.
//
// So the mutation now moves the RULE ITSELF. See CLAUSES: every
// prohibition and permission in the catalogue that is not a number is a
// switch, IN FORCE in every normal run, and each MUTATION row withdraws
// exactly one of them from the CHECKER's copy while the simulator keeps
// acting on ACT, where they are all frozen true. The board cannot move;
// only the rule the checks are written against does.
//
// That pass also killed eleven more tautologies the first audit missed,
// every one of them the same species — an assertion reading a value the
// simulator wrote one statement earlier:
//   R12  `gainsAfterSpend` was incremented inside `if (!spent)` under a
//        second `if (spent)`. Dead code. `gainsAfterSpend === 0` was
//        `0 === 0` in every duel ever run.
//   R14  `assert(khao === maxKhao)` on the line after `khao = maxKhao`.
//   R26  `assert(no Fallen)` on the line after clearing every flag.
//   R28  `assert(currentLyf === c.lyf)` after `currentLyf = c.lyf`.
//   R33  `assert(anima <= 0)` INSIDE `if (anima <= 0)`.
//   R39  the second-activation gate asked AFTER the slot was nulled,
//        where it answers R38 whatever R39 says.
//   R42  `pool.length === hand.length + necropolis.length` asserted one
//        line after `pool = [...hand, ...necropolis]`.
//   R49  `assert(soul.includes(prev))` after `soul.push(prev)`.
//   R69  `isSupreme(s) === (three Impera && uhlti && affinity)` — a
//        hand-copy of isSupreme's own body, true for any implementation
//        of either side.
//   R77  `assert(tally === t0 + 1)` after `tally = t0 + 1`.
//   R83  `assert(words.size === 0)` after emptying the Set.
// Each is now measured across the transition it claims to govern, or
// asked of a shared predicate, or moved onto a hand-built bench.
//
// Two more were the OPPOSITE fault, and are worth naming separately
// because a checker that cries wolf is retired faster than one that
// sleeps. R09 read "either something in hand is Transcendable into slot
// 0, or the hand holds no Level 1 at all" — which a hand holding a
// Level 1 of the WRONG SCHOOL fails, on a board obeying R16 perfectly.
// It has never fired because the starter decks all match, which is luck.
// And R65 asserted `esclings[0].deviant` one statement after setting it,
// then called the Deviant mark proven; the mark is now asserted where it
// costs something, at R64's refusal of a U'hlti to the being wearing it.
//
// ── WHY IT WAS EXTENDED (R08 pass, 2026-08-25) ──────────────
// caP'n rewrote R08 the same day and the rewrite did not change a
// number — it changed a WORD. "Attack" in Terror is the broad term:
// *"a tome or Impera or anything to 'attack' a being or your adversary
// directly, Lyt Attack is the actual move every being gets 1 of per
// turn."* This file modelled the SWING and the Anima route and nothing
// else, so on the opening Cycle a Tome that ruined a being, a Curio, an
// Impera and a Primera stamp that dimmed an adversary's Anima directly
// all walked past unwatched. Neither Book could be searched either —
// that clause was marked *new* by the designer and had no counterpart
// here at all — and R90's mulligan had no "if they have not drawn yet".
//
// Nothing FAILED. That is the point and it is the thing worth being
// afraid of: a permissive simulator is not a red report, it is a green
// one that would not have noticed the regression. Three gaps, all
// silent:
//   · Legal.search had no opening-Cycle bar and no `turn` to hang one
//     on, so the fare rows proved the by-level table on half-turns the
//     rule now forbids entirely.
//   · Legal.mulligan had no not-yet-drawn clause, and its window said
//     "this seat has not acted" — which cannot speak about the seat
//     that has not acted YET, and so said nothing about the seat going
//     second, who is exactly who the ruling names.
//   · There was no broad-Attack door anywhere. This was the big one.
//
// AND ONE THING THIS FILE GOT RIGHT AND KEPT. `turn < 2` — the opening
// CYCLE rather than `turn === 0` — was the reading this file had held
// all along, against a shipping engine that refused only the Lyt swing,
// and it was cited as corroboration when rules.js chose the same window
// and wrote its banner. The old ⚠ DIVERGENCE note on the Anima route
// (which said the board was probably right and this file over-strict)
// is now recorded as CLOSED IN THIS FILE'S FAVOUR, because two engines
// disagreeing is the only reason to keep two engines. The typed `2` is
// gone even so: the window is `isOpeningTurnIndex` now, so a 3-4p FFA
// follows the catalogue instead of contradicting it.
//
// THE STANDARD EVERY NEW CHECK WAS HELD TO. Not "it went red" — that
// was the claim an adversarial reviewer destroyed by gutting the rule-
// reading helper and getting a byte-identical report. Every check added
// in this pass names the mutation that reddens it AND has its counter
// branch demonstrated TRUE on a real board in the same batch: the gate
// that refuses an Attack on half-turn 0 is asked again on half-turn 2
// and must say yes; the gate that refuses the Phantom stamp is asked
// about the Machine stamp on the same half-turn and must say yes; the
// backstop that calls a dimmed Anima an Attack is asked about a RAISED
// one and must say no. Nine new clause switches exist for exactly this
// reason, five of them PERMISSION-side, whose counter-readings ADD a
// bar rather than dropping one — because a permission asserted against
// a bar that can only be removed has no failing state and is worth less
// than a declared gap.
//
// WHAT THIS PASS DID NOT MOVE. The headline count. Every new check
// deepens a code that was already proven (R07, R08, R09, R11, R88,
// R90), so MUTATION-PROVEN stayed where it was. R08's SCOPE grew by an
// order of magnitude and its number did not move at all, which is the
// honest shape of this work and is stated here so nobody reads the
// unchanged number as an unchanged file.
//
// ── WHY IT WAS REBUILT AGAIN (MISACT pass, 2026-08-25) ──────
// The clause pass above ends with a paragraph headed THE STANDARD EVERY
// NEW CHECK WAS HELD TO, and that paragraph was half a standard. It
// demanded that a check's COUNTER branch be demonstrated true on a real
// board. It never once asked anything of the IN-FORCE branch — and the
// in-force branch is the ONLY branch that runs in a real duel. A closing
// adversarial review walked through the gap twice:
//
//   ATTACK A · COUNTER INFLATION. `ruleIs(K, "R26", true, (turn % 2) === 1)`
//     — a literal `true` where the rule should be, and a counter true on
//     odd half-turns for reasons having nothing to do with Fallen.
//     Report: 80 proven / 17 vacuous / 0 FAKE, R26 still PROVEN.
//   ATTACK B · ONE LINE, WHOLE FILE. `inForce = true;` as the first
//     statement of `ruleIs`. Every clause-guarded check in the file
//     stops reading the board. Report: BYTE-IDENTICAL.
//
//   THE CONTROL, so nobody has to take that on trust — inject a real
//   engine bug (`me.khao = me.maxKhao - 1`, breaking R14):
//     bug injected, checks intact ......... 1076 violations
//     bug injected, `inForce = true` ......    0 violations
//
// The old ECHO CONTROL could not see it: it degrades BOTH branches at
// once and therefore only detects symmetric degradation.
//
// So this pass built what the banner over NOT_PROVEN_REASONS had already
// named as the real fix — MISACT, a second act-side switch that makes
// the SIMULATOR commit the specific error a rule forbids while the
// checker stays clean. Twenty-two rows, one named engine bug each, and
// two new numbers on the screen: FAULT-BLIND (a bug nobody caught) and
// the IN-FORCE CENSUS (how often each clause guard's in-force reading
// said NO to a board the roster broke). A clause now needs BOTH of its
// branches demonstrated before a red earned through it counts.
//
// WHAT IT DID TO THE NUMBER, stated plainly because the honest direction
// was expected to be DOWN. Two things happened at once. The stricter
// filter took nothing away in the clean file — every clause guard in it
// has a roster row, so every contentful counter also has a watching
// in-force branch — while the roster itself CLOSED SIXTEEN of the
// seventeen vacuous codes for real. 80 proven / 17 vacuous became
// 96 proven / 1 vacuous, and the 16 are the strongest proofs in the file
// rather than the weakest: the checker was clean, the board was wrong,
// and the check said no.
//
// THE NUMBER IS ONLY AS STRICT AS THE ROSTER IS COMPLETE, and that is
// now a forcing function rather than a hole. Delete one roster row and
// its clause loses its WATCH, the code it guards DEMOTES out of the
// proven set, and it reappears as UNDECLARED — verified by deleting
// `DESCENT_HOLDS_TOP` and watching R51 fall out (96 → 95). Add a
// clause-guarded check with no roster row and the same thing happens on
// the first run. There is no way to add a guard to this file any more
// without either proving both of its branches or being named on screen.
//
// AND WHAT IT STILL DOES NOT CATCH, because this is the third time a
// number in this file has been believed too easily. FAULT-BLIND is a
// per-CHECK alarm and PROVEN is a per-CODE number, so a code with a
// SECOND, independent proof keeps it while one of its checks is gutted.
// Attack A is the live example: with R26's duel-side check reduced to a
// constant, the roster row for FALLEN_STICKS goes BLIND and R26 leaves
// the in-force census — but R26 stays PROVEN, honestly, because the
// R31 wall gate also reads `K.SW.R26` and an adversarial assertion
// catches THAT withdrawal on its own. The screen says both things. Read
// the FAULT-BLIND line, not only the headline.
//
// ── WHY IT WAS REBUILT AGAIN (BLIND-ECHO pass, 2026-08-25) ──
// The MISACT pass above closed Attack B and reported 96 proven. It also
// left, unexamined, the population the whole apparatus does not touch.
// Measured rather than argued, on the clean file:
//
//     96  MUTATION-PROVEN
//      5  of them reddened through a `ruleIs` clause guard, which is the
//         only thing the counter census, the in-force census, the echo
//         control and the fault roster's WATCH requirement can see
//     74  reddened with NO clause attribution at all — recorded as
//         `stateFails`, and granted honesty UNCONDITIONALLY by
//         `runMutations` (`if (stateFails > 0) return true`)
//
//   ATTACK C. Replace an adversarial rejection with a bare echo of its
//     own switch — no board, no card, no state:
//         H.reject("R30", !K.SW.R30, "a second swing from one Escendant")
//     Report BYTE-IDENTICAL. Applied mechanically to all 44 switch-
//     guarded H.reject sites in the adversarial suite, the report moved
//     by ONE FAKE and the headline did not move at all: 96 proven with
//     44 checks reading nothing.
//
// THE REASON IS STRUCTURAL AND NO CENSUS FIXES IT. A mutation batch asks
// "did the expected code redden when the checker's copy was perturbed",
// and a check that reads ONLY WHETHER K WAS PERTURBED answers it
// perfectly. So this pass BUILDS THAT FAKE AND RUNS IT — `BLIND_ECHO`
// replaces the verdict of every assertion in the file with `K.__clean` —
// and prints what it scores:
//     blind file, mutation batch ....... 86 of 96
//     blind file, fault roster .......... 0
// The mutation batch cannot tell the difference. The FAULT ROSTER can,
// because it leaves the checker CLEAN and breaks the BOARD.
//
// SO THE NUMBER IS SPLIT AND THE HONEST HALF IS THE HEADLINE:
//     WATCHED        — the simulator committed a named engine bug, the
//                      checker was clean, and the check said no
//     WIRED ONLY     — reddens when the rule is withdrawn from the
//                      CHECKER's own copy, which says the check is
//                      attached to the rule's DEFINITION and says
//                      NOTHING about whether it read a board
//
// \u26a0 THE FIGURES ARE DELIBERATELY NOT TYPED HERE ANY MORE, 2026-09-15.
// This block read "WATCHED 22 / WIRED ONLY 74" and both had drifted —
// the 2026-09-14 sweep moved them to 29 and 77 — which is the exact
// failure the banner below already names: "a baseline typed into a
// comment is a second catalogue of the file's own score." A stale score
// in a docblock is worse than no score, because it reads as measured.
//   ⭐ GET THEM BY RUNNING IT:  node tools/harness/debuggerHeadless.mjs
// The drop from 96 to a WATCHED tier in the twenties was the correct
// direction and it is what that pass cost. The
// 74 are not deleted and not called fakes: they are a real, useful,
// weaker claim, and they are now labelled as one instead of being added
// into a single number that reads as strength.
//
// AND THE TAUTOLOGY IN THE PREVIOUS PASS'S OWN CONTROL, since this file
// keeps a record of what it got wrong. `runInForceEcho` demanded that
// the set of watching clauses EMPTY under the in-force stub. It empties
// for any file whatsoever: the stub sets `inForce = true` before the
// census counts `if (!inForce)`. A demand met by construction is not a
// control. It is kept for the one thing it does say (an empty `watching`
// is Attack B's signature) and replaced, as a measurement, by
// `inForceFaultLost` — how many of the roster's caught bugs the stub
// costs. Clean file: 18 of 22. Under Attack B: 0, and the alarm fires.
//
// ── WHAT THIS PASS COULD NOT CLOSE, MEASURED AND DECLARED ───
//   ATTACK D · THE ROSTER'S RESIDUAL TRUST. An assertion may read the
//     fault switch instead of the board:
//         H.assert("R20", ruleIs(K, "R20", !misact("SUMMON_TOLL"), …))
//     It is green in every clean run, red on exactly the roster row that
//     promotes it, and the report is BYTE-IDENTICAL. There is no
//     mechanical control for it in this file: the injection site and a
//     cheating assertion call the SAME function with the same argument,
//     so nothing at runtime can separate them. It is a grep-enforceable
//     discipline and is written down as one:
//         EVERY `misact(` CALL IN THIS FILE MUST BE AN INJECTION SITE
//         IN THE SIMULATOR. No assertion, no `ruleIs` argument, no
//         `Legal` gate may read it. Check with `grep -n 'misact(' `.
//
//     ⚠ AND ON 2026-09-14 IT WAS FOUND LIVE, which is why the discipline
//     is written as a grep and not as a promise. The STAMP_ONE_AXIS row
//     injected into `stampsOf` — a PURE FUNCTION OF cards.js whose
//     result nothing stored, because every reader recomputed it. So the
//     fault changed NO BOARD STATE AT ALL, R78's assertion caught it by
//     calling the same function one line later, and `Legal.primera` read
//     the switch too. R78's WATCHED credit was
//     `assert(!misact("STAMP_ONE_AXIS"))` wearing a board's clothes.
//     THE FIX WAS TO GIVE THE RULE A BOARD: the two stamps are written
//     onto the RIFT by `seatStamps` when a being arrives, the fault
//     truncates that field, and every reader reads the rift. Proven by
//     the control that could not exist before — truncate the rift's
//     stamps with NO `misact` anywhere and R78 goes red 207 times, R84
//     20,247 times. Under the old shape that bug was inexpressible.
//     The transitive case is what to watch for: a reader of a reader of
//     `misact` is still a reader. Grep for the FUNCTION, not the word.
//   THE WIRED CODES have no roster row, and most of them cannot
//     cheaply get one: they are adversarial benches over `Legal`, where
//     the "fault" would have to be an illegal ACTION the simulator
//     performs rather than a wrong state it writes. Growing the roster
//     into that space is the next real piece of work on this file, and
//     it moves WATCHED, which is now the only number worth moving.
//
// ── THE ATTACK BATTERY, RUN AGAINST THIS EXACT FILE ─────────
// Every row is a mutation applied to a COPY outside the repo and the
// whole apparatus re-run headless.
//
// ⚠ THE BASELINE IS NOT WRITTEN HERE ANY MORE. It used to read "96 wired
// / 22 WATCHED, blind 86/0" and by 2026-09-14 every one of those numbers
// had drifted — the row could not be reproduced as written, and it also
// conflated "96 wired" with what was then 96 MUTATION-PROVEN and 74
// wired. A baseline typed into a comment is a second catalogue of the
// file's own score. Print it instead:
//     node tools/harness/debuggerHeadless.mjs
// which now reports WATCHED, the wired split, the BLIND ECHO's own
// score and the UNDECLARED count, and exits non-zero on any of them.
// (For the record, the 2026-09-14 sweep moved it from 22 WATCHED / 99
// reached / 3 UNDECLARED to 29 WATCHED / 111 reached / 0 UNDECLARED,
// with wired going UP because reaching more rules reaches most of them
// weakly first. WATCHED is the number that was moved on purpose.)
//
//   B  `inForce = true` at the top of ruleIs
//        WATCHED 22 → 4 · wired 96 → 80 · inForceFaultLost 18 → 0
//        ECHO-BLIND · IN-FORCE-BLIND · 18 FAULT-BLIND · 15 UNDECLARED
//   A  R26's duel check → `ruleIs(K,"R26", true, (turn%2)===1)`
//        WATCHED 22 → 21 · FAULT-BLIND names FALLEN_STICKS → R26.
//        (Wired stays 96, honestly: the R31 wall bench catches the R26
//        clause withdrawal on its own. The screen says both things.)
//   C  all 44 switch-guarded `H.reject` sites → `!K.SW.<code>`
//        WATCHED 22 → 22, wired 96 → 96, 1 FAKE.
//        NOT CAUGHT, AND THAT IS THE POINT: none of those 44 was ever
//        in WATCHED. They are the WIRED tier and the screen now says
//        what that tier is worth instead of adding it into one number.
//   D  an assertion reads `misact("SUMMON_TOLL")` instead of the board
//        BYTE-IDENTICAL. Declared above. No runtime control exists.
//   E  the blind echo itself neutered (`if (BLIND_ECHO) ok = ok`)
//        blindCaught 0 → 22 · BLIND-ECHO-BLIND fires · WATCHED → 0.
//        The control catches its own removal.
//
// ── THE FIVE LAWS THIS FILE OBEYS ───────────────────────────
// 1. THE CATALOGUE LAW. Not one rule value is typed here. Every
//    number comes from ./rules, and every count on screen is derived
//    from RULES[] at render time. There is no second catalogue.
// 2. ASSERT BEFORE THE CLAMP. A check that runs after the code that
//    makes it true is decoration. Raw values are asserted, then
//    clamped, never the reverse.
// 3. COVERAGE IS OBSERVED, NOT DECLARED. `H.assert(code, ...)`
//    registers `code` the instant it executes. The REACHED set is
//    whatever the run actually reached. Nothing is hand-listed.
// 4. A CHECK MUST BE ABLE TO GO RED, AND IT IS ASKED TWICE. The
//    MUTATION SELF-TEST perturbs a constant or withdraws a clause in the
//    checker's own copy (K) while the simulator keeps acting on the true
//    one (ACT); any check that stays green under its own mutation is
//    reported FAKE. THE FAULT ROSTER asks the opposite and stronger
//    question — the checker is left CLEAN and the SIMULATOR commits a
//    named engine bug (MISACT), and the checks that are supposed to
//    catch it must. A roster row that reddens nothing is reported
//    FAULT-BLIND. Both numbers are on the screen, both without anyone
//    asking, because the second one exists precisely because the first
//    one alone was walked through twice in one afternoon.
// 5. EVERY GAP IS DECLARED. Three tables, three different confessions:
//    UNPROVABLE_REASONS (nothing here reaches the rule at all),
//    NOT_PROVEN_REASONS (an assertion ran and no mutation could redden
//    it — the most dangerous row on the screen, because it renders as
//    coverage), and PARTIAL_REASONS (mutation-proven at a hand-built
//    board or at the legality predicate, never watched inside a played
//    duel). A code missing from all three shows up as UNDECLARED, in
//    red, next to FAKE.
//
// ── WHAT THE NUMBERS DO NOT MEAN ────────────────────────────
// MUTATION-PROVEN says a check is wired to the rule and has a
// demonstrated failing state. It does NOT say Shatterfield.js
// implements that rule — this file is a SECOND implementation, and the
// finding is always the DISAGREEMENT, never one file's green. PARTIAL
// says the proof came off a bench rather than a duel, which is a real
// proof of a real gate and a weaker claim than watching a game obey it.
//
// ── WHAT THIS FILE STILL CANNOT PROVE ───────────────────────
// Four things, and none of them is going to be closed by trying harder
// at the same shape of check.
//
// ⚠ AND THREE PLACES THIS SIMULATOR PLAYS A SLIGHTLY DIFFERENT GAME —
// found in the 2026-09-08 coherency sweep, written down here rather
// than quietly fixed, because each one changes what the simulator DOES
// and a silent change to a second implementation is the one thing this
// file exists to make impossible. None of them has ever fired, and
// "has never fired" is luck, not correctness:
//   a. THE PAID RITE ONLY EVER RETURNS A LEVEL 1 ONTO AN EMPTY RIFT.
//      `Legal.resurrect` refuses `c.level !== 1` and refuses an
//      occupied rift, both under R46. RULEZ_V2 ch.17 allows an L2 over
//      its standing L1 and an L3 directly over its L2 with its full
//      Soul. The gate is not merely unproven, it is narrower than the
//      rule, and a legal climb is reported as an R46 refusal.
//   b. IMPERA ARE TRAINED FREE AND MATCH-BLIND. The simulator splices
//      an Impera onto a being with no fare and no Ilk/School test, so
//      R101/R102's Deviant Training path has no board here to be
//      watched on — which is why both are in UNPROVABLE_REASONS.
//   c. A LEVEL 1 TOME'S "YOUR TURN ONLY" IS NOT IN THE GATE.
//      `Legal.tomeActivate` enforces the wait and the response-window
//      ban but not the own-turn clause, so only the simulator's call
//      pattern keeps it honest. Needs the seat's parity threaded in.
//   1. PROPHECIES. R97, R98 and R99 landed together on 2026-08-25 and
//      all three are UNREACHED here, because a Prophecy is a card id
//      copied onto the field and read by nothing. No fulfilment, no
//      resolution, no `prophecySpent`, no second winner. R97 also needs
//      a table with more than two seats. See UNPROVABLE_REASONS.
//   2. R48, THE LAST OF THE VACUOUS SEVENTEEN. Sixteen of the
//      seventeen were closed by the FAULT ROSTER (see below); R48 is
//      the one measurement refused. Its fault needs a Resurrection of a
//      being that HAD a Soul, and across 75 instrumented duels the
//      simulator performed 1961 Resurrections with 0 such being. The
//      row was written, run, found permanently blind and DELETED rather
//      than left as furniture with an alarm attached. See
//      NOT_PROVEN_REASONS.
//   3. R45, which shares R46's guard and needs a per-line return
//      ledger to separate.
//   4. ANYTHING SHATTERFIELD.JS DOES. This is a second implementation.
//      A green report here is a claim about THIS file's reading of the
//      catalogue and never a claim about the shipping board. The value
//      is the DISAGREEMENT, which is why the ⚠ notes in Legal are kept
//      where a divergence was found rather than quietly reconciled.
//
// ── THE THREE MODES ─────────────────────────────────────────
//   SIMULATION  — plays duels and asserts every state it passes.
//   ADVERSARIAL — deliberately attempts ILLEGAL actions and asserts
//                 they are REJECTED. A permissive engine fails here
//                 while a simulation stays green. It also carries the
//                 BENCHES: descent, ruin, Supreme, the stamps, the
//                 Graveshift and the tally, hand-built so the rules
//                 that depend on a rare board are proven in EVERY run
//                 instead of the lucky ones.
//   MUTATION    — see law 4.
//
// ── RUNNING IT HEADLESS ─────────────────────────────────────
// `runAll` is plain ESM and touches no React; only the default export
// below is a component. A headless runner therefore needs three edits
// to a copy of this file and nothing else, and the marker it cuts at is
// in the file on purpose:
//   1. cut at the SENTINEL — one token, one occurrence in the file,
//      spelled `@@TERROR` + `_DEBUGGER_HEADLESS_CUT@@` (written split
//      here on purpose, so this recipe is not itself a second match),
//      and drop everything from that line to the end,
//   2. drop the `react` and `./worldData` imports (both are UI-only —
//      `auditWorldReachability` is called from the world-graph panel,
//      never from `runAll`),
//   3. rewrite `./x` to `./x.mjs` for starterDecks, cards, rules,
//      rules' own `./matchSeats` AND its `./necroSeal` (added to the
//      list 2026-09-08 — rules.js has imported it since the seal
//      landed and this recipe never said so), exactly as tools/harness/build.mjs
//      already does for the balancer's closure. `./theme` is dropped
//      with the other two UI-only imports, not rewritten — nothing
//      above the sentinel touches the theme object it names (verified:
//      zero uses of it before the cut; it is only reached by the
//      screen below).
// then `export { runAll }` and call it. Nothing else in the closure
// contains JSX. The permanent runner is tools/harness/debuggerHeadless.mjs,
// beside tools/harness/build.mjs, reusing that file's import rewriter
// rather than growing a second one — run it with
// `node tools/harness/debuggerHeadless.mjs [--games=N] [--seed=N]`.
//
// ── ITS SIBLING ─────────────────────────────────────────────
//   ► tools/balance.mjs drives the REAL NPC brain through the REAL
//     gameManager. This file is a SECOND implementation on purpose:
//     two engines disagreeing is how you find out which one is wrong.
//     Neither replaces the other. Run both.
// ============================================================

import { useState, useMemo } from "react";
import { SD } from "./theme";
import { STARTER_DECKS } from "./starterDecks";
// THE MAP IS A GRAPH AND A GRAPH CAN BE DISCONNECTED.
import { auditWorldReachability } from "./worldData";
import {
  CARD_TYPES,
  ESCENDANTS, TOMES, IMPERA, UHLTIS, AFFINITIES,
  PROPHECIES, CURIOS, ESCLINGS,
} from "./cards";
import {
  STARTING_ANIMA, MAX_ANIMA, STARTING_KHAO, MAX_KHAO_NAT, MAX_KHAO_ABS,
  STARTING_HAND, HAND_LIMIT, ESCLING_SLOTS, TOME_SLOTS, CURIO_SLOTS,
  GRAVESHIFT_COST, LUX_SEARCH_COST, GRIM_SEARCH_COST, grimSearchFare,
  RESURRECT_PER_TURN, canPayVoluntary,
  MULLIGAN_LIMIT, CHAIN_DEPTH_CAP, FIRST_KHAO_TURN,
  TURN_CLOCK, TURN_CLOCK_PVP, clockLimitFor, SIM_TURN_CAP,
  DUEL_TIME_LIMIT, GRIMOIRE_MIN, LUXMOIRE_MIN, LUXMOIRE_MAX,
  DECK_LIMITS, RESURRECT_COST, RESURRECT_LIMIT,
  PRIMERAS, PRIMERA_COST, HERETICAL_TRAINING_COST,
  TOME_WAIT, PERSISTENT_TOME_TAGS,
  // ── THE 2026-08-26 CANON PASS ──
  // R104 the U'hlti Luxmoire total · R62 the Ally-Ilk Curio cap. Both
  // are NUMBERS, so both belong in ACT/K and get a mutation row: a cap
  // this file typed as a literal is a cap nothing can prove.
  LUXMOIRE_UHLTI_MAX, ALLY_ILK_CURIO_MAX,
  callOnClock, clockExpired, timeoutWinner,
  // ── R08's OPENING CYCLE, imported and never re-derived ────
  // THE CATALOGUE LAW applies to windows exactly as it applies to
  // prices. This file used to type `turn < 2` in two places, which was
  // the right reading of R08's window and was cited as corroboration
  // when rules.js chose it — but a right answer typed twice is still a
  // second catalogue, and it would not have followed rules.js into a
  // 3-4p FFA where the Cycle is `seatCount` half-turns long. The window
  // now comes from the same predicate the engine asks.
  isOpeningTurnIndex, openingCycleLength, ATTACK_KINDS, adversaryDamageInPayload,
  // ── THE 2026-09-14 COHERENCY SWEEP · WHAT THIS FILE WAS STILL TYPING ──
  // THE CATALOGUE LAW (law 1) says not one rule value is typed here, and
  // this file was breaking it in six places while quoting it in its own
  // banner. Every import below replaces a number, a word or a private
  // copy of a predicate that had drifted from the shipping board:
  //   deviantFare .............. R21/R101 · a flat `1` was typed in
  //                              Legal.ascend against a 2/3/5-Khao table
  //   printedFare/canPayPrintedFare  R105 · only `khaoCost` was charged;
  //                              four Tomes print an Anima leg too
  //   isLuxSearchable .......... R106 · a paid Luxmoire search finds
  //                              Impera and ONLY Impera
  //   tomeWaitHalfTurns ........ R35 · the wait is a price on SETTING,
  //                              not a property of the card's level
  //   lytAttacksAllowed ........ R30/R124 · the allowance is a COUNT,
  //                              because two shipped cards print more
  //                              than one swing
  //   blocksAnimaLytAttack /
  //   animaOpenToLytAttack ..... R31/R119 · the MIND rift never walls the
  //                              Anima route once the Arts wake. This
  //                              file held the last private copy of the
  //                              pre-R119 wall; the balancer had already
  //                              switched to the engine's predicate
  //   mindMayAct / MIND_RIFT ... R121 · the Mind's Silence
  // A second implementation is worth keeping because it can DISAGREE.
  // It is worth nothing when it disagrees by holding a retired reading.
  deviantFare, DEVIANT_FARE, printedFare, canPayPrintedFare, isLuxSearchable,
  tomeWaitHalfTurns, TOME_WAIT_FULL_TURNS, lytAttacksAllowed, blocksAnimaLytAttack, animaOpenToLytAttack,
  // durationStillRunning — R123's bench asks the ruling's own helper, not a literal.
  durationStillRunning,
  mindMayAct, trioOccupied, MIND_RIFT, TRIO_RIFTS, rolesAwake, roleLockHalfTurns,
  wardenOpenToLytAttack, animaOpenToLytAttack as animaOpen, WARDEN_RIFT,
  ROLE_ORDER, WARDEN_ARTS_PER_TURN, cooldownHalfTurns, durationHalfTurns,
  halfTurnsPerFullTurn, tomeIsIntangible, tomeWardScope,
  PHASE, PHASES, phaseOf, phaseAllows, canEnterPhase, ATTACK_PHASE_ALLOWS, ATTACK_PHASE_BARS,
  INTANGIBLE_FULL_TURNS,
  ANIMA_LOSS_KINDS, animaLossFeedsProphecy, PER_TURN_DEVIANT_LIMITS,
  RULES as ALL_RULES, CAT_COLORS,
} from "./rules";

// ─────────────────────────────────────────────────────────────
// THE CLAUSE SWITCHES — how a rule with no NUMBER gets inside the
// fake-detector.
//
// The original mutation vocabulary was one word long: perturb a
// constant. That reaches every rule that is a price, a cap or a count,
// and it reaches NOTHING ELSE — which is why 36 of 99 codes were
// mutation-proven and 62 sat "reached" forever. R25 (Fallen are
// impervious) has no number in it. Neither does R31, R16, R49 or R22.
// A checker that can only perturb numbers can only ever prove the rules
// that are numbers, and the other sixty are on the honour system.
//
// So the mutation moves the RULE ITSELF. Every clause below is a single
// prohibition or permission out of the catalogue, IN FORCE (true) in
// every normal run. A mutation turns exactly one of them off in the
// CHECKER's copy while the simulator keeps acting on ACT, where they are
// all true and frozen. The board therefore cannot move, and any check
// that is genuinely wired to the rule must contradict the mutated copy.
//
// ⚠ THE ONE THING THIS MECHANISM CANNOT SEE, stated plainly because it
// is the exact hole the last audit found: `assert(clause ? x : !x)` goes
// red under mutation even when `x` is a tautology. The negation of
// `true` is `false`, and `false` is red. So a clause is NEVER allowed to
// wrap `!ok`. Every clause-guarded assertion below states the rule in
// one branch and a NAMED COUNTER-RULE in the other, and BOTH branches
// must be real predicates over state the simulator produced. Where that
// was impossible the rule is left unproven with a written reason in
// NOT_PROVEN_REASONS, because a declared gap is worth more than a check
// that reddens on cue and watches nothing.
// ─────────────────────────────────────────────────────────────
const CLAUSES = Object.freeze({
  // Gate clauses — read inside Legal, isSupreme, isFracturedSlot or
  // validateDeck. Turning one off makes the gate stop refusing, and the
  // assertion that demands a specific refusal code goes red.
  // ── R08 IS THREE PROHIBITIONS AND THEREFORE THREE SWITCHES ──
  // R08 was one line ("no Lyt Attack on turn 1") and is now three
  // separate bars: the SWING (`R08`), every OTHER route that hurts the
  // other side (`R08_BROAD`), and the *new* refusal to SEARCH either
  // Book (`R08_BOOKS`). One switch for all three would mean a single
  // mutation reddens whichever leg happens to still exist — delete the
  // broad legs entirely and `clause R08` still goes red off the swing,
  // so the new scope would be guarded by nothing. Three switches, three
  // mutation rows, three independent reds. A clause key is a switch
  // name, not a rule code; all three report under R08.
  //
  // ── AND THE PERMISSION SIDE NEEDS SWITCHES OF ITS OWN ───────
  // R07, R08 and R90 each GRANT as much as they forbid, and a
  // permission cannot be reddened by withdrawing a prohibition — drop
  // the bar and the permission is more permitted, not less. So the
  // four switches below are the ones whose COUNTER-READING adds a bar,
  // and every one of them is a reading somebody could plausibly have
  // shipped:
  //   R08_WINDOW  the opening Cycle never CLOSES (the bar is permanent)
  //   R08_HARM    ANY payload counts as harm, not only damage
  //   R08_KIND    the gate cannot tell an attacking card from a
  //               harmless one, so it bans every Tome and every
  //               Primera stamp on the opening Cycle — the wholesale
  //               over-fix, which breaks R09 to enforce R08
  //   R07_SPLIT   the opening Cycle is simply dry — no draws at all
  //   R11_FX      a card-effect draw CONSUMES the Turn's draw — the
  //               over-strict reading the ruling excludes in its own
  //               parenthesis ("card effects excluded")
  //   R08_FX      a card-effect SEARCH is barred like a player's — the
  //               over-strict reading caP'n struck out on 2026-08-26
  //               ("cards are exempt from the first turn search rule").
  //               R11_FX's exact twin, and named to read as one idea:
  //               a card effect is never the player's action, for the
  //               draw clause or the search clause
  //   R90_WINDOW  the window is literally `turn === 0`, so the seat
  //               going second never gets a mulligan — which is the
  //               exact reading rules.js's banner rejected, and the
  //               one this file would hold if nobody had re-read it
  R07: true, R07_SPLIT: true,
  R08: true, R08_BROAD: true, R08_BOOKS: true, R08_WINDOW: true,
  R08_HARM: true, R08_KIND: true, R08_FX: true,
  R09: true, R11: true, R11_FX: true, R12: true, R14: true,
  R15: true, R16: true, R17: true, R18: true, R19: true, R20: true,
  R21: true, R22: true, R23: true, R24: true, R25: true, R26: true,
  R27: true, R28: true, R29: true, R30: true, R31: true, R33: true,
  R34: true, R36: true, R37: true, R39: true, R40: true, R42: true,
  R43: true, R46: true, R48: true, R49: true, R50: true, R51: true,
  R52: true, R62: true, R64: true, R65: true, R66: true, R68: true,
  R69: true, R70: true, R71: true, R72: true, R73: true, R74: true,
  // R124 · the counter-reading is the one this file shipped until
  // 2026-09-14: the allowance is always exactly ONE, whatever a card
  // prints. Withdrawing it refuses the granted swing and the permission
  // assertion on the R30 bench goes red.
  R124: true,
  // R121 · the counter-reading is the one this file held by having no
  // clause at all: a Mind is an ordinary being and the trio never
  // silences it.
  R121: true, R121_LOCK: true,
  // R35 · the counter-reading is the one this file held until
  // 2026-09-14: the wait is a property of the card's LEVEL, so a Tome
  // played face-up from hand waits too.
  R35: true, R103: true,
  // R106 · the counter-reading is the one this gate held by taking no
  // card at all: a paid Luxmoire search tutors anything that lives there.
  R106: true,
  R74_SCOPE: true,
  R75: true, R76: true, R77: true, R78: true, R80: true, R81: true, R82: true,
  R83: true, R84: true, R85: true, R86: true,
  R90: true, R90_WINDOW: true, B02: true,
});

// ─────────────────────────────────────────────────────────────
// ACT — the TRUE constants. The simulator ACTS on these.
// K   — the CHECK constants. Every assertion COMPARES against these.
// In a normal run K is a copy of ACT and everything agrees. In a
// mutation run one field of K is perturbed and any honest check that
// reads it must go red. That asymmetry is the entire fake-detector.
// ─────────────────────────────────────────────────────────────
const ACT = Object.freeze({
  STARTING_ANIMA, MAX_ANIMA, STARTING_KHAO, MAX_KHAO_NAT, MAX_KHAO_ABS,
  STARTING_HAND, HAND_LIMIT, ESCLING_SLOTS, TOME_SLOTS, CURIO_SLOTS,
  GRAVESHIFT_COST, LUX_SEARCH_COST, GRIM_SEARCH_COST, RESURRECT_PER_TURN,
  MULLIGAN_LIMIT, CHAIN_DEPTH_CAP, FIRST_KHAO_TURN,
  TURN_CLOCK, TURN_CLOCK_PVP, SIM_TURN_CAP,
  DUEL_TIME_LIMIT, GRIMOIRE_MIN, LUXMOIRE_MIN, LUXMOIRE_MAX,
  DECK_LIMITS, RESURRECT_COST, RESURRECT_LIMIT,
  PRIMERA_COST, HERETICAL_TRAINING_COST, TOME_WAIT, PERSISTENT_TOME_TAGS,
  // R35 · THE WAIT IS DECLARED IN FULL TURNS and `TOME_WAIT` is a
  // DERIVED VIEW of it. Perturbing the derived map stopped reddening
  // anything the moment the gate started asking `tomeWaitHalfTurns`
  // — so the row that watched it went dead and the FAKE column said so.
  // The catalogue's own row is the one that belongs in K.
  TOME_WAIT_FULL_TURNS,
  LUXMOIRE_UHLTI_MAX, ALLY_ILK_CURIO_MAX,
  // R101 · THE DEVIANT FARE TABLE, read through K for the same reason
  // GRIM_SEARCH_COST is: a table read straight out of the import closure
  // sits BESIDE the fake-detector instead of inside it, and a fare
  // nothing can perturb is a fare nothing is watching. `deviantFare` is
  // still used — as the ORACLE the K-side lookup is checked against.
  DEVIANT_FARE,
  // Every clause of the catalogue that is NOT a number, all IN FORCE.
  // The simulator reads this copy and it never changes. See CLAUSES.
  SW: CLAUSES,
});

// ─────────────────────────────────────────────────────────────
// THE COUNTER CENSUS — the detector that had to be built, and why.
//
// `ruleIs(K, clause, inForce, counter)` states the rule when the clause
// is in force, and the COUNTER-RULE when the checker's copy has been
// mutated. The pass that introduced it counted a code MUTATION-PROVEN
// the moment a clause withdrawal turned it red, and that was the whole
// of the argument. It is not an argument. Withdrawing the clause makes
// the assertion evaluate `counter`; if `counter` is FALSE on every board
// the simulator can produce, the assertion becomes `assert(false)` and
// goes red by arithmetic. Nothing was observed. Nothing was proven.
//
// So `ruleIs` now COUNTS both of its branches on every real evaluation.
// Both are already computed — they are eager arguments, not thunks — so
// the census is free. Across a real batch it records, per clause:
//   n            — how many times the guard was asked
//   counterTrue  — how many of those the COUNTER-RULE also held on
//   inForceTrue  — how many the rule itself held on (n, in a clean run)
//
// counterTrue === 0 is the verdict. It means the counter branch is the
// constant `false` dressed as a predicate, the mutation reddens it by
// construction, and the code is reported VACUOUS — a third colour beside
// FAKE — and is NOT counted as proven. See THE VACUOUS FINDING in the
// banner for what this cost, and for the structural reason most
// counter-rules cannot pass this test.
//
// `LAST_COUNTER` is the attribution channel: `ruleIs` names the clause
// whenever it hands back the COUNTER branch, and `H.assert` consumes the
// name as it judges the value. A red is therefore traceable to the exact
// clause guard that produced it, which is what lets a code keep a proof
// earned somewhere else while losing the one earned here.
// ─────────────────────────────────────────────────────────────
const CENSUS = new Map();   // clause -> { n, inForceTrue, counterTrue }
let CENSUS_ON = false;      // ONLY the unmutated batch feeds the census
let ECHO_STUB = false;      // THE ECHO CONTROL — see runEchoControl below
let LAST_COUNTER = null;    // clause whose COUNTER branch made the value
                            // H.assert is about to judge. Consumed there.

// ═════════════════════════════════════════════════════════════
// THE IN-FORCE ATTACK, AND WHY THE COUNTER CENSUS ALONE WAS NOT
// A DETECTOR  (2026-08-25, second adversarial pass)
// ─────────────────────────────────────────────────────────────
// The counter census above asks ONE question — was the COUNTER branch
// ever true on a real board — and a closing review landed two attacks
// that answer it perfectly while watching nothing:
//
//   ATTACK A · COUNTER INFLATION. Replace a guard's IN-FORCE branch
//     with a constant and give the counter something unrelated but
//     occasionally true:
//         H.assert("R26", ruleIs(K, "R26", true, (turn % 2) === 1), …)
//     `counterTrue > 0` is satisfied by the parity of the turn counter,
//     the clause mutation still reddens the assertion by arithmetic, and
//     R26 stayed in PROVEN. The in-force branch read no board state at
//     all. Report: byte-identical, 80 proven / 17 vacuous / 0 FAKE.
//
//   ATTACK B · ONE LINE, WHOLE FILE. Add `inForce = true;` as the first
//     statement of `ruleIs`. Every clause-guarded check in the file now
//     watches nothing while the rules stand — and THE IN-FORCE BRANCH IS
//     THE ONLY BRANCH THAT RUNS IN A REAL DUEL. Report: byte-identical
//     again, 80 / 17 / 0, echoLost 4, no alarm.
//
// The control that proves Attack B is not cosmetic: inject one real
// engine bug (`me.khao = me.maxKhao - 1`, violating R14) and re-run.
//     bug injected, checks intact ......... 1076 violations
//     bug injected, `inForce = true` ......    0 violations
// The file's entire purpose evaporates under a one-line edit and the
// number does not flinch.
//
// WHY THE OLD ECHO CONTROL COULD NOT SEE IT. `ECHO_STUB` degrades BOTH
// branches at once (`inForce := true, counter := false`) and so only
// ever detects SYMMETRIC degradation. Degrading the in-force branch
// alone is invisible to it, and to every number derived from it, because
// the honest-proven set is computed from MUTATION runs — where the
// clause is withdrawn and only the COUNTER branch is ever evaluated.
// Nothing in the old mechanism evaluated `inForce` for a verdict.
//
// ─────────────────────────────────────────────────────────────
// MISACT — THE SECOND ACT-SIDE SWITCH, WHICH IS THE ACTUAL FIX
// ─────────────────────────────────────────────────────────────
// The banner over NOT_PROVEN_REASONS named the real fix a pass before
// it was built: "a second act-side switch (a MISACT beside ACT) that
// makes the simulator commit the specific error the rule forbids, so
// the assertion has a real broken board to catch instead of a withdrawn
// definition." That is what `MISACT` is.
//
// ACT is frozen and the simulator obeys it. MISACT names ONE fault at a
// time, and exactly one site in the simulator asks for it — `misact("X")`
// — and commits the specific error. The CHECKER is left un-mutated (K is
// a clean copy of ACT) so every red a fault produces is a check catching
// a genuinely wrong board, which is the strongest evidence this file can
// produce about itself.
//
// THE HAZARD, STATED PLAINLY, because a simulator that can be told to
// misbehave can be told to misbehave by accident: `MISACT.fault` is null
// in every normal run, every injection site is a ternary whose false arm
// is the original expression verbatim, and `runFaults` restores the null
// in a `finally`. The FAULT ROSTER is a table with an `expect` column,
// and a fault whose expected codes DO NOT go red is reported FAULT-BLIND
// at the top of the screen — so a dead injection site is louder than a
// silent one, not quieter.
//
// THE IN-FORCE CENSUS is what the fault batch is FOR. During a fault run
// it records, per clause, how often the IN-FORCE branch came back FALSE.
// `inForceFalse > 0` is the verdict, and it is the exact dual of
// `counterTrue > 0`:
//     counterTrue  > 0  — the counter branch is a predicate, not `false`
//     inForceFalse > 0  — the in-force branch is a predicate, not `true`
// A clause needs BOTH before a red earned through it counts. Attack A
// loses R26 (a literal `true` is never false, under any fault). Attack B
// loses EVERY clause-guarded code at once, and trips three alarms on the
// way out: FAULT-BLIND on every roster row, an empty in-force census,
// and the IN-FORCE ECHO below finding nothing to lose.
// ═════════════════════════════════════════════════════════════
const MISACT = { fault: null };   // the ONE act-side fault in flight
const misact = (f) => MISACT.fault === f;
const MISCENSUS = new Map();      // clause -> { n, inForceFalse }
let MISCENSUS_ON = false;         // ONLY the fault batch feeds it
let INFORCE_STUB = false;         // THE IN-FORCE ECHO — see runInForceEcho
let BLIND_ECHO = false;           // THE BLIND ECHO — see runBlindEcho

const ruleIs = (K, clause, inForce, counter) => {
  // THE ECHO CONTROL degrades the CHECK, never the helper: an assertion
  // reduced to `!!K.SW[clause]` says TRUE while the clause stands and
  // FALSE when it is withdrawn, and those two words ARE its two branches.
  // Stating the degradation as `inForce := true, counter := false` is
  // therefore exact, and it is what makes the control honest — the
  // census is fed the degraded check, not spared it.
  if (ECHO_STUB) { inForce = true; counter = false; }
  // THE IN-FORCE ECHO is Attack B, run on purpose, ON THE HELPER, and
  // it MUST be applied before the in-force census sees the value —
  // measuring the undegraded branch and reporting the degraded one is
  // how the first echo would have lied if it had been written that way.
  if (INFORCE_STUB) inForce = true;
  if (CENSUS_ON) {
    let c = CENSUS.get(clause);
    if (!c) { c = { n: 0, inForceTrue: 0, counterTrue: 0 }; CENSUS.set(clause, c); }
    c.n++;
    if (inForce) c.inForceTrue++;
    if (counter) c.counterTrue++;
  }
  if (MISCENSUS_ON) {
    let m = MISCENSUS.get(clause);
    if (!m) { m = { n: 0, inForceFalse: 0 }; MISCENSUS.set(clause, m); }
    m.n++;
    if (!inForce) m.inForceFalse++;
  }
  if (K.SW[clause]) return inForce;
  LAST_COUNTER = clause;
  return counter;
};

// A clause guard is CONTENTFUL only if its counter-rule was true of a
// board the simulator actually built. Never asked at all counts as
// vacuous too: an unevaluated counter has said nothing.
const clauseIsContentful = (clause) => {
  const c = CENSUS.get(clause);
  return !!c && c.n > 0 && c.counterTrue > 0;
};
// …and it WATCHES only if its in-force reading ever said NO to a board
// the fault batch deliberately broke. A branch that is true of every
// board — a literal `true`, a tautology, a value the simulator wrote one
// statement earlier — never reaches this. THIS is the half that Attacks
// A and B walked through, and it is the half that runs in a real duel.
const clauseWatches = (clause) => {
  const m = MISCENSUS.get(clause);
  return !!m && m.inForceFalse > 0;
};
// The two halves together. A red earned through a clause guard is worth
// nothing unless BOTH of the guard's branches have been demonstrated to
// be predicates over state the simulator produced.
const clauseIsProof = (clause) => clauseIsContentful(clause) && clauseWatches(clause);
const misCensusRows = () => Array.from(MISCENSUS.entries())
  .map(([clause, m]) => ({ clause, ...m, watches: m.inForceFalse > 0 }))
  .sort((a, b) => (a.clause < b.clause ? -1 : 1));
const censusRows = () => Array.from(CENSUS.entries())
  .map(([clause, c]) => ({ clause, ...c, contentful: c.n > 0 && c.counterTrue > 0 }))
  .sort((a, b) => (a.clause < b.clause ? -1 : 1));

// A mutation that withdraws one clause. Everything else stays true.
const without = (name) => ({ SW: { [name]: false } });

const makeK = (over) => {
  const k = {
    ...ACT, ...(over || {}),
    SW: { ...CLAUSES, ...((over || {}).SW || {}) },
  };
  // ── IS THIS COPY PERTURBED AT ALL? ───────────────────────────
  // ONE BIT, and THE BLIND ECHO below is the only thing that reads it.
  // A check that watches nothing can still redden on cue, because every
  // mutation row hands the checker a perturbed K and then asks whether
  // the expected code went red — so the cheapest possible fake, the one
  // that reads no board and no constant and no clause but only "was I
  // mutated", scores exactly what the honest file scores. That fake is
  // now BUILT AND RUN (see runBlindEcho), and it needs this bit to
  // exist. Non-enumerable so no spread, no census and no assertion can
  // pick it up by accident.
  Object.defineProperty(k, "__clean", {
    value: !over || Object.keys(over).length === 0, enumerable: false,
  });
  return k;
};

// R88's Grimoire fare is a LEVEL TABLE, not a flat number. Reading it
// through K (never through the imported `grimSearchFare`, which closes
// over the true table) is what puts the by-level fare inside the
// fake-detector instead of beside it. `grimSearchFare` is still used —
// as the ORACLE the K-side lookup is checked against.
const grimFareOf = (K, level) => K.GRIM_SEARCH_COST[`L${level || 1}`] ?? K.GRIM_SEARCH_COST.L3;
// R101 · the same discipline for the Deviant fare, clamped exactly as
// `deviantFare` clamps so the two cannot disagree about out-of-range.
// R35 · the wait, read through K, in the unit the catalogue declares it
// in. `tomeWaitHalfTurns` is still used — as the ORACLE below.
const tomeWaitOf = (K, level, faceDown) => {
  if (!faceDown) return 0;
  const full = (K.TOME_WAIT_FULL_TURNS || {})[`L${level || 1}`] ?? 0;
  return durationHalfTurns(full, SEATS);
};
const devFareOf = (K, shared, level) => {
  const row = (K.DEVIANT_FARE || {})[Math.max(0, Math.min(2, Number(shared) || 0))] || {};
  return row[Math.max(1, Math.min(3, Number(level) || 1))] || { khao: 0, anima: 0 };
};
const searchFareOf = (K, zone, level) => (zone === "luxmoire" ? K.LUX_SEARCH_COST : grimFareOf(K, level));

// ── R96 · THE TILL LAW, and the one exemption ────────────────
// A voluntary payment may never take your own Anima to 0. The single
// exemption is the Graveshift fare (R41), because R43's remedy has to
// stay payable. `canPayVoluntary` is imported rather than reimplemented
// so the Debugger and the engine cannot drift on the boundary case.
const tillClears = (anima, amount, exempt) => canPayVoluntary(anima, amount, { exempt: !!exempt });

// ── R107 · ONE POOL OR THE OTHER, NEVER A SPLIT ──────────────
// *"You may never mix Khao and Anima. Every fare in Terror is paid
// entirely in one pool or entirely in the other."* This file used to ask
// `khao + anima >= fare` in five Legal gates and then PAY the difference
// across both pools in five more places — so a seat with 1 Khao and 3
// Anima could pay a 4-fare here and could not on the shipping board.
// R107 was on the catalogue and watched by nothing in this simulator.
//
// `farePools` is the one answer, and it carries R96 on the Anima leg for
// free: a voluntary Anima payment must leave you above zero.
const farePools = (p, fare, exempt = false) => {
  const out = [];
  if ((p.khao || 0) >= fare) out.push("khao");
  if (tillClears(p.anima || 0, fare, exempt)) out.push("anima");
  return out;
};
// ── R100 · THE TWO LEDGERS, AND THE ONLY TWO DOORS TO THEM ───
// caP'n: *"dimming your own Anima in any way at all does not satisfy
// Prophecy requirements"*. So an Anima movement has a KIND, and the kind
// decides which counter it lands in. `animaLossFeedsProphecy` is imported
// rather than reimplemented so this file and prophecyEngine cannot drift
// on the classification, and the kinds are NAMED at every call site so a
// site reads as a classification and not as a flag somebody forgot.
//
// ⚠ EVERY ANIMA MOVEMENT IN THIS FILE GOES THROUGH ONE OF THESE TWO.
// Before they existed the file wrote `p.anima -= n` in nine places and
// kept no ledger at all, which is why R100 was declared unreachable.
const dimAnima = (p, amount, kind) => {
  const n = Math.max(0, Number(amount) || 0);
  p.anima = Math.max(0, p.anima - n);
  if (animaLossFeedsProphecy(kind)) {
    p.totalAnimaLost = (p.totalAnimaLost || 0) + n;
    p.animaLostThisTurn = (p.animaLostThisTurn || 0) + n;
  } else {
    p.totalAnimaSelfDimmed = (p.totalAnimaSelfDimmed || 0) + n;
    p.animaSelfDimmedThisTurn = (p.animaSelfDimmedThisTurn || 0) + n;
  }
  return n;
};
const gainAnima = (p, amount, cap) => {
  const n = Math.max(0, Number(amount) || 0);
  const before = p.anima;
  p.anima = Math.min(cap, p.anima + n);
  p.animaGainedThisTurn = (p.animaGainedThisTurn || 0) + (p.anima - before);
  return p.anima;
};

// Spend one fare out of ONE pool: Khao first, Anima as the fallback,
// which is the order every payment site in Shatterfield.js and NPC.js
// uses. Returns the pool it came from, or null when neither can carry it.
// ⚠ THE ANIMA LEG IS A SELF-DIM (R100) — a voluntary payment feeds no
// Prophecy, and this is the funnel every fare in the file passes through.
const payOnePool = (p, fare, exempt = false) => {
  const pools = farePools(p, fare, exempt);
  if (!pools.length) return null;
  if (pools[0] === "khao") { p.khao -= fare; p.khaoSpentThisCycle = true; return "khao"; }
  dimAnima(p, fare, ANIMA_LOSS_KINDS.SELF);
  return "anima";
};

// ── R08 · THE OPENING CYCLE, AS THIS HARNESS SEES IT ─────────
// SEATS is a fact about the SIMULATOR, not a rule: `simulateGame`
// builds exactly two players and steps `turn` as half-turns, so the
// Cycle here is `openingCycleLength(2)` long. The length itself is
// still the catalogue's, which is the point — if rules.js ever changes
// what an opening Cycle is, this file follows instead of disagreeing.
const SEATS = 2;
const inOpeningCycle = (turn) => isOpeningTurnIndex(turn, SEATS);
// THE WINDOW AS THE CHECKER SEES IT. In force it is the catalogue's
// Cycle; with R08_WINDOW withdrawn the Cycle never closes, which is
// what gives every "…and it is legal again afterwards" assertion a
// failing state. A permission asserted against a bar that can only be
// REMOVED is decoration, and decoration is what this file exists to
// refuse to count.
const openingBarApplies = (K, turn) => (K.SW.R08_WINDOW ? inOpeningCycle(turn) : true);
// ⚠ AN OMITTED TURN READS AS "NOT THE OPENING CYCLE" — `Number(undefined)`
// is NaN and `isOpeningTurnIndex` refuses NaN. Every call site in this
// file passes a turn, and the benches below assert BOTH directions of
// every window so a call site that quietly stopped passing one shows up
// as a permission that never closes.

// ── R08 · WHICH PRIMERA STAMPS ATTACK ────────────────────────
// Derived from the stamp's OWN PRINTED EFFECT in PRIMERAS, never from a
// hand-typed pair of words. RulesEnforcer spells the same two out
// literally (`school/Phantom || ilk/Beast`); this file is a second
// implementation and a second implementation that copies the first
// proves nothing. Reading the effect text means a new stamp that dims
// an adversary's Anima is caught here the day it is printed, and — far
// more usefully — the two files DISAGREE loudly if the engine's literal
// pair ever falls behind the card data.
const stampAttacks = (axis, word) => {
  const row = (PRIMERAS[axis] || {})[word];
  return /adversar(y's|ies') anima/i.test(String((row && row.effect) || ""));
};

// ── R08 · WHICH TOMES ATTACK ─────────────────────────────────
// Same discipline one zone over: `fx` is already derived from the
// printed effect by deriveFx, so a Tome that RUINS something of the
// adversary's is an Attack and nothing else in the modelled set is.
// A Tome that heals, draws or raises Khao touches only its own side and
// R08 has never had an opinion about what you do to yourself.
const tomeAttacks = (card) => !!card && (card.fx === "ruin_being" || card.fx === "ruin_tome");

// ─────────────────────────────────────────────────────────────
// SEEDED PRNG — a failure nobody can reproduce is a rumour.
// The seed is surfaced in the report and typed back into the box.
// ─────────────────────────────────────────────────────────────
function mulberry32(a) {
  let s = a >>> 0;
  return function () {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ─────────────────────────────────────────────────────────────
// CARD DB — one row per printed card, unified from cards.js.
// (This used to carry a defect note: cards.js typed the Luxmoire
// attachment cards PRIMERA, so the import boundary had to alias the
// array and every row below had to re-label it IMPERA. cards.js types
// them Impera at the source now — R78's Primera is the innate STAMP and
// never a card — so the correction is gone and nothing is re-labelled.)
// ─────────────────────────────────────────────────────────────
const T = CARD_TYPES;
const CARDS = [
  ...ESCENDANTS.map((c) => ({
    id: c.id, name: c.name, type: T.ESCENDANT,
    school: c.school, ilk: c.ilk, level: c.level,
    lyt: c.lyt ?? 0, lyf: c.lyf ?? 0, deckLimit: c.deckLimit,
  })),
  ...TOMES.map((c) => ({
    id: c.id, name: c.name, type: T.TOME,
    ilk: c.ilk, level: c.level,
    khaoCost: c.khaoCost ?? 0, animaCost: c.animaCost ?? 0,
    deckLimit: c.deckLimit, effectTag: c.effectTag || null,
    effect: c.effect || "",
    fx: deriveFx(c),
    persistent: PERSISTENT_TOME_TAGS.includes(c.effectTag),
  })),
  ...ESCLINGS.map((c) => ({
    id: c.id, name: c.name, type: T.ESCLING,
    school: c.school, ilk: c.ilk, level: 0,
  })),
  // Impera — Luxmoire attachments, R71 / R94.
  ...IMPERA.map((c) => ({
    id: c.id, name: c.name, type: T.IMPERA,
    level: c.level, owner: c.owner, school: c.school, ilk: c.ilk,
    khaoCost: c.khaoCost ?? 0, deckLimit: c.deckLimit,
  })),
  ...UHLTIS.map((c) => ({
    id: c.id, name: c.name, type: T.UHLTI,
    owner: c.owner, level: c.level,
    khaoCost: c.khaoCost ?? 0, animaCost: c.animaCost ?? 0, deckLimit: c.deckLimit,
  })),
  ...AFFINITIES.map((c) => ({
    id: c.id, name: c.name, type: T.AFFINITY,
    affinityType: c.affinityType, deckLimit: c.deckLimit,
  })),
  ...PROPHECIES.map((c) => ({ id: c.id, name: c.name, type: T.PROPHECY, deckLimit: c.deckLimit })),
  ...CURIOS.map((c) => ({ id: c.id, name: c.name, type: T.CURIO, ilk: c.ilk, deckLimit: c.deckLimit })),
];
const CARD_BY_ID = new Map(CARDS.map((c) => [c.id, c]));
const getCard = (id) => CARD_BY_ID.get(id) || null;

// Simulator effect hint, derived from the printed effect text.
function deriveFx(card) {
  const e = (card.effect || "").toLowerCase();
  if (/\+1 anima/.test(e)) return "anima+1";
  if (/\+2 anima/.test(e)) return "anima+2";
  if (/\+5 anima/.test(e)) return "anima+5";
  if (/\+1 to your max khao/.test(e)) return "khao+1";
  if (/\+2 to your max khao/.test(e)) return "khao+2";
  if (/ruin 1 tome|ruin a tome/.test(e)) return "ruin_tome";
  if (/ruin 1 being|ruin a being/.test(e)) return "ruin_being";
  if (/draw 1 card|draw one card/.test(e)) return "draw+1";
  return "noop";
}

// ── PRIMERA STAMPS (R78) ─────────────────────────────────────
// Two innate words per Escendant, one per axis. This used to search
// BOTH stamp tables for every word, because cards.js stored the two
// descriptors under inverted field names — its `school` values keyed
// rules.js `PRIMERAS.ilk` and vice versa — and searching both was the
// only way to test the stamps rather than the label. The data was
// fixed on 2026-08-25 (Ilk = Humanoid/Beast/Machine/Dragon, School =
// Space/Phantom/Dragon/Time, each under its own name), so each axis
// now reads its own table directly. Order is Ilk first, School second —
// the top-left stamp, then the top-right — which is the order
// `stampsOnSlot` slices when R84 is switched off.
function stampsOf(card) {
  if (!card || card.type !== T.ESCENDANT) return [];
  // ⚠ NO `misact` HERE ANY MORE, AND THE REASON IS THE WHOLE OF ATTACK D.
  // The STAMP_ONE_AXIS fault used to live on this line. `stampsOf` is a
  // PURE FUNCTION OF cards.js — nothing stores its result, every reader
  // recomputes it — so a fault injected here changed NO BOARD STATE at
  // all, and R78's assertion caught it by calling this same function one
  // line later. That is ATTACK D out of this file's own banner ("an
  // assertion may read the fault switch instead of the board"), and it
  // was live: R78's WATCHED credit was `assert(!misact(...))` in
  // disguise, and `Legal.primera` read the switch too, which the
  // banner's grep discipline forbids in as many words.
  // The fault is now in `seatStamps` below, which writes a FIELD ON THE
  // RIFT, and every check reads the rift.
  return ["ilk", "school"]
    .map((axis) => {
      const w = card[axis];
      const s = w ? PRIMERAS[axis][w] : null;
      // The AXIS travels with the stamp now. "Dragon" is a word on both
      // axes and they are different stamps with different effects, so a
      // gate handed only the word cannot tell an Attack from a buff.
      return s ? { word: w, axis, id: s.id } : null;
    })
    .filter(Boolean);
}

// ── R84 · THE STAMPS A BEING IS CURRENTLY CARRYING ───────────
// Read off the SLOT, not off the printed card, and that distinction is
// the whole reason R84 is provable at all. `stampsOf(card)` is a pure
// function of cards.js: asserting its length is 2 asks a question about
// the CARD DATABASE (which is R78's job) and can never be contradicted
// by anything that happens on a board. R84 is a claim about the board —
// that nothing done to a being takes a stamp off it — so it has to be
// asked of the being. The counter-rule is the one every player expects
// on first reading, and the one R84 exists to refuse: that going Deviant
// costs you the School half of your inheritance.
function stampsOnSlot(K, slot) {
  // THE RIFT IS THE SOURCE. This used to call `stampsOf(getCard(...))`,
  // i.e. it answered a question about cards.js while claiming to answer
  // one about a board — which is exactly what the comment above says it
  // must not do. `slot.stamps` is written by `seatStamps` the moment a
  // being arrives and by nothing else.
  const all = Array.isArray(slot?.stamps) ? slot.stamps : [];
  if (K.SW.R84) return all;
  return slot?.deviant ? all.slice(0, 1) : all;
}

// ── R78 / R84 · THE STAMPS ARE WRITTEN ONTO THE RIFT ─────────
// One function, called wherever a being arrives in a rift, and THE ONLY
// PLACE THE STAMP FAULT LIVES. A being's two Primeras are of the flesh
// (R84), so the board has to carry them or no check can watch anything
// take one off.
function seatStamps(slot) {
  const c = slot?.escendant ? getCard(slot.escendant.id) : null;
  const all = stampsOf(c);
  // MISACT · STAMP_ONE_AXIS — the fault R78 forbids: a being arrives
  // bearing its Ilk's stamp only, its School a matching key and nothing
  // more. It writes a rift field, so the checks that catch it are
  // reading a board and not this switch.
  slot.stamps = misact("STAMP_ONE_AXIS") ? all.slice(0, 1) : all;
  return slot.stamps;
}

// ─────────────────────────────────────────────────────────────
// THE HARNESS — the only way a rule code ever enters the report.
// `assert` REGISTERS the code on execution, so the coverage number is
// a record of what ran, not a promise about what exists.
// ─────────────────────────────────────────────────────────────
// Fold one game's tally into the batch's, ATTRIBUTION AND ALL. A merge
// that dropped `counterFails` would quietly hand every clause-sourced
// red back its old, unearned credit.
function mergeReach(map, code, v) {
  const cur = map.get(code) || { hits: 0, fails: 0, counterFails: 0, stateFails: 0, viaClause: [] };
  map.set(code, {
    hits: cur.hits + v.hits,
    fails: cur.fails + v.fails,
    counterFails: (cur.counterFails || 0) + (v.counterFails || 0),
    stateFails: (cur.stateFails || 0) + (v.stateFails || 0),
    viaClause: Array.from(new Set([...(cur.viaClause || []), ...(v.viaClause || [])])),
  });
}

function makeHarness(seed, K, opts) {
  const o = opts || {};
  return {
    seed,
    K,
    rng: mulberry32(seed),
    reached: new Map(),   // code -> { hits, fails, counterFails, stateFails, viaClause }
    refuted: new Map(),   // code -> { tried, refused }  — see refutes()
    violations: [],
    invariants: [],       // engine invariants — NOT rule coverage
    log: [],
    note(msg, type) { if (o.verbose) this.log.push({ msg, type: type || "info" }); },
    reg(code) {
      let r = this.reached.get(code);
      if (!r) { r = { hits: 0, fails: 0, counterFails: 0, stateFails: 0, viaClause: [] }; this.reached.set(code, r); }
      r.hits++;
      return r;
    },
    // ok === true  -> the rule held.  ok === false -> violation.
    //
    // WHERE A RED CAME FROM IS PART OF THE RECORD. `ruleIs` leaves the
    // name of the clause whose COUNTER branch produced this value, and
    // it is consumed here — read once, cleared always, whether the
    // assertion passed or not. A red carrying a clause name is a red the
    // MUTATION manufactured; a red with no clause name is a red the
    // BOARD produced. Only the second kind, and the first kind through a
    // clause the census found contentful, may be called proven.
    assert(code, ok, detail, phase) {
      // ── THE BLIND ECHO, applied at the ONE place every verdict in
      // this file passes through. `ok` is thrown away and replaced by
      // the cheapest fake that can exist here: "was the checker's copy
      // perturbed at all". It reads no board, no card, no constant and
      // no clause. Turning it on and re-running the whole apparatus is
      // how this file now measures what its own mutation batch is
      // worth — see runBlindEcho and THE BLIND ECHO banner.
      if (BLIND_ECHO) ok = !!(this.K && this.K.__clean);
      const viaCounter = LAST_COUNTER; LAST_COUNTER = null;
      const r = this.reg(code);
      if (!ok) {
        r.fails++;
        if (viaCounter) {
          r.counterFails++;
          if (!r.viaClause.includes(viaCounter)) r.viaClause.push(viaCounter);
        } else {
          r.stateFails++;
        }
        // ⚠ THE BLIND ECHO REDDENS EVERY ASSERTION IN EVERY MUTATION
        // ROW, so keeping its violation text would build a six-figure
        // array and a matching log nobody reads — it took the pass from
        // 0.4s to 15s the first time. The COUNTERS are what the control
        // reads; the prose is dropped.
        if (!BLIND_ECHO) {
          const v = { code, detail, phase: phase || "sim", severity: "error" };
          this.violations.push(v);
          this.log.push({ msg: `⚠ ${code}: ${detail}`, type: "violation" });
        }
      }
      return ok;
    },
    // Negative testing: `didHappen` is true when an ILLEGAL action was
    // wrongly permitted. Rejection is the pass condition.
    reject(code, didHappen, detail) {
      return this.assert(code, !didHappen, `ILLEGAL ACTION ACCEPTED — ${detail}`, "adversarial");
    },
    // ── THE COUNTER-RULE BENCH ────────────────────────────────
    // `verdict` is what the SAME NAMED PREDICATE the duel uses says
    // about a board hand-built to BREAK this rule — the board the
    // counter-rule describes and the simulator will never produce. It
    // must be FALSE. A predicate that blesses a board built to break its
    // own rule is a tautology, and this is the only place in the file
    // that can catch one.
    //
    // The discipline it rests on is ONE SHARED FUNCTION: the duel and
    // the bench must call the same `holdsRxx`, or the bench proves
    // something about a predicate nobody uses. That is enforced by
    // construction — the predicates live in one block, above both
    // callers — and it is the residual trust in this mechanism, stated
    // plainly rather than hidden.
    refutes(code, verdict, detail) {
      const r = this.refuted.get(code) || { tried: 0, refused: 0 };
      r.tried++; if (!verdict) r.refused++;
      this.refuted.set(code, r);
      return this.assert(code, !verdict,
        `TAUTOLOGY — the check blessed a board built to BREAK the rule: ${detail}`, "counter-bench");
    },
    // Engine invariants have no R-code and are never counted as rule
    // coverage. Reporting them as rules would be the same lie again.
    inv(name, ok, detail) {
      if (!ok) this.invariants.push({ name, detail });
      return ok;
    },
  };
}

// ─────────────────────────────────────────────────────────────
// LEGALITY — ONE implementation, used by BOTH the simulator (which
// only acts when it returns null) and the adversarial suite (which
// asserts it returns non-null for a deliberately illegal request).
// A predicate that is wrong is therefore wrong in both directions and
// cannot quietly pass one while failing the other.
// Returns null when legal, or the R-code that forbids it.
// ─────────────────────────────────────────────────────────────
const Legal = {
  transcend(K, p, slotIdx, cardId, turn) {
    const slot = p.field.esclings[slotIdx];
    if (!slot) return "R04";
    if (slot.escendant) return "R16";
    const c = getCard(cardId);
    if (!c || c.type !== T.ESCENDANT || c.level !== 1) return "R16";
    // R09 and R17 are PERMISSIONS, so their counter-rules ADD a
    // prohibition rather than dropping one: withdrawing R09 makes an
    // opening-Turn Transcend illegal, withdrawing R17 caps the Turn at
    // one, and the assertions that demand both be allowed go red.
    if (!K.SW.R09 && (turn || 0) === 0) return "R09";
    if (!K.SW.R17 && (p.transcendedThisTurn || 0) >= 1) return "R17";
    const esc = getCard(slot.escling);
    if (!esc) return "R16";
    // ⚠ SCHOOL ALONE IS NOT NATURAL. RULEZ_V2 ch.7: *"If both Ilk and
    // School match, the Transcend is Natural and costs nothing. If only
    // the School matches, it is a DEVIANT Transcend — also free, but the
    // being is Deviant forever and will never wear an U'hlti or an
    // Affinity."* This gate checked School only and the simulator then
    // stamped `natural: true`, so a School-only climb produced a being
    // this file would happily hand an U'hlti to. It has never fired
    // because every starter deck matches on both — luck, not correctness.
    if (K.SW.R16 && esc.school !== c.school) return "R16"; // school must match the Escling
    return null;
  },
  deviantTranscend(K, p, slotIdx, cardId) {
    const slot = p.field.esclings[slotIdx];
    if (!slot || slot.escendant) return "R65";
    const c = getCard(cardId);
    if (!c || c.type !== T.ESCENDANT || c.level !== 1) return "R65";
    // R65 is a PERMISSION. Its counter-rule refuses the cross-School
    // Transcend outright — the only thing Deviance is FOR — and the
    // assertion that demands it be allowed goes red.
    if (!K.SW.R65) {
      const esc = getCard(slot.escling);
      if (!esc || esc.school !== c.school) return "R65";
    }
    return null; // R65: costs nothing, but marks Deviant
  },
  ascend(K, p, slotIdx, cardId, deviant) {
    if (K.SW.R18 && p.ascendedThisTurn >= 1) return "R18";
    const slot = p.field.esclings[slotIdx];
    if (!slot || !slot.escendant || slot.fallen) return "R18";
    const cur = getCard(slot.escendant.id);
    const up = getCard(cardId);
    if (!cur || !up || up.type !== T.ESCENDANT) return "R19";
    if (K.SW.R19 && up.level !== cur.level + 1) return "R19";
    if (deviant) {
      if (K.SW.R66 && p.deviantAscendedThisTurn >= 1) return "R66";
      // ── R21 / R101 · THE FARE IS A TABLE, NOT A COIN ─────────
      // ⚠ THIS LINE TYPED A FLAT `1` AND ACCEPTED EITHER POOL, against a
      // table that charges 2/3/5 Khao or 2/3/4 Anima by shared traits
      // and DESTINATION level (RULEZ_V2 ch.15; the L3/0-trait square is
      // 5 Khao OR 4 Anima and is the only fare in the game where Anima
      // is the cheaper coin). A seat holding 1 Anima was waved through a
      // 3-fare, and the typed `1` broke THE CATALOGUE LAW this file
      // quotes in its own banner. It also skipped R96: paying 1 of 1
      // lands you on zero, which a voluntary payment may never do.
      // THE LEVEL IS THE DESTINATION — caP'n's instruction, and the
      // reading all three board implementations settled on.
      const shared = (cur.school === up.school ? 1 : 0) + (cur.ilk === up.ilk ? 1 : 0);
      const fare = devFareOf(K, shared, up.level);
      if (K.SW.R21 && (p.khao || 0) < fare.khao && !tillClears(p.anima || 0, fare.anima, false)) return "R21";
      return null;
    }
    if (K.SW.R19 && (up.school !== cur.school || up.ilk !== cur.ilk)) return "R19";
    return null;
  },
  // ── R08 · THE BROAD ATTACK ────────────────────────────────
  // caP'n, 2026-08-25: *"'Attack' in Terror is a very broad term that
  // can mean you used a tome or Impera or anything to 'attack' a being
  // or your adversary directly, Lyt Attack is the actual move every
  // being gets 1 of per turn."*
  //
  // So this is ONE door for every route that hurts the other side, and
  // it deliberately does not care WHICH route: a Tome, an Impera, a
  // U'hlti, a Curio, a Primera stamp, an Affinity, a Prophecy or a bare
  // effect all get the same answer. `kind` is carried for the message
  // and for the bench, which walks the whole of ATTACK_KINDS through
  // this gate; it is never read as a permission. Outside the opening
  // Cycle this passes, so it can be put in front of any damaging effect
  // without that effect having to know what else applies to it.
  attack(K, ctx) {
    const { turn } = ctx || {};
    if (K.SW.R08_BROAD && openingBarApplies(K, turn)) return "R08";
    return null;
  },
  // ── R08 · THE BACKSTOP, and why classification is not enough ──
  // Not every route declares itself an Attack. There is no list of
  // attacking cards in Terror and there should not be one — a curated
  // id set is a second catalogue that drifts the first time somebody
  // writes a new effect. rules.js answers that by recognising the
  // DAMAGE at the one place damage is written, and this file asks the
  // engine's own predicate rather than growing a rival one: an
  // adversary's Anima going down, an adversary's Necropolis growing, or
  // an adversary's being losing Lyf, on the opening Cycle, is an Attack
  // whatever card claims to have done it. Raising their Anima or
  // dimming your OWN is not, and the bench asserts both directions.
  openingDamage(K, turn, before, updates) {
    if (!K.SW.R08_BROAD) return null;
    if (!openingBarApplies(K, turn)) return null;
    // IN FORCE: only DAMAGE is an Attack. R08_HARM withdrawn: any
    // payload at all is — the over-broad reading in which handing the
    // adversary a card, or raising their Anima, is treated as hurting
    // them. That is what gives "an Anima GAIN is not an Attack" a
    // failing state instead of leaving it as a comment with an assert
    // wrapped round it.
    const harm = K.SW.R08_HARM
      ? adversaryDamageInPayload(before, updates)
      : (updates && Object.keys(updates).length ? "any payload at all" : null);
    return harm ? "R08" : null;
  },
  lytAttack(K, ctx) {
    const { turn, slot, target } = ctx;
    if (K.SW.R08 && openingBarApplies(K, turn)) return "R08";  // no attack in the opening cycle
    // ⚠ AN ESCLING MAY SWING AT A BEING. RULEZ_V2 ch.7: *"Esclings can
    // attack: they may Lyt-attack opposing Escendants with their printed
    // Lyt. They may never target Anima."* This refused a bare Escling and
    // cited R30 for it, which R30 does not say — R30 is one swing per
    // being per Turn. `animaAttack` below is the gate that really does
    // refuse an Escling, and it still does.
    if (!slot || (!slot.escendant && !slot.escling)) return "R30";
    // ── R30 + R124 · THE ALLOWANCE IS A COUNT, NOT A FLAG ────
    // ⚠ THIS WAS `slot.attackedThisTurn`, a boolean, so the allowance was
    // always exactly one — and TWO SHIPPED CARDS PRINT AN EXCEPTION.
    // King of the Anthill grants a swing per other Antdroid in combat;
    // Sword of Shadows grants its wielder one through `slot.extraAttack`.
    // The board answers `rules.lytAttacksAllowed`, which reads the
    // attacker's own rift and both battle lines; this file called a
    // legal second swing an R30 violation. `attackedThisTurn` is still
    // written the moment the first swing is spent, because three
    // auditors read it.
    const allowed = K.SW.R124
      ? lytAttacksAllowed(slot, ctx.myRifts || [], ctx.oppRifts || [], (id) => getCard(id), ctx.slotIdx ?? null)
      : 1;
    if (K.SW.R30 && (slot.attacksThisTurn || 0) >= allowed) return "R30";
    if (K.SW.R25 && slot.fallen) return "R25";            // a Fallen being cannot act
    // ── R121 · THE MIND'S SILENCE ────────────────────────────
    // caP'n, 2026-09-07: with Body, Sol and Heart all holding
    // ESCENDANTS, the being in the Mind rift *"cannot perform any arts,
    // attacks, etc of any kind at alllll except for the Warden Rift
    // Primera"*. A Lyt Attack is the most obvious thing it may not do.
    // ⚠ IT WAS ABSENT FROM THIS FILE ENTIRELY while RulesEnforcer and
    // all three NPC brains honoured it — so this simulator would swing
    // a Mind the board refuses. A FALLEN Escendant still occupies its
    // rift for the trio count; `trioOccupied` carries that.
    // R121_LOCK withdrawn installs the reading that forgets R115's
    // three-Turn lock — the Mind is silent from half-turn 0 — which is
    // the failing state the PERMISSION half needs: without it, "an
    // ordinary being before the Arts wake" is asserted against a bar
    // that can only be removed.
    if (K.SW.R121 && ctx.slotIdx === MIND_RIFT && Array.isArray(ctx.myRifts)
        && !mindMayAct(ctx.myRifts, "ATTACK", K.SW.R121_LOCK ? turn : roleLockHalfTurns(SEATS), SEATS)) return "R121";
    if (!target || !target.escendant) return "R25";
    if (K.SW.R25 && target.fallen) return "R25";          // R25: Fallen is impervious
    return null;
  },
  animaAttack(K, ctx) {
    const { turn, slot, oppField } = ctx;
    // ⚠ THE DIVERGENCE THAT USED TO STAND HERE IS CLOSED, and it closed
    // in this file's favour, which is worth recording because it is the
    // only reason to keep two implementations. The note that stood here
    // said the shipping board refused only `attackType === "Lyt"`, that
    // validateAnimaAttack DECLARED `currentTurn` and read it NOWHERE, and
    // that this file was therefore probably over-strict in refusing the
    // Anima route on the opening Cycle. It was not over-strict. caP'n's
    // 2026-08-25 ruling made "Attack" the broad word, the engine now
    // refuses the Anima strike (and the Phantom stamp's direct dim)
    // BEFORE the R31 bypass, and tools/openingTurn.selftest.mjs guards
    // it. The unread parameter was the bug; this line was the finding.
    // The window is the catalogue's now rather than a typed `2`.
    if (K.SW.R08 && openingBarApplies(K, turn)) return "R08";
    if (!slot || !slot.escendant) return "R30";
    if (K.SW.R30 && slot.fallen) return "R30";
    // R124 reaches the Anima route too — RULEZ_V2 ch.6 is titled for the
    // SWING, and an Anima strike is a Lyt Attack that met no wall.
    const allowedA = K.SW.R124
      ? lytAttacksAllowed(slot, ctx.myRifts || [], ctx.oppRifts || [], (id) => getCard(id), ctx.slotIdx ?? null)
      : 1;
    if (K.SW.R30 && (slot.attacksThisTurn || 0) >= allowedA) return "R30";
    // ── R31 + R119 · ONE PREDICATE, THE ENGINE'S OWN ─────────
    // ⚠ THIS LINE WAS THE LAST PRIVATE COPY OF THE PRE-R119 WALL IN THE
    // CODEBASE: a flat `.some` over every rift, which was right for three
    // rifts and wrong the day a fourth arrived. A being in the MIND rift
    // never walls the Anima route once the Arts wake (R119), so a
    // defender whose only survivor is a Warden has an OPEN board and this
    // gate called it closed from half-turn 6 onward. RulesEnforcer,
    // Shatterfield and tools/balance.mjs all ask
    // `blocksAnimaLytAttack`; so does this now.
    // R26's counter-rule — that Fallen STILL blocks, shielding its seat
    // through its owner's own Turn — is carried by hiding the flag from
    // the predicate, so the all-Fallen bench keeps its failing state.
    const wall = (oppField.esclings || []).some((s, j) => blocksAnimaLytAttack(
      { escendant: s.escendant, fallen: K.SW.R26 ? s.fallen : false }, j, turn, SEATS));
    if (K.SW.R31 && wall) return "R31";
    return null;
  },
  placeTome(K, p) {
    const free = p.field.tomes.findIndex((t) => t === null);
    if (free === -1) return "R38";                       // R38: TOME_SLOTS is the ceiling
    return null;
  },
  activateTome(K, p, tomeIdx, turn, inResponseWindow) {
    const t = p.field.tomes[tomeIdx];
    if (!t) return "R38";
    const c = getCard(t.id);
    if (!c) return "R38";
    const key = `L${c.level}`;
    // ── R35 · THE WAIT IS A PRICE ON SETTING ─────────────────
    // caP'n, 2026-09-13: *"Yes we can, but if you place it face down,
    // you must wait 1 full turn to activate it."* ⚠ THIS READ THE FLAT
    // `TOME_WAIT` MAP OFF `card.level` ALONE — the exact shape rules.js
    // records fixing, under which an L1 Tome played FACE-UP from hand
    // was refused outright against Chapter 13's own sentence. A Tome
    // played face-up is being activated, not set, and waits for nothing.
    // `faceDown` defaults TRUE, which is the strict direction.
    const wait = K.SW.R35
      ? tomeWaitOf(K, c.level, t.faceDown !== false)
      : (K.TOME_WAIT[key] ?? 0);
    const waited = turn - t.placedTurn;
    // R35 (L1 waits a full turn) / R36 (L2 may answer on the adversary's
    // turn) / R37 (L3 answers from hand). Wait times are data, not code.
    if (waited < wait) return c.level === 1 ? "R35" : c.level === 2 ? "R36" : "R37";
    if (K.SW.R36 && inResponseWindow && c.level === 1) return "R36";
    // R08 · A TOME THAT RUINS SOMETHING OF THEIRS IS AN ATTACK. R09
    // still permits activation on the opening Cycle, so this refuses
    // the attacking Tomes and only those — a Tome that heals, draws or
    // raises Khao stays legal on half-turn 0 exactly as R09 says.
    if ((K.SW.R08_KIND ? tomeAttacks(c) : true) && Legal.attack(K, { turn, kind: "Tome" })) return "R08";
    // ── R15 + R105 · THE PRINTED FARE, BOTH POOLS, EVERY TIME ──
    // ⚠ THIS ASKED `p.khao < c.khaoCost` AND NOTHING ELSE. `animaCost`
    // was imported into this file's card model and never read again, and
    // FOUR SHIPPED TOMES PRINT ONE (Nothing Left k3/a1, Radiant Armor
    // k2/a1, Superque k8/a1, Lyt Hearted k4/a1) — so the second
    // implementation played a game in which those four were cheaper than
    // printed. That is trap §4.8, still live in the auditor after the
    // board had fixed it. `canPayPrintedFare` carries R96 on the Anima
    // leg for free: a voluntary payment may never take you to 0.
    // R105 (caP'n 2026-09-13) — paid on the FIRST activation and EVERY
    // one after it. A persistent tag buys the right to STAY, not to fire
    // for free, so there is no first-activation branch here to find.
    if (K.SW.R15 && !canPayPrintedFare(c, { khao: p.khao, anima: p.anima })) return "R15";
    if (K.SW.R39 && t.usedThisTurn && !c.persistent) return "R39";  // R39 / B01
    // ── R103 · A FULL TURN IS TWO HALF-TURNS ─────────────────
    // caP'n: *"once per turn ... is referring to combination of the user
    // who played the card (1/2 turn) ending then the turn of their
    // opponent ending, thus creating 1 full turn."* So OPT = 2
    // half-turns, OP2 = 4, OP3 = 6, and at a 3-4 seat table a full Turn
    // is a full LAP. ⚠ THIS SIMULATOR HAD NO CADENCE AT ALL — a
    // persistent Tome fired on every one of its owner's half-turns
    // regardless of its tag, which made OPT and OP2 indistinguishable
    // here exactly as they were on the board before R103 landed.
    // The counter-reading is the pre-R103 one: compare against the RAW
    // tag number, so OP2's 4 half-turns become 2 and the tags collapse.
    if (c.persistent && t.lastFiredTurn != null) {
      const need = K.SW.R103
        ? cooldownHalfTurns(c.effectTag, SEATS)
        : (c.effectTag === "OP3" ? 3 : c.effectTag === "OP2" ? 2 : 1);
      if ((turn - t.lastFiredTurn) < need) return "R103";
    }
    return null;
  },
  // R37 · a Level 3 Tome answers FROM HAND during the adversary's turn.
  // Nothing else may, and that refusal is the whole of R37.
  activateFromHand(K, p, cardId, inResponseWindow) {
    const c = getCard(cardId);
    if (!c || c.type !== T.TOME) return "R37";
    if (K.SW.R37 && !inResponseWindow) return "R37";
    if (K.SW.R37 && c.level !== 3) return "R37";
    if (K.SW.R15 && !canPayPrintedFare(c, { khao: p.khao, anima: p.anima })) return "R15";
    return null;
  },
  graveshift(K, p) {
    if (K.SW.R40 && p.grimoire.length > 0) return "R40"; // R40: ONLY when empty
    // R43 is a PERMISSION — the remedy that makes deck-out survivable.
    // Its counter-rule withdraws the remedy entirely, and the assertion
    // that the seat which needs it most may still shift goes red.
    if (!K.SW.R43) return "R43";
    // R96 EXEMPT — and this is the reversal, not an oversight. rules.js
    // has now ruled: the Graveshift fare is the ONE voluntary payment
    // allowed to empty your own Anima, because R43's remedy must always
    // be payable. A seat on exactly the fare may still shift; if that
    // lands it on 0, R33 collects. Forbidding it would have made R96 a
    // lock-maker inside the very pass that broke the lock.
    if (!tillClears(p.anima, K.GRAVESHIFT_COST, true)) return "R41";
    return null;
  },
  // ── R07 / R08 / R11 · THE OPENING CYCLE'S DRAWS ───────────
  // caP'n, 2026-08-25: *"Both players can draw 1 card that turn from
  // their Luxmoire... Player to go second can draw from their Grimoire
  // but not search it, and as always, if you draw from one Book(deck)
  // you cannot draw from the other that turn (card effects excluded)."*
  //
  // Four clauses, and only the first two were modelled before:
  //   · ONE draw per Turn                            (R11 · drewThisTurn)
  //   · the seat going FIRST takes no Grimoire draw  (R07)
  //   · the seat going SECOND MAY                    (R07 — no bar, and
  //     the bench now asserts the permission rather than assuming it)
  //   · BOTH may draw their Luxmoire                 (R07 — likewise)
  // and the CROSS-BOOK bar, which `drewThisTurn` already enforced by
  // accident: one flag cannot say WHICH Book, so nothing here could
  // tell the second draw from the other Book apart from a plain second
  // draw. `drewBookThisTurn` records the Book, and the R11 refusal now
  // names it, which is what makes the cross-Book case a check rather
  // than a coincidence.
  //
  // ⚠ CARD EFFECTS ARE EXCLUDED, and the exclusion is a NON-EVENT here
  // by construction: nothing in this file that draws off a card effect
  // touches `drewThisTurn`, so the Turn's draw survives it. Asserted at
  // the bench rather than trusted.
  draw(K, p, zone, turn, isFirstSeat) {
    if (K.SW.R11 && p.drewThisTurn) return "R11";        // R11: one draw per Turn, either Book
    // "(card effects excluded)" — the parenthesis is a clause. In force
    // an effect that says "draw" is not the Turn's draw and leaves it
    // untouched; with R11_FX withdrawn it consumes it, which is the
    // over-strict reading the ruling names in order to exclude.
    if (!K.SW.R11_FX && p.effectDrewThisTurn) return "R11";
    if (K.SW.R07 && zone === "grimoire" && isFirstSeat && inOpeningCycle(turn)) return "R07";
    // R07 IS A SPLIT, AND THE SPLIT IS THE RULE. Its counter-reading —
    // the one this file would be holding if nobody had read the ruling
    // twice — is that the opening Cycle is simply DRY: no Grimoire draw
    // for anybody and no Luxmoire draw either. Withdrawing R07_SPLIT
    // installs exactly that, so the two PERMISSIONS R07 grants (the
    // seat going SECOND may draw its Grimoire; BOTH seats may draw
    // their Luxmoire) each get a failing state.
    if (!K.SW.R07_SPLIT && inOpeningCycle(turn)) return "R07";
    if (zone === "grimoire" && p.grimoire.length === 0) return "R40";
    if (zone === "luxmoire" && p.luxmoire.length === 0) return "R11";
    return null;
  },
  // R88 · the Grimoire fare is a LEVEL TABLE (2 for a Level 1, 4 above
  // it); the Luxmoire fare is flat. Payable in Khao or Anima, and the
  // Anima leg answers to R96.
  //
  // ⚠ R08 *new* (2026-08-25): NEITHER BOOK MAY BE SEARCHED ON THE
  // OPENING CYCLE, at any price — caP'n: *"they are also not allowed to
  // search their Luxmoire that first turn, nor Grimoire"*, and the seat
  // going second is named separately as able to draw its Grimoire "but
  // not search it". The fare is SUSPENDED, not merely unpayable, so the
  // bar is asked BEFORE the fare: a seat funded to the exact fare is
  // still refused, and telling it that it could not afford something it
  // was never allowed to buy would be the wrong refusal for the right
  // reason. `turn` is therefore a required argument, not an optional
  // one — see the note on `inOpeningCycle` about an omitted one.
  //
  // ⚠ NARROWED 2026-08-26. caP'n: *"brilliant distinction yes, cards
  // are exempt from the first turn search rule"*. The bar covers the
  // SEAT'S OWN search action — the one that pays the fare below and
  // spends the Turn's draw. `effectSearch` says a CARD did this, and a
  // card's search resolves on the opening Cycle and pays no fare
  // either, because the card already paid at activation. It is the
  // twin of `effectDrewThisTurn` in `draw` above, deliberately: one
  // idea, two clauses of the same ruling.
  //
  // ⚠ THE SEARCH BAN AND THE ATTACK BAN ARE DIFFERENT BANS AND A CARD
  // CAN BE CAUGHT BY ONE AND NOT THE OTHER — the Machine and Humanoid
  // stamps search and are exempt here, while Phantom and Beast damage
  // the adversary and are still refused by `primera` above. Neither
  // predicate knows about the other and neither should.
  search(K, p, zone, level, turn, effectSearch = false, found = null) {
    if (K.SW.R08_FX && effectSearch) return null;   // a CARD searched — exempt, and no fare
    if (K.SW.R08_BOOKS && openingBarApplies(K, turn)) return "R08";
    // ── R106 · A LUXMOIRE SEARCH FINDS IMPERA. ONLY IMPERA. ──
    // caP'n, 2026-08-26: *"you can still only search the Lux for Impera,
    // not the U/A's"* — U'hltis and Affinities live in the Luxmoire (R05)
    // but may only be DRAWN. ⚠ THIS GATE TOOK NO CARD AND HAD NO TYPE
    // TEST AT ALL, and this file's declared reason for R106 claimed
    // "there is no Luxmoire search in its action set" while the bench
    // below asserts one legal on a funded seat. A CARD EFFECT that names
    // its own target is not this search and is exempt above.
    if (K.SW.R106 && zone === "luxmoire" && found && !isLuxSearchable(getCard(found))) return "R106";
    const fare = searchFareOf(K, zone, level);
    // R107 + R96 + R88, in that order of specificity. `farePools` is
    // empty when NEITHER pool can carry the whole fare on its own — which
    // is a different refusal from "you have nothing at all", so the two
    // are still reported apart.
    const poolsS = farePools(p, fare, false);
    if (!poolsS.length) {
      // R96 names the case where Anima alone would have covered it but
      // only by landing on zero; R88 is simply not having it.
      if ((p.anima || 0) >= fare) return "R96";
      return "R88";
    }
    return null;
  },
  // ── R90 · THE MULLIGAN, REWRITTEN 2026-08-25 ──────────────
  // caP'n: *"Either player, if they have not drawn yet, they can do a
  // Mulligan on that first turn and no other."*
  //
  // Three clauses; this file modelled one and a half. The LIMIT was
  // here. "On that first turn" was modelled as `turnsTaken > 0`, which
  // is the right answer for the seat that has not moved and says
  // nothing about the seat that has — the window is the opening CYCLE,
  // so the seat going second may still mulligan on half-turn 1. And
  // "if they have not drawn yet" was not modelled AT ALL: a seat could
  // take its Luxmoire draw, look at the card, and then return the hand
  // it had just improved. That is the clause the ruling added and it is
  // the one an engine is most likely to get wrong, because on the
  // shipping board two of the three lived only in React state.
  mulligan(K, p, turn) {
    if (p.mulligans >= K.MULLIGAN_LIMIT) return "R90";   // the LIMIT — a number, mutated as one
    // IN FORCE the window is the catalogue's opening CYCLE; with
    // R90_WINDOW withdrawn it is the literal `turn === 0` reading that
    // rules.js's own banner rejected, under which the seat going second
    // never gets a mulligan at all. That is the failing state for the
    // permission half of R90.
    const t = turn ?? 0;
    const inWindow = K.SW.R90_WINDOW ? inOpeningCycle(t) : Math.floor(Number(t)) === 0;
    if (K.SW.R90 && !inWindow) return "R90";             // the opening Cycle and no other
    if (K.SW.R90 && p.drewThisTurn) return "R90";        // *new* — not after drawing
    if (p.turnsTaken > 0) return "R90";                  // and never once this seat has acted
    return null;
  },
  // ⚠ THREE SHAPES, ONE NAME. `resurrectedThisTurn` is an INTEGER on the
  // synthetic players in this file, `{ turn, count }` on a persisted
  // player record (Shatterfield.js / NPC.js), and a BOOLEAN in
  // Shatterfield's local React state. `{turn,count} >= 1` evaluates to
  // `NaN >= 1` — false — so a naive read pointed at a real record would
  // let the R89 gate pass forever and never say a word. This file's own
  // field is therefore named `paidResurrectionsThisTurn`, which cannot be
  // mistaken for any of the three, and the read below is shape-tolerant
  // regardless in case someone points it at one anyway.
  paidReturnsOf(p) {
    const v = p.paidResurrectionsThisTurn ?? p.resurrectedThisTurn;
    if (typeof v === "number") return v;
    if (typeof v === "boolean") return v ? 1 : 0;
    if (v && typeof v === "object") return Number(v.count) || 0;
    return 0;
  },
  // `method` is "paid" (the rite) or "effect" (a Tome, an Impera, the
  // Time Primera). R89's clock binds the RITE ONLY — being exempt from it
  // is precisely what those cards buy — while R77's tally binds both.
  resurrect(K, p, seal, ontoSlotIdx, method) {
    const paid = (method || "paid") === "paid";
    if (paid && Legal.paidReturnsOf(p) >= K.RESURRECT_PER_TURN) return "R89";
    const c = getCard(seal.id);
    if (!c || c.type !== T.ESCENDANT) return "R44";
    // ⚠ R45 AND R46 MEET ON THE SAME GUARD, and pretending otherwise
    // would be the nicer-looking lie. R46 forbids the SKIP (an L3 onto a
    // bare Escling); R45 demands the ORDER (L1, then L2, then L3). This
    // simulator keeps no per-line return history, so the only board it
    // can build is one where the two prohibitions are the same board,
    // and one clause therefore withdraws both. R45 is proven exactly as
    // far as R46 is and no further; see NOT_PROVEN_REASONS.
    // ── R45 / R46 · IN ORDER, AND NEVER A SKIP ───────────────
    // ⚠ THIS REFUSED `c.level !== 1` AND ANY OCCUPIED RIFT, so the paid
    // rite in this simulator could only ever put a Level 1 onto a bare
    // Escling. RULEZ_V2 ch.17 allows an L2 back over its standing L1 and
    // an L3 over its L2 with its full Soul, and the board's own
    // `validateResurrection` asks only `toLevel === fromLevel + 1` and
    // never looks at the rift at all. The gate was not merely unproven,
    // it was NARROWER THAN THE RULE — a legal climb was reported as an
    // R46 refusal — and most of Resurrection was unexercised behind it.
    const slot = p.field.esclings[ontoSlotIdx];
    if (!slot) return "R46";
    const standing = slot.escendant ? (getCard(slot.escendant.id)?.level || 0) : 0;
    if (K.SW.R46 && c.level !== standing + 1) return "R46";
    // R86 answers FIRST. A shifted card is past the paid rite whatever
    // its tally says, and answering R44 for it would mislabel the reason.
    if (K.SW.R86 && p.shifted.has(seal.uid)) return "R86";
    // R85 · the tally is PER COPY. Its counter-rule keeps ONE tally for
    // the whole seat, so an untouched second copy inherits the first
    // copy's exhausted count and the assertion that it may still return
    // goes red. (Blunt on purpose: pooling by printed name would need a
    // uid→name map this file does not keep, and every fixture that
    // exercises it holds exactly one printed name anyway.)
    const used = K.SW.R85
      ? (p.tally[seal.uid] || 0)
      : Object.values(p.tally).reduce((n, v) => n + (Number(v) || 0), 0);
    if (used >= K.RESURRECT_LIMIT[`L${c.level}`]) return "R44"; // R44 / R85 per COPY
    if (!paid) return null;                              // a card effect pays no fare
    const cost = K.RESURRECT_COST[`L${c.level}`];
    const poolsR = farePools(p, cost, false);          // R107 — one pool
    if (!poolsR.length) return (p.anima || 0) >= cost ? "R96" : "R47";
    return null;
  },
  attachImpera(K, slot, cardId) {
    const c = getCard(cardId);
    if (!c || c.type !== T.IMPERA) return "R71";
    if (!slot.escendant) return "R71";
    if (K.SW.R71 && slot.impera[c.level]) return "R71";  // R71: one per LEVEL
    return null;
  },
  attachUhlti(K, slot, cardId) {
    const c = getCard(cardId);
    if (!c || c.type !== T.UHLTI) return "R72";
    if (!slot.escendant) return "R72";
    if (K.SW.R64 && slot.deviant) return "R64";          // R64: Deviants bear neither
    if (K.SW.R72 && slot.uhlti) return "R72";            // R72: one U'hlti or Tome
    if (K.SW.R72 && slot.heretical) return "R72";
    const being = getCard(slot.escendant.id);
    if (!being || being.level !== 3) return "R69";
    return null;
  },
  attachAffinity(K, slot, cardId) {
    const c = getCard(cardId);
    if (!c || c.type !== T.AFFINITY) return "R73";
    if (!slot.escendant) return "R73";
    if (K.SW.R64 && slot.deviant) return "R64";
    if (K.SW.R73 && slot.affinity) return "R73";         // R73: one Affinity
    const being = getCard(slot.escendant.id);
    if (K.SW.R70 && (!being || being.level !== 3 || !slot.natural)) return "R70";
    if (!being) return "R70";
    if (K.SW.R70 && !slot.uhlti) return "R70";           // R70: U'hlti first, always
    return null;
  },
  heretical(K, p, slotIdx) {
    const slot = p.field.esclings[slotIdx];
    if (!slot || !slot.escendant) return "R67";
    const being = getCard(slot.escendant.id);
    if (!being || being.level !== 3) return "R67";
    if (slot.uhlti) return "R67";                        // needs an EMPTY U'hlti slot
    if (p.khao < K.HERETICAL_TRAINING_COST.khao) return "R67";
    if (p.anima < K.HERETICAL_TRAINING_COST.anima) return "R67"; // BOTH, never either
    // The rite's Anima leg is a voluntary payment like any other (R96).
    if (!tillClears(p.anima, K.HERETICAL_TRAINING_COST.anima, false)) return "R96";
    return null;
  },
  primera(K, p, slotIdx, word, phase, turn, axis) {
    if (K.SW.R80 && phase !== "own-turn") return "R80";  // R80: never in a Response Window
    const slot = p.field.esclings[slotIdx];
    if (!slot || !slot.escendant) return "R78";
    const being = getCard(slot.escendant.id);
    // THE RIFT, NOT THE CARD DATABASE. A gate that recomputed the stamps
    // from cards.js could never refuse a being whose stamps had been
    // taken off it, and — while the STAMP_ONE_AXIS fault lived in
    // `stampsOf` — it was a `Legal` gate reading a fault switch, which
    // this file's own banner forbids by name.
    const stamps = stampsOnSlot(K, slot);
    // R08 · AN ATTACKING STAMP IS AN ATTACK, and only an attacking one.
    // R09 explicitly permits activation on the opening Cycle, so a
    // wholesale ban on stamps would break a rule to enforce a rule.
    // Which stamps attack is read off the printed effect (stampAttacks),
    // never off a hand-typed pair — and the axis matters, because
    // "Dragon" is a word on both axes and neither of those attacks.
    const stampAxis = axis ?? stamps.find((s) => s.word === word)?.axis;
    if ((K.SW.R08_KIND ? stampAttacks(stampAxis, word) : true)
        && Legal.attack(K, { turn, kind: "Primera" })) return "R08";
    // R121 — a silenced Mind may fire NOTHING but its Warden Art, and a
    // Primera stamp of its own is one of the things it may not fire.
    if (K.SW.R121 && slotIdx === MIND_RIFT
        && !mindMayAct(p.field.esclings, "PRIMERA", turn, SEATS)) return "R121";
    if (K.SW.R78 && !stamps.some((s) => s.word === word)) return "R78";
    if (K.SW.R81 && slot.primeraUsedThisTurn) return "R81";   // R81: one per BEING
    if (K.SW.R82 && p.primeraWords.has(word)) return "R82";   // R82: one per WORD, whole field
    const cost = K.PRIMERA_COST[`L${being.level}`];
    const poolsP = farePools(p, cost, false);          // R107 — one pool
    if (!poolsP.length) return (p.anima || 0) >= cost ? "R96" : "R79";
    return null;
  },
  // R77 · WHICH METHODS COUNT. One predicate, asked by the simulator
  // (through ACT, where it is always true) when it decides whether to
  // bump a tally, and by the check (through K) when it asks what the
  // rule says. The counter-rule exempts card effects from the tally —
  // the exact reading R89's first cut confused this with — so the
  // assertion that EVERY method counts goes red.
  tallies(K, method) {
    if ((method || "paid") === "paid") return true;
    return !!K.SW.R77;
  },
  chainLink(K, depth) {
    if (depth >= K.CHAIN_DEPTH_CAP) return "R92";
    return null;
  },
  targetProphecyOrEscling(K, slot) {
    // ⚠ THIS GATE COULD NOT FAIL, AND IT IS WORTH SAYING HOW. It returned
    // "R74" for a bare rift AND for `null`, and every call site in the
    // file passed one or the other — so with the clause standing the
    // answer was the constant "R74" for every input this file ever gave
    // it, and `assert(gate !== null)` was `assert(true)`. Withdrawing the
    // clause then reddened it by arithmetic, which made R74's whole proof
    // `assert(K.SW.R74)`: ATTACK C out of this file's own banner.
    //
    // R74 IS A PROHIBITION AND A PERMISSION and it needs both switches.
    // IN FORCE: a Prophecy rift and a BARE Escling are untouchable by a
    // card, and an ESCENDANT standing in a rift is a legal target like
    // anything else. `R74_SCOPE` withdrawn installs the over-broad
    // reading — a card may reach nothing on the battle line at all,
    // which would make every ruin Tome in the game dead — and that is
    // the failing state the permission half never had.
    if (K.SW.R74 && (!slot || !slot.escendant)) return "R74";
    if (!K.SW.R74_SCOPE) return "R74";
    return null;
  },
  curioActivate(K, p, idx) {
    const c = p.field.curios[idx];
    if (!c) return "R75";
    if (c.used) return "R75";
    return null;
  },
};

// R29 — a being is Fractured when its Lyf stands at 0, whether it was
// knocked there or printed there. One predicate, so the board sweep and
// the card-level probe cannot disagree about what Fractured means.
function isFracturedSlot(K, slot) {
  if (!slot?.escendant) return false;
  const c = getCard(slot.escendant.id);
  const lyf = slot.currentLyf ?? c?.lyf ?? 0;
  // R29's counter-rule is the intuitive-and-wrong one: that Fractured
  // means DAMAGED, so a being that prints Lyf 0 arrives healthy. Under
  // it a 0-Lyf being reads unfractured and the assertion that it can be
  // ruined the moment it lands goes red.
  return K.SW.R29 ? lyf === 0 : lyf < (c?.lyf ?? 0);
}

// R69 — Supreme is an observation, not a permission.
function isSupreme(K, slot) {
  if (!slot?.escendant) return false;
  const c = getCard(slot.escendant.id);
  if (!c || c.level !== 3 || !slot.natural) return false;
  const imperaLevels = [1, 2, 3].filter((l) => slot.impera[l]).length;
  // R69's counter-rule is the one every player tries first: that Supreme
  // is the LOOK of the thing — a decorated L3 — rather than the whole
  // set. Under it a being one card short reads Supreme, and the bench
  // below (which strips each requirement in turn) goes red.
  return K.SW.R69
    ? (imperaLevels === 3 && !!slot.uhlti && !!slot.affinity)
    : (imperaLevels >= 1 && !!slot.uhlti);
}

// ─────────────────────────────────────────────────────────────
// DECK VALIDATION — R04/R05/R06, R53-R63, R94.
// Every violation keeps its own R-code. The previous cut rewrote them
// all to `code:"DECK"`, which erased six deck-limit rules from the
// report while still counting them as covered.
// ─────────────────────────────────────────────────────────────
function validateDeck(K, deck, label, H) {
  const out = [];
  const push = (code, detail) => out.push({ code, detail: `${label} — ${detail}`, phase: "deck", severity: "error" });
  const grimoire = deck.grimoire || {};
  const luxmoire = deck.luxmoire || {};

  // ── R04 · pre-placed Esclings ──
  const esclings = deck.esclings || [];
  H.assert("R04", esclings.length === K.ESCLING_SLOTS,
    `${label}: ${esclings.length} pre-placed Esclings (expected ${K.ESCLING_SLOTS})`, "deck");
  if (esclings.length !== K.ESCLING_SLOTS) push("R04", `${esclings.length} Esclings (expected ${K.ESCLING_SLOTS})`);

  // ── R05 · the Grimoire holds Beings and Tomes and nothing else ──
  let grimTotal = 0;
  Object.entries(grimoire).forEach(([id, n]) => {
    const c = getCard(id);
    grimTotal += n;
    if (!c) { push("R05", `unknown card ${id} in Grimoire`); return; }
    if (c.type !== T.ESCENDANT && c.type !== T.TOME) {
      // Affinities, U'hltis, Impera, Esclings, Prophecies and Curios are
      // all R05 violations HERE — never R60 (see R60's own text).
      push("R05", `${c.type} "${id}" is in the Grimoire; only Beings and Tomes belong there`);
    }
    if (c.type === T.ESCENDANT) {
      const cap = K.DECK_LIMITS[`ESCENDANT_L${c.level}`];
      const code = c.level === 1 ? "R53" : c.level === 2 ? "R54" : "R55";
      H.assert(code, n <= cap, `${label}: ${id} ×${n} exceeds L${c.level} Escendant cap ${cap}`, "deck");
      if (n > cap) push(code, `${id} ×${n} exceeds L${c.level} Escendant cap of ${cap}`);
    }
    if (c.type === T.TOME) {
      const cap = K.DECK_LIMITS[`TOME_L${c.level}`];
      const code = c.level === 1 ? "R56" : c.level === 2 ? "R57" : "R58";
      H.assert(code, n <= cap, `${label}: ${id} ×${n} exceeds L${c.level} Tome cap ${cap}`, "deck");
      if (n > cap) push(code, `${id} ×${n} exceeds L${c.level} Tome cap of ${cap}`);
    }
  });
  H.assert("R05", grimTotal >= K.GRIMOIRE_MIN,
    `${label}: Grimoire holds ${grimTotal} cards (min ${K.GRIMOIRE_MIN})`, "deck");
  if (grimTotal < K.GRIMOIRE_MIN) push("R05", `Grimoire holds ${grimTotal} cards (min ${K.GRIMOIRE_MIN})`);

  // ── R06 / R59 / R60 / R94 / R104 · the Luxmoire ──
  let luxTotal = 0;
  let affinityTotal = 0;
  let uhltiTotal = 0;
  Object.entries(luxmoire).forEach(([id, n]) => {
    const c = getCard(id);
    luxTotal += n;
    if (!c) { push("R05", `unknown card ${id} in Luxmoire`); return; }
    if (c.type !== T.IMPERA && c.type !== T.UHLTI && c.type !== T.AFFINITY) {
      push("R05", `${c.type} "${id}" is in the Luxmoire; only Impera, U'hltis and Affinities belong there`);
    }
    if (c.type === T.IMPERA) {
      // R94 — Impera mirror Escendant caps BY LEVEL. The previous cut
      // applied R53-R55 here, which is the wrong rule on the wrong card.
      const cap = K.DECK_LIMITS[`IMPERA_L${c.level}`];
      H.assert("R94", n <= cap, `${label}: Impera ${id} ×${n} exceeds L${c.level} cap ${cap}`, "deck");
      if (n > cap) push("R94", `Impera ${id} ×${n} exceeds L${c.level} cap of ${cap}`);
    }
    if (c.type === T.UHLTI) {
      H.assert("R59", n <= K.DECK_LIMITS.UHLTI, `${label}: U'hlti ${id} ×${n} exceeds ${K.DECK_LIMITS.UHLTI}`, "deck");
      if (n > K.DECK_LIMITS.UHLTI) push("R59", `U'hlti ${id} ×${n} exceeds cap of ${K.DECK_LIMITS.UHLTI}`);
    }
    if (c.type === T.UHLTI) uhltiTotal += n;
    if (c.type === T.AFFINITY) affinityTotal += n;
  });
  // ── R104 · THREE U'HLTIS TO A LUXMOIRE, IN TOTAL (caP'n, 2026-08-26)
  //   *"U'hltis rules as it was in Grimoire just for the Lux, which
  //   should be 3 U'hltis if I'm not mistaken."*
  // TWO DIFFERENT SHAPES ON ONE CARD TYPE, and the loop above only
  // catches one of them: R59 is per unique NAME (checked inside the
  // loop, where a name lives), R104 is the LANE TOTAL (checked out
  // here, where the lane lives). Four U'hltis of four different names
  // passes every per-name test in the loop and is still illegal.
  H.assert("R104", uhltiTotal <= K.LUXMOIRE_UHLTI_MAX,
    `${label}: Luxmoire holds ${uhltiTotal} U'hltis (max ${K.LUXMOIRE_UHLTI_MAX} per Luxmoire)`, "deck");
  if (uhltiTotal > K.LUXMOIRE_UHLTI_MAX) push("R104", `Luxmoire holds ${uhltiTotal} U'hltis (max ${K.LUXMOIRE_UHLTI_MAX})`);
  // R60 — max 1 per LUXMOIRE, not per name, and counted where they live.
  H.assert("R60", affinityTotal <= K.DECK_LIMITS.AFFINITY,
    `${label}: Luxmoire holds ${affinityTotal} Affinities (max ${K.DECK_LIMITS.AFFINITY} per Luxmoire)`, "deck");
  if (affinityTotal > K.DECK_LIMITS.AFFINITY) push("R60", `Luxmoire holds ${affinityTotal} Affinities (max ${K.DECK_LIMITS.AFFINITY})`);

  H.assert("R06", luxTotal >= K.LUXMOIRE_MIN && luxTotal <= K.LUXMOIRE_MAX,
    `${label}: Luxmoire holds ${luxTotal} cards (allowed ${K.LUXMOIRE_MIN}-${K.LUXMOIRE_MAX})`, "deck");
  if (luxTotal < K.LUXMOIRE_MIN) push("R06", `Luxmoire holds ${luxTotal} cards (min ${K.LUXMOIRE_MIN})`);
  if (luxTotal > K.LUXMOIRE_MAX) push("R06", `Luxmoire holds ${luxTotal} cards (max ${K.LUXMOIRE_MAX})`);

  // ── R61 / R62 · pre-placed Curios ──
  const curios = deck.curios || [];
  const curioCounts = {};
  let allyIlk = 0;
  curios.forEach((id) => {
    curioCounts[id] = (curioCounts[id] || 0) + 1;
    if (getCard(id)?.ilk === "ally") allyIlk++;
  });
  Object.entries(curioCounts).forEach(([id, n]) => {
    H.assert("R61", n <= K.DECK_LIMITS.CURIO, `${label}: Curio ${id} ×${n} exceeds ${K.DECK_LIMITS.CURIO}`, "deck");
    if (n > K.DECK_LIMITS.CURIO) push("R61", `Curio ${id} ×${n} exceeds cap of ${K.DECK_LIMITS.CURIO}`);
  });
  // ── R62 · TWO ALLY-ILK CURIOS PER SHATTERFIELD (caP'n, 2026-08-26) ──
  //   *"per Shatterfield, and update it so it's 2 because I made it 2
  //   Curios since way back then, just never wrote it in the rulebook
  //   apparently."*
  // It used to be ONE, and it used to be a LITERAL typed here — the only
  // deck limit with no constant to perturb, which is why it sat outside
  // the fake-detector. It has one now (ALLY_ILK_CURIO_MAX) and it is
  // read off K, so the mutation row below can actually redden it.
  // Counter-rule: the Ally Ilk is capped per NAME like R61, so a deck
  // one over the total stops being caught.
  const allyCap = K.SW.R62 ? K.ALLY_ILK_CURIO_MAX : K.ALLY_ILK_CURIO_MAX + 1;
  H.assert("R62", allyIlk <= allyCap, `${label}: ${allyIlk} Ally-Ilk Curios pre-placed (max ${allyCap})`, "deck");
  if (allyIlk > allyCap) push("R62", `${allyIlk} Ally-Ilk Curios (max ${allyCap})`);
  H.assert("R75", curios.length <= K.CURIO_SLOTS,
    `${label}: ${curios.length} Curios pre-placed (CURIO_SLOTS = ${K.CURIO_SLOTS})`, "deck");
  if (curios.length > K.CURIO_SLOTS) push("R75", `${curios.length} Curios (max ${K.CURIO_SLOTS} Rifts)`);

  // ── R63 · one Prophecy per Solrend ──
  const prophCount = deck.prophecy ? (Array.isArray(deck.prophecy) ? deck.prophecy.length : 1) : 0;
  H.assert("R63", prophCount <= K.DECK_LIMITS.PROPHECY,
    `${label}: ${prophCount} Prophecies (max ${K.DECK_LIMITS.PROPHECY} per Solrend)`, "deck");
  if (prophCount > K.DECK_LIMITS.PROPHECY) push("R63", `${prophCount} Prophecies (max ${K.DECK_LIMITS.PROPHECY})`);

  return out;
}

// ─────────────────────────────────────────────────────────────
// PLAYER — every card is a distinct INSTANCE with a uid. Counting
// zones with a count-map made conservation unprovable and made R85
// ("tallies are PER COPY") unrepresentable. Both are now free.
// ─────────────────────────────────────────────────────────────
// The builder ACTS, so it reads ACT. If it read K, mutating a setup
// constant would move the board AND the check together and R01-R04
// could never go red — the exact tautology this rebuild exists to kill.
function buildPlayer(deck, idx, rng) {
  let n = 0;
  const inst = (id) => ({ uid: `p${idx}#${n++}#${id}`, id });
  const expand = (map) => {
    const arr = [];
    Object.entries(map || {}).forEach(([id, count]) => {
      for (let i = 0; i < count; i++) if (getCard(id)) arr.push(inst(id));
    });
    return arr;
  };
  const shuffle = (arr) => {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };
  const esclingIds = deck.esclings || [];
  return {
    idx, deckId: deck.id, deckName: deck.name,
    anima: ACT.STARTING_ANIMA,
    khao: ACT.STARTING_KHAO, maxKhao: ACT.STARTING_KHAO,
    grimoire: shuffle(expand(deck.grimoire)),
    luxmoire: shuffle(expand(deck.luxmoire)),
    hand: [], necropolis: [],
    // ── R112 · THE EXILE RIFT, SEEDED AND (SO FAR) NEVER FILLED ──
    // The seat gains the rift the 2026-09-07 wave added: `exile` is the
    // pile itself and `exileReturned` is the one-return mark that sends
    // a card ruined a second time INTO the Exile rift instead of the
    // Necropolis and bars it from Resurrection forever (R113).
    //
    // They are seeded here for one reason and it is worth being exact
    // about, because seeding a field is the cheapest possible way to
    // look like coverage: THIS FILE'S STATE MODEL CAN NOW REPRESENT THE
    // RIFT, AND NOTHING HERE FILLS IT. The simulator performs no
    // Exiliation and no Ritual, so both stay empty in every duel this
    // harness has ever played. That is deliberate — an empty pile is an
    // honest zero, and the alternative (a hand-written Exiliation
    // invented so a mutation would have something to bite) would be a
    // second Exile engine written to be checked against the first.
    //
    // ⚠ AND THE CONSERVATION SWEEP DOES NOT COUNT THEM YET. `zoneMap`
    // lists its piles by hand, and these two are not in the list. While
    // nothing writes them that is exactly right; the FIRST commit that
    // makes an Exiliation move a card must add `exile` to `zoneMap` in
    // the same edit, or trap §4.3 gets a new place to hide — a card
    // removed from a Book with no destination the sweep can see reads as
    // conserved. Written down here rather than in a ticket.
    exile: [], exileReturned: [],
    field: {
      esclings: Array.from({ length: ACT.ESCLING_SLOTS }, (_, i) => ({
        slot: i,
        escling: esclingIds[i] || esclingIds[0] || ESCLINGS[0].id,
        escendant: null, currentLyf: null,
        // R84 — the two Primera stamps the being standing here is
        // CARRYING. Empty while the rift holds only its Escling (an
        // Escling bears none, RULEZ_V2 ch.7), written by seatStamps.
        stamps: [],
        soul: [], impera: {}, uhlti: null, affinity: null, heretical: null,
        fallen: false, attackedThisTurn: false,
        // R124 — the SWING COUNT. `attackedThisTurn` is a boolean three
        // auditors read and it stays; the allowance is this, because two
        // shipped cards print more than one swing.
        attacksThisTurn: 0,
        natural: true, deviant: false,
        primeraUsedThisTurn: false,
      })),
      tomes: Array(ACT.TOME_SLOTS).fill(null),
      curios: (deck.curios || []).slice(0, ACT.CURIO_SLOTS).map((id) => ({ id, used: false })),
      prophecy: deck.prophecy || null,
    },
    tally: {},            // uid -> resurrections used (R44 / R85, per COPY)
    soulOf: {},           // uid -> soul uids at time of ruin (R48)
    shifted: new Set(),   // uids that passed through a Graveshift (R86)
    primeraWords: new Set(),
    khaoSpentThisCycle: false,
    // ── R108 · THE PHASE GATE LIVES ON THE SEAT ──────────────
    // Not in a local, for the same reason the board keeps it on the seat
    // and not in React state: the enforcer reads it universally and a
    // remount cannot reopen your Main phase. Absent reads as MAIN, which
    // is the compatibility hinge; this file writes it explicitly so the
    // one-way step can be watched happening.
    phase: PHASE.REPLENISH,
    // ── R100 · THE SPLIT ANIMA LEDGER, WHICH WAS SIMPLY ABSENT ──
    // Anima an ADVERSARY took off you feeds every Prophecy that reads
    // Anima loss; Anima you dimmed YOURSELF — a fare, a self-dim, the
    // Graveshift, a Devotion — feeds NOTHING. Two ledgers, never mixed.
    // This simulator kept neither, so R100 had no board here at all and
    // the per-half-turn pair that `turnEngine.handOffHalfTurn` rotates
    // had no counterpart either. `totalAnimaLost` is cumulative for the
    // whole Solrend (a Prophecy reads it); the `ThisTurn` pair rotates.
    totalAnimaLost: 0,
    totalAnimaSelfDimmed: 0,
    animaLostThisTurn: 0,
    animaSelfDimmedThisTurn: 0,
    animaGainedThisTurn: 0,
    drewThisTurn: false,
    drewBookThisTurn: null,   // R11 — WHICH Book the Turn's draw came from
    effectDrewThisTurn: false, // R11 — a CARD drew, which is not the Turn's draw
    ascendedThisTurn: 0,
    deviantAscendedThisTurn: 0,
    paidResurrectionsThisTurn: 0,
    mulligans: 0,
    turnsTaken: 0,
    procTriggers: 0,      // R76 — only CARD effects may increment this
    // ── THE KHAO LEDGER (R91 / R13 / R12) ────────────────────
    // Recorded from the board as the duel runs, so the end-of-duel
    // assertions have real observations to fail on. The probes these
    // replaced were pure constant arithmetic: they reddened under
    // mutation while proving nothing about any game that was played.
    transcendedThisTurn: 0,             // R17 — there is no cap, and that is the claim
    firstNaturalGainTurn: null, // half-turn of the FIRST observed climb
    naturalKhaoSeen: ACT.STARTING_KHAO, // highest Max Khao reached by the CLIMB
    maxKhaoSeen: ACT.STARTING_KHAO,     // highest Max Khao reached at all
    spentCycles: 0,                     // cycles ENTERED having spent Khao (R12)
    climbsAfterSpend: 0,                // ...of those, how many still climbed
  };
}

// Every instance uid the player owns, by zone. The basis for the
// conservation sweep and the duplicate/two-zone checks.
function zoneMap(p) {
  const m = new Map();
  const add = (u, z) => { m.set(u, [...(m.get(u) || []), z]); };
  p.grimoire.forEach((c) => add(c.uid, "grimoire"));
  p.luxmoire.forEach((c) => add(c.uid, "luxmoire"));
  p.hand.forEach((c) => add(c.uid, "hand"));
  p.necropolis.forEach((c) => add(c.uid, "necropolis"));
  p.field.esclings.forEach((s) => {
    if (s.escendant) add(s.escendant.uid, "field");
    s.soul.forEach((c) => add(c.uid, "soul"));
    Object.values(s.impera).forEach((c) => c && add(c.uid, "soul"));
    if (s.uhlti) add(s.uhlti.uid, "soul");
    if (s.affinity) add(s.affinity.uid, "soul");
    if (s.heretical) add(s.heretical.uid, "heretical");
  });
  p.field.tomes.forEach((t) => t && add(t.uid, "tomes"));
  return m;
}

// ── THE STATE-INVARIANT SWEEP ────────────────────────────────
// Runs after every action. Conservation, uniqueness, single-zone
// residency and non-negative resources. These are ENGINE invariants,
// not catalogue rules, and they are reported in their own channel —
// dressing them up as rule coverage is exactly the inflation this
// rebuild exists to remove.
function sweep(H, players, label) {
  players.forEach((p, i) => {
    const m = zoneMap(p);
    H.inv("card-conservation", m.size === p.census,
      `P${i} holds ${m.size} card instances at "${label}" (dealt ${p.census})`);
    let dupes = 0, multi = 0;
    m.forEach((zones) => {
      if (zones.length > 1) { multi++; if (new Set(zones).size === 1) dupes++; }
    });
    H.inv("no-duplicate-instances", dupes === 0, `P${i} has ${dupes} duplicated instance ids at "${label}"`);
    H.inv("no-card-in-two-zones", multi === 0, `P${i} has ${multi} instances resident in two zones at "${label}"`);
    H.inv("non-negative-anima", p.anima >= 0, `P${i} Anima is ${p.anima} at "${label}"`);
    H.inv("non-negative-khao", p.khao >= 0, `P${i} Khao is ${p.khao} at "${label}"`);
    H.inv("khao-within-max", p.khao <= p.maxKhao, `P${i} Khao ${p.khao} exceeds maxKhao ${p.maxKhao} at "${label}"`);

    // ── R110 · THE BATTLE LINE IS FOUR ──────────────────────
    // caP'n, 2026-09-07: *"just give the player 4 esclings now and
    // update the rules to match."* The fourth rift exists because the
    // four Role Arts each live in a rift of their own (R115). This was
    // obeyed here and credited to R04 rather than watched under its own
    // code; a snapshot answers it in one comparison, so it gets one.
    H.assert("R110", p.field.esclings.length === H.K.ESCLING_SLOTS,
      `P${i} holds ${p.field.esclings.length} battle rifts at "${label}" (the line is ${H.K.ESCLING_SLOTS}, one per Role Art)`);

    // ── R104 · THREE U'HLTIS TO A LUXMOIRE, IN TOTAL ────────
    // Beyond R59's one-per-unique-name. The cap is on the Luxmoire, and
    // a mid-duel Luxmoire has been DRAWN FROM — so the invariant is the
    // MULTISET across every zone one could have travelled to, never the
    // pile's own length. (Trap §4.3's lesson one zone over: a Set cannot
    // see a copy evaporate, and a pile cannot see one leave.)
    {
      const isU = (id) => getCard(id)?.type === T.UHLTI;
      let uh = 0;
      p.luxmoire.forEach((c) => { if (isU(c.id)) uh++; });
      p.hand.forEach((c) => { if (isU(c.id)) uh++; });
      p.necropolis.forEach((c) => { if (isU(c.id)) uh++; });
      p.field.esclings.forEach((sl) => { if (sl.uhlti && isU(sl.uhlti.id)) uh++; });
      H.assert("R104", uh <= H.K.LUXMOIRE_UHLTI_MAX,
        `P${i} owns ${uh} U'hltis across every zone at "${label}" — a Luxmoire may hold at most ${H.K.LUXMOIRE_UHLTI_MAX} in total (R104), so no board can ever show more`);
    }

    // ── R103 · A FULL TURN IS TWO HALF-TURNS ────────────────
    // Every cadence stamp on the board is a half-turn number, so the
    // cadence law is a SNAPSHOT invariant: no Tome may carry a
    // last-fired stamp closer to now than its own tag allows. OPT = 2
    // half-turns, OP2 = 4, OP3 = 6, and at a 3-4 seat table a full Turn
    // is a full LAP — which is why the number comes from
    // `cooldownHalfTurns(tag, seatCount)` and is never retyped.
    // Asserted where the firing happens (see Legal.activateTome and the
    // activation block) rather than here: `firedAgainTurn` was a field
    // NOTHING WROTE, and a check reading a field nobody writes is a
    // check that cannot fire — which is the thing this file exists to
    // refuse. What a snapshot CAN say is that no stamp is in the future.
    p.field.tomes.forEach((t, j) => {
      if (!t || t.lastFiredTurn == null) return;
      H.assert("R103", t.lastFiredTurn <= (p.turnsTaken * 2) + 2,
        `P${i} Tome rift ${j} (${t.id}) carries a last-fired stamp of ${t.lastFiredTurn} at "${label}", ahead of the clock`);
    });

    // ── R74 · AN ESCLING IS ETERNAL (RULEZ_V2 ch.7) ──────────
    // The board fact R74's prohibition exists to protect, asked of BOTH
    // seats because a card effect reaches across the table — the first
    // cut of this asked only the acting seat and the roster row that
    // breaks the DEFENDER's line went blind, which is the alarm working.
    H.assert("R74", p.field.esclings.every((s) => !!s.escling),
      `P${i} has a battle rift with no Escling in it at "${label}" — an Escling is eternal and no card may remove one`);

    // R29 — a base-0-Lyf being is ALWAYS Fractured. Asserted as a live
    // invariant rather than as a comment next to a kill.
    p.field.esclings.forEach((s, j) => {
      if (!s.escendant) return;
      const c = getCard(s.escendant.id);
      if (c && c.lyf === 0) {
        H.assert("R29", s.currentLyf === 0,
          `P${i} slot ${j} holds ${c.id} (base Lyf 0) with currentLyf ${s.currentLyf} — a 0-Lyf being is always Fractured`);
      }
      // R84 — Primeras are of the flesh: two stamps, always, even after
      // descent, Deviance or a stripped Soul. Asked of the SLOT.
      H.assert("R84", stampsOnSlot(H.K, s).length === 2,
        `P${i} slot ${j}: ${c?.id} carries ${stampsOnSlot(H.K, s).length} Primera stamps (expected 2) — stamps cannot be stripped`);
    });
  });
}

// ═════════════════════════════════════════════════════════════
// THE HALF-TURN HAND-OFF, AS THIS FILE NOW MODELS IT
// ─────────────────────────────────────────────────────────────
// ⚠ THIS FILE'S HAND-OFF USED TO BE `turn++` AND NOTHING ELSE, and that
// is a different game from the one `turnEngine.handOffHalfTurn` drives.
// That function is one of the four load-bearing seams in Terror and it
// owns FIVE things at every boundary in BOTH drivers:
//   1. it rotates the OUTGOING seat's per-half-turn Anima ledger
//      (`animaGainedThisTurn` / `animaLostThisTurn` and, since R100, the
//      self-dim pair). `totalAnimaLost` is deliberately NOT rotated —
//      it is cumulative for the whole Solrend and a Prophecy reads it;
//   2. R92b — it REPLENISHES THE INCOMING SEAT'S KHAO. caP'n,
//      2026-09-13: *"at the beginning of every single turn, regardless
//      if you used any or not, replenish your current Max Khao to its
//      full amount."* The board used to refill the seat that was
//      STANDING UP, which let it answer its adversary's whole half-turn
//      out of a full pool; under the rule a seat crosses into the
//      adversary's half-turn holding whatever it did not spend;
//   3. it asks R93 on the INCREMENTED turn;
//   4. it advances the chair;
//   5. it asks for Prophecies on the newest board.
// This file cannot do (5) — it has no Prophecy zone, and that is
// declared in UNPROVABLE_REASONS. It now does (1), (2) and (4) in ONE
// place, so the per-half-turn ledger R100 splits actually rotates here,
// and the REPLENISH phase belongs to the seat SITTING DOWN rather than
// to the one that just stood up. (3) stays where it was, at the end of
// the loop, because the loop bound IS the clock.
//
// ⚠ THE LEDGER ROTATION IS THE HALF THAT MATTERS MOST, and trap §4.1 is
// why: on the board, `buildEndOfHalfTurnUpdate` once had a single caller
// and the NPC seat never rotated at all in any driver, which turned a
// conditional "if you gained Anima this turn" draw into an
// unconditional one. A second implementation that keeps no ledger
// cannot disagree with that, which is the whole reason to have one.
function handOffHalfTurn(H, K, players, fromIdx, nextIdx, nextTurn) {
  const out = players[fromIdx];
  const inc = players[nextIdx];

  // 1 · ROTATE THE OUTGOING SEAT'S PER-HALF-TURN PAIR, and prove it.
  // Measured on the board before the fix: 24 of 24 hand-offs left the
  // outgoing counter non-zero. The claim here is the transition.
  const lostIn = out.animaLostThisTurn, selfIn = out.animaSelfDimmedThisTurn;
  const totalIn = out.totalAnimaLost;
  // MISACT · LEDGER_STICKS — the fault R100's per-half-turn pair
  // forbids: the rotation never happens, so "this turn" becomes a
  // running total for the whole Solrend.
  if (!misact("LEDGER_STICKS")) {
    out.animaLostThisTurn = 0;
    out.animaSelfDimmedThisTurn = 0;
    out.animaGainedThisTurn = 0;
  }
  H.assert("R100", out.animaLostThisTurn === 0 && out.animaSelfDimmedThisTurn === 0,
    `P${fromIdx} handed off holding animaLostThisTurn ${out.animaLostThisTurn} / animaSelfDimmedThisTurn ${out.animaSelfDimmedThisTurn} (was ${lostIn}/${selfIn}) — the per-half-turn pair rotates at the boundary`);
  // ...and the CUMULATIVE ledger must survive it. Zeroing that one
  // silently disarms every Prophecy that reads Anima loss.
  H.assert("R100", out.totalAnimaLost === totalIn,
    `P${fromIdx} lost ${totalIn - out.totalAnimaLost} from totalAnimaLost at the hand-off — that ledger is cumulative for the whole Solrend and a Prophecy reads it`);

  // 2 · R92b · THE INCOMING SEAT'S POOL, not the outgoing one's.
  const carried = inc.khao;
  const outHeld = out.khao;      // what the seat STANDING UP has left
  inc.khaoCarriedIn = carried;   // what it crossed the boundary holding
  // MISACT · REFILL_OUTGOING — the fault R92b forbids, and the one the
  // board actually shipped until 2026-09-13: the seat STANDING UP is
  // refilled, so it answers the adversary's whole half-turn out of a
  // full pool while the seat sitting down opens short.
  // MISACT · KHAO_SHORT — R14's own fault, moved here from the turn
  // body because THIS is where the board replenishes.
  if (misact("REFILL_OUTGOING")) out.khao = out.maxKhao;
  else inc.khao = misact("KHAO_SHORT") ? Math.max(0, inc.maxKhao - 1) : inc.maxKhao;
  H.assert("R14", inc.khao === inc.maxKhao,
    `P${nextIdx} sat down on ${inc.khao} Khao against a Max of ${inc.maxKhao} (carried ${carried}) — R92b replenishes the seat SITTING DOWN`);
  // ⚠ AND THE OTHER HALF, WHICH IS THE HALF THE BOARD GOT WRONG. Asking
  // only "did the incoming seat open full" cannot see the shipped bug:
  // REFILL_OUTGOING is SELF-CONSISTENT — fill every seat as it stands up
  // and every seat also opens full, one half-turn early. Measured: that
  // fault reddened NOTHING against the line above. What it does change
  // is that the seat STANDING UP crosses into its adversary's half-turn
  // holding a full pool, which is exactly what let it answer, counter
  // and fire soul cards for free. So the claim is stated as the
  // transition it actually is.
  H.assert("R14", out.khao === outHeld,
    `P${fromIdx} was topped up from ${outHeld} to ${out.khao} as it STOOD UP — R92b fills the seat SITTING DOWN, and a seat that crosses the boundary full answers its adversary's whole half-turn out of a full pool`);

  // 3 · THE CHAIR. Two seats here by construction (see the SEATS
  // banner), so the next seat is the other one; the shape is kept so a
  // seat-count parameter has somewhere to land.
  out.phase = PHASE.END;
  inc.phase = PHASE.REPLENISH;
  return nextTurn;
}

// ─────────────────────────────────────────────────────────────
// SIMULATE ONE DUEL
// ─────────────────────────────────────────────────────────────
function simulateGame(H, deckA, deckB) {
  const K = H.K;
  const rng = H.rng;
  const players = [buildPlayer(deckA, 0, rng), buildPlayer(deckB, 1, rng)];

  // ── R01 / R02 · setup, asserted against K before anything moves ──
  players.forEach((p, i) => {
    H.assert("R01", p.anima === K.STARTING_ANIMA,
      `P${i} opened on ${p.anima} Anima (expected ${K.STARTING_ANIMA})`);
    H.assert("R02", p.maxKhao === K.STARTING_KHAO,
      `P${i} opened on ${p.maxKhao} Max Khao (expected ${K.STARTING_KHAO})`);
    H.assert("R32", p.anima <= K.MAX_ANIMA, `P${i} opened above the Anima ceiling ${K.MAX_ANIMA}`);
  });

  // ── R11 · the mandatory PRE-DUEL opening five, both seats ──
  players.forEach((p, i) => {
    for (let d = 0; d < ACT.STARTING_HAND && p.grimoire.length; d++) p.hand.push(p.grimoire.pop());
    H.assert("R03", p.hand.length === K.STARTING_HAND,
      `P${i} opened with ${p.hand.length} cards (expected ${K.STARTING_HAND})`);
    H.assert("R11", p.hand.length === K.STARTING_HAND,
      `P${i} did not take the mandatory pre-duel opening ${K.STARTING_HAND}`);
    p.census = p.grimoire.length + p.luxmoire.length + p.hand.length;
  });

  // ── R90 · one mulligan, on the OPENING CYCLE, BEFORE DRAWING ──
  // The window and the not-yet-drawn clause are asserted here, at the
  // one moment in a duel when both are live, and both are asked of K
  // against a board built by ACT.
  {
    const p = players[0];
    // THE SEAT GOING SECOND may mulligan too, on half-turn 1 — the
    // clause the literal `turn === 0` reading silently deletes. Asked
    // of the seat that has not moved, which is the state it describes.
    H.assert("R90", Legal.mulligan(K, players[1], 1) === null,
      "the seat going SECOND was refused a mulligan on half-turn 1 — R90's window is the opening CYCLE, not turn 0");
    H.reject("R90", Legal.mulligan(K, players[1], openingCycleLength(SEATS)) !== "R90",
      `a mulligan on half-turn ${openingCycleLength(SEATS)}, one past the opening Cycle`);
    if (Legal.mulligan(ACT, p, 0) === null) {
      p.grimoire.push(...p.hand); p.hand = [];
      for (let i = p.grimoire.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [p.grimoire[i], p.grimoire[j]] = [p.grimoire[j], p.grimoire[i]];
      }
      for (let d = 0; d < ACT.STARTING_HAND && p.grimoire.length; d++) p.hand.push(p.grimoire.pop());
      p.mulligans++;
      H.assert("R90", p.hand.length === K.STARTING_HAND,
        `P0 redrew ${p.hand.length} after the mulligan (expected the same ${K.STARTING_HAND})`);
    }
    H.assert("R90", Legal.mulligan(K, p, 0) === "R90", "a second mulligan was still legal after the first");
    // *NEW* 2026-08-25 · "if they have not drawn yet". A seat that has
    // taken its draw may not then return the hand that draw improved.
    // Asked on a COPY so the duel below still opens on a fresh seat.
    const drawn = { ...players[1], drewThisTurn: true };
    H.reject("R90", Legal.mulligan(K, drawn, 1) !== "R90",
      "a mulligan taken AFTER the seat's draw — R90 allows it only before");
  }

  sweep(H, players, "post-deal");

  // ── R09 · the first seat CAN act on Turn 1 ──
  // ⚠ THIS CHECK USED TO BE ABLE TO FIRE ON A LEGAL BOARD, which is the
  // opposite failure to the one this file usually hunts. It read "either
  // some card in hand is Transcendable into slot 0, or the hand holds no
  // Level 1 Being at all" — and a hand holding a Level 1 of the WRONG
  // SCHOOL satisfies neither, on a board where R16 is being obeyed
  // perfectly. It has never fired because the starter decks all match,
  // which is luck, not correctness. The claim is narrowed to the one
  // R09 actually makes: where a school-matching L1 IS in hand, the
  // opening Turn does not refuse it.
  {
    const p = players[0];
    const escClass = getCard(p.field.esclings[0].escling)?.school;
    const matching = p.hand.find((c) => {
      const x = getCard(c.id);
      return x?.type === T.ESCENDANT && x.level === 1 && x.school === escClass;
    });
    if (matching) H.assert("R09", Legal.transcend(K, p, 0, matching.id, 0) === null,
      `P0 held the school-matching L1 ${matching.id} on half-turn 0 and Transcending was refused (R09 permits it)`);
  }

  let turn = 0;            // HALF-turns, exactly as match.turn counts them
  let gameOver = false, winner = -1, endRule = null, endBy = null;

  // The loop stops at the RULE (TURN_CLOCK) and only falls through to
  // the harness backstop (SIM_TURN_CAP) if the clock somehow failed to
  // stop it — which is itself the R93 finding.
  for (; turn < ACT.SIM_TURN_CAP && !gameOver; turn++) {
    if (turn >= ACT.TURN_CLOCK) break;
    const active = turn % 2;
    const me = players[active];
    const opp = players[1 - active];
    const isFirstSeat = active === 0;

    // ══ START OF MY TURN ══════════════════════════════════════
    // R26 · F1 FIX: the engine clears Fallen the moment the owner's turn
    // begins (Shatterfield 4807-4809 / 5148-5150). The old sim set the
    // flag and never cleared it, so every descended being was
    // permanently bypassable and R26 was permanently, silently green.
    // ⚠ `assert(!some(fallen))` one line after clearing every flag was a
    // tautology and is gone. What is measured is the TRANSITION: how
    // many beings stood Fallen coming into the Turn, and how many still
    // do after the clear. R26's counter-rule is that Fallen lasts a full
    // CYCLE — that it shields its seat through its owner's own Turn —
    // and on any board that entered the Turn with a Fallen being, the
    // two readings differ.
    const fallenIn = me.field.esclings.filter((s) => s.fallen).length;
    // MISACT · FALLEN_STICKS — the fault R26 forbids: Fallen lasts a
    // whole Cycle and shields its seat through its owner's own Turn.
    me.field.esclings.forEach((s) => { if (s.fallen && !misact("FALLEN_STICKS")) s.fallen = false; });
    const fallenOut = me.field.esclings.filter((s) => s.fallen).length;
    H.assert("R26", ruleIs(K, "R26", fallenOut === 0, fallenOut === fallenIn),
      `P${active} came into its own Turn with ${fallenIn} Fallen being(s) and left the clear with ${fallenOut} — Fallen is a HALF-turn state`);

    // R83 · every Primera limit resets at the start of my next Turn.
    // Same correction: the old line asserted an empty Set one statement
    // after emptying it. The measurement is the CARRY — what was spent
    // last Turn and whether it survived the boundary. R83's counter-rule
    // is that the limits are per DUEL, not per Turn.
    const wordsIn = me.primeraWords.size;
    const beingsIn = me.field.esclings.filter((s) => s.primeraUsedThisTurn).length;
    // MISACT · PRIMERA_CARRY — the fault R83 forbids: the one-per-word
    // and one-per-being limits are per DUEL and never reset.
    if (!misact("PRIMERA_CARRY")) {
      me.primeraWords = new Set();
      me.field.esclings.forEach((s) => { s.primeraUsedThisTurn = false; });
    }
    const wordsOut = me.primeraWords.size;
    const beingsOut = me.field.esclings.filter((s) => s.primeraUsedThisTurn).length;
    H.assert("R83", ruleIs(K, "R83",
      wordsOut === 0 && beingsOut === 0,
      wordsOut === wordsIn && beingsOut === beingsIn),
      `P${active} carried ${wordsIn} spent word(s) and ${beingsIn} spent being(s) into the next Turn (${wordsOut}/${beingsOut} survived the boundary)`);

    // ── THE ABSTAIN CADENCE ──────────────────────────────────
    // R12 makes the natural climb conditional on spending NOTHING for a
    // whole cycle, and a simulator that activates a Khao Garden every
    // single turn never once satisfies it — which left R91 and R13 with
    // no climb to observe on some seeds and made them read FAKE at
    // random. Banking a turn is an ordinary, legal play; modelling it
    // makes the climb reachable in EVERY duel instead of the lucky ones.
    // This changes what the simulator does, never what the checks demand.
    // The opening cycle abstains too, so the FIRST climb lands on
    // FIRST_KHAO_TURN itself rather than five turns later — otherwise
    // mutating that constant upward still clears the observed gain and
    // R91 reads FAKE for reasons that have nothing to do with the rule.
    me.abstainThisTurn = inOpeningCycle(turn) || (turn % 5 === 4);

    me.drewThisTurn = false;
    me.drewBookThisTurn = null;   // R11 — WHICH Book, not merely whether
    me.effectDrewThisTurn = false;
    me.transcendedThisTurn = 0;
    me.ascendedThisTurn = 0;
    me.deviantAscendedThisTurn = 0;
    me.paidResurrectionsThisTurn = 0;
    me.field.tomes.forEach((t) => { if (t) t.usedThisTurn = false; });

    // ── R91 / R12 / R13 · the Khao climb ─────────────────────
    // The engine grants this at the START of the owner's incoming turn,
    // gated on a per-CYCLE spend flag and on turn >= FIRST_KHAO_TURN.
    // The old sim granted at end-of-turn on a per-TURN flag (F7/F8).
    const khaoBefore = me.maxKhao;
    // ⚠ THE OLD R12 LEDGER COULD NOT COUNT. `gainsAfterSpend` was
    // incremented inside `if (!me.khaoSpentThisCycle)`, under a second
    // `if (me.khaoSpentThisCycle)` — a contradiction, so it was dead
    // code, so the end-of-duel assertion `gainsAfterSpend === 0` was
    // `0 === 0` in every duel ever run. It is replaced by two counters
    // taken from OUTSIDE the gate: how many cycles were entered having
    // spent Khao, and how many of those still climbed.
    const spentLastCycle = me.khaoSpentThisCycle;
    // MISACT · KHAO_CLIMB_ALWAYS — the fault R12 forbids: the natural
    // climb is unconditional, +1 a Turn whatever was spent.
    // ── R13 · THE NATURAL CLIMB STOPS AT 10 AND NEVER WRITES DOWN ──
    // ⚠ THIS WAS `Math.min(ACT.MAX_KHAO_NAT, maxKhao + 1)` AND IT HAD THE
    // SAME BUG NPC.js RECORDS FIXING: a seat sitting at maxKhao 12 after
    // a Khao Arcadia was written BACK DOWN to 10 by its own bookkeeping,
    // deleting the card's whole effect. Only the NATURAL climb stops at
    // 10 (R13); anything a card pushed above it is kept, up to
    // MAX_KHAO_ABS. Shatterfield's start-of-turn handler has always
    // guarded on `currentMax < MAX_KHAO_NAT` — this now does too.
    // FOUND BY THE HAND-OFF, not by reading: once the pool was
    // replenished at the boundary instead of at the seat's own turn
    // start, the stale 12 survived beside a maxKhao of 10 and the
    // `khao-within-max` invariant broke 46 times in 10 duels.
    const atNaturalCeiling = me.maxKhao >= ACT.MAX_KHAO_NAT;
    // MISACT · CEILING_IGNORED — the fault R13 forbids: the natural
    // climb keeps going past the natural ceiling. Injected at the GUARD,
    // because with the guard in place the old `Math.min` could not be
    // the reason anything was true — which is LAW 2, and the comment
    // under the old line claimed it while the code broke it.
    // \u2b50 R132, 2026-09-15 \u2014 THE FLOOR IS A LAP, NOT THE NUMBER 2.
    // `turn` counts HALF-turns. `FIRST_KHAO_TURN` (2) reads as "not your
    // first Turn" only at a two-seat table; at 3-4 seats a full Turn is a
    // full LAP (R127), so a bare `>= 2` hands the climb to seats still on
    // their FIRST Turn \u2014 which is what the engine did until caP'n ruled
    // the climb lands "at the beginning of turn 2... after 1 full turn of
    // not using Khao". Derived here from the seat count rather than copied
    // from turnEngine, because the whole worth of this file is that it
    // reaches the same answer by its own road.
    // \u26a0 AND IT IS A NO-OP TODAY, WHICH IS THE POINT OF SAYING SO: SEATS
    // is 2, so `halfTurnsPerFullTurn(SEATS)` IS `FIRST_KHAO_TURN` and this
    // gate cannot currently tell the rule from the coincidence. THE
    // ENGINE'S 3-4 SEAT CLIMB FLOOR IS THEREFORE UNWATCHED BY THIS
    // REFEREE \u2014 a declared blind spot, not a covered one. It is proven
    // instead by lifted execution in tools/rulings0915.selftest.mjs, which
    // exercises the floor at 2, 3 and 4 seats. The day this harness seats
    // more than two, this line starts earning its keep on its own.
    const climbFloor = Math.max(ACT.FIRST_KHAO_TURN, halfTurnsPerFullTurn(SEATS));
    if (turn >= climbFloor
        && (misact("KHAO_CLIMB_ALWAYS") || !me.khaoSpentThisCycle)
        && (!atNaturalCeiling || misact("CEILING_IGNORED"))) {
      const climbed = me.maxKhao + 1;
      // ASSERT BEFORE THE VALUE IS WRITTEN — the clamp must never be the
      // reason the assertion is true.
      H.assert("R13", climbed <= K.MAX_KHAO_NAT,
        `P${active} natural Khao climb landed on ${climbed}, past the natural ceiling ${K.MAX_KHAO_NAT}`);
      // ...and it may never write a CARD-BOOSTED ceiling back down.
      H.assert("R13", climbed >= me.maxKhao,
        `P${active} natural Khao climb wrote Max Khao ${me.maxKhao} down to ${climbed} — only the NATURAL climb stops at ${K.MAX_KHAO_NAT}; what a card pushed above it is kept`);
      if (climbed > khaoBefore) {
        if (me.firstNaturalGainTurn === null) me.firstNaturalGainTurn = turn;
        me.naturalKhaoSeen = Math.max(me.naturalKhaoSeen, climbed);
      }
      me.maxKhao = climbed;
    }
    if (turn >= climbFloor && spentLastCycle) {
      me.spentCycles++;
      if (me.maxKhao > khaoBefore) me.climbsAfterSpend++;
    }
    me.maxKhaoSeen = Math.max(me.maxKhaoSeen, me.maxKhao);
    // R91 + R132 \u2014 asked on the LAP floor for the same reason the gate
    // above is. `K.` not `ACT.`: an assertion must never be softened by
    // the same mutation it exists to catch.
    const trueFloor = Math.max(K.FIRST_KHAO_TURN, halfTurnsPerFullTurn(SEATS));
    H.assert("R91", !(turn < trueFloor && me.maxKhao > khaoBefore),
      `P${active} gained natural Khao on half-turn ${turn}, before the climb floor ${trueFloor} (FIRST_KHAO_TURN ${K.FIRST_KHAO_TURN}, lap ${halfTurnsPerFullTurn(SEATS)})`);
    H.assert("R12", !(spentLastCycle && me.maxKhao > khaoBefore),
      `P${active} gained natural Khao after spending Khao in the cycle`);
    // The NATURAL ceiling binds the climb; the ABSOLUTE ceiling binds
    // everything, cards included. Asserting the natural one against a
    // card-boosted pool is how a real 12 gets reported as a bug.
    H.assert("R13", me.maxKhao <= K.MAX_KHAO_ABS,
      `P${active} Max Khao is ${me.maxKhao}, past the absolute ceiling ${K.MAX_KHAO_ABS}`);
    me.khaoSpentThisCycle = false;

    // R14 · refill to max. `assert(khao === maxKhao)` on the line after
    // `khao = maxKhao` is the definition of a check that cannot fail, so
    // what is asserted instead is the refill against the CARRY — the
    // Khao actually left over from last Turn. R14's counter-rule is that
    // Khao carries over and is topped up by nothing, and on any Turn
    // that followed a spend the two readings are different numbers.
    const khaoCarried = me.khao;
    // ── R14 / R92b · THE POOL WAS FILLED AT THE HAND-OFF ─────
    // It used to be refilled HERE, unconditionally, which is the reading
    // the board itself held until 2026-09-13 and is a different game:
    // a seat that is topped up as its own Turn opens never crosses the
    // boundary holding what it did not spend. `handOffHalfTurn` above
    // does the replenish for the seat SITTING DOWN, and the only thing
    // that moves the pool here is the CLIMB, which writes maxKhao and
    // khao together exactly as Shatterfield's start-of-turn handler does.
    if (me.maxKhao > khaoBefore) me.khao = me.maxKhao;
    // Asked against what the seat actually carried ACROSS the boundary,
    // which is the number R14's counter-rule ("Khao carries over and is
    // topped up by nothing") disagrees with on every Turn that followed
    // a spend. On half-turns 0 and 1 there is no prior hand-off, so the
    // carry is the current pool and both readings agree — which is
    // honest, not vacuous: the census needs the counter true SOMEWHERE.
    const carriedIn = me.khaoCarriedIn ?? me.khao;
    H.assert("R14", ruleIs(K, "R14", me.khao === me.maxKhao, me.khao === carriedIn && carriedIn !== me.maxKhao),
      `P${active} entered its Turn on ${me.khao} Khao, having carried ${carriedIn} across the boundary against a Max of ${me.maxKhao}`);

    // ── R108 · REPLENISH IS OVER; THE DRAW PHASE OPENS ───────
    // Five phases, in the catalogue's own order, on the SEAT. Nothing
    // above this line is a card being played, so nothing above it can
    // be barred; everything below is asked of the gate.
    H.assert("R108", canEnterPhase(me.phase, PHASE.DRAW),
      `P${active} tried to enter the Draw phase from ${me.phase} — R108's phases only run forwards`);
    me.phase = PHASE.DRAW;

    H.note(`── half-turn ${turn} · P${active} (${me.deckName}) · Anima ${me.anima} · Khao ${me.khao}/${me.maxKhao} · Hand ${me.hand.length}`, "turn");

    // ── R08 · THE BACKSTOP, WATCHING A REAL TURN ─────────────
    // Every gate above refuses a route it was TOLD about. This watches
    // the adversary's SEAT instead, in the shape rules.js reads, and
    // asks at the end of the Turn whether anything at all got through.
    // ⚠ WHAT THIS ONE CAN AND CANNOT DO, because the difference is the
    // whole standard. Withdrawing R08_BROAD does NOT redden it: the
    // predicate answers null and the check goes green, so it is no
    // proof that the opening Cycle is barred. Withdrawing R08_HARM
    // DOES — the seat genuinely changed across the Turn, so the
    // over-broad "any payload is harm" reading catches a real payload
    // and this line reports it. That is worth stating precisely: it
    // proves the check READS THE BOARD, and it does not prove the rule.
    // What it is FOR is the divergence — it fires the moment this
    // file's own simulator hurts an adversary on the opening Cycle by
    // any route, which is exactly the failure that would otherwise sit
    // under a green report forever. The rule itself is proven on the
    // bench, where both directions are asked of boards built for them.
    const oppSeatBefore = {
      anima: opp.anima,
      necropolis: opp.necropolis.map((c) => ({ ...c })),
      field: { esclings: opp.field.esclings.map((sl) => ({ escendant: sl.escendant, currentLyf: sl.currentLyf })) },
    };

    // ══ DRAW ══════════════════════════════════════════════════
    // R07 · the first seat takes no GRIMOIRE draw on Turn 1 — the
    // Luxmoire draw is explicitly still allowed, which is the half of
    // R07 the old sim never modelled at all.
    const wantGrim = Legal.draw(ACT, me, "grimoire", turn, isFirstSeat);
    if (isFirstSeat && inOpeningCycle(turn)) {
      // Asked of K, not ACT. The old line read the TRUE gate and then
      // announced that the true gate agreed with itself.
      H.assert("R07", Legal.draw(K, me, "grimoire", turn, isFirstSeat) === "R07",
        "P0 was permitted a Grimoire draw on Turn 1");
      const luxOK = Legal.draw(ACT, me, "luxmoire", turn, isFirstSeat);
      H.assert("R07", Legal.draw(K, me, "luxmoire", turn, isFirstSeat) === null || me.luxmoire.length === 0,
        "P0 was refused the Luxmoire draw on Turn 1 — R07 forbids only the Grimoire draw");
      if (luxOK === null) { me.hand.push(me.luxmoire.pop()); me.drewThisTurn = true; me.drewBookThisTurn = "luxmoire"; }
    } else if (wantGrim === null) {
      // R07 · THE SEAT GOING SECOND MAY DRAW ITS GRIMOIRE on the opening
      // Cycle, and that permission is asserted where it is exercised
      // rather than left implied by the absence of a refusal. It is the
      // half of the ruling that names the second seat by itself, and a
      // gate that barred the whole Cycle instead of the first seat
      // would pass every "no Grimoire draw" test ever written.
      if (!isFirstSeat && inOpeningCycle(turn)) {
        H.assert("R07", Legal.draw(K, me, "grimoire", turn, isFirstSeat) === null,
          `P${active} went SECOND and was refused its Grimoire draw on half-turn ${turn} — R07 bars only the seat going FIRST`);
      }
      me.hand.push(me.grimoire.pop());
      me.drewThisTurn = true;
      me.drewBookThisTurn = "grimoire";
    } else if (me.grimoire.length === 0) {
      // ── R40 / R41 / R42 / R43 · GRAVESHIFT ──
      const gs = Legal.graveshift(ACT, me);
      // R96's ONE exemption. A seat on exactly the fare may still shift;
      // this asserts the exemption is honoured rather than assuming it.
      H.assert("R96", me.anima !== ACT.GRAVESHIFT_COST || Legal.graveshift(K, me) !== "R41",
        `P${active} sat on exactly the ${K.GRAVESHIFT_COST} fare with an empty Grimoire and was refused — R96 exempts R43's remedy`);
      if (gs === null) {
        const before = me.anima;
        const handBefore = me.hand.length, necroBefore = me.necropolis.length;
        const pool = [...me.hand, ...me.necropolis];
        dimAnima(me, ACT.GRAVESHIFT_COST, ANIMA_LOSS_KINDS.SELF);  // R100 — a fare is a SELF dim
        H.assert("R41", before - me.anima === K.GRAVESHIFT_COST,   // K: the check reads the rule
          `P${active} Graveshift cost ${before - me.anima} Anima (rule says exactly ${K.GRAVESHIFT_COST})`);
        pool.forEach((c) => me.shifted.add(c.uid));  // R86 — past the paid rite forever
        // MISACT · SHIFT_LEAVES_HAND — the fault R42 forbids: the shift
        // raises only the Necropolis and leaves the hand where it is.
        if (misact("SHIFT_LEAVES_HAND")) {
          me.grimoire = [...me.necropolis];
          me.necropolis = [];
        } else {
          me.grimoire = pool;
          me.hand = []; me.necropolis = [];
        }
        // ⚠ `pool.length === hand.length + necropolis.length` was asserted
        // BEFORE the move, one line after `pool = [...hand, ...necropolis]`.
        // It restated its own construction. The claim is made AFTER the
        // move now, against numbers taken before it, and R42's
        // counter-rule — that the shift raises only the Necropolis and
        // leaves the hand where it is — is a different number whenever
        // the shifting seat was holding anything at all.
        H.assert("R42", ruleIs(K, "R42",
          me.grimoire.length === handBefore + necroBefore && me.hand.length === 0 && me.necropolis.length === 0,
          me.grimoire.length === necroBefore),
          `P${active} Graveshift raised ${me.grimoire.length} cards from a hand of ${handBefore} and a Necropolis of ${necroBefore}`);
        for (let i = me.grimoire.length - 1; i > 0; i--) {
          const j = Math.floor(rng() * (i + 1));
          [me.grimoire[i], me.grimoire[j]] = [me.grimoire[j], me.grimoire[i]];
        }
        H.assert("R40", me.grimoire.length > 0, `P${active} Graveshift produced an empty Grimoire`);
        // If the exempt fare emptied the seat, R33 must collect — the
        // exemption buys the RIGHT to shift, never immunity to zero.
        if (me.anima <= 0) {
          // ⚠ WAS `assert("R33", me.anima <= 0)` INSIDE `if (anima <= 0)` —
          // a tautology, and this file's own header lists that exact shape
          // among the ones it had already killed. What R33 claims here is
          // that the duel ENDS: the Graveshift fare is the one exemption
          // to the till and the price of the exemption is the Solrend.
          // Asserted on the consequence, which can be false.
          gameOver = true; winner = 1 - active; endRule = "R33"; endBy = "anima";
          H.assert("R33", gameOver && endRule === "R33" && winner === 1 - active,
            `P${active} paid the exempt Graveshift fare to ${me.anima} Anima and the Solrend did not end`);
        }
        H.note(`  P${active} GRAVESHIFT — ${K.GRAVESHIFT_COST} Anima, ${me.grimoire.length} cards returned`, "action");
      }
    }
    // R10 · DRAW-THEN-DISCARD. F6 FIX: the old sim discarded first,
    // which is Rulez v1 and the opposite of both the rule and the engine.
    H.assert("R10", me.hand.length <= K.HAND_LIMIT + 1,
      `P${active} holds ${me.hand.length} cards straight after the draw — even a draw at the cap leaves at most ${K.HAND_LIMIT + 1}`);
    while (me.hand.length > ACT.HAND_LIMIT) {
      me.necropolis.push(me.hand.shift());
      H.note(`  P${active} discarded to the hand limit`, "action");
    }
    H.assert("R10", me.hand.length <= K.HAND_LIMIT,
      `P${active} ended the draw phase holding ${me.hand.length} (limit ${K.HAND_LIMIT})`);
    H.assert("R11", !me.drewThisTurn || Legal.draw(K, me, "grimoire", turn, isFirstSeat) === "R11",
      `P${active} was permitted a second draw in one Turn`);
    // R11 · ...AND FROM THE OTHER BOOK. caP'n calls this "as always",
    // and "as always" is exactly how a clause goes unchecked: one
    // `drewThisTurn` flag refuses a second draw without ever knowing
    // WHICH Book, so the cross-Book case rode on the same-Book one.
    // Asked only when the other Book still holds cards, or the empty-
    // zone leg would answer R11 for a reason that is not R11's clause.
    if (me.drewBookThisTurn) {
      const otherBook = me.drewBookThisTurn === "grimoire" ? "luxmoire" : "grimoire";
      if (me[otherBook].length > 0) {
        H.assert("R11", Legal.draw(K, me, otherBook, turn, isFirstSeat) === "R11",
          `P${active} drew from its ${me.drewBookThisTurn} and was still permitted a ${otherBook} draw the same Turn`);
      }
    }
    sweep(H, players, `draw t${turn}`);

    // ── R108 · MAIN ──────────────────────────────────────────
    H.assert("R108", canEnterPhase(me.phase, PHASE.MAIN),
      `P${active} tried to enter Main from ${me.phase} — R108's phases only run forwards`);
    me.phase = PHASE.MAIN;
    // EVERY card action below is legal in Main, and that is the
    // permission half. Asked of the catalogue's OWN bar list so a new
    // barred action type is covered here the day it is added.
    Object.keys(ATTACK_PHASE_BARS).forEach((act) => {
      H.assert("R108", phaseAllows(act, me.phase).allowed,
        `P${active} was barred from ${act} in its MAIN phase — R108 shuts the hand only once Attack is DECLARED`);
    });

    // ══ TRANSCEND · R16 / R17 / R20 ═══════════════════════════
    let transcends = 0;
    me.field.esclings.forEach((slot, i) => {
      if (slot.escendant) return;
      const hIdx = me.hand.findIndex((c) => Legal.transcend(ACT, me, i, c.id, turn) === null);
      if (hIdx === -1) return;
      const anima0 = me.anima, khao0 = me.khao;
      // The gate is asked of K BEFORE the slot is filled — afterwards it
      // answers R16 for "occupied" and says nothing about the rule.
      const gateK = Legal.transcend(K, me, i, me.hand[hIdx].id, turn);
      // R09 · the first seat may act on its opening Turn.
      if (turn === 0) H.assert("R09", gateK === null,
        `P${active} was refused a Transcend on half-turn 0 — R09 is exactly that permission`);
      // R17 · nothing caps Transcends per Turn, and this is the second
      // or later one in this Turn, so the gate must still say yes.
      if (me.transcendedThisTurn >= 1) H.assert("R17", gateK === null,
        `P${active} was refused Transcend number ${me.transcendedThisTurn + 1} of the Turn — R17 caps nothing`);
      const card = me.hand.splice(hIdx, 1)[0];
      const c = getCard(card.id);
      // R16 · asserted on the state, not on the intention
      H.assert("R16", getCard(slot.escling)?.school === c.school,
        `P${active} Transcended ${c.id} (${c.school}) onto a ${getCard(slot.escling)?.school} Escling`);
      slot.escendant = card; slot.currentLyf = c.lyf;
      slot.natural = true; slot.deviant = false; slot.soul = [];
      seatStamps(slot);                        // R78 — it arrives wearing both
      transcends++; me.transcendedThisTurn++;
      // MISACT · SUMMON_TOLL — the fault R20 forbids: a natural
      // Transcend is priced at a Khao like every other summon in the
      // genre. Asserted BEFORE the toll would be legal to notice.
      if (misact("SUMMON_TOLL")) me.khao = Math.max(0, me.khao - 1);
      // R20 · natural Transcend is free. Its counter-rule prices it at a
      // Khao, which is what every other summon in the genre costs and
      // exactly what R20 exists to deny.
      H.assert("R20", ruleIs(K, "R20",
        me.anima === anima0 && me.khao === khao0,
        me.khao === khao0 - 1),
        `P${active} paid for a natural Transcend (Anima ${anima0}→${me.anima}, Khao ${khao0}→${me.khao})`);
      // R78 · the being arrives already carrying its two stamps. The
      // counter-rule gives it ONE — the reading where a being bears its
      // Ilk's stamp and its School is only a matching key.
      // ⚠ READS slot.stamps, NOT stampsOf(c). The old line called the
      // same pure function the fault was injected into, so it answered
      // "was I told to misbehave" rather than "what is on this rift".
      H.assert("R78", ruleIs(K, "R78", slot.stamps.length === 2, slot.stamps.length === 1),
        `P${active} Transcended ${c.id} and rift ${i} carries ${slot.stamps.length} Primera stamp(s) (expected exactly 2)`);
      H.note(`  P${active} Transcended ${c.id} → slot ${i}`, "action");
    });
    // R17 · there is NO cap on Transcends per Turn. Proven by observing
    // that after the pass no legal Transcend remains unspent.
    H.assert("R17", !me.field.esclings.some((s, i) =>
      !s.escendant && me.hand.some((c) => Legal.transcend(ACT, me, i, c.id, turn) === null)),
      `P${active} left a legal Transcend unplayed after ${transcends} — R17 caps nothing`);
    // ── R74 · ASKED OF EVERY RIFT, AND THE ANSWER MUST TRACK OCCUPANCY ──
    // The old line asked the gate about a rift it had chosen for being
    // BARE and then accepted any non-null answer — which the gate gives
    // for every input while R74 stands. Asked of the whole line, the
    // verdict has to follow a board fact, and both readings have a
    // failing state: withdraw R74 and the bare rifts stop being refused,
    // withdraw R74_SCOPE and the occupied ones stop being reachable.
    me.field.esclings.forEach((s, j) => {
      const verdict = Legal.targetProphecyOrEscling(K, s);
      H.assert("R74", s.escendant ? verdict === null : verdict === "R74",
        `P${active} rift ${j} ${s.escendant ? `holds ${s.escendant.id} and a card effect was refused (${verdict})` : "is a bare Escling and a card effect was allowed to target it"}`);
    });

    // ══ ASCEND · R18 / R19 / R20 ══════════════════════════════
    for (let i = 0; i < me.field.esclings.length; i++) {
      const slot = me.field.esclings[i];
      const hIdx = me.hand.findIndex((c) => Legal.ascend(ACT, me, i, c.id, false) === null);
      if (hIdx === -1) continue;
      const anima0 = me.anima, khao0 = me.khao;
      const prev = slot.escendant;
      const card = me.hand.splice(hIdx, 1)[0];
      const up = getCard(card.id), cur = getCard(prev.id);
      H.assert("R19", up.school === cur.school && up.ilk === cur.ilk,
        `P${active} natural-Ascended ${cur.id} (${cur.school}/${cur.ilk}) into ${up.id} (${up.school}/${up.ilk})`);
      // MISACT · ASCEND_SEALS_SOUL — the fault R49 forbids: the being
      // you Ascend over is spent and sealed in the Necropolis instead of
      // becoming Soul.
      if (misact("ASCEND_SEALS_SOUL")) me.necropolis.push(prev);
      else slot.soul.push(prev);            // R49 · the soul is what is attached
      slot.escendant = card; slot.currentLyf = up.lyf;
      seatStamps(slot);                        // R78/R84 — the new flesh, its own two
      me.ascendedThisTurn++;
      // MISACT · SUMMON_TOLL — the same fault one rite over.
      if (misact("SUMMON_TOLL")) me.khao = Math.max(0, me.khao - 1);
      H.assert("R20", ruleIs(K, "R20",
        me.anima === anima0 && me.khao === khao0,
        me.khao === khao0 - 1),
        `P${active} paid for a natural Ascend (Anima ${anima0}→${me.anima}, Khao ${khao0}→${me.khao})`);
      // R49 · the Soul is what is ATTACHED. Its counter-rule is the
      // other obvious reading — that the being you Ascend over is spent,
      // sealed in the Necropolis like anything else that leaves the
      // field — and the two put the same card in different zones.
      H.assert("R49", ruleIs(K, "R49",
        slot.soul.some((s) => s.uid === prev.uid),
        me.necropolis.some((c) => c.uid === prev.uid) && !slot.soul.some((s) => s.uid === prev.uid)),
        `P${active} slot ${i} lost the Ascended-over being from its Soul`);
      H.note(`  P${active} Ascended → ${up.id} in slot ${i}`, "action");
      break;
    }
    // R18 · asserted on the STATE, and asserted to be unrepeatable
    H.assert("R18", me.ascendedThisTurn <= 1,
      `P${active} Ascended ${me.ascendedThisTurn} times in one Turn (max 1)`);
    // ── R102 · ONE ASCEND PER TURN, *TOTAL* ──────────────────
    // Rulez v2 ch.4 and the glossary, stated twice: *"Ascend one
    // Escendant per turn (one total, regardless of Natural or Deviant)"*.
    // The Natural/Deviant distinction buys no extra climb — so the two
    // counters SHARE one budget, and the claim is that the Deviant door
    // is shut by a NATURAL Ascend and not only by another Deviant one.
    // Read off the catalogue's own table so the 1 is not typed here.
    H.assert("R102", me.ascendedThisTurn <= PER_TURN_DEVIANT_LIMITS.ascend,
      `P${active} took ${me.ascendedThisTurn} Ascends in one Turn against a budget of ${PER_TURN_DEVIANT_LIMITS.ascend} TOTAL — Natural and Deviant share it`);
    if (me.ascendedThisTurn >= PER_TURN_DEVIANT_LIMITS.ascend) {
      const anyUp = me.hand.find((c) => getCard(c.id)?.type === T.ESCENDANT);
      if (anyUp) H.assert("R102", me.field.esclings.every((sl, j) =>
        !sl.escendant || Legal.ascend(K, me, j, anyUp.id, true) !== null),
        `P${active} spent its Turn's one Ascend NATURALLY and was still offered a DEVIANT one — R102 makes the budget one TOTAL`);
    }
    // AND THE TRANSCEND BUDGET IS UNTOUCHED BY IT — any number per Turn
    // (ch.4, and R17), which is the permission half of the same ruling.
    H.assert("R102", PER_TURN_DEVIANT_LIMITS.deviantTranscend !== 1,
      `the catalogue caps Deviant Transcends at ${PER_TURN_DEVIANT_LIMITS.deviantTranscend} per Turn — R102 allows ANY number`);
    if (me.ascendedThisTurn >= 1) {
      H.assert("R18", me.field.esclings.every((s, i) => me.hand.every((c) => Legal.ascend(K, me, i, c.id, false) !== null)),
        `P${active} was still permitted an Ascend after using the Turn's one`);
    }

    // ══ SOUL BUILDING · R71 / R72 / R73 / R69 / R70 ═══════════
    me.field.esclings.forEach((slot, i) => {
      if (!slot.escendant) return;
      // Impera first, then U'hlti, then Affinity — the order R70 requires.
      const impIdx = me.hand.findIndex((c) => Legal.attachImpera(ACT, slot, c.id) === null);
      if (impIdx !== -1) {
        const card = me.hand.splice(impIdx, 1)[0];
        const c = getCard(card.id);
        slot.impera[c.level] = card;
        // This used to assert `!!slot.impera[c.level]` one line after
        // writing it. The rule is that the SECOND one is refused.
        H.assert("R71", Legal.attachImpera(K, slot, card.id) === "R71",
          `P${active} slot ${i} was permitted a second Impera of level ${c.level}`);
      }
      const uIdx = me.hand.findIndex((c) => Legal.attachUhlti(ACT, slot, c.id) === null);
      if (uIdx !== -1) {
        slot.uhlti = me.hand.splice(uIdx, 1)[0];
        H.assert("R72", Legal.attachUhlti(K, slot, slot.uhlti.id) === "R72",
          `P${active} slot ${i} was permitted a second U'hlti`);
      }
      const aIdx = me.hand.findIndex((c) => Legal.attachAffinity(ACT, slot, c.id) === null);
      if (aIdx !== -1) {
        slot.affinity = me.hand.splice(aIdx, 1)[0];
        H.assert("R70", !!slot.uhlti && slot.natural && getCard(slot.escendant.id).level === 3,
          `P${active} slot ${i} carries an Affinity without a Natural L3 bearing a U'hlti`);
        H.assert("R73", Legal.attachAffinity(K, slot, slot.affinity.id) === "R73",
          `P${active} slot ${i} was permitted a second Affinity`);
      }
      if (isSupreme(K, slot)) {
        // No `ruleIs` here, and that is the point: the clause lives in
        // isSupreme, so under R69's counter-rule a being one card short
        // walks into this block and this assertion — which demands the
        // WHOLE set — is what catches it.
        H.assert("R69", slot.natural && getCard(slot.escendant.id).level === 3
          && [1, 2, 3].every((l) => slot.impera[l]) && !!slot.uhlti && !!slot.affinity,
          `P${active} slot ${i} was read as Supreme without the full Natural L3 + 3 Impera + U'hlti + Affinity`);
      }
    });

    // ══ RESURRECTION · R44-R48 / R77 / R85 / R86 / R89 ════════
    {
      const seals = me.necropolis.filter((c) => getCard(c.id)?.type === T.ESCENDANT);
      const emptyIdx = me.field.esclings.findIndex((s) => !s.escendant);
      if (seals.length && emptyIdx !== -1 && !me.abstainThisTurn) {
        const seal = seals.find((s) => Legal.resurrect(ACT, me, s, emptyIdx, "paid") === null);
        if (seal) {
          const c = getCard(seal.id);
          const cost = ACT.RESURRECT_COST[`L${c.level}`];
          const khao0 = me.khao, anima0 = me.anima;
          const paidFrom = payOnePool(me, cost);   // R107 — one pool, never a split
          const fromAnima = paidFrom === "anima" ? cost : 0;
          // R96 · the till. A voluntary payment may never land the payer
          // on 0 Anima; only damage may (R33). Asserted on the RESULT.
          H.assert("R96", fromAnima === 0 || me.anima > 0,
            `P${active} paid ${fromAnima} Anima for a Resurrection and landed on ${me.anima} — R96 forbids paying yourself to zero`);
          H.assert("R47", (khao0 - me.khao) + (anima0 - me.anima) === K.RESURRECT_COST[`L${c.level}`],
            `P${active} paid ${(khao0 - me.khao) + (anima0 - me.anima)} for an L${c.level} Resurrection (rule: ${K.RESURRECT_COST[`L${c.level}`]})`);
          me.necropolis = me.necropolis.filter((x) => x.uid !== seal.uid);
          const slot = me.field.esclings[emptyIdx];
          slot.escendant = seal; slot.currentLyf = c.lyf; slot.soul = [];
          seatStamps(slot);                    // R84 — a returned being wears its stamps
          // R48 · the Soul comes back with it
          const soulUids = me.soulOf[seal.uid] || [];
          // ⚠ NO MISACT ROW HERE, AND THE MEASUREMENT THAT SETTLED IT.
          // A `RESURRECT_BARE` fault was written, run and removed. It
          // reddened nothing, because the fault it commits — returning
          // the being and leaving its Soul sealed — needs a Resurrection
          // OF A BEING THAT HAD A SOUL, and this simulator never
          // performs one. Instrumented over 75 duels it took 1961
          // Resurrections and 0 of them had a Soul to leave behind:
          // souls are only written by `soulOf` at a full ruin, and a
          // being that was Ascended over is almost never the one the
          // rite picks back up. A roster row that can never bite is
          // furniture with an alarm attached, so it is gone and R48 is
          // declared instead. See NOT_PROVEN_REASONS.
          const returned = me.necropolis.filter((x) => soulUids.includes(x.uid));
          me.necropolis = me.necropolis.filter((x) => !soulUids.includes(x.uid));
          slot.soul = returned;
          // R48's counter-rule is that a Resurrected being comes back
          // BARE — its Soul stays sealed — which is the reading that
          // makes the rite cheap and is precisely what R48 denies. It is
          // a different number only when the sealed being HAD a Soul, so
          // on a board where nothing was ever Ascended over this stays
          // green and the mutation self-test reports it FAKE. That is
          // the honest outcome, not a bug in the check.
          H.assert("R48", ruleIs(K, "R48",
            returned.length === soulUids.length,
            returned.length === 0 && soulUids.length > 0),
            `P${active} Resurrected ${c.id} without its Soul (${returned.length} of ${soulUids.length} returned)`);
          const before = me.tally[seal.uid] || 0;
          const twinUidPre = me.necropolis.find((x) => x.id === seal.id && x.uid !== seal.uid)?.uid;
          const twinBefore = twinUidPre ? (me.tally[twinUidPre] || 0) : null;
          me.tally[seal.uid] = before + 1;
          // R44 / R85 · the tally is per COPY, and it is asserted BEFORE
          // the next attempt rather than trusted.
          H.assert("R44", me.tally[seal.uid] <= K.RESURRECT_LIMIT[`L${c.level}`],
            `P${active} Resurrected ${seal.uid} ${me.tally[seal.uid]}× (L${c.level} tally is ${K.RESURRECT_LIMIT[`L${c.level}`]} per copy)`);
          // `me.tally[u] === (me.tally[u] || 0)` is true for every truthy
          // value, which is every tally. The real question is whether a
          // SECOND COPY of the same printed card was charged for this one.
          if (twinUidPre) {
            H.assert("R85", (me.tally[twinUidPre] || 0) === twinBefore,
              `P${active} returning ${seal.uid} moved another copy's tally from ${twinBefore} to ${me.tally[twinUidPre]} — R85 makes tallies PER COPY, not per name`);
          }
          // ⚠ `tally[uid] === before + 1` on the line after
          // `tally[uid] = before + 1` proved arithmetic, not a rule. The
          // question R77 actually asks is WHICH METHODS COUNT, and that
          // is one shared predicate: the simulator asks it through ACT
          // when it decides to bump, the check asks it through K.
          H.assert("R77", Legal.tallies(K, "paid") && me.tally[seal.uid] === before + 1,
            `P${active} a paid Resurrection did not increment the tally (R77: every method counts)`);
          H.assert("R45", c.level === 1,
            `P${active} Resurrected an L${c.level} directly (R45: L1 first, in order)`);
          // R46 was `assert(true)`. It is now the real question: with an
          // L2/L3 sealed in the same Necropolis, is the skip refused?
          // ── R45 / R46 · THE SKIP IS ONTO A BARE RIFT ─────────
          // ⚠ THIS ASKED ABOUT THE RIFT THE RITE HAD JUST FILLED, and
          // with the climb allowed (see Legal.resurrect) an L2 back over
          // its standing L1 is LEGAL — RULEZ_V2 ch.17 — so the old
          // assertion reported a lawful climb as an R46 violation the
          // moment the gate stopped being narrower than the rule. R46's
          // actual claim is the SKIP: a higher level straight onto a
          // BARE Escling, with nothing under it.
          const higher = me.necropolis.find((x) => (getCard(x.id)?.level || 0) > 1
            && getCard(x.id)?.type === T.ESCENDANT);
          const bareIdx = me.field.esclings.findIndex((sl) => !sl.escendant);
          if (higher && bareIdx !== -1) {
            const v46 = Legal.resurrect(K, me, higher, bareIdx);
            H.assert("R46", v46 === "R46" || v46 === "R89",
              `P${active} was permitted to Resurrect L${getCard(higher.id).level} ${higher.id} straight onto a bare Escling (${v46}) — R45/R46 demand the levels in order`);
          }
          // ...and the CLIMB is the permission half, on the rift the rite
          // just filled: the next level up is allowed over what stands.
          {
            const standing = getCard(slot.escendant.id);
            const nextUp = me.necropolis.find((x) => getCard(x.id)?.type === T.ESCENDANT
              && (getCard(x.id)?.level || 0) === standing.level + 1);
            if (nextUp) H.assert("R46", Legal.resurrect(K, me, nextUp, emptyIdx, "effect") !== "R46",
              `P${active} was refused an L${standing.level + 1} Resurrection over its standing L${standing.level} — RULEZ_V2 ch.17 allows the climb`);
          }
          me.paidResurrectionsThisTurn++;
          H.assert("R89", me.paidResurrectionsThisTurn <= K.RESURRECT_PER_TURN,
            `P${active} took ${me.paidResurrectionsThisTurn} PAID Resurrections this Turn (limit ${K.RESURRECT_PER_TURN})`);
          H.assert("R89", seals.every((s) => Legal.resurrect(K, me, s, emptyIdx, "paid") !== null),
            `P${active} was still permitted the paid rite after using the Turn's one`);
          // R89 (rewritten today) binds the RITE only. A card-effect
          // return — a Tome, an Impera, the Time Primera — rides
          // alongside it, and that exemption is what those cards buy.
          const rider = me.necropolis.find((x) => getCard(x.id)?.type === T.ESCENDANT
            && getCard(x.id)?.level === 1 && !me.shifted.has(x.uid));
          if (rider) {
            H.assert("R89", Legal.resurrect(K, me, rider, emptyIdx, "effect") !== "R89",
              `P${active} a card-effect return was blocked by the paid clock — R89 exempts it`);
            // ...and R77 still tallies it, which is the half that is NOT exempt.
            const t0 = me.tally[rider.uid] || 0;
            if (Legal.tallies(ACT, "effect")) me.tally[rider.uid] = t0 + 1;
            H.assert("R77", Legal.tallies(K, "effect") && me.tally[rider.uid] === t0 + 1,
              `P${active} a card-effect return did not touch the tally — R77 counts EVERY method`);
            me.tally[rider.uid] = t0; // the rider was a probe, not a play
          }
          H.note(`  P${active} Resurrected ${c.id} (tally ${me.tally[seal.uid]})`, "action");
        }
        // R86 · a card that shifted is past the paid rite for good
        const shiftedSeal = seals.find((s) => me.shifted.has(s.uid) && getCard(s.id)?.level === 1);
        if (shiftedSeal) {
          // Probe a CLEAN context: this Turn's Resurrection and the now-
          // filled slot would answer R89/R46 first and hide the R86 case.
          const probe = {
            ...me, paidResurrectionsThisTurn: 0,
            field: { ...me.field, esclings: me.field.esclings.map((s, ix) => (ix === emptyIdx ? { ...s, escendant: null } : s)) },
          };
          H.assert("R86", Legal.resurrect(K, probe, shiftedSeal, emptyIdx, "paid") === "R86",
            `P${active} was permitted the paid rite on ${shiftedSeal.uid}, which passed through a Graveshift`);
        }
      }
    }

    // ══ TOMES · R34-R39 / R15 / B01 / B02 / R92 ═══════════════
    if (Legal.placeTome(ACT, me) === null) {
      const tIdx = me.hand.findIndex((c) => getCard(c.id)?.type === T.TOME);
      if (tIdx !== -1) {
        const anima0 = me.anima, khao0 = me.khao;
        const card = me.hand.splice(tIdx, 1)[0];
        const free = me.field.tomes.findIndex((t) => t === null);
        // MISACT · RIFT_FACEUP — the fault R34 forbids: the ordinary
        // trap-card economy, where a Rift lands face-UP.
        me.field.tomes[free] = { ...card, faceDown: !misact("RIFT_FACEUP"), placedTurn: turn, usedThisTurn: false };
        // R34's counter-rule is the ordinary trap-card economy of every
        // other game: a Rift costs a Khao and the card lands face-up.
        // Both halves are read off the board, and both differ.
        H.assert("R34", ruleIs(K, "R34",
          me.anima === anima0 && me.khao === khao0 && me.field.tomes[free].faceDown,
          me.khao === khao0 - 1 && !me.field.tomes[free].faceDown),
          `P${active} placed a Tome face-up or for a price (Anima ${anima0}→${me.anima}, Khao ${khao0}→${me.khao})`);
        H.note(`  P${active} placed ${card.id} face-down in Rift ${free}`, "action");
      }
    }
    H.assert("R38", me.field.tomes.filter(Boolean).length <= K.TOME_SLOTS,
      `P${active} holds ${me.field.tomes.filter(Boolean).length} Tomes (max ${K.TOME_SLOTS} Rifts)`);

    me.field.tomes.forEach((t, i) => {
      if (!t) return;
      const c = getCard(t.id);
      const key = `L${c.level}`;
      const waited = turn - t.placedTurn;
      const gate = me.abstainThisTurn ? "R15" : Legal.activateTome(ACT, me, i, turn, false);
      // ── R08 · A TOME IS A ROUTE TO THE OTHER SIDE ───────────
      // Asked on the opening Cycle only, where the two rules meet:
      // a Tome that RUINS something of theirs is refused (R08), and a
      // Tome that heals, draws or raises Khao is NOT (R09). Same gate,
      // same half-turn, two answers — which is what makes the second
      // one a check rather than an echo of the first.
      if (inOpeningCycle(turn) && Legal.activateTome(ACT, me, i, turn, false) !== "R35") {
        if (tomeAttacks(c)) {
          H.assert("R08", Legal.activateTome(K, me, i, turn, false) === "R08",
            `P${active} was permitted the Tome ${t.id} (${c.fx}) on half-turn ${turn} — it ruins something of the adversary's, and that is an Attack`);
        } else {
          H.assert("R09", Legal.activateTome(K, me, i, turn, false) !== "R08",
            `P${active} was refused the harmless Tome ${t.id} (${c.fx}) on half-turn ${turn} — R09 permits activation and R08 bars only Attacks`);
        }
      }
      // R35/R36/R37 — asserted against the DATA in TOME_WAIT, so
      // mutating TOME_WAIT.L1 turns this red instead of leaving it true.
      const code = c.level === 1 ? "R35" : c.level === 2 ? "R36" : "R37";
      const waitK = tomeWaitOf(K, c.level, t.faceDown !== false);
      H.assert(code, !(waited < waitK) || me.abstainThisTurn || Legal.activateTome(ACT, me, i, turn, false) !== null,
        `P${active} could activate L${c.level} Tome ${t.id} after ${waited} half-turns (the wait is ${waitK})`);
      if (gate !== null) return;

      t.faceDown = false;
      // ── R105 · THE ACTIVATION FARE, IN BOTH POOLS ────────────
      // A card that prints two numbers is naming two pools itself, which
      // is the exemption R107 has always carried — that is not the split
      // R107 forbids. Lyf printed on a Treasure is its durability and is
      // never money, which is why `printedFare` reads exactly two fields.
      const fare = printedFare(c);
      const khaoBeforeUse = me.khao, animaBeforeUse = me.anima;
      H.assert("R15", khaoBeforeUse >= fare.khao && (fare.anima === 0 || tillClears(me.anima, fare.anima, false)),
        `P${active} activated ${t.id} holding ${khaoBeforeUse} Khao / ${animaBeforeUse} Anima against a printed fare of ${fare.khao} Khao + ${fare.anima} Anima`);
      // MISACT · FARE_PAID_ONCE — the fault R105 forbids, and it is the
      // reading this codebase actually held between 2026-08-26 and
      // 2026-09-13: a persistent card buys the right to fire for free
      // after the first time. caP'n overturned it in full — *"you must
      // pay its fare again, thats the cost of it being able to stay on
      // the field indefinitely."*
      const skipFare = misact("FARE_PAID_ONCE") && c.persistent && t.everFired;
      if (!skipFare) {
        me.khao -= fare.khao;
        if (fare.anima > 0) dimAnima(me, fare.anima, ANIMA_LOSS_KINDS.SELF);  // R100 — a fare is a SELF dim
        if (fare.khao > 0) me.khaoSpentThisCycle = true;
      }
      t.everFired = true;
      // R105 · EVERY activation pays. Asserted as the transition, so a
      // reading in which a recurring card's second firing is free — the
      // one caP'n overturned in full on 2026-09-13 — is a different pair
      // of numbers and this line reports it.
      H.assert("R105", (khaoBeforeUse - me.khao) === fare.khao && (animaBeforeUse - me.anima) === fare.anima,
        `P${active} paid ${khaoBeforeUse - me.khao} Khao + ${animaBeforeUse - me.anima} Anima to fire ${t.id} (printed: ${fare.khao} Khao + ${fare.anima} Anima) — the fare is paid EVERY time, first and after`);
      // R103 — the cadence stamp, in the same half-turn units the board
      // keeps `lastUsedTurn` in, so the SAME cooldownHalfTurns answers
      // both and the two can never drift.
      if (t.lastFiredTurn != null) {
        const need103 = cooldownHalfTurns(c.effectTag, SEATS);
        H.assert("R103", (turn - t.lastFiredTurn) >= need103,
          `P${active} fired ${t.id} (${c.effectTag || "untagged"}) ${turn - t.lastFiredTurn} half-turn(s) after its last firing — ${c.effectTag || "the fallback"} is ${need103} half-turns, because a full Turn is TWO`);
      }
      t.lastFiredTurn = turn;
      t.usedThisTurn = true;

      // Card-driven effects. A CARD may push Max Khao to MAX_KHAO_ABS —
      // the old sim clamped card effects to MAX_KHAO_NAT (F8), which
      // silently made the absolute ceiling unreachable and R13 vacuous.
      if (c.fx === "khao+1" || c.fx === "khao+2") {
        const raw = me.maxKhao + (c.fx === "khao+2" ? 2 : 1);
        // MISACT · CEILING_IGNORED — the same fault one ceiling up: a
        // CARD writes past the absolute cap.
        const applied = misact("CEILING_IGNORED") ? raw : Math.min(ACT.MAX_KHAO_ABS, raw);
        // A card that pushes INTO the ceiling is clamped, not illegal —
        // what is illegal is a card-boosted pool that LANDS above it.
        H.assert("R13", applied <= K.MAX_KHAO_ABS,
          `P${active} a card left Max Khao at ${applied}, past the absolute ceiling ${K.MAX_KHAO_ABS}`);
        me.maxKhao = applied;
        me.maxKhaoSeen = Math.max(me.maxKhaoSeen, applied);
        me.procTriggers++;
      } else if (c.fx && c.fx.startsWith("anima+")) {
        const gain = Number(c.fx.slice(6)) || 1;
        // MISACT · CEILING_IGNORED — and R32's: a heal writes past 50.
        const applied = misact("CEILING_IGNORED") ? me.anima + gain : Math.min(ACT.MAX_ANIMA, me.anima + gain);
        // ASSERT THE APPLIED VALUE, NOT THE CLAMP. ACT applies it and K
        // judges it, so this still goes red when MAX_ANIMA is mutated —
        // but a card healing INTO the ceiling is clamped, not illegal.
        H.assert("R32", applied <= K.MAX_ANIMA,
          `P${active} a card left Anima at ${applied}, past the ceiling ${K.MAX_ANIMA}`);
        gainAnima(me, applied - me.anima, misact("CEILING_IGNORED") ? Infinity : ACT.MAX_ANIMA);
        me.procTriggers++;
      } else if (c.fx === "ruin_being") {
        const victim = opp.field.esclings.find((s) => s.escendant);
        const bare = opp.field.esclings.find((s) => !s.escendant);
        // MISACT · CARD_HITS_ESCLING — the fault R74 forbids: the ruin
        // effect finds no Escendant, reaches for the bare rift beside it
        // and takes the ETERNAL Escling off the board. The only injection
        // site; the check that catches it counts Esclings on the line.
        if (misact("CARD_HITS_ESCLING")) (bare || opp.field.esclings[0]).escling = null;
        if (victim) { ruinOrDescend(H, K, opp, victim, "card"); me.procTriggers++; }
      } else if (c.fx === "draw+1" && me.grimoire.length) {
        me.hand.push(me.grimoire.pop());
        me.effectDrewThisTurn = true;   // R11 — a CARD drew, NOT the Turn's draw
        me.procTriggers++;
        while (me.hand.length > ACT.HAND_LIMIT) me.necropolis.push(me.hand.shift());
      }

      // R39 · ruin after use unless the tag says otherwise
      if (!c.persistent) {
        // B01 · THE SECOND ACTIVATION, asked while the Tome is still on
        // the field. The old line asked AFTER the slot was nulled, where
        // the gate answers R38 ("there is no Tome there") whatever R39
        // says — a refusal that arrives for the wrong reason is not a
        // proof of the right one. Asked here it is R39's own refusal,
        // and it needs the seat to still afford the card or R15 would
        // answer first.
        if (me.khao >= (c.khaoCost || 0)) {
          H.assert("B01", Legal.activateTome(K, me, i, turn, false) === "R39",
            `P${active} could activate ruined Tome ${t.id} a second time this Turn — the Devil's Advocate loop`);
          H.assert("R39", Legal.activateTome(K, me, i, turn, false) === "R39",
            `P${active} spent Tome ${t.id} was still activatable this Turn`);
        }
        // MISACT · TOME_STAYS — the fault B02 was written for: the
        // regression itself, a used Tome that stays on the field and is
        // never sealed.
        if (!misact("TOME_STAYS")) {
          me.field.tomes[i] = null;
          me.necropolis.push({ uid: t.uid, id: t.id });
        }
        // B02 · and it is OFF the field and IN the Necropolis. The
        // counter-rule is the regression itself: a used Tome that stays
        // where it is and is never sealed.
        H.assert("B02", ruleIs(K, "B02",
          me.field.tomes[i] === null && me.necropolis.some((x) => x.uid === t.uid),
          me.field.tomes[i] !== null && !me.necropolis.some((x) => x.uid === t.uid)),
          `P${active} Tome ${t.id} (tag ${c.effectTag || "none"}) remained on the field after activation`);
      } else {
        // `gate === null || khao < cost` covered every case the gate can
        // return, so it could not be false. The claim worth making is
        // narrower and real: a persistent Tome is never locked out BY R39.
        H.assert("B01", Legal.activateTome(K, me, i, turn, false) !== "R39",
          `P${active} persistent Tome ${t.id} (${c.effectTag}) was ruined-locked by R39 despite its tag`);
      }
      H.note(`  P${active} activated ${t.id} (${c.fx})`, "action");
    });

    // ── THE RUNAWAY GUARD, WHICH IS NOT R92 ──────────────────
    // ⚠ THIS PAIR USED TO BE `H.assert("R92", ...)`, AND IT ASSERTED THE
    // INVERSE OF R92. RULEZ_V2 ch.15 on how long a chain may run:
    // *"None. Not twelve, not five, not any number."* rules.js agrees at
    // the constant itself — "runaway guard ONLY — never a game bound".
    // So crediting the catalogue with R92 off a depth cap reported a rule
    // as watched while asserting the opposite of it.
    // AND THE FIRST LINE COULD NOT FAIL. `chainLink(ACT, K.CHAIN_DEPTH_CAP)`
    // evaluates `K.CHAIN_DEPTH_CAP >= ACT.CHAIN_DEPTH_CAP` — true in every
    // clean run, and true under the only mutation that existed, which
    // RAISED K. A check whose in-force reading is a theorem is decoration.
    // The same claim already had a correct home in the invariant channel
    // (see "the runaway guard, which is NOT R92" in runAdversarial); this
    // is its second, and it goes to the same lane. R92 itself is DECLARED
    // in UNPROVABLE_REASONS — this simulator has no declaration stack.
    H.inv("chain-depth-guard", Legal.chainLink(K, ACT.CHAIN_DEPTH_CAP) !== null,
      `the runaway guard did not stop link ${ACT.CHAIN_DEPTH_CAP + 1} (cap ${K.CHAIN_DEPTH_CAP}) — a harness backstop, NOT R92`);

    // ══ CURIOS · R75 ══════════════════════════════════════════
    me.field.curios.forEach((cu, i) => {
      if (Legal.curioActivate(ACT, me, i) !== null) return;
      const tomesBefore = me.field.tomes.filter(Boolean).length;
      cu.used = true;
      me.procTriggers++;
      // MISACT · CURIO_TAKES_RIFT — the fault R75 forbids: a Curio is a
      // Tome by another name and eats one of the three Tome Rifts.
      if (misact("CURIO_TAKES_RIFT")) {
        const mf = me.field.tomes.findIndex((t) => t === null);
        if (mf >= 0) me.field.tomes[mf] = { uid: `${cu.uid}#mis`, id: cu.id, faceDown: true, placedTurn: turn, usedThisTurn: true };
      }
      // A Curio behaves like a Tome but occupies its OWN Rift — it must
      // never consume one of the TOME_SLOTS.
      // R75's counter-rule is that a Curio is just a Tome by another
      // name and takes one of the three Tome Rifts with it.
      H.assert("R75", ruleIs(K, "R75",
        me.field.tomes.filter(Boolean).length === tomesBefore,
        me.field.tomes.filter(Boolean).length === tomesBefore + 1),
        `P${active} Curio ${cu.id} consumed a Tome Rift instead of its own`);
      H.assert("R75", Legal.curioActivate(K, me, i) === "R75",
        `P${active} Curio ${cu.id} was reusable after activation`);
    });

    // ══ PRIMERA STAMPS · R79-R83 ══════════════════════════════
    me.field.esclings.forEach((slot, i) => {
      if (!slot.escendant) return;
      const being = getCard(slot.escendant.id);
      const stamps = stampsOnSlot(K, slot);
      const pick = me.abstainThisTurn ? null : stamps.find((s) =>
        Legal.primera(ACT, me, i, s.word, "own-turn", turn, s.axis) === null);
      // ── R08 · THE STAMPS, ON THE OPENING CYCLE ──────────────
      // R09 permits ACTIVATION on the opening Cycle and R08 forbids
      // ATTACKS, so the stamps are the one place the two rules meet and
      // the gate has to tell them apart rather than pick a side. Both
      // answers are read off the SAME gate on the SAME half-turn: an
      // attacking stamp is refused, a harmless one is not. Neither
      // branch is `assert(false)` dressed up, because the board
      // produces both — every Escendant carries one stamp per axis.
      if (inOpeningCycle(turn)) {
        stamps.forEach((s) => {
          if (stampAttacks(s.axis, s.word)) {
            H.assert("R08", Legal.primera(K, me, i, s.word, "own-turn", turn, s.axis) === "R08",
              `P${active} was permitted the ${s.axis} stamp "${s.word}" on half-turn ${turn} — it dims the adversary's Anima, and that is an Attack`);
          } else {
            H.assert("R09", Legal.primera(K, me, i, s.word, "own-turn", turn, s.axis) !== "R08",
              `P${active} was refused the harmless ${s.axis} stamp "${s.word}" on half-turn ${turn} — R09 permits activation and R08 bars only Attacks`);
          }
        });
      }
      if (!pick) return;
      const cost = ACT.PRIMERA_COST[`L${being.level}`];
      const khao0 = me.khao, anima0 = me.anima;
      const paidFromP = payOnePool(me, cost);   // R107 — one pool, never a split
      const fromAnima = paidFromP === "anima" ? cost : 0;
      H.assert("R96", fromAnima === 0 || me.anima > 0,
        `P${active} paid ${fromAnima} Anima for a Primera and landed on ${me.anima} — R96 forbids paying yourself to zero`);
      H.assert("R79", (khao0 - me.khao) + (anima0 - me.anima) === K.PRIMERA_COST[`L${being.level}`],
        `P${active} paid ${(khao0 - me.khao) + (anima0 - me.anima)} for an L${being.level} Primera (rule: ${K.PRIMERA_COST[`L${being.level}`]})`);
      slot.primeraUsedThisTurn = true;
      me.primeraWords.add(pick.word);
      me.procTriggers++;
      // R81 · the being is spent, both stamps at once
      H.assert("R81", stamps.every((s) => Legal.primera(K, me, i, s.word, "own-turn", turn, s.axis) === "R81"),
        `P${active} slot ${i} was permitted a second Primera in one Turn`);
      // R82 · and the WORD is spent across the whole field
      const other = me.field.esclings.findIndex((s, j) => j !== i && s.escendant
        && !s.primeraUsedThisTurn
        && stampsOnSlot(K, s).some((st) => st.word === pick.word));
      if (other !== -1) {
        H.assert("R82", Legal.primera(K, me, other, pick.word, "own-turn", turn, pick.axis) === "R82",
          `P${active} reused the word "${pick.word}" on slot ${other} in the same Turn`);
      }
      // R80 · never in a Response Window
      H.assert("R80", Legal.primera(K, me, i, pick.word, "response", turn, pick.axis) === "R80",
        `P${active} was permitted a Primera inside a Response Window`);
    });

    // ══ R108 · DECLARE THE ATTACK PHASE ═══════════════════════
    // caP'n, 2026-08-26: *"After main phase is over — playing all your
    // cards, ascending, resurrection, everything — now you declare
    // attack phase, you cannot play any more cards."* One-way, inside
    // your own Turn, and EXPLICIT: swinging in Main stays legal, so this
    // simulator declares it here — after every card action it wanted and
    // before the swing — which is the shape a real seat uses.
    //
    // ⚠ THE PHASE GATE WAS SIMPLY ABSENT FROM THIS FILE. `me.phase` is a
    // seat field the shipping board writes and the enforcer reads
    // universally; this simulator had no phases and never declared one,
    // so R108 was declared unreachable. It is an ALLOW-LIST, and that is
    // the half worth watching: a new action type is barred by DEFAULT,
    // so the check reads the catalogue's own two lists rather than a
    // hand-typed pair — which is what makes it still right tomorrow.
    H.assert("R108", canEnterPhase(me.phase, PHASE.ATTACK),
      `P${active} declared Attack from ${me.phase} — R108's phases only run forwards`);
    // MISACT · PHASE_OPEN — the fault R108 forbids: declaring Attack
    // leaves the hand open, so a seat can hold its cards back, swing,
    // and then keep playing.
    me.phase = misact("PHASE_OPEN") ? PHASE.MAIN : PHASE.ATTACK;
    {
      // THE BARS. Every card action the catalogue names is refused now.
      const stillOpen = Object.keys(ATTACK_PHASE_BARS)
        .filter((act) => phaseAllows(act, me.phase).allowed);
      H.assert("R108", stillOpen.length === 0,
        `P${active} declared the Attack phase and ${stillOpen.length} card action(s) were still legal: ${stillOpen.join(", ")} — declaring Attack closes your hand`);
      // AND THE PERMISSIONS, on the same board and the same half-turn,
      // because a bar asserted without its permission is half a check:
      // the Lyt Attack itself, an Anima strike, a card already on a
      // Soul, a Primera stamp (of the flesh, R84) and the Arts survive.
      const wronglyShut = ATTACK_PHASE_ALLOWS
        .filter((act) => !phaseAllows(act, me.phase).allowed);
      H.assert("R108", wronglyShut.length === 0,
        `P${active} declared the Attack phase and ${wronglyShut.length} permitted action(s) were shut: ${wronglyShut.join(", ")} — R108 keeps the swing, the Soul, the stamps and the Arts`);
    }

    // ══ COMBAT · R08 / R22-R31 / B03 / R33 ════════════════════
    for (let i = 0; i < me.field.esclings.length && !gameOver; i++) {
      const slot = me.field.esclings[i];
      if (!slot.escendant) continue;
      const atk = getCard(slot.escendant.id);
      const lyt = atk.lyt || 0;

      // R08 · the opening cycle is silent, and the refusal is asserted
      if (inOpeningCycle(turn)) {
        const anyTarget = opp.field.esclings.find((s) => s.escendant);
        H.assert("R08", Legal.lytAttack(K, { turn, slot, target: anyTarget }) === "R08",
          `P${active} was permitted a Lyt Attack on half-turn ${turn}`);
        H.assert("R08", Legal.animaAttack(K, { turn, slot, oppField: opp.field }) !== null,
          `P${active} was permitted an Anima attack on half-turn ${turn}`);
        continue;
      }

      // ── R31 / R119 · WHAT ACTUALLY HOLDS THE WALL ───────────
      // Fractured beings BLOCK; only Fallen is bypassed — AND, since
      // 2026-09-07, the MIND rift never walls the Anima route once the
      // Arts wake (R119: "You dont need to ruin the warden or leave it in
      // a fallen state to dim Anima, only the other 3"). This file held
      // the LAST private copy of the pre-R119 wall: a flat `.some` over
      // every rift, which called a legally OPEN board closed from
      // half-turn 6 onward. The balancer had already switched to the
      // engine's predicate; this asks the same one.
      const targets = opp.field.esclings.filter((s, j) =>
        blocksAnimaLytAttack({ escendant: s.escendant, fallen: s.fallen }, j, turn, SEATS));
      // MISACT · WALL_IGNORED — the fault R31 and B03 forbid: the seat
      // swings at Anima straight through a wall that is still standing.
      if (targets.length === 0 || misact("WALL_IGNORED")) {
        const gate = Legal.animaAttack(ACT, { turn, slot, oppField: opp.field });
        if (gate !== null && !misact("WALL_IGNORED")) { H.assert("R31", false, `P${active} Anima route refused with an empty wall (${gate})`); continue; }
        // ── B03 · THE BYPASS REGRESSION, ASKED OF THE ENGINE ────
        // ⚠ THIS WAS A RESTATEMENT OF THE BRANCH IT STOOD IN. It read
        // `!opp.field.esclings.some((s) => s.escendant && !s.fallen)`,
        // which IS `targets.length === 0` — the loop guard three lines
        // up, with nothing in between that could move the array. It
        // could not be false, and it is the exact species this file's own
        // header lists as killed at R33 ("assert(anima<=0) INSIDE
        // if (anima<=0)"). It asks rules.js's own predicate now, which is
        // a SECOND opinion rather than an echo of the first, and the
        // roster row above gives it a board that is genuinely wrong.
        H.assert("B03", animaOpenToLytAttack(
          opp.field.esclings.map((s) => ({ escendant: s.escendant, fallen: s.fallen })), turn, SEATS),
          `P${active} struck Anima while ${targets.length} being(s) still held the wall — the engine's own predicate says that seat's Anima was closed`);
        // R26 · a Fallen being does not shield
        const fallenStanding = opp.field.esclings.filter((s) => s.escendant && s.fallen).length;
        // `assert(true)` proved that the code reached this line and
        // nothing else. The real claim is that the Fallen wall did not
        // stop the route — asked of the predicate, on this board.
        if (fallenStanding) H.assert("R26",
          Legal.animaAttack(K, { turn, slot, oppField: opp.field }) === null,
          `P${active} was blocked by ${fallenStanding} Fallen being(s) — R26 makes Fallen bypassable immediately`);
        const raw = opp.anima - lyt;
        const procs0 = opp.procTriggers;
        // R100 — an ADVERSARY took this off them, so it is the only kind
        // of loss a Prophecy may ever read.
        dimAnima(opp, lyt, ANIMA_LOSS_KINDS.ADVERSARY);
        // MISACT · DAMAGE_PROCS — the fault R76 forbids: damage is an
        // event and events fire triggers, which is what every card game
        // that is NOT Terror does.
        if (misact("DAMAGE_PROCS")) opp.procTriggers++;
        // R76 · a game action is not a card effect and triggers nothing
        // R76's counter-rule is the one every card game that is NOT
        // Terror uses: damage is an event and events fire triggers.
        H.assert("R76", ruleIs(K, "R76",
          opp.procTriggers === procs0,
          opp.procTriggers > procs0),
          `P${active} Lyt Attack fired ${opp.procTriggers - procs0} proc effect(s) on P${1 - active} — game-actions trigger none`);
        slot.attackedThisTurn = true; slot.attacksThisTurn = (slot.attacksThisTurn || 0) + 1;
        H.note(`  P${active} struck Anima for ${lyt} → ${opp.anima}`, "attack");
        if (opp.anima <= 0) {
          // ⚠ `assert(opp.anima <= 0)` INSIDE `if (opp.anima <= 0)` was a
          // restatement of the branch it stood in and could not be
          // false. What R33 actually claims is about the VERDICT: the
          // seat that reached 0 is the seat that LOST. Its counter-rule
          // is the inversion — that emptying your own Anima is how you
          // win — and the two hand the duel to different seats.
          // MISACT · VERDICT_INVERTED — the fault R33 forbids: emptying
          // your own Anima is how you WIN.
          gameOver = true; winner = misact("VERDICT_INVERTED") ? 1 - active : active;
          endRule = "R33"; endBy = "anima";
          H.assert("R33", ruleIs(K, "R33",
            players[winner].anima > 0 && players[1 - winner].anima <= 0,
            players[winner].anima <= 0),
            `P${1 - active} reached ${opp.anima} Anima and the duel was awarded to P${winner}, who holds ${players[winner].anima}`);
        }
        continue;
      }

      // Prefer a target we can actually finish; otherwise the first wall.
      const target = targets.find((s) => (s.currentLyf ?? 99) === 0)
        || targets.find((s) => lyt >= (s.currentLyf ?? 99))
        || targets[0];
      const gate = Legal.lytAttack(ACT, { turn, slot, target, myRifts: me.field.esclings, oppRifts: opp.field.esclings, slotIdx: i });
      // ── R121 · AND THE MIND'S SILENCE IS ASKED BOTH WAYS ─────
      // A pure predicate over this seat's own battle line, so it can be
      // asserted on every rift of every half-turn: the Mind is silent
      // exactly while Body, Sol and Heart all hold Escendants, and an
      // ordinary being the rest of the time. A FALLEN Escendant still
      // occupies its rift for the count — Fallen is a half-turn state of
      // a being that is standing there, not an absence.
      if (i === MIND_RIFT) {
        // The silence has THREE board conditions and this file states
        // them independently rather than calling the gate's own
        // predicate back at it: the Arts must be awake (R115's three
        // full Turns — six half-turns at two seats), the Mind rift must
        // be held, and Body, Sol and Heart must ALL hold Escendants.
        // The first cut of this line asked `trioOccupied` alone and
        // reported five legal swings as violations, because it had
        // forgotten the lock — which is the mistake rules.js warns about
        // at `mindIsSilenced` in as many words.
        const silent = rolesAwake(turn, SEATS)
          && !!slot.escendant
          && trioOccupied(me.field.esclings);
        H.assert("R121", silent ? gate === "R121" : gate !== "R121",
          `P${active} Mind rift: Body/Sol/Heart ${silent ? "ALL hold Escendants and the swing was allowed" : "are not all held and the swing was refused for R121"} (gate ${gate})`);
      }
      if (gate !== null) {
        // The only legitimate refusals here are R30 (already swung),
        // R25 (Fallen) and R121 (a silenced Mind). Anything else means
        // the predicate is broken.
        H.assert(gate, gate === "R30" || gate === "R25" || gate === "R121",
          `P${active} Lyt Attack refused for ${gate} against a standing, non-Fallen target`);
        continue;
      }
      // R25 · and the refusal is proven positively against a Fallen one
      const aFallen = opp.field.esclings.find((s) => s.escendant && s.fallen);
      if (aFallen) {
        H.assert("R25", Legal.lytAttack(K, { turn, slot, target: aFallen }) === "R25",
          `P${active} was permitted a Lyt Attack on Fallen ${aFallen.escendant.id}`);
      }

      const before = target.currentLyf ?? 0;
      if (before === 0) {
        // R29 · already Fractured → the second hit finishes it (R23/R24)
        ruinOrDescend(H, K, opp, target, "lyt");
      } else if (lyt >= before) {
        // ── R22 · F2 FIX ───────────────────────────────────────
        // FRACTURE IS NOT RUIN. Lyf drops to 0 and the being SURVIVES.
        // The old sim ruined on this hit, which erased R22, R23 and R24
        // as separate rules and made the Fracture state unobservable.
        target.currentLyf = 0;
        // MISACT · FRACTURE_RUINS — the fault R22 exists to forbid, and
        // the exact fusion this file was rebuilt to undo: Lyt >= Lyf
        // ruins outright, with no Fractured state in between.
        const fracturedId = target.escendant?.id;
        if (misact("FRACTURE_RUINS")) { target.escendant = null; target.soul = []; }
        // R22's counter-rule is the fusion this file was rebuilt to
        // undo: that Lyt ≥ Lyf RUINS outright, with no Fractured state
        // in between. Under it the slot would be bare; it is not.
        H.assert("R22", ruleIs(K, "R22",
          target.escendant !== null && target.currentLyf === 0,
          target.escendant === null),
          `P${active} Fractured ${target.escendant?.id} and it did not survive with 0 Lyf`);
        H.note(`  P${active} FRACTURED ${fracturedId} (Lyt ${lyt} ≥ Lyf ${before})`, "attack");
      } else {
        // ── R27 · F5 FIX · chip damage is APPLIED ──────────────
        // MISACT · CHIP_NOOP — the fault R27 forbids: a hit under the
        // Lyf does nothing at all.
        target.currentLyf = misact("CHIP_NOOP") ? before : before - lyt;
        // R27's counter-rule is that a hit under the Lyf does NOTHING —
        // the reading that would make chip damage not merely wasteful
        // but invisible. The board moved; that reading says it did not.
        H.assert("R27", ruleIs(K, "R27",
          target.currentLyf > 0 && target.currentLyf < before,
          target.currentLyf === before),
          `P${active} chip damage on ${target.escendant.id} left Lyf at ${target.currentLyf} (was ${before}, Lyt ${lyt})`);
        H.note(`  P${active} chipped ${target.escendant.id} → Lyf ${target.currentLyf}`, "attack");
      }
      slot.attackedThisTurn = true; slot.attacksThisTurn = (slot.attacksThisTurn || 0) + 1;
      // ── R30 / R124 · THE SWING IS SPENT — UNLESS A CARD PRINTS MORE ──
      // Asked of the engine's own allowance rather than of a flag, so a
      // being carrying a granted extra swing is NOT reported as an R30
      // violation for taking it. Both directions off the same rift.
      const allowanceHere = lytAttacksAllowed(slot, me.field.esclings, opp.field.esclings,
                                              (id) => getCard(id), i);
      const verdict30 = Legal.lytAttack(K, { turn, slot, target, myRifts: me.field.esclings, oppRifts: opp.field.esclings, slotIdx: i });
      if (slot.attacksThisTurn >= allowanceHere) {
        H.assert("R30", verdict30 === "R30",
          `P${active} rift ${i} has taken all ${allowanceHere} of its swings and could swing again`);
      } else {
        H.assert("R124", verdict30 !== "R30",
          `P${active} rift ${i} was refused swing ${slot.attacksThisTurn + 1} of an allowance of ${allowanceHere} — R124 grants it`);
      }
      H.assert("R124", slot.attacksThisTurn <= allowanceHere,
        `P${active} rift ${i} took ${slot.attacksThisTurn} swings against an allowance of ${allowanceHere}`);
      sweep(H, players, `attack t${turn}`);
    }

    // ══ END OF MY TURN · R28 ══════════════════════════════════
    // ⚠ THIS COMMENT WAS WRONG AND THE CODE UNDER IT WAS RIGHT. It read
    // "Lyf resets to full at the end of EACH Turn, for every being,
    // Fallen included. Chip damage therefore cannot accumulate (R27)" —
    // while the loop three lines down has always carried `if (p !== me)
    // return;`, i.e. it refreshes THE ACTING SEAT'S beings and nobody
    // else's. caP'n ruled on 2026-08-26: *"as coded is better ... the
    // being's Lyf stays chipped or even in a fractured state until its
    // own turn."* So: Lyf refreshes at the end of its OWN seat's Turn,
    // Fallen included; an adversary's chipped or Fractured beings are
    // untouched and stay that way until their own Turn comes round.
    // Chip damage on an adversary's board is REAL PRESSURE, not waste.
    [me, opp].forEach((p, w) => {
      p.field.esclings.forEach((s, j) => {
        if (!s.escendant) return;
        const c = getCard(s.escendant.id);
        // R124 — the COUNT resets with the flag. A count left standing is
        // a being that can never swing again, which is the strict
        // direction and therefore the silent one.
        if (w === 0) { s.attackedThisTurn = false; s.attacksThisTurn = 0; }
        if (p !== me) return;
        const chipped = s.currentLyf !== null && s.currentLyf < c.lyf && s.currentLyf > 0;
        const lyfIn = s.currentLyf;
        // MISACT · LYF_PERSISTS — the fault R28 forbids (and R27's
        // second leg with it): damage survives the Turn boundary and
        // chip damage accumulates.
        s.currentLyf = misact("LYF_PERSISTS") ? s.currentLyf : c.lyf;
        // ⚠ `assert(currentLyf === c.lyf)` one line after
        // `currentLyf = c.lyf` was another tautology. What is asserted
        // is the RESET against what the being carried into the end
        // phase. R28's counter-rule is that damage PERSISTS between
        // Turns — the reading under which chip damage accumulates and
        // the board-lock R87 exists to break would never have formed.
        H.assert("R28", ruleIs(K, "R28", s.currentLyf === c.lyf, s.currentLyf === lyfIn),
          `P${p.idx} slot ${j} ended the Turn on Lyf ${lyfIn} of a base ${c.lyf} and left it on ${s.currentLyf} — Lyf resets every Turn`);
        H.assert("R27", ruleIs(K, "R27",
          !chipped || s.currentLyf === c.lyf,
          chipped && s.currentLyf === lyfIn),
          `P${p.idx} slot ${j} carried chip damage across the Turn boundary`);
      });
    });
    if (inOpeningCycle(turn)) {
      const oppSeatAfter = {
        anima: opp.anima,
        necropolis: opp.necropolis.map((c) => ({ ...c })),
        field: { esclings: opp.field.esclings.map((sl) => ({ escendant: sl.escendant, currentLyf: sl.currentLyf })) },
      };
      const got = adversaryDamageInPayload(oppSeatBefore, oppSeatAfter);
      H.assert("R08", Legal.openingDamage(K, turn, oppSeatBefore, oppSeatAfter) === null,
        `P${active} reached P${1 - active} on half-turn ${turn} — ${got || "the backstop saw harm"}. R08 makes the opening Cycle silent by every route`);
    }
    me.turnsTaken++;
    sweep(H, players, `end t${turn}`);

    // ── THE HAND-OFF · the one place a half-turn changes hands ──
    // Everything above is inside a half-turn. This is the boundary, and
    // it is the boundary the SHIPPING engine owns in one function; see
    // handOffHalfTurn's banner for the five things that function does
    // and the two this file still cannot.
    if (!gameOver) handOffHalfTurn(H, K, players, active, 1 - active, turn + 1);
  }

  // ══ THE KHAO LEDGER · R91 / R13 / R12 ══════════════════════
  // These read what the BOARD did, not what two constants say. The
  // probes they replaced were `!climbs(FIRST_KHAO_TURN - 1, false)` and
  // `!climbs(x, true)` — both `!false` — and a third that compared
  // ACT.FIRST_KHAO_TURN with K.FIRST_KHAO_TURN and never touched a game.
  players.forEach((p, i) => {
    H.assert("R91", p.firstNaturalGainTurn === null || p.firstNaturalGainTurn >= K.FIRST_KHAO_TURN,
      `P${i} took its first natural Khao gain on half-turn ${p.firstNaturalGainTurn}, before FIRST_KHAO_TURN ${K.FIRST_KHAO_TURN}`);
    H.assert("R13", p.naturalKhaoSeen <= K.MAX_KHAO_NAT,
      `P${i} climbed Max Khao to ${p.naturalKhaoSeen} naturally, past the natural ceiling ${K.MAX_KHAO_NAT}`);
    H.assert("R13", p.maxKhaoSeen <= K.MAX_KHAO_ABS,
      `P${i} reached Max Khao ${p.maxKhaoSeen}, past the absolute ceiling ${K.MAX_KHAO_ABS}`);
    // R12's counter-rule is that the climb is unconditional — a +1 a
    // Turn whatever you spent, which is what most of the genre does and
    // what R12 exists to refuse. It is a different number only on a
    // board that actually spent Khao; a duel where nobody ever paid for
    // anything leaves both readings at 0 and the self-test reports R12
    // FAKE, which is the honest answer for that duel.
    H.assert("R12", ruleIs(K, "R12",
      p.climbsAfterSpend === 0,
      p.spentCycles > 0 && p.climbsAfterSpend === p.spentCycles),
      `P${i} took ${p.climbsAfterSpend} natural Khao gain(s) across ${p.spentCycles} cycle(s) entered on a spend`);
  });

  // ══ THE CLOCK · R93 / R87 ══════════════════════════════════
  // Terror has no draw (R43/R87). An unresolved duel is CALLED, and the
  // verdict comes from rules.js — never from a "draws" bucket here.
  const match = {
    seatCount: 2, turn,
    players: {
      0: { anima: players[0].anima, grimoire: players[0].grimoire, field: { esclings: players[0].field.esclings.map((s) => ({ escendant: s.escendant?.id || null, fallen: s.fallen })) } },
      1: { anima: players[1].anima, grimoire: players[1].grimoire, field: { esclings: players[1].field.esclings.map((s) => ({ escendant: s.escendant?.id || null, fallen: s.fallen })) } },
    },
  };
  // R93 has two bells now: TURN_CLOCK for every simulated and non-PvP
  // duel, TURN_CLOCK_PVP for a live human game where R87's minutes are
  // the real governor. `callOnClock` picks by mode on its own, so the
  // default path is exercised rather than argued around.
  // ⚠ ASKED OF A SHAPE THE GAME ACTUALLY WRITES (2026-09-15). This line
  // used to hand clockLimitFor `{ mode: "pvp" }` — a label nothing in
  // the game ever stamps (gameManager writes "1v1", "ffa3", "2v2"...), so
  // it stayed green for weeks while every live human duel rang at 150.
  // A live PvP board is two signed-in humans: uids on every seat and no
  // brain seat. The classic NPC injection (App.handleEnterNPC) leaves the
  // brain's seat with no uid, and the co-op boss carries controller "ai";
  // both must read as the 150 bell.
  const livePvP = { mode: "1v1", seatCount: 2, players: { 0: { uid: "hostUid" }, 1: { uid: "guestUid" } } };
  const vsNpc   = { mode: "1v1", seatCount: 2, players: { 0: { uid: "hostUid" }, 1: { name: "Champion NPC" } } };
  const coop    = { mode: "story-coop", seatCount: 3, players: { 0: { uid: "a" }, 1: { uid: null, controller: "ai" }, 2: { uid: "b" } } };
  H.assert("R93", clockLimitFor(livePvP) === K.TURN_CLOCK_PVP,
    `clockLimitFor rang a live two-human duel at ${clockLimitFor(livePvP)} half-turns (rule: ${K.TURN_CLOCK_PVP})`);
  H.assert("R93", clockLimitFor(vsNpc) === K.TURN_CLOCK && clockLimitFor(coop) === K.TURN_CLOCK,
    `clockLimitFor rang a brain-seated duel at ${clockLimitFor(vsNpc)} / ${clockLimitFor(coop)} half-turns (rule: ${K.TURN_CLOCK})`);
  H.assert("R93", clockLimitFor(match) === K.TURN_CLOCK,
    `clockLimitFor rang a simulated duel at ${clockLimitFor(match)} half-turns (rule: ${K.TURN_CLOCK})`);
  let called = null;
  if (!gameOver) {
    called = callOnClock(match);   // no turnLimit: prove the mode default
    H.assert("R93", clockExpired(match, K.TURN_CLOCK),
      `the duel stopped at half-turn ${turn} without reaching TURN_CLOCK ${K.TURN_CLOCK} — the clock never called it`);
    H.assert("R93", turn <= K.TURN_CLOCK,
      `the duel ran to half-turn ${turn}, past TURN_CLOCK ${K.TURN_CLOCK} — the clock should have called it`);
    H.assert("R93", called !== null && called.winner != null,
      `R93 expired at half-turn ${turn} and callOnClock returned no verdict — Terror has no draw`);
    if (called) { winner = called.winner; endRule = called.rule; endBy = called.by; }
  } else {
    H.assert("R93", !clockExpired(match, K.TURN_CLOCK) || called !== null,
      `the duel passed TURN_CLOCK ${K.TURN_CLOCK} without being called`);
  }
  // R87 · the human clock. Twenty minutes cannot elapse headless (rules.js
  // says so in its own banner), so what is provable here is the VERDICT,
  // and that is what is proven — never a draw, always a seat.
  {
    // The elapsed second handed to the engine is the RULE's (K), and the
    // engine answers from its own imported DUEL_TIME_LIMIT (ACT). That
    // asymmetry is what finally put R87's twenty minutes inside the
    // fake-detector: mutate the limit in either direction and one of the
    // two assertions below has to break. Too high, and the duel is
    // already called a second before the rule says it should be; too
    // low, and it is not called at the rule's own second.
    const early = callOnClock(match, { turnLimit: Number.MAX_SAFE_INTEGER, elapsedSeconds: K.DUEL_TIME_LIMIT - 1 });
    H.assert("R87", early === null,
      `the duel was already called at ${K.DUEL_TIME_LIMIT - 1}s, one second inside R87's ${K.DUEL_TIME_LIMIT}`);
    const v = callOnClock(match, { turnLimit: Number.MAX_SAFE_INTEGER, elapsedSeconds: K.DUEL_TIME_LIMIT });
    H.assert("R87", v !== null && v.rule === "R87" && v.winner != null && v.by !== "draw",
      `at ${K.DUEL_TIME_LIMIT}s the duel produced ${v ? JSON.stringify(v) : "no verdict"} — R87 awards a seat, never a draw`);
    const direct = timeoutWinner(match);
    H.assert("R87", v === null || direct.winner === v.winner,
      `callOnClock and timeoutWinner disagree on the R87 verdict (${v && v.winner} vs ${direct.winner})`);
  }
  // R43 · running out of cards is never the loss
  H.assert("R43", !(gameOver && (players[0].grimoire.length === 0 || players[1].grimoire.length === 0) && endRule !== "R33"),
    `a duel ended on an empty Grimoire rather than on Anima — R43 forbids deck-out as a loss`);

  return { winner, turns: turn, endRule, endBy, players };
}

// ── RUIN OR DESCEND · R23 / R24 / R50 / R51 / R52 ────────────
// F3 FIX: the descent target is read from the slot's SOUL, exactly as
// Shatterfield 7408-7470 does. The old sim searched the GLOBAL card
// pool for any card of the same school/ilk one level down, which
// conjured a being the player never owned and made R51/R52 meaningless.
function ruinOrDescend(H, K, owner, slot, source) {
  const top = getCard(slot.escendant.id);
  const prev = slot.soul.find((c) => {
    const x = getCard(c.id);
    return x?.type === T.ESCENDANT && x.level === top.level - 1;
  });
  // MISACT · RUIN_EXILES — the fault R23 forbids: a ruined being leaves
  // the duel instead of entering the Necropolis, under which there is
  // nothing for R44 to bring back and no tally to keep.
  if (!misact("RUIN_EXILES")) owner.necropolis.push(slot.escendant);

  const soulHeld = slot.soul.length;
  const hereticalBefore = slot.heretical;
  if (prev && top.level > 1) {
    // Level-bound Impera ruin with their level; lower Escendants,
    // U'hltis and Affinities ride down (R51 / R52).
    const kept = slot.soul.filter((c) => {
      const x = getCard(c.id);
      if (!x) return false;
      if (x.type === T.ESCENDANT) return true;
      // R51/R52 — EVERY Impera stays, at every level (caP'n, 13 Sept
      // 2026; the July "level-bound" reading is overturned). RULEZ_V2
      // :541 "Soul attachments are never level-specific", :563.
      if (x.type === T.IMPERA) return true;
      return x.type === T.UHLTI || x.type === T.AFFINITY;
    });
    const lost = slot.soul.filter((c) => !kept.includes(c));
    lost.forEach((c) => owner.necropolis.push(c));
    // MISACT · DESCENT_RUINS_IMPERA — the fault R52 forbids from the
    // Impera side, and the exact shape this file itself shipped until
    // 13 Sept 2026: the descent ruins the Impera of the level it left.
    // The rule is that an Impera is a lesson a being cannot unlearn, so
    // the correct behaviour here is to touch `slot.impera` NOT AT ALL.
    const imperaBefore = { ...slot.impera };
    if (misact("DESCENT_RUINS_IMPERA") && slot.impera[top.level]) {
      owner.necropolis.push(slot.impera[top.level]); delete slot.impera[top.level];
    }
    // R68 · the Heretical Tome does not ride down — it is not Soul
    // MISACT · HERETICAL_RIDES_DOWN — the fault R68 forbids: a Tome
    // trained onto the being rides down with everything else it wore.
    if (slot.heretical && !misact("HERETICAL_RIDES_DOWN")) { owner.necropolis.push(slot.heretical); slot.heretical = null; }

    const prevCard = getCard(prev.id);
    const uhltiBefore = slot.uhlti, affBefore = slot.affinity;
    slot.escendant = prev;
    seatStamps(slot);                          // R84 — a descent takes no stamp
    slot.currentLyf = prevCard.lyf;
    slot.soul = kept.filter((c) => c.uid !== prev.uid);
    // MISACT · DESCENT_HOLDS_TOP — the fault R51 forbids from the other
    // side: the ruined top level stays in the Soul it should have left.
    if (misact("DESCENT_HOLDS_TOP")) slot.soul.push({ uid: `${prev.uid}#mis`, id: top.id });
    // MISACT · DESCENT_DROPS_ATTACH — the fault R52 forbids: U'hltis and
    // Affinities ruin with the level they were attached to. (Nothing on
    // a being is level-bound any more — not the Impera either.)
    if (misact("DESCENT_DROPS_ATTACH")) { slot.uhlti = null; slot.affinity = null; }
    slot.attackedThisTurn = false;
    // MISACT · DESCENT_NOT_FALLEN — the fault R24 forbids: the survivor
    // lands ready to act instead of Fallen.
    slot.fallen = !misact("DESCENT_NOT_FALLEN");   // R24 · and it CLEARS at the owner's turn start

    // R24's counter-rule is R22's fusion one level up: that a Fractured
    // L2/L3 is simply ruined and there is no descent at all.
    H.assert("R24", ruleIs(K, "R24",
      getCard(slot.escendant.id).level === top.level - 1 && slot.fallen === true,
      slot.escendant === null),
      `P${owner.idx} descended ${top.id} to ${slot.escendant.id} (level ${getCard(slot.escendant.id).level}) — R24 wants exactly one level down, Fallen`);
    // R51's counter-rule is that the whole Soul ruins with the top level
    // and the survivor lands bare. Different only when a Soul survived,
    // which is what the descent bench in the adversarial suite is for.
    H.assert("R51", ruleIs(K, "R51",
      slot.soul.every((c) => (getCard(c.id)?.level ?? 0) <= prevCard.level || getCard(c.id)?.type !== T.ESCENDANT),
      slot.soul.length === 0 && soulHeld > 0),
      `P${owner.idx} left a being above L${prevCard.level} in the Soul after descent (${slot.soul.length} of ${soulHeld} held)`);
    // R52's counter-rule is that attachments ruin with the level they
    // were trained onto.
    H.assert("R52", ruleIs(K, "R52",
      slot.uhlti === uhltiBefore && slot.affinity === affBefore,
      (uhltiBefore || affBefore) && slot.uhlti === null && slot.affinity === null),
      `P${owner.idx} lost an attached U'hlti or Affinity to a descent that did not ruin the whole Soul`);
    // R52 · THE IMPERA HALF. Same rule, the half that changed on 13 Sept
    // 2026. The trained set must come out of a descent byte-identical:
    // same levels, same uids. This is the check the old behaviour fails.
    H.assert("R52", ruleIs(K, "R52",
      [1, 2, 3].every((l) => (slot.impera[l]?.uid ?? null) === (imperaBefore[l]?.uid ?? null)),
      [1, 2, 3].some((l) => imperaBefore[l] && !slot.impera[l])),
      `P${owner.idx} lost a trained Impera to a descent — an Impera is a lesson a being cannot unlearn (RULEZ_V2 :563)`);
    // R68's counter-rule is the intuitive one: a Tome trained onto the
    // being rides down with it like everything else it was wearing.
    H.assert("R68", ruleIs(K, "R68",
      slot.heretical === null,
      hereticalBefore !== null && slot.heretical === hereticalBefore),
      `P${owner.idx} carried a Heretical Tome down onto an L${prevCard.level} — it is not Soul and does not descend`);
    H.note(`  P${owner.idx} DESCENDED ${top.id} → ${prev.id} (Fallen) via ${source}`, "attack");
    return;
  }

  // No lower level in the Soul — full ruin, and the Soul goes with it.
  const soulUids = slot.soul.map((c) => c.uid);
  // MISACT · SOUL_EXILED — the fault R50 forbids: a ruined being's Soul
  // is exiled rather than sealed, which would make R48's "keeps their
  // Souls" impossible to honour.
  if (!misact("SOUL_EXILED")) slot.soul.forEach((c) => owner.necropolis.push(c));
  Object.values(slot.impera).forEach((c) => c && owner.necropolis.push(c));
  if (slot.uhlti) owner.necropolis.push(slot.uhlti);
  if (slot.affinity) owner.necropolis.push(slot.affinity);
  if (slot.heretical) owner.necropolis.push(slot.heretical);
  owner.soulOf[slot.escendant?.uid || ""] = soulUids;
  // R50's counter-rule is that a ruined being's Soul is EXILED — gone
  // from the duel rather than sealed with it — which is the reading that
  // would make R48's "keeps their Souls" impossible to honour.
  H.assert("R50", ruleIs(K, "R50",
    soulUids.every((u) => owner.necropolis.some((c) => c.uid === u)),
    soulUids.length > 0 && !soulUids.some((u) => owner.necropolis.some((c) => c.uid === u))),
    `P${owner.idx} ruined ${top.id} and its Soul (${soulUids.length} card(s)) did not reach the Necropolis`);
  // R23 used to restate the branch condition it was standing inside, so
  // it could not be false. It now asserts the OUTCOME: the ruined being
  // is in the Necropolis and the slot it held is bare.
  const ruinedUid = slot.escendant.uid;
  slot.escendant = null; slot.currentLyf = null; slot.soul = [];
  slot.impera = {}; slot.uhlti = null; slot.affinity = null; slot.heretical = null;
  slot.fallen = false; slot.natural = true; slot.deviant = false;
  // R23's counter-rule is exile again, one card up: that a ruined being
  // leaves the duel instead of entering the Necropolis — under which
  // there would be nothing for R44 to bring back and no tally to keep.
  H.assert("R23", ruleIs(K, "R23",
    owner.necropolis.some((c) => c.uid === ruinedUid) && slot.escendant === null,
    !owner.necropolis.some((c) => c.uid === ruinedUid) && slot.escendant === null),
    `P${owner.idx} ruined ${top.id} and it is not in the Necropolis, or its slot is not bare`);
  H.note(`  P${owner.idx} RUINED ${top.id} via ${source}`, "attack");
}

// ─────────────────────────────────────────────────────────────
// THE ADVERSARIAL SUITE
// Every scenario below deliberately attempts an ILLEGAL action on a
// hand-built board and asserts REJECTION. A permissive engine fails
// here while a happy-path simulation stays green all day.
// ─────────────────────────────────────────────────────────────
function runAdversarial(H, deckA, deckB) {
  const K = H.K;
  const rng = mulberry32(H.seed ^ 0x5eed);
  const P = () => buildPlayer(deckA, 0, rng);
  const O = () => buildPlayer(deckB, 1, rng);
  const beingL1 = ESCENDANTS.find((c) => c.level === 1);
  const beingL2 = ESCENDANTS.find((c) => c.level === 2 && c.school === beingL1.school && c.ilk === beingL1.ilk);
  const beingL3 = ESCENDANTS.find((c) => c.level === 3 && c.school === beingL1.school && c.ilk === beingL1.ilk);
  const uhlti = UHLTIS[0];
  const affinity = AFFINITIES[0];
  const put = (p, i, id, extra) => {
    const s = p.field.esclings[i];
    s.escendant = { uid: `adv#${i}#${id}`, id };
    s.currentLyf = getCard(id).lyf;
    seatStamps(s);
    Object.assign(s, extra || {});
    return s;
  };

  // ── attack a Fallen being · R25 ──
  {
    const me = P(), opp = O();
    const atk = put(me, 0, beingL1.id);
    const tgt = put(opp, 0, beingL1.id, { fallen: true });
    H.reject("R25", Legal.lytAttack(K, { turn: 4, slot: atk, target: tgt }) !== "R25",
      "Lyt Attack on a Fallen Escendant");
  }
  // ── a second Ascend in one Turn · R18 ──
  if (beingL2) {
    const me = P();
    put(me, 0, beingL1.id); put(me, 1, beingL1.id);
    me.ascendedThisTurn = 1;
    H.reject("R18", Legal.ascend(K, me, 1, beingL2.id, false) !== "R18", "a second Ascend in one Turn");
  }
  // ── Ascend across School/Ilk without paying Deviance · R19 ──
  {
    const me = P();
    put(me, 0, beingL1.id);
    const mismatch = ESCENDANTS.find((c) => c.level === 2 && (c.school !== beingL1.school || c.ilk !== beingL1.ilk));
    if (mismatch) H.reject("R19", Legal.ascend(K, me, 0, mismatch.id, false) !== "R19",
      "a natural Ascend across mismatched School/Ilk");
  }
  // ── a second Deviant Ascend · R66 ──
  {
    const me = P();
    put(me, 0, beingL1.id);
    me.deviantAscendedThisTurn = 1;
    const any2 = ESCENDANTS.find((c) => c.level === 2);
    if (any2) H.reject("R66", Legal.ascend(K, me, 0, any2.id, true) !== "R66", "a second Deviant Ascend in one Turn");
  }
  // ── Graveshift with cards still in the Grimoire · R40 ──
  {
    const me = P();
    H.reject("R40", Legal.graveshift(K, me) !== "R40", "a Graveshift with a non-empty Grimoire");
  }
  // ── Graveshift without the fare · R41 ──
  {
    const me = P();
    me.grimoire = []; me.anima = ACT.GRAVESHIFT_COST - 1;
    H.reject("R41", Legal.graveshift(K, me) !== "R41", `a Graveshift on ${me.anima} Anima against a ${K.GRAVESHIFT_COST} fare`);
  }
  // ── activate an L1 Tome the turn it was placed · R35 ──
  {
    const me = P();
    const t = TOMES.find((c) => c.level === 1);
    me.field.tomes[0] = { uid: "adv#t", id: t.id, faceDown: true, placedTurn: 6, usedThisTurn: false };
    me.khao = 99;
    H.reject("R35", Legal.activateTome(K, me, 0, 6, false) !== "R35",
      `an L1 Tome activated the Turn it was placed (wait is ${K.TOME_WAIT.L1})`);
  }
  // ── R36 · who may answer inside a Response Window ─────────
  // An L2 Tome may, once placed. An L1 may not — that refusal is R36's
  // content, and it was previously reachable only through a computed
  // code string, which is the same as not being referenced at all.
  {
    const me = P();
    me.khao = ACT.MAX_KHAO_ABS;
    const t2 = TOMES.find((c) => c.level === 2);
    const t1 = TOMES.find((c) => c.level === 1);
    me.field.tomes[0] = { uid: "adv#r36a", id: t2.id, faceDown: true, placedTurn: 0, usedThisTurn: false };
    me.field.tomes[1] = { uid: "adv#r36b", id: t1.id, faceDown: true, placedTurn: 0, usedThisTurn: false };
    H.assert("R36", Legal.activateTome(K, me, 0, ACT.TOME_WAIT.L2 + 4, true) === null,
      `an L2 Tome was refused inside a Response Window after waiting ${K.TOME_WAIT.L2} — R36 permits it`);
    H.reject("R36", Legal.activateTome(K, me, 1, 8, true) !== "R36",
      "an L1 Tome answering inside a Response Window");
  }
  // ── R37 · an L3 Tome answers FROM HAND ────────────────────
  {
    const me = P();
    me.khao = ACT.MAX_KHAO_ABS;
    const t3 = TOMES.find((c) => c.level === 3);
    const t2 = TOMES.find((c) => c.level === 2);
    if (t3) {
      H.assert("R37", Legal.activateFromHand(K, me, t3.id, true) === null,
        "an L3 Tome was refused from hand during the adversary's turn — R37 is exactly that permission");
      H.reject("R37", Legal.activateFromHand(K, me, t3.id, false) !== "R37",
        "an L3 Tome activated from hand on its owner's own turn");
    }
    if (t2) H.reject("R37", Legal.activateFromHand(K, me, t2.id, true) !== "R37",
      "an L2 Tome activated from HAND — only a Level 3 may");
  }

  // ── activate a Tome you cannot pay for · R15 ──
  {
    const me = P();
    const t = TOMES.find((c) => (c.khaoCost || 0) > 0 && c.level === 2) || TOMES[0];
    me.field.tomes[0] = { uid: "adv#t2", id: t.id, faceDown: true, placedTurn: 0, usedThisTurn: false };
    me.khao = 0;
    H.reject("R15", Legal.activateTome(K, me, 0, 9, false) !== "R15", "a Tome activated with 0 Khao against a Khao cost");
  }
  // ── place a Tome with every Rift full · R38 ──
  {
    const me = P();
    me.field.tomes = me.field.tomes.map((_, i) => ({ uid: `adv#f${i}`, id: TOMES[0].id, faceDown: true, placedTurn: 0 }));
    H.reject("R38", Legal.placeTome(K, me) !== "R38", `a Tome onto a board already holding ${me.field.tomes.length} (rule allows ${K.TOME_SLOTS})`);
  }
  // ── exceed the hand limit · R10 ──
  {
    const me = P();
    me.hand = Array.from({ length: ACT.HAND_LIMIT + 2 }, (_, i) => ({ uid: `adv#h${i}`, id: beingL1.id }));
    // Draw-then-discard is the RULE; what is illegal is ENDING above the cap.
    while (me.hand.length > ACT.HAND_LIMIT) me.necropolis.push(me.hand.shift());
    H.reject("R10", me.hand.length > K.HAND_LIMIT,
      `a hand of ${me.hand.length} surviving the discard step (limit ${K.HAND_LIMIT})`);
  }
  // ── a second draw in one Turn · R11 ──
  {
    const me = P(); me.drewThisTurn = true;
    H.reject("R11", Legal.draw(K, me, "grimoire", 4, false) !== "R11", "a second draw in one Turn");
  }
  // ── the first seat's Turn-1 Grimoire draw · R07 ──
  {
    const me = P();
    H.reject("R07", Legal.draw(K, me, "grimoire", 0, true) !== "R07", "the first seat's Grimoire draw on Turn 1");
    H.assert("R07", Legal.draw(K, me, "luxmoire", 0, true) === null,
      "the first seat's Luxmoire draw on Turn 1 was refused — R07 forbids only the Grimoire draw");
  }

  // ══════════════════════════════════════════════════════════
  // R08 · THE OPENING CYCLE, IN THE SCOPE IT GREW ON 2026-08-25
  // ──────────────────────────────────────────────────────────
  // WHAT THIS FILE COULD NOT SEE THE MORNING OF THE RULING, stated
  // plainly because it is the reason this block exists: the simulator
  // refused the Lyt swing and the Anima route on the opening Cycle and
  // NOTHING ELSE. A Tome that ruined a being, an Impera, a Curio, a
  // Primera stamp that dimmed an adversary's Anima directly — every one
  // of them walked through half-turn 0 unwatched, and a search of
  // either Book was not even a question anyone was asking. R08 is a
  // broad word now and this is the broad bench.
  //
  // EVERY ROW BELOW IS ASKED IN BOTH DIRECTIONS. A refusal inside the
  // Cycle is worth nothing without the same gate saying YES one
  // half-turn later, or saying yes to the harmless card on the same
  // half-turn — otherwise the "proof" is `assert(false)` in costume,
  // which is the fault an adversarial reviewer took this file apart
  // for the last time it counted.
  {
    const lastOpening = openingCycleLength(SEATS) - 1;
    const firstFree = openingCycleLength(SEATS);

    // ── 1 · "ATTACK" KNOWS NO KINDS ──────────────────────────
    // The whole of ATTACK_KINDS through one gate. The list is the
    // catalogue's, so a route added to rules.js is tested here the day
    // it lands instead of the day somebody remembers to retype it.
    ATTACK_KINDS.forEach((kind) => {
      for (let t = 0; t <= lastOpening; t++) {
        H.reject("R08", Legal.attack(K, { turn: t, kind }) !== "R08",
          `a ${kind} Attack on half-turn ${t} — "Attack" is the broad word and covers every route to the other side`);
      }
      H.assert("R08", Legal.attack(K, { turn: firstFree, kind }) === null,
        `a ${kind} Attack was still refused on half-turn ${firstFree} — R08's window is the opening Cycle and nothing after it`);
    });

    // ── 2 · THE BACKSTOP READS THE DAMAGE, NOT THE CARD ──────
    // There is no list of attacking cards in Terror and there should
    // not be one. rules.js recognises the harm at the one place harm is
    // written; this file asks that same predicate rather than growing a
    // rival one, so the two cannot drift into agreeing by accident.
    // ⚠ ONE `escendant` OBJECT, SHARED BY THE BOARD AND THE PAYLOAD.
    // The first cut built a fresh `{ uid, id }` literal in the update as
    // well as in the seat, and rules.js's backstop compares the two with
    // `b.escendant !== o.escendant` — REFERENCE equality on an object.
    // So "a being of theirs HEALING" came back as "their being in rift 1
    // was replaced" and R08 reported a violation on every seed, in a
    // bench whose whole point is that a benign payload is NOT an Attack.
    // The check was crying wolf at its own fixture, which is the failure
    // mode this file's header names at R09 and R65 and is worse than
    // silence. The bench now changes exactly the one field it is about.
    //
    // ⚠ AND THE THING THE FIXTURE WAS HIDING, LEFT FOR rules.js:
    // `adversaryDamageInPayload` clause 3a is a reference comparison, so
    // ANY adversary-side payload that rebuilds the `escendant` object —
    // which is what writing an update through Firebase does — reads as
    // "their being was replaced" and is refused for the opening Cycle.
    // It errs toward refusing and so is not a hole; it is also not what
    // the clause says it is testing. Not fixed here: rules.js is not
    // this file's to edit, and a SECOND implementation quietly agreeing
    // with the first is how this file stops being evidence.
    const ADV_BEING = { uid: "adv#r08b0", id: beingL1.id };
    const seat = () => ({
      anima: ACT.STARTING_ANIMA,
      necropolis: [{ uid: "adv#r08n0", id: beingL1.id }],
      field: { esclings: [{ escendant: ADV_BEING, currentLyf: 4 }] },
    });
    const harmed = [
      ["their Anima dimmed", { anima: ACT.STARTING_ANIMA - 1 }],
      ["their Necropolis grown — a being of theirs was ruined",
        { necropolis: [{ uid: "adv#r08n0", id: beingL1.id }, { uid: "adv#r08n1", id: beingL1.id }] }],
      ["a being of theirs down a Lyf",
        { field: { esclings: [{ escendant: ADV_BEING, currentLyf: 3 }] } }],
    ];
    harmed.forEach(([what, upd]) => {
      H.reject("R08", Legal.openingDamage(K, 0, seat(), upd) !== "R08",
        `${what} on half-turn 0 — the backstop is what catches a route that never declared itself an Attack`);
      H.assert("R08", Legal.openingDamage(K, firstFree, seat(), upd) === null,
        `${what} was still refused on half-turn ${firstFree} — the backstop is only ever asked inside the Cycle`);
    });
    // ...and NOTHING ELSE counts. R08 has never had an opinion about
    // what you do to yourself, or about a card that HELPS them.
    const benign = [
      ["an Anima GAIN", { anima: ACT.STARTING_ANIMA + 1 }],
      ["a SHRINKING Necropolis — a resurrection, not a ruin", { necropolis: [] }],
      ["a being of theirs HEALING",
        { field: { esclings: [{ escendant: ADV_BEING, currentLyf: 9 }] } }],
      ["a payload that touches neither Anima, Necropolis nor Lyf", { khao: 3 }],
    ];
    benign.forEach(([what, upd]) => {
      H.assert("R08", Legal.openingDamage(K, 0, seat(), upd) === null,
        `${what} was called an Attack on half-turn 0 — R08 only ever meant harm`);
    });

    // ── 3 · THE TOMES ────────────────────────────────────────
    const tomeRows = TOMES.map((c) => getCard(c.id)).filter(Boolean);
    const attackTome = tomeRows.find((c) => tomeAttacks(c));
    const calmTome = tomeRows.find((c) => !tomeAttacks(c));
    const benchTome = (c, t) => {
      const me = P();
      me.khao = ACT.MAX_KHAO_ABS;
      // Placed early enough that R35's wait has elapsed — otherwise the
      // wait answers first and this proves TOME_WAIT, not R08.
      me.field.tomes[0] = { uid: "adv#r08t", id: c.id, faceDown: true,
                            placedTurn: t - (ACT.TOME_WAIT[`L${c.level}`] || 0), usedThisTurn: false };
      return Legal.activateTome(K, me, 0, t, false);
    };
    if (attackTome) {
      H.reject("R08", benchTome(attackTome, 0) !== "R08",
        `the Tome ${attackTome.id} (${attackTome.fx}) on half-turn 0 — it ruins something of the adversary's`);
      H.assert("R08", benchTome(attackTome, firstFree) !== "R08",
        `the Tome ${attackTome.id} was still barred by R08 on half-turn ${firstFree}`);
    }
    if (calmTome) {
      H.assert("R09", benchTome(calmTome, 0) !== "R08",
        `the harmless Tome ${calmTome.id} (${calmTome.fx}) was barred on half-turn 0 — R09 permits activation and R08 bars only Attacks`);
    }

    // ── 4 · THE PRIMERA STAMPS ───────────────────────────────
    // The one place R08 and R09 meet head-on. R09 says activation is
    // legal on the opening Cycle; R08 says an Attack is not. A gate that
    // picked either side wholesale would break the other rule, so the
    // discrimination itself is the thing under test — and both answers
    // come off the same gate, on the same half-turn, from real cards.
    const hasAttackStamp = (c) => stampsOf(getCard(c.id)).some((st) => stampAttacks(st.axis, st.word));
    const armed = ESCENDANTS.find((c) => c.level === 1 && hasAttackStamp(c));
    const calm = ESCENDANTS.find((c) => c.level === 1 && !hasAttackStamp(c));
    if (armed) {
      const me = P(); me.khao = ACT.MAX_KHAO_ABS;
      put(me, 0, armed.id);
      stampsOf(getCard(armed.id)).filter((st) => stampAttacks(st.axis, st.word)).forEach((st) => {
        H.reject("R08", Legal.primera(K, me, 0, st.word, "own-turn", 0, st.axis) !== "R08",
          `the ${st.axis} stamp "${st.word}" on half-turn 0 — it reaches the adversary's Anima, and that is an Attack`);
        H.assert("R08", Legal.primera(K, me, 0, st.word, "own-turn", firstFree, st.axis) !== "R08",
          `the ${st.axis} stamp "${st.word}" was still barred on half-turn ${firstFree}`);
      });
    }
    if (calm) {
      const me = P(); me.khao = ACT.MAX_KHAO_ABS;
      put(me, 0, calm.id);
      stampsOf(getCard(calm.id)).forEach((st) => {
        H.assert("R09", Legal.primera(K, me, 0, st.word, "own-turn", 0, st.axis) !== "R08",
          `the harmless ${st.axis} stamp "${st.word}" was barred on half-turn 0 — R09 permits activation and R08 bars only Attacks`);
      });
    }

    // ── 5 · NEITHER BOOK MAY BE SEARCHED (*new*) ─────────────
    // THE FARE IS SUSPENDED, NOT MERELY UNPAYABLE, and the seat below
    // is what makes that a different sentence: it is funded past every
    // fare in the table and is refused anyway. A seat told "you cannot
    // afford it" would be told the wrong thing for the right reason.
    ["luxmoire", "grimoire"].forEach((zone) => {
      [1, 2, 3].forEach((lvl) => {
        const rich = P();
        rich.khao = ACT.MAX_KHAO_ABS; rich.anima = ACT.MAX_ANIMA;
        for (let t = 0; t <= lastOpening; t++) {
          H.reject("R08", Legal.search(K, rich, zone, lvl, t) !== "R08",
            `an L${lvl} ${zone} search on half-turn ${t}, funded to ${rich.khao} Khao and ${rich.anima} Anima`);
          H.reject("R88", Legal.search(K, rich, zone, lvl, t) !== "R08",
            `the R88 fare charged on half-turn ${t} for a search R08 forbids at any price — the fare is suspended, not merely unpayable`);
        }
        H.assert("R88", Legal.search(K, rich, zone, lvl, firstFree) === null,
          `an L${lvl} ${zone} search funded to ${rich.khao} Khao was refused on half-turn ${firstFree} — the suspension lifts with the Cycle`);
        // ── AND THE CARD-EFFECT EXEMPTION (caP'n, 2026-08-26) ──
        // *"cards are exempt from the first turn search rule"*. The
        // SAME seat, the SAME Book, the SAME half-turn — only the
        // searcher differs, which is the whole ruling. A broke seat is
        // used on purpose: a card effect pays no fare, so an exempt
        // search must resolve for a seat that could not have afforded
        // the player's version of it.
        const broke = P();
        broke.khao = 0; broke.anima = 1;
        for (let t = 0; t <= lastOpening; t++) {
          H.assert("R08", Legal.search(K, broke, zone, lvl, t, true) === null,
            `a CARD-EFFECT L${lvl} ${zone} search was refused on half-turn ${t} — R08 bars the seat's own search action, not a card's printed power`);
        }
      });
    });

    // ── 6 · THE DRAWS ────────────────────────────────────────
    // caP'n names FOUR draw clauses and this file modelled two. Both
    // permissions are asserted here, against a counter-reading that
    // dries the whole Cycle out, so neither is a comment with an
    // assert wrapped round it.
    H.assert("R07", Legal.draw(K, P(), "luxmoire", 0, true) === null,
      "the seat going FIRST was refused its Luxmoire draw on the opening Cycle — R07 bars only the Grimoire");
    H.assert("R07", Legal.draw(K, P(), "luxmoire", lastOpening, false) === null,
      "the seat going SECOND was refused its Luxmoire draw on the opening Cycle");
    H.assert("R07", Legal.draw(K, P(), "grimoire", lastOpening, false) === null,
      "the seat going SECOND was refused its Grimoire draw — R07 names it as the seat that MAY");
    H.assert("R07", Legal.draw(K, P(), "grimoire", firstFree, true) === null,
      `the seat going first was still barred from its Grimoire on half-turn ${firstFree} — R07's bar is the opening Cycle only`);
    [["luxmoire", "grimoire"], ["grimoire", "luxmoire"]].forEach(([took, then]) => {
      const me = P();
      me.drewThisTurn = true; me.drewBookThisTurn = took;
      H.reject("R11", Legal.draw(K, me, then, firstFree, false) !== "R11",
        `a ${then} draw by a seat that already drew from its ${took} this Turn — one Book bars the other`);
    });
    // "(card effects excluded)". An effect that says "draw" is not the
    // Turn's draw, and the Turn's draw survives it.
    const fxDrew = P();
    fxDrew.hand.push(fxDrew.grimoire.pop());   // exactly what a draw+1 Tome does
    fxDrew.effectDrewThisTurn = true;
    H.assert("R11", Legal.draw(K, fxDrew, "grimoire", firstFree, false) === null,
      "a card-effect draw consumed the Turn's draw — R11's own parenthesis excludes card effects");
  }
  // ── attack Anima through a standing being · R31 / B03 ──
  {
    const me = P(), opp = O();
    const atk = put(me, 0, beingL1.id);
    put(opp, 0, beingL1.id);
    H.reject("R31", Legal.animaAttack(K, { turn: 4, slot: atk, oppField: opp.field }) !== "R31",
      "an Anima strike with a standing Escendant in the way");
    H.reject("B03", Legal.animaAttack(K, { turn: 4, slot: atk, oppField: opp.field }) !== "R31",
      "the bypass-protection regression signature");
    // and a FRACTURED being still blocks — only Fallen is bypassed
    opp.field.esclings[0].currentLyf = 0;
    H.reject("R31", Legal.animaAttack(K, { turn: 4, slot: atk, oppField: opp.field }) !== "R31",
      "an Anima strike past a FRACTURED being — only Fallen is bypassable");
    // ...and it IS legal once every defender is Fallen
    opp.field.esclings[0].fallen = true;
    H.assert("R26", Legal.animaAttack(K, { turn: 4, slot: atk, oppField: opp.field }) === null,
      "an Anima strike past a Fallen being was refused — R26 makes Fallen bypassable immediately");
  }
  // ── a second swing from one being · R30 ──
  {
    const me = P(), opp = O();
    const atk = put(me, 0, beingL1.id, { attackedThisTurn: true, attacksThisTurn: 1 });
    const tgt = put(opp, 0, beingL1.id);
    const ctx30 = { turn: 4, slot: atk, target: tgt, myRifts: me.field.esclings, oppRifts: opp.field.esclings, slotIdx: 0 };
    H.reject("R30", Legal.lytAttack(K, ctx30) !== "R30", "a second swing from one Escendant");
    // ── R124 · AND A CARD THAT PRINTS AN EXTRA SWING GETS IT ──
    // The permission half, on the SAME rift one line later, so neither
    // branch is a constant: one swing spent is refused, and the same
    // rift wearing Sword of Shadows' grant is not. Then the grant is
    // spent and the refusal returns.
    atk.extraAttack = true;
    H.assert("R124", lytAttacksAllowed(atk, me.field.esclings, opp.field.esclings, (id) => getCard(id), 0) === 2,
      `a being carrying a granted extra swing reads an allowance of ${lytAttacksAllowed(atk, me.field.esclings, opp.field.esclings, (id) => getCard(id), 0)} (expected 2)`);
    H.assert("R124", Legal.lytAttack(K, ctx30) === null,
      "a being carrying a printed extra swing was refused its second — R124 is exactly that exception");
    atk.attacksThisTurn = 2;
    H.reject("R124", Legal.lytAttack(K, ctx30) !== "R30",
      "a THIRD swing from a being granted only one extra — the allowance is a number, not a licence");
  }
  // ── a Lyt Attack in the opening cycle · R08 ──
  {
    const me = P(), opp = O();
    const atk = put(me, 0, beingL1.id);
    const tgt = put(opp, 0, beingL1.id);
    H.reject("R08", Legal.lytAttack(K, { turn: 1, slot: atk, target: tgt }) !== "R08", "a Lyt Attack on half-turn 1");
  }
  // ── Transcend onto a mismatched Escling School · R16 ──
  {
    const me = P();
    const wrong = ESCENDANTS.find((c) => c.level === 1 && c.school !== getCard(me.field.esclings[0].escling)?.school);
    if (wrong) H.reject("R16", Legal.transcend(K, me, 0, wrong.id) !== "R16",
      `a ${wrong.school} L1 Transcended onto a ${getCard(me.field.esclings[0].escling)?.school} Escling`);
  }
  // ── an Affinity with no U'hlti · R70 ──
  if (beingL3) {
    const me = P();
    const s = put(me, 0, beingL3.id, { natural: true });
    H.reject("R70", Legal.attachAffinity(K, s, affinity.id) !== "R70", "an Affinity onto an L3 with no U'hlti");
    s.uhlti = { uid: "adv#u", id: uhlti.id };
    H.assert("R70", Legal.attachAffinity(K, s, affinity.id) === null,
      "a legal Affinity onto a Natural L3 bearing a U'hlti was refused");
    s.affinity = { uid: "adv#a", id: affinity.id };
    H.reject("R73", Legal.attachAffinity(K, s, affinity.id) !== "R73", "a second Affinity on one being");
    H.reject("R72", Legal.attachUhlti(K, s, uhlti.id) !== "R72", "a second U'hlti on one being");
    // ── R69 · SUPREME IS THE WHOLE SET, NEVER A SUBSET ──
    // The old pair of lines compared isSupreme to a hand-copy of its own
    // body — `isSupreme(s) === (three Impera && uhlti && affinity &&
    // natural)` — which is true for any implementation of either side
    // and is the definition of a check that watches nothing. What is
    // asked here instead is the only question R69 poses: assemble the
    // whole set, confirm it reads Supreme, then take ONE requirement
    // away at a time and confirm each removal breaks it.
    H.assert("R69", !isSupreme(K, s), "a being with no Impera was read as Supreme");
    [1, 2, 3].forEach((l) => {
      const imp = IMPERA.find((c) => c.level === l);
      if (imp) s.impera[l] = { uid: `adv#i${l}`, id: imp.id };
    });
    const haveAllImpera = [1, 2, 3].every((l) => s.impera[l]);
    if (haveAllImpera) {
      H.assert("R69", isSupreme(K, s),
        "a Natural L3 wearing three Impera, a U'hlti and an Affinity did not read as Supreme");
      const strip = (mut, what) => {
        const copy = { ...s, impera: { ...s.impera } };
        mut(copy);
        H.assert("R69", !isSupreme(K, copy), `a being with no ${what} was read as Supreme`);
      };
      strip((c) => { delete c.impera[1]; }, "Level 1 Impera");
      strip((c) => { delete c.impera[2]; }, "Level 2 Impera");
      strip((c) => { delete c.impera[3]; }, "Level 3 Impera");
      strip((c) => { c.uhlti = null; }, "U'hlti");
      strip((c) => { c.affinity = null; }, "Affinity");
      strip((c) => { c.natural = false; }, "Natural bearing (it is Deviant)");
    }
    // ── R71 · one Impera per level ──
    const dup = IMPERA.find((c) => c.level === 1);
    if (dup) H.reject("R71", Legal.attachImpera(K, s, dup.id) !== "R71", "a second L1 Impera on one being");
    // ── R64 · a Deviant bears neither ──
    const d = put(P(), 1, beingL3.id, { deviant: true, natural: false });
    H.reject("R64", Legal.attachUhlti(K, d, uhlti.id) !== "R64", "a U'hlti onto a Deviant being");
    H.reject("R64", Legal.attachAffinity(K, d, affinity.id) !== "R64", "an Affinity onto a Deviant being");
  }
  // ── a second Primera on one being, and a reused WORD · R81 / R82 ──
  {
    const me = P();
    const s0 = put(me, 0, beingL1.id);
    // Asked of the RIFT. Asking cards.js how many stamps a card has is a
    // question about the card database that no board can contradict.
    const words = stampsOnSlot(K, s0);
    H.assert("R78", words.length === 2, `${beingL1.id} stands in a rift carrying ${words.length} Primera stamps (expected 2)`);
    s0.primeraUsedThisTurn = true;
    H.reject("R81", Legal.primera(K, me, 0, words[0].word, "own-turn") !== "R81",
      "a second Primera on a being that already used one");
    const me2 = P();
    put(me2, 0, beingL1.id); put(me2, 1, beingL1.id);
    me2.primeraWords.add(words[0].word);
    H.reject("R82", Legal.primera(K, me2, 1, words[0].word, "own-turn") !== "R82",
      `the word "${words[0].word}" reused on a second being in one Turn`);
    H.reject("R80", Legal.primera(K, P(), 0, words[0].word, "response") !== "R80",
      "a Primera inside a Response Window");
    // R79 · the fare is the level, and it must be payable
    const broke = P();
    put(broke, 0, beingL1.id);
    broke.khao = 0; broke.anima = 0;
    H.reject("R79", Legal.primera(K, broke, 0, words[0].word, "own-turn") !== "R79",
      `an L1 Primera with nothing at all to pay its fare of ${K.PRIMERA_COST.L1}`);
  }
  // ── Heretical Training without BOTH prices · R67 ──
  if (beingL3) {
    const me = P();
    put(me, 0, beingL3.id);
    me.khao = ACT.HERETICAL_TRAINING_COST.khao; me.anima = ACT.HERETICAL_TRAINING_COST.anima - 1;
    H.reject("R67", Legal.heretical(K, me, 0) !== "R67",
      `Heretical Training on ${me.anima} Anima against a price of ${K.HERETICAL_TRAINING_COST.anima} Anima AND ${K.HERETICAL_TRAINING_COST.khao} Khao`);
    // One past the Anima price: R96 forbids the rite emptying the seat,
    // so "fully paid" now means paid AND still standing.
    me.anima = ACT.HERETICAL_TRAINING_COST.anima + 1; me.khao = ACT.HERETICAL_TRAINING_COST.khao;
    H.assert("R67", Legal.heretical(K, me, 0) === null, "a fully paid Heretical Training on an L3 with an empty U'hlti slot was refused");
    me.anima = ACT.HERETICAL_TRAINING_COST.anima;
    H.reject("R96", Legal.heretical(K, me, 0) !== "R96",
      `a Heretical Training paid out of exactly ${me.anima} Anima, landing the payer on 0`);
    me.anima = ACT.HERETICAL_TRAINING_COST.anima + 1;
    me.field.esclings[0].uhlti = { uid: "adv#u2", id: uhlti.id };
    H.reject("R67", Legal.heretical(K, me, 0) !== "R67", "Heretical Training onto a being that already bears a U'hlti");
  }
  // ── R65 · Deviant Transcend costs nothing but marks Deviant ──
  {
    const me = P();
    const wrong = ESCENDANTS.find((c) => c.level === 1 && c.school !== getCard(me.field.esclings[0].escling)?.school);
    if (wrong) {
      const anima0 = me.anima, khao0 = me.khao;
      H.assert("R65", Legal.deviantTranscend(K, me, 0, wrong.id) === null,
        "a Deviant Transcend across Schools was refused — R65 permits it");
      me.field.esclings[0].escendant = { uid: "adv#dev", id: wrong.id };
      seatStamps(me.field.esclings[0]);
      me.field.esclings[0].deviant = true; me.field.esclings[0].natural = false;
      // Only the PRICE is asserted here. `&& esclings[0].deviant` was in
      // this line too, one statement after `esclings[0].deviant = true` —
      // the simulator's own literal read back and called a proof. What
      // the Deviant MARK is worth is asserted where it costs something:
      // R64 refuses a U'hlti and an Affinity to the being wearing it.
      H.assert("R65", me.anima === anima0 && me.khao === khao0,
        `a Deviant Transcend charged a price (Anima ${anima0}→${me.anima}, Khao ${khao0}→${me.khao})`);
      H.assert("R64", Legal.attachUhlti(K, me.field.esclings[0], uhlti.id) === "R64",
        "a being marked Deviant by a Deviant Transcend was still offered a U'hlti");
    }
  }
  // ── searching without the fare · R88 ──
  // ⚠ EVERY ROW IN THIS BLOCK NOW PASSES A TURN, AND IT IS A TURN PAST
  // THE OPENING CYCLE. R08 suspends both fares inside the Cycle, so a
  // fare row asked at half-turn 0 would answer R08 and prove nothing
  // about R88 — the by-level table would have gone dark behind the new
  // rule, which is the quietest way a check dies.
  {
    const paid = openingCycleLength(SEATS) + 2;   // clear of R08's window
    const me = P();
    me.khao = 0; me.anima = 0;
    H.reject("R88", Legal.search(K, me, "luxmoire", 1, paid) !== "R88", `a Luxmoire search with nothing to pay it (fare ${K.LUX_SEARCH_COST})`);
    H.reject("R88", Legal.search(K, me, "grimoire", 1, paid) !== "R88", `a Grimoire search with nothing to pay it (L1 fare ${grimFareOf(K, 1)})`);

    // ── R88 · THE FARE IS A LEVEL TABLE ──────────────────────
    // The flat number this file used to read was V'ael's invention;
    // the board has always charged less for a Level 1 search. Both
    // rungs are funded to the TRUE fare and judged against the RULE's,
    // so mutating either entry of the table reddens exactly one of them.
    [1, 2, 3].forEach((lvl) => {
      const funded = P();
      funded.khao = grimFareOf(ACT, lvl); funded.anima = 0;
      H.assert("R88", Legal.search(K, funded, "grimoire", lvl, paid) === null,
        `an L${lvl} Grimoire search funded to ${funded.khao} Khao was refused — the rule asks ${grimFareOf(K, lvl)}`);
      const short = P();
      short.khao = grimFareOf(ACT, lvl) - 1; short.anima = 0;
      H.reject("R88", Legal.search(K, short, "grimoire", lvl, paid) !== "R88",
        `an L${lvl} Grimoire search one Khao short of its ${grimFareOf(K, lvl)} fare`);
      // The Debugger's K-side table must agree with the engine's oracle.
      H.assert("R88", grimFareOf(ACT, lvl) === grimSearchFare(lvl),
        `the Debugger reads the L${lvl} Grimoire fare as ${grimFareOf(ACT, lvl)} while grimSearchFare says ${grimSearchFare(lvl)}`);
    });
    const luxFunded = P();
    luxFunded.khao = ACT.LUX_SEARCH_COST; luxFunded.anima = 0;
    H.assert("R88", Legal.search(K, luxFunded, "luxmoire", 1, paid) === null,
      `a Luxmoire search funded to ${luxFunded.khao} Khao was refused — the rule asks ${K.LUX_SEARCH_COST}`);

    // ── R106 · WHAT A PAID LUXMOIRE SEARCH MAY FIND ──────────
    // caP'n, 2026-08-26: *"you can still only search the Lux for Impera,
    // not the U/A's"* — and *"because we added the U'hlti and Affinity
    // into the Lux we can't have it so easy."* Both live there (R05) and
    // both may only be DRAWN. Asked with the CARD in hand, on the same
    // funded seat and the same half-turn, so neither branch is a
    // constant: the Impera is found, the U'hlti and the Affinity are not.
    const anImpera = IMPERA[0], aUhlti = UHLTIS[0], anAffinity = AFFINITIES[0];
    H.assert("R106", Legal.search(K, luxFunded, "luxmoire", 1, paid, false, anImpera.id) === null,
      `a paid Luxmoire search was refused the Impera ${anImpera.id} — Impera is exactly what it finds`);
    H.reject("R106", Legal.search(K, luxFunded, "luxmoire", 1, paid, false, aUhlti.id) !== "R106",
      `a paid Luxmoire search tutoring the U'hlti ${aUhlti.id} — U'hltis are DRAWN, never searched`);
    H.reject("R106", Legal.search(K, luxFunded, "luxmoire", 1, paid, false, anAffinity.id) !== "R106",
      `a paid Luxmoire search tutoring the Affinity ${anAffinity.id} — Affinities are DRAWN, never searched`);
    // A CARD EFFECT that names its own target is not this search and is
    // bound only by its own text (R08's 2026-08-26 exemption).
    H.assert("R106", Legal.search(K, luxFunded, "luxmoire", 1, paid, true, aUhlti.id) === null,
      "a CARD EFFECT naming a U'hlti in the Luxmoire was barred by R106 — the bar is on the seat's own paid search");
    // ...and a GRIMOIRE search is not bound by it at all.
    H.assert("R106", Legal.search(K, luxFunded, "grimoire", 1, paid, false, aUhlti.id) !== "R106",
      "a Grimoire search answered R106 — that clause is about the Luxmoire");
  }

  // ── R29 · a 0-Lyf being is ALWAYS Fractured ───────────────
  // This lived only as a board sweep, so a duel between two decks that
  // print no 0-Lyf being never reached it and R29 rendered as a gap with
  // no reason. It is a property of the CARD, so it is proven from cards.
  {
    const zero = ESCENDANTS.find((c) => (c.lyf ?? 0) === 0);
    const solid = ESCENDANTS.find((c) => (c.lyf ?? 0) > 0);
    if (zero) {
      const me = P();
      const sl = put(me, 0, zero.id);
      H.assert("R29", sl.currentLyf === 0,
        `${zero.id} prints Lyf 0 and took the field on ${sl.currentLyf} — a 0-Lyf being is always Fractured`);
      H.assert("R29", isFracturedSlot(K, sl),
        `${zero.id} prints Lyf 0 and does not read as Fractured — it can be ruined immediately`);
    }
    if (solid) {
      const me2 = P();
      const sl2 = put(me2, 1, solid.id);
      H.assert("R29", !isFracturedSlot(K, sl2),
        `${solid.id} prints Lyf ${solid.lyf} and read as Fractured on arrival`);
    }
  }
  // ── THE DESCENT BENCH · R24 / R51 / R52 / R68 ─────────────
  // A descent that has anything to SAY about R51, R52 and R68 needs an
  // Ascended L3 wearing a U'hlti, an Affinity and a Heretical Tome, and
  // then needs someone to knock it down. That is a rare accident in a
  // random duel, and while it never happened those three rules were
  // asserted only on boards where every clause was vacuously true — a
  // Soul of nothing survives intact, a U'hlti that was never there is
  // never lost, and a Heretical Tome that does not exist does not
  // descend. Built by hand here, the descent happens in every run.
  if (beingL2 && beingL3) {
    const owner = P();
    const slot = owner.field.esclings[0];
    slot.escendant = { uid: "bench#l3", id: beingL3.id };
    seatStamps(slot);
    slot.currentLyf = 0;
    slot.soul = [{ uid: "bench#s1", id: beingL1.id }, { uid: "bench#s2", id: beingL2.id }];
    [1, 3].forEach((l) => {
      const imp = IMPERA.find((c) => c.level === l);
      if (imp) slot.impera[l] = { uid: `bench#i${l}`, id: imp.id };
    });
    slot.uhlti = { uid: "bench#u", id: uhlti.id };
    slot.affinity = { uid: "bench#a", id: affinity.id };
    slot.heretical = { uid: "bench#h", id: TOMES[0].id };
    // Every R24 / R51 / R52 / R68 assertion lives inside ruinOrDescend,
    // so the bench does not restate them — it supplies the one board on
    // which they are not vacuous.
    ruinOrDescend(H, K, owner, slot, "bench");
    H.assert("R24", getCard(slot.escendant?.id)?.level === beingL2.level,
      `the descent bench landed on ${slot.escendant?.id || "an empty slot"} instead of the L${beingL2.level} in its Soul`);
    H.assert("R68", owner.necropolis.some((c) => c.uid === "bench#h"),
      "the Heretical Tome did not reach the Necropolis when its bearer descended");
  }
  // ── THE RUIN BENCH · R23 / R50 ────────────────────────────
  // Same argument one level down: an L1 with a Soul is what makes R50 a
  // claim rather than `[].every(...)`.
  {
    const owner = P();
    const slot = owner.field.esclings[1];
    slot.escendant = { uid: "bench#r", id: beingL1.id };
    seatStamps(slot);
    slot.currentLyf = 0;
    slot.soul = [{ uid: "bench#rs", id: uhlti.id }];
    slot.uhlti = { uid: "bench#ru", id: uhlti.id };
    ruinOrDescend(H, K, owner, slot, "bench");
    H.assert("R23", slot.escendant === null && owner.necropolis.some((c) => c.uid === "bench#r"),
      "the ruin bench left a being on the field or failed to seal it");
  }
  // ── R84 · PRIMERAS ARE OF THE FLESH ───────────────────────
  // The three things that are supposed to be unable to take a stamp
  // off a being, done to one on purpose: Deviance, a stripped Soul, and
  // a descent. The sweep inside the duel only ever sees Natural beings
  // with their Souls intact, so it had nothing to disagree with.
  {
    const dev = P();
    const sd = put(dev, 0, beingL1.id, { deviant: true, natural: false, soul: [], uhlti: null, affinity: null });
    H.assert("R84", stampsOnSlot(K, sd).length === 2,
      `a Deviant ${beingL1.id} carries ${stampsOnSlot(K, sd).length} Primera stamps — Deviance does not strip them`);
    const bare = P();
    const sb = put(bare, 1, beingL1.id, { soul: [], impera: {}, uhlti: null, affinity: null, heretical: null });
    H.assert("R84", stampsOnSlot(K, sb).length === 2,
      `a being stripped of its whole Soul carries ${stampsOnSlot(K, sb).length} Primera stamps — stamps are not Soul`);
    if (beingL2 && beingL3) {
      const fallenOwner = P();
      const sf = fallenOwner.field.esclings[2];
      sf.escendant = { uid: "bench#f3", id: beingL3.id };
      seatStamps(sf);
      sf.currentLyf = 0;
      sf.soul = [{ uid: "bench#f2", id: beingL2.id }];
      ruinOrDescend(H, K, fallenOwner, sf, "bench");
      H.assert("R84", stampsOnSlot(K, sf).length === 2,
        `a being that descended to ${sf.escendant?.id} carries ${stampsOnSlot(K, sf).length} Primera stamps — a descent takes no stamp`);
    }
  }
  // ── R39 / B01 · A SPENT TOME IS SPENT ─────────────────────
  // The duel-side proof needs a non-persistent Tome to be activated
  // with the Khao still in hand to pay it, which is two coincidences
  // deep and did not happen on every seed — R39 rendered as an
  // UNREACHED rule with no reason filed on the unlucky ones, which is
  // the worst-looking possible outcome for a rule that is in fact
  // watched. Built by hand, both halves fire in every run.
  {
    const me = P();
    me.khao = ACT.MAX_KHAO_ABS;
    const spent = TOMES.find((c) => !ACT.PERSISTENT_TOME_TAGS.includes(c.effectTag) && c.level !== 1);
    const persistent = TOMES.find((c) => ACT.PERSISTENT_TOME_TAGS.includes(c.effectTag));
    if (spent) {
      me.field.tomes[0] = { uid: "adv#r39", id: spent.id, faceDown: false, placedTurn: 0, usedThisTurn: true };
      H.reject("R39", Legal.activateTome(K, me, 0, 9, false) !== "R39",
        `a second activation of ${spent.id} (tag ${spent.effectTag || "none"}) in one Turn`);
      H.reject("B01", Legal.activateTome(K, me, 0, 9, false) !== "R39",
        "the Devil's Advocate loop — a spent Tome answering twice");
    }
    if (persistent) {
      me.field.tomes[1] = { uid: "adv#r39p", id: persistent.id, faceDown: false, placedTurn: 0, usedThisTurn: true };
      H.assert("B01", Legal.activateTome(K, me, 1, 9, false) !== "R39",
        `persistent Tome ${persistent.id} (${persistent.effectTag}) was ruin-locked by R39 despite its tag`);
    }
  }
  // ── R09 · the first seat may act on its opening Turn ───────
  {
    const me = P();
    const escClass = getCard(me.field.esclings[0].escling)?.school;
    const match = ESCENDANTS.find((c) => c.level === 1 && c.school === escClass);
    if (match) H.assert("R09", Legal.transcend(K, me, 0, match.id, 0) === null,
      "the first seat was refused a Transcend on half-turn 0 — R09 is exactly that permission");
  }
  // ── R17 · nothing caps Transcends per Turn ────────────────
  {
    const me = P();
    me.transcendedThisTurn = 1;
    const escClass = getCard(me.field.esclings[1].escling)?.school;
    const match = ESCENDANTS.find((c) => c.level === 1 && c.school === escClass);
    if (match) H.assert("R17", Legal.transcend(K, me, 1, match.id, 4) === null,
      "a second Transcend in one Turn was refused — R17 caps nothing, and that is the whole rule");
  }
  // ── R38 · the Rift ceiling, proven off the board's luck ───
  // The board assertion needs all TOME_SLOTS filled before it can say
  // anything, and some deck pairs never fill them. Built by hand here so
  // the ceiling is checked in every run rather than the lucky ones.
  {
    const me = P();
    for (let i = 0; i < ACT.TOME_SLOTS; i++) {
      me.field.tomes[i] = { uid: `adv#cap${i}`, id: TOMES[0].id, faceDown: true, placedTurn: 0, usedThisTurn: false };
    }
    H.assert("R38", me.field.tomes.filter(Boolean).length <= K.TOME_SLOTS,
      `a board carrying ${me.field.tomes.filter(Boolean).length} Tomes cleared a ceiling of ${K.TOME_SLOTS} Rifts`);
    H.reject("R38", Legal.placeTome(K, me) !== "R38",
      `a Tome placed onto a board already holding ${ACT.TOME_SLOTS}`);
  }

  // ── R96 · THE TILL LAW, and its single exemption ──────────
  {
    // Exactly enough Anima and no Khao: the payment would land the payer
    // on 0. R96 says that is ILLEGAL, not fatal — and the code returned
    // must be R96, not R33. R33 is the consequence; R96 is the rule.
    const till = P();
    till.khao = 0; till.anima = grimFareOf(ACT, 1);
    // Past the opening Cycle, or R08 answers before the till ever does.
    H.reject("R96", Legal.search(K, till, "grimoire", 1, openingCycleLength(SEATS) + 2) !== "R96",
      `a Grimoire search paid entirely out of ${till.anima} Anima, landing the payer on 0`);
    const oneMore = P();
    oneMore.khao = 0; oneMore.anima = grimFareOf(ACT, 1) + 1;
    H.assert("R96", Legal.search(K, oneMore, "grimoire", 1, openingCycleLength(SEATS) + 2) === null,
      `a search leaving 1 Anima behind was refused — R96 forbids reaching 0, not approaching it`);
    // ...and the same law on the two other voluntary tills.
    const rez = P();
    rez.necropolis.push({ uid: "adv#till", id: beingL1.id });
    rez.khao = 0; rez.anima = ACT.RESURRECT_COST.L1;
    H.reject("R96", Legal.resurrect(K, rez, rez.necropolis[0], 0, "paid") !== "R96",
      `a Resurrection paid entirely out of ${rez.anima} Anima, landing the payer on 0`);
    const stamp = P();
    put(stamp, 0, beingL1.id);
    stamp.khao = 0; stamp.anima = ACT.PRIMERA_COST.L1;
    const w = stampsOf(getCard(beingL1.id))[0].word;
    H.reject("R96", Legal.primera(K, stamp, 0, w, "own-turn") !== "R96",
      `a Primera paid entirely out of ${stamp.anima} Anima, landing the payer on 0`);
    // THE ONE EXEMPTION. R43's remedy must always be payable.
    const shifter = P();
    shifter.grimoire = []; shifter.anima = ACT.GRAVESHIFT_COST;
    H.assert("R96", Legal.graveshift(K, shifter) === null,
      `a seat on exactly the ${K.GRAVESHIFT_COST} fare with an empty Grimoire was refused the Graveshift — R96 exempts it (R43's remedy must stay payable)`);
    H.assert("R43", Legal.graveshift(K, shifter) === null,
      `R43's own remedy was priced out of reach of the seat that needs it most`);
  }
  // ── the mulligan, in the shape the 2026-08-25 ruling gave it · R90 ──
  // caP'n: "Either player, if they have not drawn yet, they can do a
  // Mulligan on that first turn and no other." Three clauses. The LIMIT
  // was already here; the WINDOW was modelled as "before this seat has
  // acted", which says nothing about the seat that has not acted yet;
  // and "if they have not drawn yet" was not modelled at all.
  {
    const me = P(); me.mulligans = ACT.MULLIGAN_LIMIT;
    H.reject("R90", Legal.mulligan(K, me, 0) !== "R90", `mulligan number ${me.mulligans + 1} (limit ${K.MULLIGAN_LIMIT})`);
    const late = P(); late.turnsTaken = 1;
    H.reject("R90", Legal.mulligan(K, late, 0) !== "R90", "a mulligan taken after the first Turn");
    // *NEW* · not once this seat has drawn.
    const drew = P(); drew.drewThisTurn = true;
    H.reject("R90", Legal.mulligan(K, drew, 0) !== "R90",
      "a mulligan by a seat that had already taken its draw — a seat could otherwise look at the card and then return the hand it improved");
    // THE WINDOW IS THE CYCLE, and both ends of it are asked. The seat
    // going second still has its mulligan on the last half-turn of the
    // Cycle; one half-turn later nobody does.
    H.assert("R90", Legal.mulligan(K, P(), openingCycleLength(SEATS) - 1) === null,
      `a mulligan on half-turn ${openingCycleLength(SEATS) - 1} was refused — the window is the opening CYCLE, which is what gives the seat going second one at all`);
    H.reject("R90", Legal.mulligan(K, P(), openingCycleLength(SEATS)) !== "R90",
      `a mulligan on half-turn ${openingCycleLength(SEATS)}, one past the opening Cycle — "that first turn and no other"`);
  }
  // ── the runaway guard, which is NOT R92 · CHAIN_DEPTH_CAP ──
  // ⚠ RE-CHANNELLED 2026-09-08. This asserted the depth cap as a RULE
  // refusal under R92, and R92 says the opposite: RULEZ_V2 ch.15, on how
  // long a chain may run — *"None. Not twelve, not five, not any
  // number."* rules.js agrees at the constant itself: "runaway guard
  // ONLY — never a game bound". Reporting the guard under R92 credited
  // the catalogue with watching a rule while asserting its inverse.
  // It is an ENGINE INVARIANT, so it goes in the invariant channel.
  H.inv("chain-depth-guard", Legal.chainLink(K, ACT.CHAIN_DEPTH_CAP) !== null,
    `the runaway guard did not stop link ${ACT.CHAIN_DEPTH_CAP + 1} (cap ${K.CHAIN_DEPTH_CAP}) — this is a harness backstop, NOT R92: a chain has no length limit`);
  // ── a second Resurrection in one Turn, and past the tally · R89/R44/R85/R86 ──
  {
    const me = P();
    const seal = { uid: "adv#r1", id: beingL1.id };
    me.necropolis.push(seal);
    me.khao = ACT.RESURRECT_COST.L1; me.anima = 40;
    H.assert("R47", Legal.resurrect(K, me, seal, 0, "paid") === null,
      `a Resurrection funded to the true fare was refused — the rule asks ${K.RESURRECT_COST.L1}`);
    me.paidResurrectionsThisTurn = ACT.RESURRECT_PER_TURN;
    H.reject("R89", Legal.resurrect(K, me, seal, 0, "paid") !== "R89",
      `PAID Resurrection number ${me.paidResurrectionsThisTurn + 1} in one Turn (limit ${K.RESURRECT_PER_TURN})`);
    // R89 was rewritten today: the clock binds the RITE only. A card
    // effect rides alongside it — being exempt IS what those cards buy.
    H.assert("R89", Legal.resurrect(K, me, seal, 0, "effect") !== "R89",
      "a card-effect return was blocked by the paid clock — R89 exempts Tomes, Impera and the Time Primera");
    // ⚠ shape hazard, named where it bites: a persisted player record
    // carries `resurrectedThisTurn` as `{ turn, count }` and React state
    // carries it as a boolean. Both must read as a number here or the
    // gate passes forever in silence.
    H.assert("R89", Legal.paidReturnsOf({ resurrectedThisTurn: { turn: 4, count: 1 } }) === 1
      && Legal.paidReturnsOf({ resurrectedThisTurn: true }) === 1
      && Legal.paidReturnsOf({ paidResurrectionsThisTurn: 2 }) === 2,
      "the R89 gate cannot read all three shapes of resurrectedThisTurn — {turn,count}, boolean and integer");
    me.paidResurrectionsThisTurn = 0;
    me.tally[seal.uid] = ACT.RESURRECT_LIMIT.L1;
    H.reject("R44", Legal.resurrect(K, me, seal, 0, "paid") !== "R44", `return number ${me.tally[seal.uid] + 1} of one L1 copy (tally is ${K.RESURRECT_LIMIT.L1})`);
    // R85 · a SECOND COPY of the same name still has its own full tally
    const twin = { uid: "adv#r2", id: beingL1.id };
    me.necropolis.push(twin);
    H.assert("R85", Legal.resurrect(K, me, twin, 0, "paid") === null,
      "a second COPY was blocked by the first copy's exhausted tally — R85 makes tallies per copy");
    // R86 · a shifted card is past the paid rite
    me.shifted.add(twin.uid);
    H.reject("R86", Legal.resurrect(K, me, twin, 0, "paid") !== "R86", "the paid rite on a card that passed through a Graveshift");
    // R46 · no skipping levels onto an Escling
    if (beingL3) {
      const l3 = { uid: "adv#r3", id: beingL3.id };
      me.shifted = new Set(); me.necropolis.push(l3);
      H.reject("R46", Legal.resurrect(K, me, l3, 0, "paid") !== "R46", "an L3 Resurrected straight onto an Escling");
      H.reject("R45", Legal.resurrect(K, me, l3, 0, "paid") !== "R46", "a Resurrection out of level order");
    }
    // R47 · the fare must be payable
    const broke = P();
    broke.necropolis.push({ uid: "adv#r4", id: beingL1.id });
    broke.khao = 0; broke.anima = 0;
    H.reject("R47", Legal.resurrect(K, broke, broke.necropolis[0], 0, "paid") !== "R47",
      `an L1 Resurrection on 0 Khao and 1 Anima against a fare of ${K.RESURRECT_COST.L1}`);
  }
  // ── a Curio used twice, and a card reaching an Escling · R75 / R74 ──
  {
    const me = P();
    if (me.field.curios.length) {
      me.field.curios[0].used = true;
      H.reject("R75", Legal.curioActivate(K, me, 0) !== "R75", "a Curio activated twice");
    }
    // BOTH DIRECTIONS OFF ONE FIXTURE, so neither branch is a constant:
    // the bare rift is refused, the occupied one beside it is not.
    H.reject("R74", Legal.targetProphecyOrEscling(K, me.field.esclings[0]) !== "R74",
      "a card effect targeting a bare Escling");
    const held = put(me, 1, beingL1.id);
    H.assert("R74", Legal.targetProphecyOrEscling(K, held) === null,
      `a card effect was refused an Escendant standing in rift 1 — R74 protects the Prophecy and the bare Esclings, not the battle line`);
  }

  // ── THE WARDEN BENCH · R119 ──────────────────────────────
  // THE SHIELD IS THE TANK'S, NOT THE WARDEN'S. caP'n: *"Warden: tank
  // must be ruined and either left in a fallen state, or only esclings
  // left on board to attack the Warden with Lyt attacks. All other ways
  // are always viable."* Two doors open it, and everything that is not a
  // SWING — a Tome, an Impera, an U'hlti, a Curio, a stamp, an Affinity
  // — reaches a Warden on any Turn. Both predicates are the engine's
  // own; this file asks them and states the two clauses independently.
  {
    const awake = roleLockHalfTurns(SEATS);
    const line = (over) => {
      const l = TRIO_RIFTS.map(() => ({ escendant: null, fallen: false }));
      l[MIND_RIFT] = { escendant: { uid: "w", id: beingL1.id }, fallen: false };
      TRIO_RIFTS.forEach((r) => { l[r] = { escendant: { uid: "t" + r, id: beingL1.id }, fallen: false }; });
      return Object.assign(l, over || {});
    };
    // THE SHIELD HOLDS while the Body stands.
    const held = line();
    H.reject("R119", wardenOpenToLytAttack(held, awake, SEATS),
      "a Warden was Lyt-attackable with its seat's Body rift still standing — that shield is what the Warden pays for");
    // DOOR ONE — the Body ruined AND LEFT FALLEN. Ruined alone is not
    // enough and Fallen alone is not enough; caP'n named both.
    const fallenBody = line(); fallenBody[TRIO_RIFTS[0]].fallen = true;
    H.assert("R119", wardenOpenToLytAttack(fallenBody, awake, SEATS),
      "the Warden stayed shielded with its Body Fallen — that is the first of the two doors caP'n named");
    // DOOR TWO — only Esclings left beside it.
    const bareLine = line();
    TRIO_RIFTS.forEach((r) => { bareLine[r] = { escendant: null, fallen: false }; });
    H.assert("R119", wardenOpenToLytAttack(bareLine, awake, SEATS),
      "the Warden stayed shielded with nothing but Esclings beside it — that is the second door");
    // ⚠ A WARDEN IS NOT A WALL. caP'n: *"You dont need to ruin the warden
    // or leave it in a fallen state to dim Anima, only the other 3"* —
    // *"pretend as though the Warden isn't there is a good way to explain
    // that."* Three standing beings hold the wall; the fourth does not.
    // THIS IS THE ONE LEG THAT CHANGES AN ANSWER THIS FILE ALREADY GAVE,
    // and it is the reason Legal.animaAttack now asks the engine's
    // predicate instead of its own private `.some` over every rift.
    const wardenOnly = TRIO_RIFTS.map(() => ({ escendant: null, fallen: false }));
    wardenOnly[MIND_RIFT] = { escendant: { uid: "w", id: beingL1.id }, fallen: false };
    H.assert("R119", animaOpen(wardenOnly, awake, SEATS) === true,
      "a seat whose ONLY survivor stands in the Mind rift had its Anima called closed — for that question the Warden is invisible");
    H.reject("R119", animaOpen(wardenOnly, awake - 1, SEATS),
      `the Mind rift stopped walling Anima on half-turn ${awake - 1}, inside R115's three-full-Turn lock — for those six half-turns a Warden is an ordinary being and walls like one`);
    // ...and one of the other three DOES hold the wall, on the same board.
    const oneOfThree = TRIO_RIFTS.map(() => ({ escendant: null, fallen: false }));
    oneOfThree[TRIO_RIFTS[1]] = { escendant: { uid: "s", id: beingL1.id }, fallen: false };
    H.reject("R119", animaOpen(oneOfThree, awake, SEATS),
      "a standing being in the Sol rift did not hold the Anima wall — three of the four always do");
    // ONE WARDEN ART PER AENARCHIST PER TURN — the single place a
    // per-seat law reaches a Deviant Primera, and caP'n wrote it himself.
    H.assert("R119", WARDEN_ARTS_PER_TURN === 1,
      `the Warden Art cap reads ${WARDEN_ARTS_PER_TURN} per seat per Turn — caP'n: "Each Aenarchist can only use 1 of their Warden Primeras per turn", across BOTH options`);
  }

  // ── THE CADENCE BENCH · R35 / R103 ───────────────────────
  // Two rules about the same thing — HOW LONG a Tome waits — measured in
  // the one unit Terror uses. A full Turn is TWO half-turns, so OPT = 2,
  // OP2 = 4, OP3 = 6, and at a 3-4 seat table a full Turn is a full lap.
  // Neither was watchable inside a played duel: this simulator only ever
  // SETS Tomes face-down (so R35's face-up clause had no board) and had
  // no cadence at all (so a persistent Tome fired every half-turn).
  {
    const t1 = TOMES.find((c) => c.level === 1);
    const setDown = P(); setDown.khao = ACT.MAX_KHAO_ABS; setDown.anima = ACT.MAX_ANIMA;
    setDown.field.tomes[0] = { uid: "b#r35d", id: t1.id, faceDown: true, placedTurn: 8, usedThisTurn: false };
    // R35 · SET face-down, it waits a FULL Turn — two half-turns.
    H.reject("R35", Legal.activateTome(K, setDown, 0, 8, false) !== "R35",
      `an L1 Tome set face-down and flipped on the same half-turn (the wait is ${tomeWaitHalfTurns(1, { faceDown: true, seatCount: SEATS })} half-turns — one FULL Turn)`);
    H.reject("R35", Legal.activateTome(K, setDown, 0, 9, false) !== "R35",
      "an L1 Tome set face-down and flipped one HALF-turn later — a full Turn is two half-turns, not one");
    H.assert("R35", Legal.activateTome(K, setDown, 0, 8 + tomeWaitHalfTurns(1, { faceDown: true, seatCount: SEATS }), false) === null,
      "an L1 Tome that waited its full Turn was still refused");
    // R35 · PLAYED FACE-UP from hand, it is being ACTIVATED, not set,
    // and waits for nothing. caP'n: *"if you place it face down, you
    // must wait 1 full turn"* — the price is on the SETTING.
    const faceUp = P(); faceUp.khao = ACT.MAX_KHAO_ABS; faceUp.anima = ACT.MAX_ANIMA;
    faceUp.field.tomes[0] = { uid: "b#r35u", id: t1.id, faceDown: false, placedTurn: 8, usedThisTurn: false };
    // The K-side lookup must agree with the engine's own helper in both
    // directions and on every rung — the thing that stops the copy in
    // `tomeWaitOf` from becoming a second catalogue.
    [1, 2, 3].forEach((lv) => [true, false].forEach((fd) => {
      H.assert("R35", tomeWaitOf(ACT, lv, fd) === tomeWaitHalfTurns(lv, { faceDown: fd, seatCount: SEATS }),
        `the Debugger reads the L${lv} ${fd ? "face-down" : "face-up"} wait as ${tomeWaitOf(ACT, lv, fd)} while tomeWaitHalfTurns says ${tomeWaitHalfTurns(lv, { faceDown: fd, seatCount: SEATS })}`);
    }));
    H.assert("R35", Legal.activateTome(K, faceUp, 0, 8, false) === null,
      "an L1 Tome played FACE-UP from hand was made to wait — the wait is a price on SETTING, not a property of the card's level (Chapter 13)");

    // R103 · a persistent Tome re-fires on its TAG's cadence.
    const persistent = TOMES.find((c) => ACT.PERSISTENT_TOME_TAGS.includes(c.effectTag)
      && c.effectTag !== "ATV" && (c.khaoCost || 0) === 0 && (c.animaCost || 0) === 0)
      || TOMES.find((c) => ACT.PERSISTENT_TOME_TAGS.includes(c.effectTag));
    if (persistent) {
      const need = cooldownHalfTurns(persistent.effectTag, SEATS);
      const pp = P(); pp.khao = ACT.MAX_KHAO_ABS; pp.anima = ACT.MAX_ANIMA;
      pp.field.tomes[0] = { uid: "b#r103", id: persistent.id, faceDown: false,
                            placedTurn: 0, usedThisTurn: false, lastFiredTurn: 10 };
      H.reject("R103", Legal.activateTome(K, pp, 0, 10 + need - 1, false) !== "R103",
        `${persistent.id} (${persistent.effectTag}) fired again ${need - 1} half-turn(s) after its last firing — its cadence is ${need} half-turns, because a full Turn is TWO`);
      H.assert("R103", Legal.activateTome(K, pp, 0, 10 + need, false) === null,
        `${persistent.id} (${persistent.effectTag}) was refused at exactly its ${need}-half-turn cadence`);
      // ...and the tags are DISTINGUISHABLE, which is the whole finding
      // R103 landed on: before it, both consumers compared against the
      // raw tag number and OPT and OP2 answered the same at a seat's own
      // cadence. Asked of the shared helper so no number is typed here.
      H.assert("R103", cooldownHalfTurns("OP2", SEATS) === 2 * cooldownHalfTurns("OPT", SEATS)
        && cooldownHalfTurns("OP3", SEATS) === 3 * cooldownHalfTurns("OPT", SEATS),
        `OPT/OP2/OP3 read ${cooldownHalfTurns("OPT", SEATS)}/${cooldownHalfTurns("OP2", SEATS)}/${cooldownHalfTurns("OP3", SEATS)} half-turns — they must be 1, 2 and 3 FULL Turns apart or the tags are indistinguishable`);
      // AND A FULL TURN IS A FULL LAP at a bigger table (R103's own
      // generalisation, and R08's opening Cycle carries it the same way).
      H.assert("R103", cooldownHalfTurns("OPT", 4) === halfTurnsPerFullTurn(4),
        `at a four-seat table OPT reads ${cooldownHalfTurns("OPT", 4)} half-turns against a full lap of ${halfTurnsPerFullTurn(4)}`);
    }
  }

  // ── THE WARD AND THE TREASURE BENCH · R123 / R125 ────────
  // Both landed on 2026-09-13 and BOTH SETS OF CARDS WERE DEAD — they
  // raised field flags that nothing read. Both are answered at the
  // PAYLOAD, like R08, because that is the only layer at which "beings"
  // and "cards" are different questions; this file asks the engine's own
  // two predicates, which is a second opinion rather than a second
  // implementation of them.
  {
    const now = 12;
    // ── R123 · THE TOME WARD, AND ITS TWO SCOPES ────────────
    // caP'n: *"Cards is general and Beings is specific, written and
    // chosen specifically for those purposes."* Shower of Lyt wards
    // BEINGS for the turn; Phoenix Aura wards CARDS for two FULL Turns.
    H.assert("R123", tomeWardScope({}, now) === null,
      "an unwarded field reported a ward");
    H.assert("R123", tomeWardScope({ protectedFromTomesThisTurn: true, protectedTomeTurn: now }, now) === "beings",
      "Shower of Lyt's ward did not read as the BEINGS scope");
    H.assert("R123", tomeWardScope({ protectedFromTomesThisTurn: true, protectedTomeTurn: now }, now + 1) === null,
      "Shower of Lyt's ward outlived the half-turn it was played on — it is keyed to that turn so a stale flag cannot ward forever");
    // ⚠ FLIPPED 2026-09-14 BY RULING, and THIS FILE REPORTED THE
    // DISAGREEMENT ITSELF — which is the whole reason a second
    // implementation is worth keeping. caP'n ruled a bare "N turns" is N
    // FULL Turns and the window is EXCLUSIVE: *"'for 3 turns' … means 6
    // half turns, once the 6 half turns are over, you can proceed."*
    // `rules.durationStillRunning` moved from `<=` to `<` and
    // `tomeWardScope` was routed through it, so the expiry half-turn is
    // now the FIRST one on which the ward is gone rather than the last on
    // which it holds. This bench still demanded the inclusive reading and
    // came back `violations 2` on the next headless run, naming the rule.
    //
    // ⭐ AND THE HOUSE LAW DECIDES WHICH SIDE MOVES: a ruling from caP'n
    // beats both books and every implementation, so the CHECK is what
    // changes. Note what did NOT happen — nobody "fixed" the engine to
    // satisfy the Debugger, which is the balance-change-in-a-coherency-
    // costume this file exists to make visible.
    //
    // Asked against the helper as well as against the literal, so the
    // next ruling on this boundary moves the bench automatically.
    const auraUntil = now + durationHalfTurns(2, SEATS);
    H.assert("R123", tomeWardScope({ protectedFromTomesUntilTurn: auraUntil }, auraUntil - 1) === "cards",
      `Phoenix Aura's ward expired early — two FULL Turns is ${durationHalfTurns(2, SEATS)} half-turns and it must hold through all of them`);
    H.assert("R123", tomeWardScope({ protectedFromTomesUntilTurn: auraUntil }, auraUntil) === null,
      "Phoenix Aura's ward outlived its window — the boundary is EXCLUSIVE, so the expiry half-turn is the first one WITHOUT the ward");
    H.assert("R123", durationStillRunning(auraUntil - 1, auraUntil) && !durationStillRunning(auraUntil, auraUntil),
      "the ward's own boundary and rules.durationStillRunning disagree — one of them is not reading the ruling");
    // "cards" WINS when both are up — it is the strictly wider ward and
    // a seat holding both bought the wider one.
    H.assert("R123", tomeWardScope({ protectedFromTomesUntilTurn: auraUntil, protectedFromTomesThisTurn: true, protectedTomeTurn: now }, now) === "cards",
      "a field holding BOTH wards reported the narrower one — 'cards' is strictly wider and is what was bought");
    // ⚠ AND NEITHER WARD TOUCHES ANIMA OR KHAO: those are pools, not
    // cards. Asserted so a future widening of the ward has to come past
    // a red line rather than past a comment.
    // ⚠ RE-AIMED 2026-09-14, and it was the SAME stale half-turn one line
    // down: this asked the ward AT `auraUntil`, which the exclusive ruling
    // made the first half-turn WITHOUT a ward, so the honest answer became
    // `null` and this row reported it as an illegal scope. The intent is
    // right and worth keeping — the ward has exactly two scopes and a pool
    // is neither — so it is asked across the WHOLE window and past it,
    // rather than on one hand-picked turn that a boundary ruling can move.
    {
      const seen = new Set();
      for (let t = now; t <= auraUntil + 2; t++) {
        seen.add(tomeWardScope({ protectedFromTomesUntilTurn: auraUntil }, t));
        seen.add(tomeWardScope({ protectedFromTomesThisTurn: true, protectedTomeTurn: now }, t));
      }
      const legal = new Set(["cards", "beings", null]);
      H.assert("R123", [...seen].every((v) => legal.has(v)),
        `the ward returned a scope outside {cards, beings, null} — there are exactly two scopes and a pool is neither. Saw: ${JSON.stringify([...seen])}`);
      H.assert("R123", seen.has("cards") && seen.has("beings") && seen.has(null),
        `the ward bench never produced all three answers, so it is not exercising the scopes it claims to watch. Saw: ${JSON.stringify([...seen])}`);
    }

    // ── R125 · AN INTANGIBLE TREASURE CANNOT BE RUINED ──────
    // Khao Paradise and Stars and Skies are Intangible for the HALF-turn
    // they were constructed (a named boundary means that boundary); the
    // Necklace of Akula for three FULL Turns from the same anchor.
    // The predicate keys the long duration off the CARD ID
    // (INTANGIBLE_FULL_TURNS), never off a flag on the instance — so the
    // fixtures are real card ids out of the catalogue and no number and
    // no id is typed here.
    const flagged = TOMES.find((c) => /khao_paradise|stars_and_skies/.test(c.id)) || TOMES[0];
    const akulaId = Object.keys(INTANGIBLE_FULL_TURNS)[0];
    const akulaFull = INTANGIBLE_FULL_TURNS[akulaId];
    H.assert("R125", tomeIsIntangible({ id: flagged.id, intangible: true, constructedTurn: now }, now, SEATS) === true,
      `${flagged.id} was ruinable on the very half-turn it was constructed`);
    H.assert("R125", tomeIsIntangible({ id: flagged.id, intangible: true, constructedTurn: now }, now + 1, SEATS) === false,
      "'the turn it was constructed' outlived that half-turn — a NAMED boundary means that boundary, not a number of Turns");
    H.assert("R125", tomeIsIntangible({ id: flagged.id }, now, SEATS) === false,
      "a Tome with no Intangible flag reported itself Intangible");
    H.assert("R125", tomeIsIntangible({ id: TOMES[0].id, intangible: true }, now, SEATS) === false,
      "a Treasure with no construction anchor reported itself Intangible — with no anchor there is no window");
    const akulaEnd = now + durationHalfTurns(akulaFull, SEATS) - 1;
    H.assert("R125", tomeIsIntangible({ id: akulaId, constructedTurn: now }, akulaEnd, SEATS) === true,
      `${akulaId} was ruinable inside its ${akulaFull} FULL Turns (${durationHalfTurns(akulaFull, SEATS)} half-turns from ${now})`);
    H.assert("R125", tomeIsIntangible({ id: akulaId, constructedTurn: now }, akulaEnd + 1, SEATS) === false,
      `${akulaId} stayed Intangible past its ${akulaFull} full Turns`);
    // ⚠ AND THE TWO WINDOWS ARE DIFFERENT LENGTHS ON PURPOSE — a bare
    // number of turns is FULL Turns (R103), a named boundary is the
    // half-turn. If these ever read the same, one of the two card texts
    // has been flattened into the other.
    H.assert("R125", durationHalfTurns(akulaFull, SEATS) > 1,
      `${akulaId}'s ${akulaFull}-turn window reads ${durationHalfTurns(akulaFull, SEATS)} half-turns — a bare number of turns is FULL Turns`);
  }

  // ── THE MIND'S SILENCE BENCH · R121 / R115's LOCK ────────
  // R121 is a PURE PREDICATE OVER ONE BATTLE LINE, which makes it the
  // cheapest rule in the 2026-09-07 wave to watch and the reason this
  // file's own declared gap for it said "reachable in principle". All
  // four states are built here so neither branch is ever a constant and
  // neither depends on a lucky shuffle:
  //   trio held + Arts awake ....... the Mind may do NOTHING but its Art
  //   trio held + Arts asleep ...... an ordinary being (R115's 3 Turns)
  //   trio broken + Arts awake ..... the Mind wakes the moment one falls
  //   trio held by an ESCLING ...... a bare Escling does not occupy a
  //                                  rift for this count — caP'n's own
  //                                  words, "with Ascendants not Esclings"
  {
    const awake = roleLockHalfTurns(SEATS);        // the first half-turn the Arts are up
    const asleep = awake - 1;
    const full = () => {
      const m = P();
      TRIO_RIFTS.forEach((r) => put(m, r, beingL1.id));
      put(m, MIND_RIFT, beingL1.id);
      return m;
    };
    const mk = (p, t) => ({ turn: t, slot: p.field.esclings[MIND_RIFT],
      target: { escendant: { uid: "x", id: beingL1.id }, fallen: false },
      myRifts: p.field.esclings, oppRifts: [], slotIdx: MIND_RIFT });

    const a = full();
    H.reject("R121", Legal.lytAttack(K, mk(a, awake)) !== "R121",
      `a Lyt Attack by the Mind on half-turn ${awake} with Body, Sol and Heart all holding Escendants`);
    H.reject("R121", Legal.primera(K, a, MIND_RIFT, stampsOnSlot(K, a.field.esclings[MIND_RIFT])[0].word, "own-turn", awake) !== "R121",
      "a Primera stamp of its own by a silenced Mind — only the Warden Art survives the silence");
    // THE LOCK. caP'n: *"it locks all roles effects even the warden and
    // Tank."* So for three full Turns a Warden is an ordinary being, and
    // that is the PERMISSION this bar is asserted against.
    const b = full();
    H.assert("R121", Legal.lytAttack(K, mk(b, asleep)) !== "R121",
      `the Mind was silenced on half-turn ${asleep}, inside R115's three-full-Turn lock — for those six half-turns a Warden is an ordinary being`);
    // ONE OF THE THREE FALLS AND THE MIND WAKES.
    const c = full();
    c.field.esclings[TRIO_RIFTS[0]].escendant = null;
    H.assert("R121", Legal.lytAttack(K, mk(c, awake)) !== "R121",
      "the Mind stayed silent with its Body rift empty — let one of the three fall and the Mind wakes");
    // A BARE ESCLING DOES NOT HOLD A RIFT FOR THIS COUNT.
    const d = full();
    d.field.esclings[TRIO_RIFTS[0]].escendant = null;   // the Escling is still there
    H.assert("R121", !trioOccupied(d.field.esclings),
      "a rift holding only its Escling counted toward the trio — caP'n: \"with Ascendants not Esclings\"");
    // ⚠ AND A FALLEN ESCENDANT DOES STILL HOLD ITS RIFT. Fallen is a
    // half-turn state of a being that is standing there, not an absence,
    // and reading it the other way would WAKE a seat's Mind precisely
    // because its own Body had just been knocked down — which rewards
    // being beaten.
    const e = full();
    e.field.esclings[TRIO_RIFTS[0]].fallen = true;
    H.reject("R121", Legal.lytAttack(K, mk(e, awake)) !== "R121",
      "a FALLEN Escendant stopped holding its rift for the trio count — Fallen is a being standing there, not an absence");
  }

  // ── THE CEILING AND THE ESCLING BENCH · R13 / R32 / R74 ───
  // MEASURED, NOT ASSUMED, and the measurement is the whole reason this
  // bench exists. Instrumented across the entire apparatus, this
  // simulator resolves Tome effects in these proportions:
  //     441 noop · 285 khao+1 · 6 draw+1 · 5 ruin_tome
  // and ZERO `ruin_being`, ZERO `anima+`. The two starter decks it runs
  // carry neither kind. So:
  //   · R32's only EXECUTING duel-side assertion was the setup line
  //     `p.anima <= K.MAX_ANIMA` — 30 <= 50, a theorem. Its card-heal
  //     assertion sits behind a branch that never runs.
  //   · R74's card-targeting path has no board to spoil in a duel.
  // Both are hand-built here so they run in EVERY pass instead of the
  // lucky ones — the same reason THE DESCENT BENCH exists, and the same
  // reason a roster row that cannot bite is furniture with an alarm on.
  {
    const me = P(), opp = O();
    // ── R32 · A HEAL INTO THE CEILING IS CLAMPED; PAST IT IS NOT LEGAL ──
    // The seat is funded to one under the cap and drinks 5, so the raw
    // value is genuinely over and the clamp genuinely does work. Asked
    // of the APPLIED value against the RULE's ceiling, which is what
    // makes the MAX_ANIMA mutation and the fault both redden it.
    me.anima = ACT.MAX_ANIMA - 1;
    const rawHeal = me.anima + 5;
    // MISACT · CEILING_IGNORED — the fault R32 forbids: a gain site
    // forgets the clamp and writes straight past 50.
    me.anima = misact("CEILING_IGNORED") ? rawHeal : Math.min(ACT.MAX_ANIMA, rawHeal);
    H.assert("R32", me.anima <= K.MAX_ANIMA,
      `a +5 heal on a seat holding ${ACT.MAX_ANIMA - 1} left Anima at ${me.anima}, past the ceiling ${K.MAX_ANIMA}`);
    H.assert("R32", me.anima >= Math.min(ACT.MAX_ANIMA, K.MAX_ANIMA),
      `a +5 heal on a seat holding ${ACT.MAX_ANIMA - 1} left Anima at ${me.anima} — R32 CAPS a gain, it does not refuse one`);

    // ── R74 · A RUIN EFFECT REACHES AN ESCENDANT, NEVER AN ESCLING ──
    const holder = put(opp, 0, beingL1.id);
    const bareRift = opp.field.esclings[1];
    H.assert("R74", Legal.targetProphecyOrEscling(K, holder) === null,
      "a ruin effect was refused the Escendant in rift 0 — R74 protects the Prophecy and the bare Esclings, not the battle line");
    H.reject("R74", Legal.targetProphecyOrEscling(K, bareRift) !== "R74",
      "a ruin effect targeting the bare Escling in rift 1");
    // MISACT · CARD_HITS_ESCLING — the fault R74 forbids: the effect
    // finds no Escendant in the rift beside its victim and takes the
    // ETERNAL Escling off the board instead (RULEZ_V2 ch.7 — Esclings
    // "are eternal"). This is the roster's deterministic injection site;
    // the duel-side one only fires on a deck that ships a ruin_being
    // Tome, and neither starter deck does.
    if (misact("CARD_HITS_ESCLING")) bareRift.escling = null;
    H.assert("R74", opp.field.esclings.every((s) => !!s.escling),
      "a card effect removed the Escling from a battle rift — an Escling is eternal and no card may reach one");
  }
  // ── R33 · 0 ANIMA IS THE LOSS, asked of the shipping verdict ──
  // The duel-side R33 check only speaks on runs that actually end on
  // Anima, which made it a coin toss between seeds — the worst property
  // a proof can have. rules.js ranks contestants by Anima FIRST, so a
  // board with one seat on 0 already contains R33's whole claim, and it
  // can be asked deterministically. The counter-rule is the inversion:
  // that emptying your own Anima is how a Solrend is won.
  {
    const board = {
      seatCount: 2, turn: 0,
      players: {
        0: { anima: 0, grimoire: [], field: { esclings: [] } },
        1: { anima: 5, grimoire: [], field: { esclings: [] } },
      },
    };
    const v = timeoutWinner(board);
    H.assert("R33", ruleIs(K, "R33", v.winner === 1, v.winner === 0),
      `a board with P0 on 0 Anima against P1 on 5 was awarded to P${v.winner} — R33 makes 0 Anima the LOSS`);
  }
  // ── R33 · you may not pay yourself to zero ──
  {
    const me = P();
    me.khao = 0; me.anima = 1;
    // R33 ENDS a duel at 0 Anima; R96 is what stops you walking there on
    // purpose. This asserts the boundary belongs to R96 and that R33 is
    // not being used as a stand-in for it.
    H.assert("R33", Legal.search(K, me, "grimoire", 1, openingCycleLength(SEATS) + 2) !== "R33",
      "a voluntary payment was refused under R33 — that boundary is R96's, and R33 is only the consequence");
    // ⚠ WAS `assert("R33", me.anima > 0)` two lines after `me.anima = 1`
    // — true by construction and therefore not a check. The claim worth
    // asserting is that the till REFUSES the fare that would land this
    // seat on zero, which is what the line above set up.
    H.assert("R33", Legal.search(K, me, "grimoire", 1, openingCycleLength(SEATS) + 2) !== null,
      `a seat on ${me.anima} Anima was allowed to pay a Grimoire fare that lands it on zero`);
  }

  // ── POSITIVE PATHS THAT MUST ALWAYS RUN ──────────────────
  // R41 and R47 are fare checks, and a fare check that only fires when a
  // random duel happens to reach it is a check nobody can rely on. Both
  // are executed here deterministically: the payment is made at the TRUE
  // price and asserted against the RULE's price.
  {
    const me = P();
    me.grimoire = [];
    me.hand = [{ uid: "adv#gs1", id: beingL1.id }];
    me.necropolis = [{ uid: "adv#gs2", id: beingL1.id }];
    me.anima = ACT.MAX_ANIMA;
    const before = me.anima;
    const pool = [...me.hand, ...me.necropolis];
    dimAnima(me, ACT.GRAVESHIFT_COST, ANIMA_LOSS_KINDS.SELF);
    H.assert("R41", before - me.anima === K.GRAVESHIFT_COST,
      `a Graveshift moved ${before - me.anima} Anima (the rule says exactly ${K.GRAVESHIFT_COST})`);
    const handBefore = me.hand.length, necroBefore = me.necropolis.length;
    // MISACT · SHIFT_LEAVES_HAND — see the duel site. The bench always
    // fires with one card in each zone, so the fault always bites here.
    if (misact("SHIFT_LEAVES_HAND")) { me.grimoire = [...me.necropolis]; me.necropolis = []; }
    else { me.grimoire = pool; me.hand = []; me.necropolis = []; }
    pool.forEach((c) => me.shifted.add(c.uid));
    // `grimoire.length === pool.length` one line after `grimoire = pool`
    // was a tautology, and the duel's own R42 check only had something
    // to say on the seeds where a Graveshift happened to fire with a
    // card still in hand. This bench always fires, with one card in each
    // zone, so R42's counter-rule — that the shift raises the Necropolis
    // and leaves the hand alone — is a different number every run.
    H.assert("R42", ruleIs(K, "R42",
      me.grimoire.length === handBefore + necroBefore && me.hand.length === 0,
      me.grimoire.length === necroBefore),
      `a Graveshift raised ${me.grimoire.length} cards from a hand of ${handBefore} and a Necropolis of ${necroBefore}`);
    H.assert("R43", me.grimoire.length > 0 && me.anima > 0,
      "an emptied Grimoire cost the duel — R43 forbids deck-out as a loss");
    H.reject("R86", Legal.resurrect(K, me, { uid: "adv#gs1", id: beingL1.id }, 0, "paid") !== "R86",
      "the paid rite on a card that just passed through the Graveshift");
  }
  {
    const me = P();
    const seal = { uid: "adv#res", id: beingL1.id };
    me.necropolis = [seal];
    me.khao = ACT.MAX_KHAO_ABS; me.anima = ACT.MAX_ANIMA;
    const k0 = me.khao, a0 = me.anima;
    const cost = ACT.RESURRECT_COST.L1;
    // ⚠ THIS LINE USED TO SPLIT THE FARE ACROSS BOTH POOLS
    // (`khao -= min(khao,cost); anima -= cost - that`), which is the
    // exact shape R107 forbids. It never actually split — the bench
    // funds the seat to MAX_KHAO_ABS, so the Anima leg was always 0 —
    // but a payment expression the catalogue forbids has no business
    // sitting in the file that watches for it. One pool, one funnel.
    payOnePool(me, cost);
    H.assert("R47", (k0 - me.khao) + (a0 - me.anima) === K.RESURRECT_COST.L1,
      `an L1 Resurrection cost ${(k0 - me.khao) + (a0 - me.anima)} (the rule says ${K.RESURRECT_COST.L1})`);
    const t0 = me.tally[seal.uid] || 0;
    me.tally[seal.uid] = t0 + 1;
    H.assert("R77", Legal.tallies(K, "paid") && me.tally[seal.uid] === t0 + 1,
      "a paid Resurrection did not increment the tally — R77: every method counts, no exceptions");
  }
  // ── R77 · EVERY METHOD COUNTS, and the teeth of that are R44 ──
  // `tally = t0 + 1` followed by `assert(tally === t0 + 1)` is
  // arithmetic, not a rule, and the duel-side R77 check only speaks on
  // seeds where a Resurrection actually happened. The rule's real
  // question is WHICH METHODS COUNT, and it is asked here of the one
  // shared predicate — and then of its consequence: a copy returned by
  // CARD EFFECT alone must still run out of returns. R77's counter-rule
  // is the reading R89's first cut confused this with: that the tally
  // binds the paid rite and card effects ride free forever.
  {
    const eff = P();
    const s77 = { uid: "adv#r77", id: beingL1.id };
    eff.necropolis = [s77];
    eff.khao = ACT.MAX_KHAO_ABS; eff.anima = ACT.MAX_ANIMA;
    let bumps = 0;
    for (let n = 0; n < ACT.RESURRECT_LIMIT.L1 + 5; n++) {
      if (Legal.tallies(ACT, "effect")) { eff.tally[s77.uid] = (eff.tally[s77.uid] || 0) + 1; bumps++; }
    }
    H.assert("R77", Legal.tallies(K, "effect") && Legal.tallies(K, "paid"),
      "the tally does not count every method — R77 admits no exception by method or by source");
    H.assert("R77", Legal.resurrect(K, eff, s77, 0, "paid") === "R44",
      `a copy returned ${bumps} time(s) by CARD EFFECT alone was still offered the paid rite — R77 counts those returns too`);
  }
  // ── R21 · a Deviant Ascend is never free ──
  {
    const me = P();
    put(me, 0, beingL1.id);
    me.khao = 0; me.anima = 0;
    // ⚠ THE FIXTURE USED TO BE `ESCENDANTS.find(c => c.level === 2)`, ANY
    // L2 — and with the fare table wired in, that exposed the bench as
    // wrong rather than the gate: a climb sharing BOTH words is a NATURAL
    // Ascend and is FREE (DEVIANT_FARE[2] is zeros on every rung, and the
    // catalogue says so at the table itself), so "a Deviant Ascend with
    // nothing to pay it" was being asked about an Ascend that costs
    // nothing. R21 needs a genuine MISMATCH to have a fare at all.
    const dev2 = ESCENDANTS.find((c) => c.level === 2
      && (c.school !== beingL1.school || c.ilk !== beingL1.ilk));
    if (dev2) {
      const shared2 = (beingL1.school === dev2.school ? 1 : 0) + (beingL1.ilk === dev2.ilk ? 1 : 0);
      const f2 = deviantFare(shared2, dev2.level);
      H.reject("R21", Legal.ascend(K, me, 0, dev2.id, true) !== "R21",
        `a Deviant Ascend to L${dev2.level} on 0/0 against a fare of ${f2.khao} Khao or ${f2.anima} Anima`);
      // ── R101 · AND THE FARE IS THE TABLE'S, TO THE COIN ──────
      // One under each leg is refused; exactly on the Khao leg is not.
      // Funded from ACT and judged against K, so moving either column of
      // the table reddens exactly one of these.
      const short = P(); put(short, 0, beingL1.id);
      short.khao = f2.khao - 1; short.anima = 0;
      H.reject("R101", Legal.ascend(K, short, 0, dev2.id, true) !== "R21",
        `a Deviant Ascend one Khao short of its ${f2.khao} fare`);
      // ...and the K-side table must agree with the engine's own oracle on
      // every rung, in both columns. That is what stops the copy above
      // from quietly becoming a second catalogue.
      [0, 1, 2].forEach((sh) => [1, 2, 3].forEach((lv) => {
        const mine = devFareOf(ACT, sh, lv), theirs = deviantFare(sh, lv);
        H.assert("R101", mine.khao === theirs.khao && mine.anima === theirs.anima,
          `the Debugger reads the ${sh}-shared L${lv} Deviant fare as ${mine.khao}/${mine.anima} while deviantFare says ${theirs.khao}/${theirs.anima}`);
      }));
      const ok2 = P(); put(ok2, 0, beingL1.id);
      ok2.khao = deviantFare(shared2, dev2.level).khao; ok2.anima = 0;
      H.assert("R101", Legal.ascend(K, ok2, 0, dev2.id, true) === null,
        `a Deviant Ascend funded to exactly ${ok2.khao} Khao was refused — the rule asks ${f2.khao}`);
      // R96 on the Anima leg: exactly the fare in Anima lands you on 0,
      // and a voluntary payment may never do that.
      const till = P(); put(till, 0, beingL1.id);
      till.khao = 0; till.anima = f2.anima;
      H.reject("R101", Legal.ascend(K, till, 0, dev2.id, true) !== "R21",
        `a Deviant Ascend paying its whole ${f2.anima}-Anima fare down to zero — R96 forbids a voluntary payment that empties you`);
      // ── R101 · THE ASYMMETRIC SQUARE · L3 / 0 TRAITS ─────────
      // 5 KHAO OR 4 ANIMA — deliberately asymmetric, and the only fare
      // in Terror where Anima is the cheaper coin. caP'n's own number,
      // so it gets its own rung rather than riding the L2 row.
      const dev3 = ESCENDANTS.find((c) => c.level === 3 && beingL2
        && (c.school !== beingL2.school || c.ilk !== beingL2.ilk));
      if (dev3 && beingL2) {
        const l3 = P(); const sl3 = put(l3, 0, beingL2.id);
        const sh3 = (beingL2.school === dev3.school ? 1 : 0) + (beingL2.ilk === dev3.ilk ? 1 : 0);
        const f3 = deviantFare(sh3, 3);
        l3.khao = f3.khao; l3.anima = 0;
        H.assert("R101", Legal.ascend(K, l3, 0, dev3.id, true) === null,
          `a Deviant L3 Ascend funded to exactly ${l3.khao} Khao was refused — the rule asks ${devFareOf(K, sh3, 3).khao}`);
        const l3short = P(); put(l3short, 0, beingL2.id);
        l3short.khao = f3.khao - 1; l3short.anima = 0;
        H.reject("R101", Legal.ascend(K, l3short, 0, dev3.id, true) !== "R21",
          `a Deviant L3 Ascend one Khao short of its ${f3.khao} fare`);
        // AND THE ANIMA LEG IS CHEAPER, which is the whole point of the
        // square: the Khao-short seat above is refused, and a seat
        // holding only the smaller ANIMA number (plus R96's one, since a
        // voluntary payment may not empty you) is not.
        const l3anima = P(); put(l3anima, 0, beingL2.id);
        l3anima.khao = 0; l3anima.anima = f3.anima + 1;
        H.assert("R101", Legal.ascend(K, l3anima, 0, dev3.id, true) === null,
          `a Deviant L3 Ascend funded to ${l3anima.anima} Anima was refused — the rule asks ${devFareOf(K, sh3, 3).anima} Anima, which is LESS than its ${devFareOf(K, sh3, 3).khao} Khao`);
        H.assert("R101", f3.anima <= f3.khao,
          `the L3 / 0-trait Deviant fare reads ${f3.khao} Khao and ${f3.anima} Anima — the Anima leg is the cheaper coin and that asymmetry is caP'n's own`);
        void sl3;
      }
    }
  }

  // ── PURE-RULE PROBES · R91 / R13 ─────────────────────────
  // The Khao climb is arithmetic, and arithmetic should not have to wait
  // for a duel to wander into the right state before it can be checked.
  {
    const stamped = P();
    put(stamped, 0, beingL1.id);
    stamped.khao = ACT.MAX_KHAO_ABS;
    const pk0 = stamped.khao;
    stamped.khao -= ACT.PRIMERA_COST.L1;
    H.assert("R79", pk0 - stamped.khao === K.PRIMERA_COST.L1,
      `an L1 Primera cost ${pk0 - stamped.khao} (the rule says ${K.PRIMERA_COST[`L${1}`]})`);

  }

  // ── R05 / R53-R63 / R94 · deliberately illegal decks ──────
  // A deck check that never sees an illegal deck is a check nobody has
  // watched fail. These fixtures make every deck rule fire at least once.
  const base = deckA;
  const firstOf = (type, level) => CARDS.find((c) => c.type === type && (level == null || c.level === level));
  // The FIXTURES are built one past the TRUE cap (ACT) and validated
  // against the RULE cap (K). Building them from K would move the deck
  // and the limit together, and every one of these would be green
  // forever — which is exactly what the audit found the first time.
  const ally = CURIOS.find((c) => c.ilk === "ally");
  const luxNoAffinity = Object.fromEntries(
    Object.entries(base.luxmoire || {}).filter(([id]) => getCard(id)?.type !== T.AFFINITY));
  const bad = [
    { name: "over-cap L1 Being", code: "R53", deck: { ...base, grimoire: { ...base.grimoire, [firstOf(T.ESCENDANT, 1).id]: ACT.DECK_LIMITS.ESCENDANT_L1 + 1 } } },
    { name: "over-cap L2 Being", code: "R54", deck: { ...base, grimoire: { ...base.grimoire, [firstOf(T.ESCENDANT, 2).id]: ACT.DECK_LIMITS.ESCENDANT_L2 + 1 } } },
    { name: "over-cap L3 Being", code: "R55", deck: { ...base, grimoire: { ...base.grimoire, [firstOf(T.ESCENDANT, 3).id]: ACT.DECK_LIMITS.ESCENDANT_L3 + 1 } } },
    { name: "over-cap L1 Tome", code: "R56", deck: { ...base, grimoire: { ...base.grimoire, [firstOf(T.TOME, 1).id]: ACT.DECK_LIMITS.TOME_L1 + 1 } } },
    { name: "over-cap L2 Tome", code: "R57", deck: { ...base, grimoire: { ...base.grimoire, [firstOf(T.TOME, 2).id]: ACT.DECK_LIMITS.TOME_L2 + 1 } } },
    { name: "over-cap L3 Tome", code: "R58", deck: { ...base, grimoire: { ...base.grimoire, [firstOf(T.TOME, 3).id]: ACT.DECK_LIMITS.TOME_L3 + 1 } } },
    { name: "duplicate U'hlti", code: "R59", deck: { ...base, luxmoire: { ...base.luxmoire, [UHLTIS[0].id]: ACT.DECK_LIMITS.UHLTI + 1 } } },
    { name: "over-cap Affinities in one Luxmoire", code: "R60", deck: { ...base, luxmoire: { ...luxNoAffinity, ...Object.fromEntries(AFFINITIES.slice(0, ACT.DECK_LIMITS.AFFINITY + 1).map((a) => [a.id, 1])) } } },
    { name: "Affinity smuggled into the Grimoire", code: "R05", deck: { ...base, grimoire: { ...base.grimoire, [AFFINITIES[0].id]: 1 } } },
    { name: "Grimoire under the floor", code: "R05", deck: { ...base, grimoire: { [firstOf(T.ESCENDANT, 1).id]: 1 } } },
    { name: "Luxmoire under the floor", code: "R06", deck: { ...base, luxmoire: { [UHLTIS[0].id]: 1 } } },
    { name: "over-cap L1 Impera", code: "R94", deck: { ...base, luxmoire: { ...base.luxmoire, [IMPERA.find((c) => c.level === 1).id]: ACT.DECK_LIMITS.IMPERA_L1 + 1 } } },
    { name: "over-cap Curio", code: "R61", deck: { ...base, curios: Array(ACT.DECK_LIMITS.CURIO + 1).fill(CURIOS[0].id) } },
    { name: "one Ally-Ilk Curio past the Shatterfield cap", code: "R62",
      deck: { ...base, curios: ally ? Array(ACT.ALLY_ILK_CURIO_MAX + 1).fill(ally.id) : [] } },
    // ── R104 · the Luxmoire's U'hlti TOTAL, which is not a per-name cap
    // and therefore is not DECK_LIMITS.UHLTI. Built from DISTINCT names
    // so R59 cannot be the thing that catches it: one over the total,
    // every one of them legal on its own.
    { name: "one U'hlti past the Luxmoire total", code: "R104",
      deck: { ...base, luxmoire: { ...base.luxmoire,
        ...Object.fromEntries(UHLTIS.slice(0, ACT.LUXMOIRE_UHLTI_MAX + 1).map((u) => [u.id, 1])) } } },
    { name: "two Prophecies", code: "R63", deck: { ...base, prophecy: [PROPHECIES[0].id, PROPHECIES[1].id] } },
    // The name used to be "four Esclings", typed when the battle line
    // was three. R110 made ESCLING_SLOTS four, so the fixture builds
    // FIVE and the name became a lie the day the constant moved — a
    // small one, and exactly the species this file keeps a chapter of.
    // It is named after what it DOES now, which no future ruling on the
    // width of the line can falsify.
    { name: "one Escling past the battle line", code: "R04", deck: { ...base, esclings: Array(ACT.ESCLING_SLOTS + 1).fill(ESCLINGS[0].id) } },
  ];
  bad.forEach(({ name, code, deck }) => {
    const quiet = makeHarness(H.seed, K, {});
    const outs = validateDeck(K, deck, name, quiet);
    // The FIXTURE must be caught. If a deliberately illegal deck passes,
    // the deck check is decoration and this says so under the rule's code.
    H.assert(code, outs.some((v) => v.code === code),
      `the deliberately illegal fixture "${name}" passed deck validation without a ${code} violation`);
  });
}

// ─────────────────────────────────────────────────────────────
// THE MUTATION SELF-TEST
// Perturb one constant in the CHECKER's copy while the simulator keeps
// acting on the true one. Every listed code must go red. One that stays
// green cannot be failed by anything and is reported as FAKE.
// ─────────────────────────────────────────────────────────────
const MUTATIONS = [
  // DIRECTION MATTERS. A mutation must make an honest check FAIL, so it
  // moves the RULE away from what the simulator actually did — tighter
  // where a state is asserted, LOOSER where a rejection is demanded
  // (loosening a cap is what lets an illegal action through the gate).
  { key: "HAND_LIMIT",           make: () => ({ HAND_LIMIT: HAND_LIMIT - 3 }),                                            expect: ["R10"] },
  // ── THE 2026-08-26 CANON PASS. Both LOOSEN a cap, because both are
  // proved by an illegal DECK being caught: move the cap up and the
  // fixture that is one over becomes legal, and the check must go dark.
  { key: "LUXMOIRE_UHLTI_MAX",   make: () => ({ LUXMOIRE_UHLTI_MAX: LUXMOIRE_UHLTI_MAX + 1 }),                            expect: ["R104"] },
  { key: "ALLY_ILK_CURIO_MAX",   make: () => ({ ALLY_ILK_CURIO_MAX: ALLY_ILK_CURIO_MAX + 1 }),                            expect: ["R62"] },
  { key: "GRAVESHIFT_COST",      make: () => ({ GRAVESHIFT_COST: GRAVESHIFT_COST + 7 }),                                  expect: ["R41"] },
  { key: "MAX_KHAO_NAT",         make: () => ({ MAX_KHAO_NAT: STARTING_KHAO }),                                           expect: ["R13"] },
  // ⚠ WAS `TOME_WAIT: { L1: 0 }` AND IT HAD GONE DEAD. `TOME_WAIT` is a
  // DERIVED VIEW computed from the full-Turn table; once the gate asked
  // the catalogue's own row instead of the derived map, perturbing the
  // map moved nothing and the row reddened nothing. The FAKE column is
  // what said so, which is the column doing its job.
  { key: "TOME_WAIT_FULL_TURNS.L1", make: () => ({ TOME_WAIT_FULL_TURNS: { ...TOME_WAIT_FULL_TURNS, L1: 0 } }),           expect: ["R35"] },
  { key: "STARTING_ANIMA",       make: () => ({ STARTING_ANIMA: STARTING_ANIMA + 5 }),                                    expect: ["R01"] },
  { key: "STARTING_KHAO",        make: () => ({ STARTING_KHAO: STARTING_KHAO + 2 }),                                      expect: ["R02"] },
  { key: "STARTING_HAND",        make: () => ({ STARTING_HAND: STARTING_HAND + 2 }),                                      expect: ["R03", "R11"] },
  // R110 IS THE SAME NUMBER AS R04 AND IS NOW CLAIMED UNDER BOTH. R04
  // is the deck's pre-placement, R110 is the battle LINE the board must
  // show, and one constant moves both. It used to be left off on the
  // grounds of not double-counting — but the result was a rule reported
  // as an UNEXPLAINED gap while being watched in two places.
  { key: "ESCLING_SLOTS",        make: () => ({ ESCLING_SLOTS: ESCLING_SLOTS + 1 }),                                      expect: ["R04", "R110"] },
  { key: "MAX_ANIMA",            make: () => ({ MAX_ANIMA: STARTING_ANIMA - 1 }),                                         expect: ["R32"] },
  { key: "MAX_KHAO_ABS",         make: () => ({ MAX_KHAO_ABS: STARTING_KHAO }),                                           expect: ["R13"] },
  { key: "GRIMOIRE_MIN",         make: () => ({ GRIMOIRE_MIN: GRIMOIRE_MIN + 500 }),                                      expect: ["R05"] },
  { key: "LUXMOIRE_MIN",         make: () => ({ LUXMOIRE_MIN: LUXMOIRE_MAX + 1 }),                                        expect: ["R06"] },
  { key: "FIRST_KHAO_TURN",      make: () => ({ FIRST_KHAO_TURN: FIRST_KHAO_TURN + 4 }),                                  expect: ["R91"] },
  { key: "TOME_SLOTS",           make: () => ({ TOME_SLOTS: TOME_SLOTS - 1 }),                                            expect: ["R38"] },
  { key: "RESURRECT_COST.L1",    make: () => ({ RESURRECT_COST: { ...RESURRECT_COST, L1: RESURRECT_COST.L1 + 3 } }),      expect: ["R47"] },
  { key: "PRIMERA_COST.L1",      make: () => ({ PRIMERA_COST: { ...PRIMERA_COST, L1: PRIMERA_COST.L1 + 2 } }),            expect: ["R79"] },
  // ⚠ NO `CHAIN_DEPTH_CAP` ROW, AND THE ABSENCE IS THE FINDING. It used to
  // sit here expecting R92, and R92 is the rule that says a chain has NO
  // length bound (RULEZ_V2 ch.15: "None. Not twelve, not five, not any
  // number"). A mutation row for a constant that is not a rule credits
  // the catalogue with watching something the file asserts the inverse
  // of. The cap is a runaway guard and is watched in the INVARIANT
  // channel, which has no rule code and is counted as no coverage.
  { key: "MULLIGAN_LIMIT",       make: () => ({ MULLIGAN_LIMIT: MULLIGAN_LIMIT + 1 }),                                    expect: ["R90"] },
  { key: "RESURRECT_PER_TURN",   make: () => ({ RESURRECT_PER_TURN: RESURRECT_PER_TURN + 1 }),                            expect: ["R89"] },
  { key: "RESURRECT_LIMIT.L1",   make: () => ({ RESURRECT_LIMIT: { ...RESURRECT_LIMIT, L1: RESURRECT_LIMIT.L1 + 2 } }),   expect: ["R44"] },
  { key: "LUX_SEARCH_COST",      make: () => ({ LUX_SEARCH_COST: LUX_SEARCH_COST + 20 }),                                 expect: ["R88"] },
  // The by-level Grimoire fare was outside the fake-detector entirely:
  // MUTATIONS perturbed the Luxmoire fare and never this table, so the
  // rung that actually changed today was the one nothing was watching.
  { key: "GRIM_SEARCH_COST.L1",  make: () => ({ GRIM_SEARCH_COST: { ...GRIM_SEARCH_COST, L1: GRIM_SEARCH_COST.L1 + 3 } }), expect: ["R88", "R96"] },
  { key: "GRIM_SEARCH_COST.L2",  make: () => ({ GRIM_SEARCH_COST: { ...GRIM_SEARCH_COST, L2: GRIM_SEARCH_COST.L2 + 3 } }), expect: ["R88"] },
  { key: "TURN_CLOCK_PVP",       make: () => ({ TURN_CLOCK_PVP: TURN_CLOCK_PVP + 111 }),                                  expect: ["R93"] },
  { key: "HERETICAL.anima",      make: () => ({ HERETICAL_TRAINING_COST: { ...HERETICAL_TRAINING_COST, anima: HERETICAL_TRAINING_COST.anima + 5 } }), expect: ["R67"] },
  { key: "DECK_LIMITS.AFFINITY", make: () => ({ DECK_LIMITS: { ...DECK_LIMITS, AFFINITY: DECK_LIMITS.AFFINITY + 1 } }),   expect: ["R60"] },
  { key: "DECK_LIMITS.CURIO",    make: () => ({ DECK_LIMITS: { ...DECK_LIMITS, CURIO: DECK_LIMITS.CURIO + 1 } }),         expect: ["R61"] },
  { key: "DECK_LIMITS.UHLTI",    make: () => ({ DECK_LIMITS: { ...DECK_LIMITS, UHLTI: DECK_LIMITS.UHLTI + 1 } }),         expect: ["R59"] },
  { key: "DECK_LIMITS.IMPERA_L1",make: () => ({ DECK_LIMITS: { ...DECK_LIMITS, IMPERA_L1: DECK_LIMITS.IMPERA_L1 + 1 } }), expect: ["R94"] },
  { key: "DECK_LIMITS.ESC_L1",   make: () => ({ DECK_LIMITS: { ...DECK_LIMITS, ESCENDANT_L1: DECK_LIMITS.ESCENDANT_L1 + 1 } }), expect: ["R53"] },
  { key: "DECK_LIMITS.ESC_L2",   make: () => ({ DECK_LIMITS: { ...DECK_LIMITS, ESCENDANT_L2: DECK_LIMITS.ESCENDANT_L2 + 1 } }), expect: ["R54"] },
  { key: "DECK_LIMITS.ESC_L3",   make: () => ({ DECK_LIMITS: { ...DECK_LIMITS, ESCENDANT_L3: DECK_LIMITS.ESCENDANT_L3 + 1 } }), expect: ["R55"] },
  { key: "DECK_LIMITS.TOME_L2",  make: () => ({ DECK_LIMITS: { ...DECK_LIMITS, TOME_L2: DECK_LIMITS.TOME_L2 + 1 } }),     expect: ["R57"] },
  { key: "DECK_LIMITS.TOME_L3",  make: () => ({ DECK_LIMITS: { ...DECK_LIMITS, TOME_L3: DECK_LIMITS.TOME_L3 + 1 } }),     expect: ["R58"] },
  { key: "DECK_LIMITS.PROPHECY", make: () => ({ DECK_LIMITS: { ...DECK_LIMITS, PROPHECY: DECK_LIMITS.PROPHECY + 1 } }),   expect: ["R63"] },
  { key: "DECK_LIMITS.TOME_L1",  make: () => ({ DECK_LIMITS: { ...DECK_LIMITS, TOME_L1: DECK_LIMITS.TOME_L1 + 1 } }),     expect: ["R56"] },
  { key: "CURIO_SLOTS",          make: () => ({ CURIO_SLOTS: 0 }),                                                        expect: ["R75"] },
  { key: "TURN_CLOCK",           make: () => ({ TURN_CLOCK: 4 }),                                                         expect: ["R93"] },
  // R87's twenty minutes cannot ELAPSE headless, but the second it
  // falls on can be moved. The pair of assertions in simulateGame hands
  // the engine K's second and reads the engine's own answer, so a limit
  // moved in either direction breaks one of them.
  { key: "DUEL_TIME_LIMIT",      make: () => ({ DUEL_TIME_LIMIT: DUEL_TIME_LIMIT + 60 }),                                 expect: ["R87"] },

  // ═══════════════════════════════════════════════════════════════
  // THE 2026-09-07 WAVE, AND THE FIVE ROWS THAT ARE NOT HERE
  // ───────────────────────────────────────────────────────────────
  // R110-R121 landed together: a fourth battle rift, the Exile rift and
  // its Ritual, the rounding law, four Role Arts and the Mind's Silence.
  // They brought five new NUMBERS with them — EXILE_FARE,
  // EXILE_RETRIEVAL_ANIMA_FLOOR, ROLE_LOCK_FULL_TURNS,
  // WARDEN_ARTS_PER_TURN and ROLE_FARE — and the obvious next move was
  // to type five rows into this table, one per constant, and let the
  // count on the screen go up by five.
  //
  // NOT ONE OF THEM IS WRITTEN, AND THAT IS THE FINDING RATHER THAN AN
  // OMISSION. A mutation row is a claim that perturbing a number makes
  // an HONEST CHECK FAIL. It cannot make anything fail if nothing in
  // this file ever reads the number, and a row that reddens nothing is
  // not a gap — it is a number that watches nothing, which is worse,
  // because it renders as coverage and nobody reads the FAKE column
  // twice. This file's own history is the argument: a reviewer once
  // replaced the rule-reading helper with one that read NOTHING AT ALL
  // and the report came back byte-identical.
  //
  // MEASURED, NOT ASSUMED. Grep this file: there is no Exiliation, no
  // Ritual, no Exile rift transfer, no ROLES table, no rift-to-Art
  // mapping, no Warden shield, no role clock. The seat now has an
  // `exile` pile (see buildPlayer) and nothing ever pushes to it. So:
  //   · EXILE_FARE ................. no Exiliation is ever performed;
  //     move the fare and no board changes and no check notices.
  //   · EXILE_RETRIEVAL_ANIMA_FLOOR  no Ritual is ever attempted; the
  //     floor gates a door this simulator does not have.
  //   · ROLE_LOCK_FULL_TURNS ....... the three-Turn lock silences Arts
  //     that are not modelled; silence is this file's only state.
  //   · WARDEN_ARTS_PER_TURN ....... a per-seat cap on firings that
  //     never happen. A cap of 0 and a cap of 99 produce the same duel.
  //   · ROLE_FARE .................. no rift lends an Art here, so no
  //     fare is ever quoted, refused or paid.
  //
  // Each is therefore DECLARED, by rule code and with the cost of
  // closing it, in UNPROVABLE_REASONS — the table this file keeps for
  // exactly this confession. THE HONEST ARITHMETIC OF THIS PASS IS
  // ZERO NEW MUTATION ROWS AND FIVE NEW DECLARED GAPS, and the headline
  // does not move. Anyone who later builds an Exiliation, a Ritual or a
  // Role rift into the simulator should delete the matching row from
  // UNPROVABLE_REASONS and write its mutation HERE, in the same commit —
  // the constant and the check that reads it belong to one edit.
  // ═══════════════════════════════════════════════════════════════

  // ═══════════════════════════════════════════════════════════════
  // THE CLAUSE MUTATIONS — one withdrawn prohibition each.
  // ───────────────────────────────────────────────────────────────
  // Everything above perturbs a NUMBER, which is why everything above
  // could only ever reach the rules that ARE numbers. Each row below
  // withdraws one clause of the catalogue from the CHECKER's copy while
  // the simulator keeps acting on ACT, where all of them are frozen
  // true. The board cannot move; only the rule the checks are written
  // against does. See CLAUSES for the discipline these obey, and for
  // the one thing this mechanism cannot see.
  // ═══════════════════════════════════════════════════════════════
  { key: "clause R07",  make: () => without("R07"), expect: ["R07"] },
  { key: "clause R08",  make: () => without("R08"), expect: ["R08"] },
  // ── R08's NEW SCOPE, ONE MUTATION PER LEG ──────────────────
  // `clause R08` above withdraws the SWING bar only, so it would still
  // go red off the Lyt Attack even if every broad leg were deleted.
  // These four are what make the new scope guarded rather than merely
  // present, and the last two are the PERMISSION side — a bar that can
  // only be removed cannot redden an assertion that something is
  // ALLOWED, so their counter-readings add a bar instead.
  { key: "clause R08_BROAD (every other route)", make: () => without("R08_BROAD"), expect: ["R08"] },
  { key: "clause R08_BOOKS (neither Book)",      make: () => without("R08_BOOKS"), expect: ["R08", "R88"] },
  { key: "clause R08_WINDOW (Cycle never ends)", make: () => without("R08_WINDOW"), expect: ["R08", "R88"] },
  { key: "clause R08_HARM (any payload is harm)",make: () => without("R08_HARM"),  expect: ["R08"] },
  { key: "clause R08_KIND (ban every card)",     make: () => without("R08_KIND"),  expect: ["R09"] },
  { key: "clause R07_SPLIT (the Cycle is dry)",  make: () => without("R07_SPLIT"), expect: ["R07"] },
  { key: "clause R11_FX (an effect draw counts)",make: () => without("R11_FX"),    expect: ["R11"] },
  { key: "clause R08_FX (an effect search is barred)", make: () => without("R08_FX"), expect: ["R08"] },
  { key: "clause R90 (window + not-yet-drawn)",  make: () => without("R90"),       expect: ["R90"] },
  { key: "clause R90_WINDOW (turn 0 only)",      make: () => without("R90_WINDOW"),expect: ["R90"] },
  { key: "clause R09",  make: () => without("R09"), expect: ["R09"] },
  { key: "clause R11",  make: () => without("R11"), expect: ["R11"] },
  { key: "clause R12",  make: () => without("R12"), expect: ["R12"] },
  { key: "clause R14",  make: () => without("R14"), expect: ["R14"] },
  { key: "clause R15",  make: () => without("R15"), expect: ["R15"] },
  { key: "clause R16",  make: () => without("R16"), expect: ["R16"] },
  { key: "clause R17",  make: () => without("R17"), expect: ["R17"] },
  // R18 and R102 are ONE budget wearing two codes — R18 is "one Ascend
  // per Turn", R102 is "one TOTAL, Natural or Deviant" — and one guard
  // answers both, the same way R31/B03 and R39/B01 are paired here.
  { key: "clause R18",  make: () => without("R18"), expect: ["R18", "R102"] },
  { key: "clause R19",  make: () => without("R19"), expect: ["R19"] },
  { key: "clause R20",  make: () => without("R20"), expect: ["R20"] },
  { key: "clause R21",  make: () => without("R21"), expect: ["R21"] },
  { key: "clause R22",  make: () => without("R22"), expect: ["R22"] },
  { key: "clause R23",  make: () => without("R23"), expect: ["R23"] },
  { key: "clause R24",  make: () => without("R24"), expect: ["R24"] },
  { key: "clause R25",  make: () => without("R25"), expect: ["R25"] },
  { key: "clause R26",  make: () => without("R26"), expect: ["R26"] },
  { key: "clause R27",  make: () => without("R27"), expect: ["R27"] },
  { key: "clause R28",  make: () => without("R28"), expect: ["R28"] },
  { key: "clause R29",  make: () => without("R29"), expect: ["R29"] },
  { key: "clause R30",  make: () => without("R30"), expect: ["R30"] },
  // R31 and B03 are one prohibition wearing two codes: B03 is the
  // regression signature of R31 breaking, and one gate answers both.
  { key: "clause R31",  make: () => without("R31"), expect: ["R31", "B03"] },
  { key: "clause R33",  make: () => without("R33"), expect: ["R33"] },
  { key: "clause R34",  make: () => without("R34"), expect: ["R34"] },
  { key: "clause R36",  make: () => without("R36"), expect: ["R36"] },
  { key: "clause R37",  make: () => without("R37"), expect: ["R37"] },
  // Same pairing: B01 is the Devil's Advocate loop, which is R39's
  // ruin-after-use failing. One clause, both codes.
  { key: "clause R39",  make: () => without("R39"), expect: ["R39", "B01"] },
  { key: "clause R40",  make: () => without("R40"), expect: ["R40"] },
  { key: "clause R42",  make: () => without("R42"), expect: ["R42"] },
  { key: "clause R43",  make: () => without("R43"), expect: ["R43"] },
  // R45 and R46 meet on ONE guard in this simulator — see the note in
  // Legal.resurrect and NOT_PROVEN_REASONS. Listing both here would
  // claim two proofs for one gate; only R46 is claimed.
  { key: "clause R46",  make: () => without("R46"), expect: ["R46"] },
  { key: "clause R48",  make: () => without("R48"), expect: ["R48"] },
  { key: "clause R49",  make: () => without("R49"), expect: ["R49"] },
  { key: "clause R50",  make: () => without("R50"), expect: ["R50"] },
  { key: "clause R51",  make: () => without("R51"), expect: ["R51"] },
  { key: "clause R52",  make: () => without("R52"), expect: ["R52"] },
  { key: "clause R62",  make: () => without("R62"), expect: ["R62"] },
  { key: "clause R64",  make: () => without("R64"), expect: ["R64"] },
  { key: "clause R65",  make: () => without("R65"), expect: ["R65"] },
  { key: "clause R66",  make: () => without("R66"), expect: ["R66"] },
  { key: "clause R68",  make: () => without("R68"), expect: ["R68"] },
  { key: "clause R69",  make: () => without("R69"), expect: ["R69"] },
  { key: "clause R70",  make: () => without("R70"), expect: ["R70"] },
  { key: "clause R71",  make: () => without("R71"), expect: ["R71"] },
  { key: "clause R72",  make: () => without("R72"), expect: ["R72"] },
  { key: "clause R73",  make: () => without("R73"), expect: ["R73"] },
  { key: "clause R74",  make: () => without("R74"), expect: ["R74"] },
  { key: "clause R74_SCOPE (a card reaches nothing)", make: () => without("R74_SCOPE"), expect: ["R74"] },
  { key: "clause R124 (the allowance is always one)",  make: () => without("R124"),     expect: ["R124"] },
  { key: "clause R121 (a Mind is an ordinary being)",  make: () => without("R121"),     expect: ["R121"] },
  { key: "clause R121_LOCK (silent from half-turn 0)", make: () => without("R121_LOCK"), expect: ["R121"] },
  { key: "clause R35 (the wait is the card's level)",  make: () => without("R35"),   expect: ["R35"] },
  { key: "clause R103 (cadence in raw tag numbers)",   make: () => without("R103"),  expect: ["R103"] },
  { key: "clause R106 (a Lux search tutors anything)", make: () => without("R106"),     expect: ["R106"] },
  // R101 · perturb the ASYMMETRIC square — L3 / 0 traits is 5 Khao OR 4
  // Anima, the only fare in Terror where Anima is the cheaper coin.
  { key: "DEVIANT_FARE.0.L3", make: () => ({ DEVIANT_FARE: { ...DEVIANT_FARE, 0: { ...DEVIANT_FARE[0], 3: { khao: DEVIANT_FARE[0][3].khao + 2, anima: DEVIANT_FARE[0][3].anima + 2 } } } }), expect: ["R101"] },
  { key: "DEVIANT_FARE.0.L2", make: () => ({ DEVIANT_FARE: { ...DEVIANT_FARE, 0: { ...DEVIANT_FARE[0], 2: { khao: DEVIANT_FARE[0][2].khao + 2, anima: DEVIANT_FARE[0][2].anima + 2 } } } }), expect: ["R101"] },
  { key: "clause R75",  make: () => without("R75"), expect: ["R75"] },
  { key: "clause R76",  make: () => without("R76"), expect: ["R76"] },
  { key: "clause R77",  make: () => without("R77"), expect: ["R77"] },
  { key: "clause R78",  make: () => without("R78"), expect: ["R78"] },
  { key: "clause R80",  make: () => without("R80"), expect: ["R80"] },
  { key: "clause R81",  make: () => without("R81"), expect: ["R81"] },
  { key: "clause R82",  make: () => without("R82"), expect: ["R82"] },
  { key: "clause R83",  make: () => without("R83"), expect: ["R83"] },
  { key: "clause R84",  make: () => without("R84"), expect: ["R84"] },
  { key: "clause R85",  make: () => without("R85"), expect: ["R85"] },
  { key: "clause R86",  make: () => without("R86"), expect: ["R86"] },
  { key: "clause B02",  make: () => without("B02"), expect: ["B02"] },
];

// THREE DEALS, NOT ONE. A counter-rule that reads the board — R48's
// returned Soul, R51's survivors, R12's spent cycles — has nothing to
// say on a duel that never reached that state, and a single shuffle
// decides whether it did. One deal made the FAKE column a coin toss
// between seeds, which is the worst possible property for the one
// number in this file nobody is allowed to fudge. Three deals per
// mutation costs about three seconds for the whole table on a laptop —
// slower than the one-deal version it replaces, and worth every
// millisecond, because the thing it buys is that the FAKE column means
// the same thing twice in a row. A code that stays green across all
// three deals is reported FAKE and means it.
const MUTATION_DEALS = 3;

function runMutations(seed, deckA, deckB) {
  return MUTATIONS.map((m) => {
    const K = makeK(m.make());
    const H = makeHarness(seed, K, {});
    try {
      validateDeck(K, deckA, "Deck A", H);
      validateDeck(K, deckB, "Deck B", H);
      runAdversarial(H, deckA, deckB);
      for (let d = 0; d < MUTATION_DEALS; d++) {
        const gh = makeHarness(seed + d, K, {});
        simulateGame(gh, deckA, deckB);
        gh.reached.forEach((v, code) => mergeReach(H.reached, code, v));
      }
    } catch (e) {
      return { key: m.key, expect: m.expect, red: [], fake: m.expect, error: String(e && e.message) };
    }
    const red = m.expect.filter((c) => (H.reached.get(c)?.fails || 0) > 0);
    const fake = m.expect.filter((c) => !red.includes(c));
    // ── THE HONEST HALF OF THE RED COLUMN ────────────────────
    // A red earned through a clause guard is only worth the content of
    // that guard's COUNTER-RULE, which the census measured on real
    // boards during the unmutated batch. A red the BOARD produced
    // (stateFails — no clause guard involved) always counts.
    // ⚠ `clauseIsProof`, NOT `clauseIsContentful`. The counter census
    // alone was walked through twice by the closing review — see THE
    // IN-FORCE ATTACK over `ruleIs`. A clause now has to have BOTH of
    // its branches demonstrated: the counter true on a real board, and
    // the in-force reading FALSE on a board the fault roster broke.
    // ── AND THE TWO ROUTES ARE RECORDED APART, because they are two
    // different claims and one line used to launder them into one.
    //   ATTRIBUTED  — the red came through a `ruleIs` clause guard whose
    //                 BOTH branches the two censuses found to be real
    //                 predicates over state the simulator produced.
    //   UNATTRIBUTED— the red carried no clause name at all. It is
    //                 recorded as `stateFails` and waved through
    //                 UNCONDITIONALLY by the line below, and that one
    //                 line is the whole of the argument for the great
    //                 majority of this file's "proofs". It says the
    //                 assertion contradicted a perturbed K. It does NOT
    //                 say it read a board — a check reading only "was I
    //                 mutated" answers it perfectly, which is exactly
    //                 what the BLIND ECHO measures and prints.
    // Both are returned so `runAll` can print the split at the number
    // rather than explaining it in a banner nobody reads twice.
    const byClause = red.filter((c) => ((H.reached.get(c).viaClause) || []).some(clauseIsProof));
    const byState  = red.filter((c) => !byClause.includes(c) && (H.reached.get(c).stateFails || 0) > 0);
    const honest = red.filter((c) => byClause.includes(c) || byState.includes(c));
    const vacuous = red.filter((c) => !honest.includes(c));
    const via = {};
    red.forEach((c) => { via[c] = (H.reached.get(c)?.viaClause || []).slice(); });
    return { key: m.key, expect: m.expect, red, honest, byClause, byState, vacuous, via, fake, error: null };
  });
}

// ═════════════════════════════════════════════════════════════
// THE FAULT ROSTER — one named engine bug per row, and the column
// that says which codes are supposed to catch it.
// ─────────────────────────────────────────────────────────────
// Every row is a specific error the catalogue forbids, committed by the
// SIMULATOR on purpose, with the CHECKER left clean. A row is a claim
// with two halves and both are checked:
//   1. the fault reddens the codes in `expect` — otherwise the row is
//      reported FAULT-BLIND, which means either the injection site is
//      dead or the check is asleep, and either is a defect,
//   2. while it is running, the IN-FORCE branch of the clause guard on
//      those codes comes back FALSE at least once — which is what
//      promotes the clause from "reddens on cue" to "watches the board".
//
// COLLATERAL is not counted. A broken board reddens neighbours (a seat
// that opens a Khao short fails prices it can no longer pay), and
// crediting those to this fault would be the same overstatement in a
// new colour. Only declared-and-observed reds count, and the collateral
// is listed beside the row so it can be read and not spent.
//
// ⚠ THE ROSTER IS NOT A COVERAGE TARGET. Rows exist where a fault could
// be committed at ONE site with the false arm left verbatim. Where a
// rule's assertion reads CARD DATA rather than board state, or where
// the duel never builds the board the fault would spoil, there is no
// honest row to write and the code is declared in NOT_PROVEN_REASONS
// instead. Adding a row that cannot bite is how this table would rot
// into the thing it was built to replace.
// ═════════════════════════════════════════════════════════════
const FAULTS = [
  { key: "misact FALLEN_STICKS",       fault: "FALLEN_STICKS",       expect: ["R26"] },
  { key: "misact PRIMERA_CARRY",       fault: "PRIMERA_CARRY",       expect: ["R83"] },
  { key: "misact KHAO_CLIMB_ALWAYS",   fault: "KHAO_CLIMB_ALWAYS",   expect: ["R12"] },
  { key: "misact KHAO_SHORT",          fault: "KHAO_SHORT",          expect: ["R14"] },
  { key: "misact SHIFT_LEAVES_HAND",   fault: "SHIFT_LEAVES_HAND",   expect: ["R42"] },
  { key: "misact SUMMON_TOLL",         fault: "SUMMON_TOLL",         expect: ["R20"] },
  { key: "misact ASCEND_SEALS_SOUL",   fault: "ASCEND_SEALS_SOUL",   expect: ["R49"] },
  { key: "misact STAMP_ONE_AXIS",      fault: "STAMP_ONE_AXIS",      expect: ["R78"] },
  { key: "misact RIFT_FACEUP",         fault: "RIFT_FACEUP",         expect: ["R34"] },
  { key: "misact TOME_STAYS",          fault: "TOME_STAYS",          expect: ["B02"] },
  { key: "misact CURIO_TAKES_RIFT",    fault: "CURIO_TAKES_RIFT",    expect: ["R75"] },
  { key: "misact DAMAGE_PROCS",        fault: "DAMAGE_PROCS",        expect: ["R76"] },
  { key: "misact VERDICT_INVERTED",    fault: "VERDICT_INVERTED",    expect: ["R33"] },
  { key: "misact FRACTURE_RUINS",      fault: "FRACTURE_RUINS",      expect: ["R22"] },
  { key: "misact CHIP_NOOP",           fault: "CHIP_NOOP",           expect: ["R27"] },
  { key: "misact LYF_PERSISTS",        fault: "LYF_PERSISTS",        expect: ["R28", "R27"] },
  { key: "misact RUIN_EXILES",         fault: "RUIN_EXILES",         expect: ["R23"] },
  { key: "misact HERETICAL_RIDES_DOWN", fault: "HERETICAL_RIDES_DOWN", expect: ["R68"] },
  { key: "misact DESCENT_HOLDS_TOP",   fault: "DESCENT_HOLDS_TOP",   expect: ["R51"] },
  { key: "misact DESCENT_DROPS_ATTACH", fault: "DESCENT_DROPS_ATTACH", expect: ["R52"] },
  { key: "misact DESCENT_RUINS_IMPERA", fault: "DESCENT_RUINS_IMPERA", expect: ["R52"] },
  { key: "misact DESCENT_NOT_FALLEN",  fault: "DESCENT_NOT_FALLEN",  expect: ["R24"] },
  { key: "misact SOUL_EXILED",         fault: "SOUL_EXILED",         expect: ["R50"] },
  // ── THE 2026-09-14 SWEEP · SIX ROWS THAT MOVE `WATCHED` ────
  // Every one of these replaces a check that could not fail with a clean
  // checker. The banner's own closing note says growing the roster is
  // "the only number worth moving"; this is that work, and the number it
  // moves is WATCHED.
  { key: "misact CARD_HITS_ESCLING",   fault: "CARD_HITS_ESCLING",   expect: ["R74"] },
  { key: "misact WALL_IGNORED",        fault: "WALL_IGNORED",        expect: ["B03"] },
  { key: "misact CEILING_IGNORED",     fault: "CEILING_IGNORED",     expect: ["R13", "R32"] },
  { key: "misact LEDGER_STICKS",       fault: "LEDGER_STICKS",       expect: ["R100"] },
  { key: "misact REFILL_OUTGOING",     fault: "REFILL_OUTGOING",     expect: ["R14"] },
  { key: "misact PHASE_OPEN",          fault: "PHASE_OPEN",          expect: ["R108"] },
  { key: "misact FARE_PAID_ONCE",      fault: "FARE_PAID_ONCE",      expect: ["R105"] },
];

// THREE DEALS, for the same reason MUTATION_DEALS is three: a fault that
// only bites on a board one shuffle produces would make the FAULT column
// a coin toss between seeds, and the fault column is now load-bearing.
const FAULT_DEALS = 3;

// ── RUN THE ROSTER ───────────────────────────────────────────
// The checker is CLEAN (`makeK()` with nothing withdrawn), so every red
// here is a check catching a genuinely wrong board — the strongest
// evidence this file can produce about itself, and the only evidence
// that speaks to the branch a real duel actually evaluates.
function runFaults(seed, deckA, deckB, opts) {
  const stub = !!(opts && opts.stub);
  const before = { fault: MISACT.fault, on: MISCENSUS_ON, stub: INFORCE_STUB };
  try {
    MISCENSUS_ON = true;
    INFORCE_STUB = stub;
    const K = makeK();
    return FAULTS.map((f) => {
      MISACT.fault = f.fault;
      const H = makeHarness(seed, K, {});
      try {
        runAdversarial(H, deckA, deckB);
        for (let d = 0; d < FAULT_DEALS; d++) {
          const gh = makeHarness(seed + d, K, {});
          simulateGame(gh, deckA, deckB);
          gh.reached.forEach((v, code) => mergeReach(H.reached, code, v));
        }
      } catch (e) {
        // A fault that CRASHES the simulator has proven nothing about
        // any check, and saying so is the point. It is not a red.
        return { key: f.key, fault: f.fault, expect: f.expect, red: [], blind: f.expect,
                 collateral: [], error: String(e && e.message) };
      }
      const allRed = Array.from(H.reached.entries())
        .filter(([, r]) => (r.fails || 0) > 0).map(([c]) => c).sort();
      const red = f.expect.filter((c) => allRed.includes(c));
      const blind = f.expect.filter((c) => !allRed.includes(c));
      const collateral = allRed.filter((c) => !f.expect.includes(c));
      return { key: f.key, fault: f.fault, expect: f.expect, red, blind, collateral, error: null };
    });
  } finally {
    MISACT.fault = before.fault;
    MISCENSUS_ON = before.on;
    INFORCE_STUB = before.stub;
  }
}

// ─────────────────────────────────────────────────────────────
// THE IN-FORCE ECHO — Attack B, run on purpose, every pass.
//
// The first echo control degrades BOTH branches of every clause guard
// and so can only see SYMMETRIC degradation. This one degrades the
// IN-FORCE branch ALONE — literally `inForce = true` inside `ruleIs`,
// the one-line edit that made this file stop watching the board while
// the report stayed byte-identical — and re-runs the whole fault
// roster underneath it.
//
// The demand is absolute rather than statistical: under the stub, NO
// clause may have an in-force reading that ever said no, because there
// is no longer an in-force reading. `inForceLost` is the set of clauses
// that gave up their watch; it MUST equal the set that had one. If it
// does not, the in-force census is measuring something other than the
// branch a duel evaluates, and `inForceBlind` says so at the top of the
// screen, in red, without anyone asking.
// ─────────────────────────────────────────────────────────────
//
// ⚠ AND THE HALF OF IT THAT WAS ARITHMETIC, SAID OUT LOUD (2026-08-25,
// blind-echo pass). The `watching` half of this control is a TAUTOLOGY
// and was shipped as a demand. `INFORCE_STUB` sets `inForce = true`
// inside `ruleIs` BEFORE the in-force census counts `if (!inForce)`, so
// `inForceFalse` cannot increment, so the returned survivor set is
// empty FOR ANY FILE CONTENT WHATSOEVER. "Under the stub the set MUST
// empty" is satisfied by construction, not by measurement, and a demand
// met by construction is the exact disease this file exists to refuse.
// It is kept — because `inForceLost === watching` still makes the
// screen show WHICH clauses had a watch to lose, and an EMPTY
// `watching` is a genuine alarm (it is what Attack B produces) — but it
// is no longer the whole control. The measured half is below: the fault
// roster re-run under the stub, whose reds are a real number that a
// degraded file loses and an honest one keeps.
function runInForceEcho(seed, deckA, deckB) {
  const saved = Array.from(MISCENSUS.entries()).map(([k, v]) => [k, { ...v }]);
  try {
    MISCENSUS.clear();
    const stubbed = runFaults(seed, deckA, deckB, { stub: true });
    return {
      // The tautological half. Always []. See the note above.
      survived: Array.from(MISCENSUS.entries())
        .filter(([, m]) => m.inForceFalse > 0).map(([k]) => k),
      // THE MEASURED HALF. Which named engine bugs are still caught when
      // every clause guard's in-force branch is replaced by `true`?
      // Every one that is caught anyway was caught by a check that is
      // NOT clause-guarded; every one that is lost was being watched by
      // a guard that reads the board. A file already degraded the way
      // Attack B degrades it loses NOTHING here, and that zero is the
      // alarm.
      caught: Array.from(new Set(stubbed.flatMap((f) => f.red))),
    };
  } finally {
    MISCENSUS.clear();
    saved.forEach(([k, v]) => MISCENSUS.set(k, v));
  }
}

// ═════════════════════════════════════════════════════════════
// THE BLIND ECHO — ATTACK C, RUN ON PURPOSE, AND THE NUMBER IT
// TAKES AWAY  (2026-08-25, third adversarial pass)
// ─────────────────────────────────────────────────────────────
// THE ATTACK. Two passes of this file built machinery around `ruleIs`:
// the counter census, the in-force census, the echo control, the fault
// roster. Every one of them polices ONE population — the assertions
// that call `ruleIs`. It is not the big population. Measured on the
// clean file, of 96 codes counted MUTATION-PROVEN:
//
//     5   reddened through a `ruleIs` clause guard (censused both ways)
//    74   reddened with NO clause attribution at all
//
// A red with no clause attribution is recorded as a `stateFails` and
// `runMutations` grants it honesty UNCONDITIONALLY — `if (stateFails >
// 0) return true`. Nothing censuses it, no echo degrades it, no roster
// row watches it. So the whole apparatus guards 5 of the 96 and 74 ride
// free, and the free ride is trivially exploitable:
//
//   ATTACK C. Replace an adversarial rejection with a bare echo of its
//   own switch, reading no board state whatsoever:
//       H.reject("R30", !K.SW.R30, "a second swing from one Escendant")
//   Green on a clean K, red under `clause R30`, `stateFails`, honest,
//   PROVEN. Report: BYTE-IDENTICAL — 96 / 1 vacuous / 0 FAKE.
//   Mechanically applied to all 44 switch-guarded `H.reject` sites in
//   the adversarial suite the report moved by ONE FAKE and the headline
//   did not move at all: 96 proven, with 44 checks reading nothing.
//
// WHY NO AMOUNT OF CENSUS FIXES THIS. The mutation batch asks one
// question — "did the expected code redden when the checker's copy was
// perturbed" — and a check that reads ONLY WHETHER K WAS PERTURBED
// answers it perfectly. That is not a hole in the implementation; it is
// what a mutation batch IS. The only instrument in this file that a
// board-blind check cannot pass is the FAULT ROSTER, because a roster
// row leaves the checker CLEAN and breaks the BOARD, and a check that
// reads only K sees a clean K and says yes.
//
// SO THE FAKE IS BUILT AND RUN, EVERY PASS. `BLIND_ECHO` replaces the
// verdict of EVERY assertion in the file with `K.__clean` — the
// cheapest fake that can exist here — and the whole apparatus is
// re-run underneath it. Two numbers come back and both are printed:
//
//   blindProven  — what a file that watches NOTHING scores on the
//                  mutation batch. It comes back at parity with the
//                  real file's MUTATION-PROVEN, which is the entire
//                  indictment of that number stated as a measurement
//                  instead of an opinion.
//   blindCaught  — what the same file scores on the FAULT ROSTER. It
//                  MUST be empty. If it is not, the roster has been
//                  fooled too and `blindBlind` says so in red.
//
// AND THE HONEST NUMBER FALLS OUT OF IT WITHOUT BEING TYPED:
//   WATCHED = MUTATION-PROVEN minus everything the blind file also
//   "proved" — the codes whose proof a check reading nothing CANNOT
//   reproduce. In practice that is exactly the fault-roster set, and it
//   is 22, not 96. The other 74 are WIRED — reddens when the rule is
//   withdrawn from the checker's own copy, which says the check is
//   attached to the DEFINITION — and are no longer allowed to be read
//   as evidence that anything watched a board.
// ═════════════════════════════════════════════════════════════
function runBlindEcho(seed, deckA, deckB) {
  const before = BLIND_ECHO;
  const saved = Array.from(MISCENSUS.entries()).map(([k, v]) => [k, { ...v }]);
  try {
    BLIND_ECHO = true;
    const ms = runMutations(seed, deckA, deckB);
    MISCENSUS.clear();
    const fs2 = runFaults(seed, deckA, deckB);
    return {
      proven: Array.from(new Set(ms.flatMap((m) => m.honest))),
      red: Array.from(new Set(ms.flatMap((m) => m.red))),
      caught: Array.from(new Set(fs2.flatMap((f) => f.red))),
    };
  } finally {
    BLIND_ECHO = before;
    MISCENSUS.clear();
    saved.forEach(([k, v]) => MISCENSUS.set(k, v));
  }
}

// ─────────────────────────────────────────────────────────────
// THE ECHO CONTROL — the stub experiment, made permanent.
//
// An adversarial review deleted the body of every clause-guarded
// assertion and replaced it with a literal echo of the switch —
// `!!K.SW[clause]`, reading no board state at all — and the report came
// back BYTE-IDENTICAL: 97 proven, 0 FAKE, both with the checks and
// without them. That is the whole indictment, and a number that cannot
// tell the difference between watching the board and watching nothing
// is not a number.
//
// So the experiment runs on every mutation pass now. `ECHO_STUB` sets
// every clause guard to its degraded form, the census sees the degraded
// form, and the honest-proven set is re-derived. The count MUST DROP,
// by exactly the codes whose only proof was a clause guard. If it does
// not drop, the mechanism is watching nothing again and the report says
// so in red, at the top, without anyone asking.
// ─────────────────────────────────────────────────────────────
function runEchoControl(seed, deckA, deckB) {
  const before = { on: CENSUS_ON, echo: ECHO_STUB };
  const saved = Array.from(CENSUS.entries()).map(([k, v]) => [k, { ...v }]);
  try {
    CENSUS.clear();
    ECHO_STUB = true;
    CENSUS_ON = true;
    const K = makeK();
    const H = makeHarness(seed, K, {});
    validateDeck(K, deckA, "Deck A", H);
    validateDeck(K, deckB, "Deck B", H);
    runAdversarial(H, deckA, deckB);
    simulateGame(makeHarness(seed, K, {}), deckA, deckB);
    CENSUS_ON = false;
    const ms = runMutations(seed, deckA, deckB);
    return Array.from(new Set(ms.flatMap((m) => m.honest)));
  } finally {
    ECHO_STUB = before.echo;
    CENSUS_ON = before.on;
    CENSUS.clear();
    saved.forEach(([k, v]) => CENSUS.set(k, v));
  }
}

// ─────────────────────────────────────────────────────────────
// WHY A RULE IS OUT OF REACH. The SET is derived (catalogue minus
// whatever the run touched); only the REASON is written by a person,
// because "no reason given" is how a gap becomes furniture.
// ─────────────────────────────────────────────────────────────
// A rule with no assertion needs a REASON on screen, not a reason in a
// comment the UI cannot read. Everything the catalogue holds and this
// harness does not reach must appear here; the fallback below is a
// confession, not a description, and it says so.
// ⚠ SEVEN CONFESSIONS WERE DELETED FROM THIS TABLE ON 2026-09-14, AND
// THE DELETION IS THE POINT. R100, R101, R102, R103, R108, R110 and R119
// each stood here saying this simulator could not reach a rule, and each
// is now reached — most of them by asking the engine's own predicate
// instead of a private copy, two by building the mechanic. A confession
// left standing after the thing it confesses is fixed is the same
// species of lie as a check that cannot fail, one register quieter:
// this file's own header says so at NOT_PROVEN_REASONS. If any of that
// work is ever reverted, the code falls out of REACHED and reappears in
// the UNDECLARED count, in red, without anyone having to remember to
// put its reason back.
const UNPROVABLE_REASONS = {
  // ── caP'n'S SEVEN ANSWERS OF 2026-09-15 · R132-R135 ─────
  // AND THIS FILE CAUGHT ALL FOUR THE MINUTE THEY ENTERED THE CATALOGUE,
  // in red, without anyone remembering to look:
  //     UNDECLARED 4 (unreached-no-reason 4 ["R132","R133","R134","R135"])
  // Which is the mechanism working twice over, because R132 is a rule
  // about a moment and this file cannot see moments. Reasons filed, each
  // honest about what is NOT proven rather than dressing a limitation up
  // as coverage.
  R132: "WHEN THE MAX-KHAO CLIMB LANDS. THE MOMENT IS NOT A SNAPSHOT PROPERTY, and that is the whole difficulty: this file audits a SEQUENCE OF BOARDS, and a board cannot say when within a half-turn a write happened. A climb granted at the end of a seat's own half-turn and one granted as it sits down produce the SAME snapshot one step later — the same structural blindness R126 is declared for ('a Turn ended by the clock and a Turn ended by a seat pressing END TURN are the same board'). ⚠ AND IT IS EXACTLY THE BLINDNESS THAT LET THE BUG LIVE: four copies of this rule granted at two different moments and every board in between was legal, which is why the brain sat a full Turn behind the player on Max Khao for the whole duel and no instrument said a word. ⭐ WHAT IS PROVEN, AND WHERE. (1) ONE SEAM: turnEngine.handOffHalfTurn holds the only copy, and the renderer and all three brains hold none — by scan, in canon0826 and rulings0915, inverted so a returning copy reddens. (2) THE FLOOR, AT EVERY SEAT COUNT: `Math.max(FIRST_KHAO_TURN, halfTurnsPerFullTurn(seatCount))` is lifted and executed at 2, 3 and 4 seats in rulings0915, including the direction property that it can only ever RAISE R91's floor and never lower it. THIS FILE CANNOT REACH THAT HALF AT ALL — SEATS is 2, so the lap length IS FIRST_KHAO_TURN here and the gate cannot tell the rule from the coincidence; the blind spot is declared in place at the gate itself. (3) THE POOL COUPLING: the climb and the refill ride ONE atomic chair write, so a seat cannot be observed sitting down with a climbed ceiling and a stale pool — by scan on the ledger paths. (4) THE WINDOW HAS EXACTLY ONE CLOSER, which is the assertion that would have caught the crash-recovery path where a brain that crashed a Turn it had spent Khao on had the spend forgiven and climbed anyway. ⚠ WHAT R12's OWN ASSERTION ABOVE DOES AND DOES NOT COVER: `climbsAfterSpend === 0` proves no climb follows a spend, which is R12's condition. It says nothing about WHEN a legal climb arrives, so it would have stayed green through the entire four-copy drift. COST TO CLOSE IT HERE: a per-seat log of the half-turn each climb landed on together with the pool the seat then PLAYED that half-turn with — the coupling, not the write — plus a seat-count parameter through buildPlayer/simulateGame for the floor. The second is the same work as R127's 4-seat mode.",
  R133: "WHAT THE CLOCK BURIES. THERE IS NO WALL CLOCK IN THIS HARNESS, the same structural reason R87 and R126 are out of reach, and R133 is only reachable THROUGH the clock: it governs the automatic bury that fires when a Turn timer expires over R10's hand cap, and nothing here expires. The BUTTON door — refuse the Turn, raise the modal, let the seat pick — is renderer state and equally out of reach. ⭐ WHAT IS PROVEN, AND WHERE: `forcedDiscardPick` is LIFTED OUT OF THE SOURCE BY ITS OWN TEXT AND EXECUTED in rulings0915, against five real hands including the three shapes the old tie-breaks would have sent elsewhere (a cheapest Tome first, an early duplicate, a single card), plus the determinism property — the pick reads no card data at all, so every client racing the same grace-expiry hand-off reaches the same card. A SCAN WOULD NOT HAVE DONE: 'hand.length - 1' passes on a commented-out line. ⚠ AND THE DELIBERATELY BROKEN MIRROR IS WATCHED TOO: npcPickDiscardIdx still prefers the cheapest Tome, because a brain choosing is the equivalent of a human picking from the modal and that pick carries a measured five-point clock-call result. The mirror must not be restored, and the assertion reddens if it is. COST TO CLOSE IT HERE: a fake clock, which R126's reason already argues buys less than a browser does.",
  R134: "V'AEL'S VEIL COSTS NOTHING. NOT A DUEL RULE AT ALL — the only code in the catalogue whose subject is the OVERWORLD rather than the Shatterfield. The Veil is a story cloak that lets two people cross the map undetected; it has no seat, no pool, no half-turn and no board, so there is no state here for it to be a property of. It is in the catalogue because caP'n ruled on it and every ruling gets a row, not because a duel can observe it. ⭐ WHAT IS PROVEN, AND WHERE: `stepCost` is EXECUTED in rulings0915 and must return no cost at any progress, `ledgerLine` must name no debt, the stored shape is pinned to steps and nothing else, and the price PROSE must survive as marked flavour — caP'n keeps placeholders on purpose and deleting his writing to land a mechanical ruling would be the wrong trade. vaelsTools.js was ADDED TO THE HARNESS MIRROR on 2026-09-15 specifically so this could be executed rather than scanned, taking the mirror from 25 roots to 26 and moving nothing else in the output set. ⚠ AND THE FINDING THAT CAME WITH IT IS WHY THE ROW MATTERS MORE THAN THE RULE: the ledger it retires had NO CALLERS ANYWHERE, while the file's own banner claimed the debt was 'counted, shown in the chrome, and persisted'. A comment describing a system that does not exist is the most expensive kind. COST TO CLOSE IT HERE: nothing worth building. An Overworld simulator is not this file's job.",
  R136: "ANYONE MAY ANSWER. THIS FILE SEATS TWO (SEATS = 2) AND HAS NO RESPONSE WINDOW OF ITS OWN, so the property \u2014 every living adversary of the declarer holds a window, not only the targeted seat \u2014 cannot be reached here: at two seats the set of adversaries IS the targeted seat, and the rule is invisible by construction. Proven instead by executing matchSeats.answerersFor and collectPvPResponses at 2, 3 and 4 seats and in 2v2 in tools/unify0915.selftest.mjs (the FFA table yields every living adversary; a teammate is excluded; an eliminated seat is excluded; the first answer by arrival wins the link; all-declined settles the window). The PvP transport itself (pendingResponses/{seat}) is React-side and is asserted by scan. Declared, not covered.",
  R135: "A DURATION'S CLOCK STARTS WHERE IT IS STAMPED. THE RULE IS A PARITY PROPERTY AND PARITY NEEDS TWO RUNS: it says a duration stamped on an ADVERSARY'S half-turn lasts exactly as long as one stamped on your own, so contradicting it means stamping the same effect at an odd anchor and an even one and comparing lifetimes. This simulator stamps what the simulated seats happen to play, at whatever anchors a game reaches, and never the same effect twice at controlled parities — so it can watch that SOME durations expire on time and never that the anchor's parity buys nothing. ⚠ AND THE TEMPTING SHORTCUT IS BARRED BY THE CATALOGUE LAW: calling `durationExpiryTurn` here and agreeing with it proves nothing, and disagreeing usefully would mean typing the lap length and the multiplication into this file — a second catalogue written to check the first, which is the disease this file was rebuilt to cure. ⭐ WHAT IS PROVEN, AND WHERE: the parity property is LIFTED AND EXECUTED in rulings0915 — a 1-Turn and a 3-Turn duration stamped on every half-turn 0 through 7 each live exactly `N x halfTurnsPerFullTurn`, odd anchors equal to even ones, holding at 3 and 4 seats too; the expiry is asserted NOT to be a multiple of the lap length, which is what a boundary-snap would make it; and the climb's own floor is checked from the other side — a spend on an adversary's half-turn costs exactly two quiet half-turns and one on your own costs three, never fewer than two. A snapping mutation reddens six of those assertions. ⚠ THIS ROW EXISTS BECAUSE A RULE WAS WRITTEN WRONG WHILE THE CODE WAS RIGHT, which no instrument can catch: R132's row over-read caP'n's earlier ruling into 'every clock pays on a Turn boundary', everything stayed green, and he volunteered the correction himself. See TERROR_REFEREE.md 4.15 item 5. COST TO CLOSE IT HERE: a duration bench that stamps one effect at a chosen anchor and steps the board, which is the same shape as THE DESCENT BENCH.",

  // ── caP'n'S FOUR RULINGS OF 2026-09-14 · R128-R131 ─────────
  // All four arrived as answers at the end of the coherency sweep, and
  // THIS FILE FLAGGED ALL FOUR AS UNDECLARED the minute they entered the
  // catalogue — in red, without anyone remembering to look. Reasons
  // filed, each honest about what is NOT proven rather than dressing a
  // limitation up as coverage.
  R128: "WHEN AN IMPERA'S PRINTED NUMBER IS PAID. A PAYMENT LEAVES NO TRACE IN A SNAPSHOT — the pools are lower and nothing says why, so an Impera sitting on a Soul looks identical whether its fare was charged or not. This is the same structural blindness as R21's Deviant fare and R88's search fares, both declared above. ⚠ WHAT IS PROVEN, AND WHERE: the two halves are lifted-and-executed in the board and brain proofs (`trainingFare` against `cards.js` for all 36 Impera, and the brain refusing rather than going negative at 2 Khao), and the ORDERING — every charge above the splice, so a Training cannot take the card out of hand and then fail to pay — is measured by byte offset rather than asserted. Cost to close it here: a ledger of what was spent and on what, which is a second economy written to be checked against the first.",
  R129: "THE TWO FED TREASURES. The state IS in the snapshot — `fed`, `fedThisTurn`, `fedAmountThisTurn` are all fields, and `fed <= threshold` and the per-Turn allowance are genuine snapshot properties a bench could watch. It is unreached for the reason R123 and R125 are: THIS SIMULATOR CONSTRUCTS NO TREASURE. Neither starter deck ships either Necklace (which is the rarity doctrine, not a gap — see R131), so no board here ever grows one to audit. ⚠ AND THE BALANCER CANNOT COVER IT EITHER, for the same reason, which is why both halves are proven by lifted execution: the allowance across a Turn boundary, the store clearing at the threshold, the second payout being reachable, and the currency coming from the card rather than the step. Cost to close it here: a Treasure bench, the same shape as THE DESCENT BENCH.",
  R130: "A FULFILLED PROPHECY CANNOT BE ANSWERED. PROPHECIES DO NOT EXIST IN THIS SIMULATOR'S STATE MODEL — stated at length for R97/R98/R99 below, and unchanged: `buildPlayer` copies `deck.prophecy` onto the field and nothing reads it again. There is no fulfilment, no resolution, no `prophecySpent` and no seat that can win by one. ⚠ AND THIS RULE IS A NEGATIVE, which is the hardest shape for a state checker: it asserts that no response window ever opens on a path. That is proven by EXHAUSTION over the source instead — all four `requestWindow` kinds enumerated across eight engine files, both Prophecy doors isolated by brace-walk and scanned for sixteen answer mechanisms (zero hits), `isAenarchistArt('Prophecy')` false so R120 is not a route in, and a seat holding all eighteen Nihil/Omnia with 9,999 Khao offered eight options of which none is a Prophecy. That check can fail: injecting one `requestWindow` into the handler takes it from 0 hits to 1.",
  R131: "A CARD IN NO STARTER DECK IS NOT A DEAD CARD. NOT WATCHABLE BY CONSTRUCTION, and uniquely so: it is a rule ABOUT THE INSTRUMENTS rather than about a board. It says that a card this harness and the balancer never fire is not thereby evidence of a dead card, because the rarity doctrine deliberately keeps the good cards out of the starter decks and behind packs, bosses and easter eggs. A checker cannot watch its own blind spot. What it changes is the STANDARD OF PROOF: when a fix touches a card no starter deck carries, the report must say the simulator did not see it and prove it by lifted execution — 'the balancer was green' may never stand in for 'the card works'. Enforced by discipline and by the reports, not by an assertion.",

  // ── R126 / R127 · THE TWO ROWS WRITTEN FROM THE BOARD (2026-09-14) ──
  // Both were added to the catalogue on 2026-09-14, and THIS FILE CAUGHT
  // THEM THE SAME MINUTE: the headless runner reported
  //     UNDECLARED 2 (unreached-no-reason 2 ["R126","R127"])
  // in red, without anyone remembering to look. That is the mechanism
  // working — a rule with no watcher and no stated reason is a gap, and
  // a gap with no reason filed is the thing this table exists to refuse.
  // So the reasons are filed, and they are honest about what is NOT
  // proven rather than dressing a limitation up as coverage.
  R126: "THE 45-SECOND HALF-TURN CLOCK. There is no wall clock in this harness — the same structural reason R87 is out of reach, stated in rules.js's own banner and the reason R93's 150-half-turn bound exists at all. Worse than R87, R126 leaves NO TRACE IN A SNAPSHOT even in principle: R87 at least produces a verdict this file can check the second of, while R126 force-ends a Turn and produces nothing — a Turn ended by the clock and a Turn ended by a seat pressing END TURN are the same board. Its four HOLD conditions (a response window, a running chain, a cinematic hold, a PvP declaration in flight) are all renderer state, and its controller==='ai' skip is a property of who is playing rather than of the position. ⚠ WHAT COULD BE WATCHED, AND IS NOT: nothing here, but the SHIPPING side is checkable in a browser and was — 2026-09-14, a live Solrend, the clock read 0:45 at hand-off and ticked to 0:26 while a card was being chosen. Cost to close it HERE: a fake clock in the harness and a reason to trust it, which buys less than the browser did.",
  R127: "THE SEAT SHAPES. This simulator seats TWO, from the `SEATS` constant up, and every bench in it is 1v1. The 2v2 clause is the part with real content — teammates share ONE Anima pool and nothing else, read through matchSeats.getEffectiveAnima — and it is a genuine snapshot property that a 4-seat simulator could watch directly: two seats whose Anima moves together and whose Khao, hands, boards and Books never do. It is unreached here for want of a table, not for want of a check. ⚠ AND THE HALF THAT DOES BITE AT TWO SEATS IS ALREADY WATCHED ELSEWHERE: halfTurnsPerFullTurn(seatCount) is what every cadence and duration in the catalogue is measured on, and the engine's own predicates are asked with 3 and 4 seats across this file's benches. Cost to close it: a 4-seat mode in the harness — the same shape of work as THE DESCENT BENCH, and the first thing to build if 2v2 is ever balanced rather than merely legal.",

  // ── THE PROPHECY CODES · R97 / R98 / R99 (2026-08-25) ──────
  // Three codes landed together and all three are OUT OF REACH here,
  // for one shared structural reason and one arithmetic one. The shared
  // reason is that PROPHECIES DO NOT EXIST IN THIS SIMULATOR'S STATE
  // MODEL. `buildPlayer` copies `deck.prophecy` onto the field and
  // nothing ever reads it again; validateDeck counts Prophecies for
  // R63's deck limit and that is the entire extent of their presence.
  // There is no fulfilment condition, no resolution, no `prophecySpent`
  // and no seat that can win by one. Adding them wholesale would be a
  // second Prophecy engine written to be checked against the first,
  // which is how a Debugger stops being evidence.
  //
  // The arithmetic reason is THE CATALOGUE LAW, and it is worth naming
  // because it is what stops the easy-looking version of this work.
  // `prophetsToll` is a pure function and this file could call it — but
  // calling it and agreeing with it proves nothing, and DISAGREEING
  // with it usefully would mean typing "greater than 30", "halve,
  // round up" and "a flat 15" into this file. Law 1 says not one rule
  // value is typed here, and a second catalogue that exists only to
  // check the first is the exact disease this file was rebuilt to cure.
  // rules.js exports no constants for the 30 or the 15, so there is no
  // honest way to state the toll here at all.
  R97: "THE PROPHET'S TOLL. Unreachable twice over. (1) SEATING: the toll only exists OUTSIDE 1v1, and this simulator builds exactly two players and steps `turn % 2`; there is no third seat for 'every adversary' to mean anything about. (2) PROPHECIES: nothing here fulfils one, so the toll has no trigger. And the tempting shortcut — bench `prophetsToll` on a table of numbers — is barred by THE CATALOGUE LAW: the only way to contradict it is to retype 30, 15 and 'round up' into this file, and a second catalogue is worse than a declared gap. COST TO CLOSE: a seat-count parameter through buildPlayer/simulateGame (three-to-four players, a turn order, `firstSeatOf`), plus a Prophecy zone with a fulfilment condition — roughly the same work as R98 and R99, which is why all three are declared together. It is guarded engine-side by tools/openingTurn.selftest.mjs section 9 instead.",
  R98: "CO-WINNERS. `simulateGame` returns ONE `winner` integer and the only way it is ever set is R33 (a seat at 0 Anima) or R93 (the clock). A Solrend with two winners needs a SECOND, simultaneous source of victory, and in Terror that source is a Prophecy — which this file does not model. Benching `normalizeWinners` instead would be a hand-copy of its own body asserted against itself, which is precisely the species of tautology this file's header names at R69 and killed. COST TO CLOSE: a `winners` array threaded through the end-of-duel path and a Prophecy that can fulfil on the blow that empties an adversary's Anima. Guarded engine-side by tools/openingTurn.selftest.mjs sections 10 and 15 and by matchSeats' own didSeatWin/winnersOfMatch.",
  R99: "A PROPHECY IS SPENT WHEN IT RESOLVES. There is nothing here for `prophecySpent` to be written on: no Prophecy ever resolves, so no Prophecy can resolve twice. This is the purest of the three — it is not an arithmetic gap or a seating gap, it is an ABSENT MECHANIC, and a check written against an absent mechanic can only ever be a check written against itself. COST TO CLOSE: a Prophecy zone with a resolution path and both of the engine's doors (the automatic hand-off check and the manual button) modelled here, so that the second firing has somewhere to be refused. Guarded engine-side by tools/openingTurn.selftest.mjs, which is where the three-firing Ace of Shades defect was caught.",
  // ── THE 2026-09-07 WAVE · FIVE CONSTANTS THIS HARNESS CANNOT SEE ──
  // Written the day the wave landed, in the same pass that DECLINED to
  // add their mutation rows (see THE 2026-09-07 WAVE in MUTATIONS). The
  // shape is the same as the Prophecy codes above and so is the reason:
  // the mechanic is ABSENT from this simulator's state model, and a
  // check written against an absent mechanic can only ever be a check
  // written against itself. Every row names the constant it leaves
  // unwatched, so a grep for the constant lands on its own confession.
  R111: "EXILIATION, AND THE FARE NOTHING HERE PAYS. `EXILE_FARE` (L1 2 / L2 3 / L3 4, Khao or Anima, never split) is UNWATCHED. This simulator performs no Exiliation: there is no art that removes a card from a Book, a hand or the Necropolis, no OP2 clock for it to spend, and no policy that would ever choose to. A mutation of EXILE_FARE moves a price nothing in this file quotes, so it would redden nothing and the row was deliberately not written. COST TO CLOSE: an Exiliation action in the simulator (choose a card, pay by its LEVEL out of one pool, write the card into the seat's `exile` pile), plus the R103 cooldown ledger the Ritual needs anyway, plus `exile` added to `zoneMap` so conservation counts the new pile. Guarded engine-side only, for now.",
  R112: "THE EXILE RIFT IS SEEDED AND NEVER FILLED. `buildPlayer` now carries `exile` and `exileReturned`, so the state model can REPRESENT the rift — which is not the same as watching it, and is recorded here so the seeded field is never mistaken for coverage. Nothing writes either one, so the one-return mark can never be spent, ruin can never be re-routed away from the Necropolis, and the Resurrection bar can never bite. ⚠ AND THE CONSERVATION SWEEP DOES NOT COUNT THE PILE YET: `zoneMap` lists its zones by hand and `exile` is not among them, which is correct while the pile is always empty and becomes trap 4.3 the instant it is not. Whoever fills it adds it there in the same edit.",
  R113: "THE RITUAL, AND THE FLOOR THAT GATES A DOOR THAT ISN'T THERE. `EXILE_RETRIEVAL_ANIMA_FLOOR` (10) is UNWATCHED, and so is the half-your-Anima fare it opens onto. Nothing here retrieves anything from Exile, because nothing here exiles anything (R111), so the floor gates an action the simulator cannot attempt at any Anima. Moving it to 0 or to 500 produces the identical duel and the identical report. COST TO CLOSE: R111 first, then a retrieval action reading `canReachExileRift` and `exileRetrievalFare` through K, plus the R114 rounding it depends on — at 33 Anima the fare is 17, and that odd number is the only cheap way to prove the rounding law landed against the actor.",
  R115: "THE ROLE ARTS ARE NOT IN THIS FILE AT ALL, so `ROLE_FARE` and `ROLE_LOCK_FULL_TURNS` are both UNWATCHED. A rift here is a slot with a being in it; it has no ROLE, no Art, and no name. Nothing reads ROLE_ORDER, nothing calls riftArtIsAwake, and no fare is ever quoted for standing where the Body, Sol, Heart or Mind rift is. So ROLE_FARE's asymmetric Body square (3 Khao OR 1 Anima) could be inverted and no check in this file would move, and ROLE_LOCK_FULL_TURNS could be set to 0 or 300 without changing a single duel, because the thing it silences for three full Turns is silent here permanently. ⚠ THE TEMPTING SHORTCUT IS BARRED BY THE CATALOGUE LAW: benching roleFare against a table of expected prices means retyping 3, 1, 5 and 5 into this file, and a second catalogue that exists only to check the first is the disease this file was rebuilt to cure. COST TO CLOSE: a role on each rift index, an Art-activation path that pays through K, and the half-turn clock rolesAwake reads — at which point ROLE_FARE tightens and ROLE_LOCK_FULL_TURNS loosens into two honest mutation rows.",
  R92: "THE RESPONSE CHAIN. R92 is last-declared-first, who may answer (Nihil and Omnia only, L3 from hand, L2+ from a face-down field card) and a rebuttal of the same level or higher — and it states in RULEZ_V2 ch.15 that a chain has NO length bound at all: \"None. Not twelve, not five, not any number.\" This simulator has no declaration stack, so not one clause of it is reachable here. ⚠ IT USED TO BE COUNTED PROVEN, off a pair of assertions that the CHAIN_DEPTH_CAP runaway guard was a game bound — i.e. off the inverse of the rule, and the in-force half of that pair was a theorem (`K.CAP >= ACT.CAP`) that no mutation in the table could falsify. Both are gone; the cap is watched in the invariant channel, where it has no rule code. COST TO CLOSE: import ChainLaw's own predicates and drive a declaration stack here, which makes this file a CONSUMER of the shipping chain law rather than a second implementation of it — a real trade-off, stated rather than taken. Watched engine-side by ChainLaw.selftest.mjs.",
  R95: "MODE OVERRIDES. Boss phases, Endless Depths and the Gauntlet live in bossPhases.js and their own runners — a duel simulator has no phase table and no rung to carry Anima across. Out of reach from this screen by construction, and saying so is better than a check that cannot fail.",
  // ── THE CODES THIS FILE HAS NEVER REACHED, DECLARED 2026-09-08 ──────
  // This file's own law, five paragraphs up: "EVERY GAP IS DECLARED. A
  // code missing from all three shows up as UNDECLARED, in red, next to
  // FAKE." Fifteen codes were missing from all three — the 2026-08-26
  // canon wave and most of the 2026-09-06/07 Exile Wave — so the screen
  // was printing them in red with no reason beside them, which is the
  // one thing the law exists to prevent. Each is out of reach for a
  // stated structural reason, and a reason is what turns a red line into
  // a known limit.
  R107: "NO MIXING KHAO AND ANIMA — now OBEYED here (farePools/payOnePool, 2026-09-08) but still not WATCHED: proving it needs a mutation that re-splits a fare and a bench that notices, and the fault roster has no action-space row for it yet. Obeying a rule and watching it are different claims and this file says which is which.",
  R109: "TEAM AND SEAT GEOMETRY. SEATS is 2 here by construction — see the SEATS banner — so nothing about 3- and 4-seat tables is reachable from this screen.",
  R114: "THE HOUSE ROUNDS AGAINST YOU. Nothing this simulator does rounds: every fare it pays is an integer from a table. Watched where a rounding actually happens — the Prophet's Toll, in tools/solrendEnd.selftest.mjs.",
  R116: "THE TERROR ART. No Art is performed here (see R115) and the Sol rift's free ruin has no shipping call site to disagree with yet.",
  R117: "THE HEALER ART. Same reason as R116; it slips two Resurrection brakes this simulator would first have to be able to perform.",
  R118: "THE TANK ART. Same reason as R116. Its passive immunity is to R116's ruin, which is itself unreachable here.",
  R120: "AN ART IS NOT A CARD. This is a question about what a NULLIFIER may reach, and no card in the game carries the `answerScope` field yet (rules.js says so at the field's own definition), so today the rule has nothing to decide about on any board.",
  R122: "THE CADENCE TAGS BIND EVERY CARD. This simulator has no Soul-card ACTIVATION lane at all — it trains Impera, bonds U'hltis and swears Affinities, and then reads them as static numbers — so there is nothing here for a per-card cooldown to gate. Watched by tools/cardText.selftest.mjs (every tagged card reaches the gate) and by the board's own soulUsed stamp.",
};

// ─────────────────────────────────────────────────────────────
// REACHED, AND STILL NOT PROVEN. A rule that an assertion touched but
// that no mutation could turn red is the most dangerous row on the
// screen: it LOOKS watched. Every one of them needs a reason written by
// a person, and the count of the ones with no reason is its own number
// on the report, next to FAKE, because that is the number that grows
// when somebody adds a check and forgets to add the mutation.
// ─────────────────────────────────────────────────────────────
const NOT_PROVEN_REASONS = {
  // ══════════════════════════════════════════════════════════
  // THE VACUOUS SEVENTEEN — WHAT THEY WERE, AND WHAT CLOSED
  // SIXTEEN OF THEM  (2026-08-25, MISACT pass)
  // ──────────────────────────────────────────────────────────
  // Seventeen codes used to stand here. Every one was RED under its
  // clause mutation and none was counted, because the counter census
  // found its COUNTER-RULE true of no board the simulator ever built.
  //
  // THE SHARED REASON, stated once because it was one fact and not
  // seventeen: a `ruleIs` guard on a STATE ASSERTION is vacuous BY
  // CONSTRUCTION. The rule says the board is X; the counter-rule says
  // the board is not-X; and the simulator acts on ACT, where the rule
  // is frozen in force, so it produces X every time and not-X never.
  // Withdrawing the clause turned the assertion into `assert(false)`,
  // which reddens by arithmetic and observes nothing.
  //
  // AND THE FIX WAS NAMED HERE BEFORE IT WAS BUILT: "not a better
  // counter-rule — for most of them there is no coherent second reading
  // that a correct board also satisfies. It is a MUTATION THAT MOVES
  // THE SIMULATOR: a second act-side switch (a MISACT beside ACT) that
  // makes the simulator commit the specific error the rule forbids, so
  // the assertion has a real broken board to catch instead of a
  // withdrawn definition."
  //
  // That is now built. See THE FAULT ROSTER. Sixteen of the seventeen
  // are closed by it, and the proof they now carry is the STRONGER of
  // the two this file can issue — the checker was clean, the board was
  // wrong, and the check said no:
  //
  //   R12 KHAO_CLIMB_ALWAYS   R20 SUMMON_TOLL        R22 FRACTURE_RUINS
  //   R23 RUIN_EXILES         R24 DESCENT_NOT_FALLEN R27 CHIP_NOOP
  //   R33 VERDICT_INVERTED    R34 RIFT_FACEUP        R42 SHIFT_LEAVES_HAND
  //   R49 ASCEND_SEALS_SOUL   R50 SOUL_EXILED        R52 DESCENT_DROPS_ATTACH
  //   R52 DESCENT_RUINS_IMPERA R68 HERETICAL_RIDES_DOWN
  //   R76 DAMAGE_PROCS        R78 STAMP_ONE_AXIS
  //   B02 TOME_STAYS
  //
  // ⚠ THEY ARE NOT LISTED HERE ANY MORE ON PURPOSE. A confession left
  // standing after the thing it confesses is fixed is the same species
  // of lie as a check that cannot fail, one register quieter. If a
  // roster row is ever deleted, its code loses the fault proof, the
  // clause guard loses its WATCH along with it, and the code reappears
  // in this table's UNDECLARED count — in red, by itself, without
  // anyone having to remember to put it back.
  //
  // ONE OF THE SEVENTEEN IS STILL OPEN, and it is open for a reason
  // measurement settled rather than argument.
  R48: "VACUOUS COUNTER, AND NO FAULT CAN REACH IT HERE. The counter-rule is that a Resurrected being comes back BARE with its Soul left sealed; the Soul always returns, so `returned.length === 0 && soulUids.length > 0` never holds. The MISACT row that would have closed it (`RESURRECT_BARE`) was written, run and REMOVED: instrumented across 75 duels the simulator performed 1961 Resurrections and NOT ONE of them was of a being that had a Soul, so there is no board for the fault to spoil and the row was permanently FAULT-BLIND. `soulOf` is only written at a full ruin, and the rite's target is chosen without reference to it. COST TO CLOSE: a Resurrection bench that Ascends, ruins and then raises the same line — the same shape as THE DESCENT BENCH, which is what made R51/R52/R68 reachable — plus moving R48's assertion out of the duel body and into a function both can call.",
  R45: "SHARES R46'S GUARD, and cannot be separated from it here. R46 forbids the SKIP (a Lord straight onto a bare Escling); R45 demands the ORDER (Beb, then Champion, then Lord). Telling them apart needs a record of WHICH LEVELS OF A LINE HAVE ALREADY COME BACK this Solrend, and this simulator keeps a tally per COPY (R85) and nothing per line. On every board it can build, the skip and the out-of-order return are the same board, so one clause withdraws both and claiming two proofs for one gate would be the nicer-looking lie. Cost to close it: a per-line return ledger on the player (line id -> highest level returned), written wherever a tally is written, plus a fixture that returns an L1 and then asks for the L3 of the same line. Worth having; not built speculatively.",
};

// ─────────────────────────────────────────────────────────────
// PARTIAL means proven at a HAND-BUILT board or at the legality
// predicate, and never watched happening inside a played duel. It is a
// real proof of a real gate — every one of these is mutation-proven —
// and it is NOT the same thing as a rule the simulator watched a duel
// obey, so it is not quietly counted as one. The set is written down
// rather than derived, because "the simulator never got there" is a
// fact about this harness that only a person can certify.
// ─────────────────────────────────────────────────────────────
const PARTIAL_REASONS = {
  R119: "THE WARDEN ART. The shield and BOTH of its doors, the not-a-wall clause, its interaction with R115's three-Turn lock and the one-per-seat-per-Turn cap are all proven at the engine's own predicates (`wardenOpenToLytAttack`, `blocksAnimaLytAttack`, `WARDEN_ARTS_PER_TURN`) on hand-built battle lines. What is NOT watched is a Warden Art being FIRED — no rift lends an Art in this simulator, so the two OP2 options, the card paid into the Necropolis and the bounty for ruining a Warden have no board here. ⚠ THE NOT-A-WALL CLAUSE IS NOW LIVE IN THE DUEL, though: `Legal.animaAttack` asks `blocksAnimaLytAttack` per rift instead of its own flat `.some`, so this file and the shipping board finally agree about whether a seat whose only survivor is a Warden has an OPEN Anima route. Until 2026-09-14 they did not, and neither was watching the other on it.",
  R123: "THE TOME WARD. Both scopes and both durations are proven at the engine's own `tomeWardScope` — the beings ward keyed to its half-turn, the cards ward over two FULL Turns, and 'cards' winning when both stand — but this simulator never PLAYS Shower of Lyt or Phoenix Aura, so no ward is ever raised inside a duel and no Tome payload is ever turned away by one. The ward is answered at the PAYLOAD, like R08; watching it in a duel needs a Tome attack this simulator does not model (measured: ZERO `ruin_being` resolutions across the whole apparatus, because neither starter deck ships one).",
  R125: "AN INTANGIBLE TREASURE CANNOT BE RUINED. Both windows are proven at the engine's own `tomeIsIntangible` — the half-turn window of a named boundary, the three FULL Turns of the Necklace of Akula, the missing-anchor case and the untagged case — but this simulator constructs no Treasure and performs no Tome ruin against one, so nothing here is ever REFUSED for intangibility. Same missing mechanic as R123.",
  R21: "DEVIANCE IS NEVER PLAYED. The simulator only ever Transcends and Ascends naturally — it has no reason to pay for a crooked line — so R21's fare is proven at the gate and never watched being paid.",
  R31: "The Anima route is proven at the predicate from four directions (a standing being blocks, a FRACTURED being still blocks, an all-Fallen board opens, the bypass signature B03). On a played board the gate is asked and obeyed, so the board never produces a REFUSAL for R31 to catch — B03 is the code that watches the strike land.",
  R36: "Response-window TIMING. The wait is proven from TOME_WAIT and the L1 refusal is asserted, but this harness runs seats back-to-back with no window between them, so nothing here answers into a live declaration.",
  R37: "Same missing window as R36 — the from-hand permission is proven at the predicate, never on a board.",
  R64: "Deviance is never played (see R21), so no Deviant being ever stands in a duel for a U'hlti or an Affinity to be refused.",
  R65: "Deviance is never played (see R21). The permission and the free price are proven on a hand-built board.",
  R66: "Deviance is never played (see R21), so the one-per-Turn clock is proven at the gate only.",
  R67: "Heretical Training needs an L3 with an empty U'hlti slot and 6 Khao AND 6 Anima at the same moment; the simulator has no policy that would spend a duel getting there. Both prices and the empty-slot requirement are proven on a hand-built board.",
  R69: "Supreme needs a Natural L3 wearing three Impera, a U'hlti and an Affinity — a full Luxmoire build a random duel does not assemble. Proven on the bench, which asserts the whole set reads Supreme and then removes each of the six requirements in turn.",
  R70: "An Affinity needs a Natural L3 already bearing a U'hlti, which the simulator never reaches inside a duel. Proven on a hand-built board.",
  R73: "Same reach as R70 — no Affinity is ever attached inside a played duel, so the one-per-being cap is proven at the gate.",
  R87: "Twenty minutes of wall clock cannot elapse headless; rules.js says so in its own banner. What is proven is the VERDICT and the SECOND it falls on — the duel is not called one second early and is called at the rule's own second — never the elapsing.",
  R88: "The simulator takes no search action; it has no policy for what it would search FOR. Both fares, both directions of the by-level Grimoire table and the R96 leg are proven at the predicate against grimSearchFare as the oracle — and, since 2026-08-25, so is R08's *new* SUSPENSION of both fares on the opening Cycle, asked of a seat funded past every fare in the table so that 'suspended' and 'unpayable' cannot be confused. Every fare row in the bench now passes a turn CLEAR of the Cycle; without that they would have started answering R08 and the by-level table would have gone dark behind the new rule, which is the quietest way a check dies.",
};

const PARTIAL_ONLY = new Set(Object.keys(PARTIAL_REASONS));

// ─────────────────────────────────────────────────────────────
// THE RUN
// ─────────────────────────────────────────────────────────────
function runAll({ deckA, deckB, games, seed, verbose, mutate }) {
  const K = makeK();
  const H = makeHarness(seed, K, { verbose });

  // THE CENSUS IS FED BY THE REAL BATCH AND NOTHING ELSE. A mutated run
  // is a board the rules were withdrawn from; asking it what the rules
  // look like on a real board would be circular. So the switch goes on
  // here, and off before the first mutation.
  CENSUS.clear();
  CENSUS_ON = true;
  ECHO_STUB = false;

  const deckViolations = [
    ...validateDeck(K, deckA, "Deck A", H),
    ...validateDeck(K, deckB, "Deck B", H),
  ];
  runAdversarial(H, deckA, deckB);

  const outcomes = [];
  for (let g = 0; g < games; g++) {
    const gh = makeHarness(seed + g, K, { verbose: verbose && g === 0 });
    let r;
    try {
      r = simulateGame(gh, deckA, deckB);
    } catch (e) {
      gh.inv("simulator-crash", false, `game ${g + 1} threw: ${String(e && e.message)}`);
      r = { winner: -1, turns: 0, endRule: null, endBy: null };
    }
    outcomes.push(r);
    gh.reached.forEach((v, code) => mergeReach(H.reached, code, v));
    gh.violations.forEach((v) => H.violations.push({ ...v, game: g + 1 }));
    gh.invariants.forEach((v) => H.invariants.push({ ...v, game: g + 1 }));
    if (g === 0) gh.log.forEach((l) => H.log.push(l));
  }

  CENSUS_ON = false;
  const census = censusRows();

  // ── THE FAULT ROSTER RUNS BEFORE THE MUTATIONS ────────────
  // ORDER IS LOAD-BEARING. `runMutations` asks `clauseIsProof`, and half
  // of that answer is the IN-FORCE CENSUS the fault roster fills. Run
  // the mutations first and every clause guard looks unwatched, which
  // would understate the file exactly as badly as the old order
  // overstated it. The census is cleared here so a second Run in one
  // session cannot inherit the first one's watches.
  MISCENSUS.clear();
  const faults = mutate ? runFaults(seed, deckA, deckB) : null;
  const miscensus = misCensusRows();

  const mutations = mutate ? runMutations(seed, deckA, deckB) : null;
  // THE ECHO CONTROL, on every mutation pass. See runEchoControl.
  const echoProven = mutate ? runEchoControl(seed, deckA, deckB) : null;
  // THE IN-FORCE ECHO, its dual, on the same pass. See runInForceEcho.
  const inForceEcho = mutate ? runInForceEcho(seed, deckA, deckB) : null;
  // THE BLIND ECHO — Attack C, run on purpose. See runBlindEcho.
  const blindEcho = mutate ? runBlindEcho(seed, deckA, deckB) : null;

  // ── TWO NUMBERS, BECAUSE THEY ARE TWO DIFFERENT CLAIMS ────
  // REACHED means an assertion for this code executed. It says the rule
  // was being watched. It does NOT say the check could have failed.
  // MUTATION-PROVEN means a mutation run actually turned this code red,
  // so the check demonstrably has a failing state. One number doing both
  // jobs is the same disease as a hand-maintained coverage list, one
  // level up: a wall of green that will not say what it was watching.
  const catalogue = ALL_RULES.map((r) => r.code);
  const reached = catalogue.filter((c) => H.reached.has(c));
  const unproven = catalogue.filter((c) => !H.reached.has(c));
  const fired = catalogue.filter((c) => (H.reached.get(c)?.fails || 0) > 0);
  // Derived from what the mutation runs OBSERVED going red, never from
  // the `expect` column — an expectation is a claim, and this file does
  // not count claims.
  // MUTATION-PROVEN is the HONEST half of the red column now — a red
  // manufactured by a vacuous counter-rule is not a proof and is no
  // longer counted as one. VACUOUS is the third colour: reached,
  // reddened, and reddened by arithmetic rather than by observation.
  //
  // THERE ARE NOW TWO ROUTES TO A PROOF AND THEY ARE REPORTED APART.
  //   CLAUSE  — a clause was withdrawn from the CHECKER's copy and the
  //             assertion contradicted it, through a guard whose BOTH
  //             branches the two censuses found to be real predicates.
  //   FAULT   — the SIMULATOR committed a named engine bug with the
  //             checker left clean, and the assertion caught it. This is
  //             the stronger of the two and the only one that speaks to
  //             the branch a real duel evaluates.
  // The headline is the union. Keeping them apart on the screen is what
  // stops the union from being read as one kind of evidence.
  const provenClause = mutations
    ? Array.from(new Set(mutations.flatMap((m) => m.honest))).filter((c) => catalogue.includes(c))
    : [];
  const provenFault = faults
    ? Array.from(new Set(faults.flatMap((f) => f.red))).filter((c) => catalogue.includes(c))
    : [];
  const mutationProven = Array.from(new Set([...provenClause, ...provenFault]));
  const redRaw = mutations
    ? Array.from(new Set(mutations.flatMap((m) => m.red))).filter((c) => catalogue.includes(c))
    : [];
  // TWO FAILURE MODES, TWO WORDS, because they need different fixes.
  //   VACUOUS — the COUNTER branch was true of no board. The red is
  //             arithmetic. Fix: a counter-rule that is a coherent
  //             second reading, or a fault row.
  //   DEMOTED — the counter branch is fine and the IN-FORCE branch is
  //             the constant the closing review's Attack A and Attack B
  //             both produced. Nothing watches the duel. Fix: a fault
  //             row that makes the in-force reading say no.
  const clausesOf = (c) => (mutations || []).flatMap((m) => (m.via && m.via[c]) || []);
  const demoted = redRaw.filter((c) => !mutationProven.includes(c)
    && clausesOf(c).some(clauseIsContentful));
  const vacuous = redRaw.filter((c) => !mutationProven.includes(c) && !demoted.includes(c));
  const reachedOnly = reached.filter((c) => !mutationProven.includes(c));
  const partial = reached.filter((c) => PARTIAL_ONLY.has(c));
  const fakes = mutations ? Array.from(new Set(mutations.flatMap((m) => m.fake))) : [];
  // The control's verdict, computed here so the screen cannot round it
  // off. `echoLost` is what the degraded checks gave up. It MUST NOT be
  // empty: an empty echoLost is the exact symptom the review found —
  // a report that says the same thing whether the checks read the board
  // or read nothing — and `echoBlind` is the alarm that names it.
  const echoLost = echoProven ? provenClause.filter((c) => !echoProven.includes(c)) : [];
  const echoBlind = !!mutate && echoLost.length === 0;
  // ── THE IN-FORCE ECHO'S VERDICT ───────────────────────────
  // `watching` is the set of clause guards whose in-force reading said
  // NO to a board the roster broke. Under the stub that set MUST empty:
  // an in-force branch replaced by `true` has nothing left to say. An
  // empty `watching` in the first place is the same alarm from the other
  // side — it is what Attack B produces, and what the file looked like
  // before this pass.
  const watching = miscensus.filter((m) => m.watches).map((m) => m.clause);
  const inForceSurvived = (inForceEcho && inForceEcho.survived) || [];
  const inForceLost = watching.filter((c) => !inForceSurvived.includes(c));
  // ⚠ `inForceSurvived` IS EMPTY BY CONSTRUCTION — see the note over
  // runInForceEcho. The only load-bearing half of this alarm is
  // `watching.length === 0`, which is what Attack B produces, and the
  // MEASURED replacement is `inForceFaultLost` immediately below.
  const inForceBlind = !!mutate && (watching.length === 0 || inForceSurvived.length > 0);
  // THE MEASURED IN-FORCE CONTROL. Named engine bugs the roster still
  // catches when every clause guard's in-force branch is forced true —
  // and therefore the ones it LOSES, which is the drop a degraded file
  // cannot show. Zero lost with a non-empty roster is the alarm.
  const inForceFaultKept = (inForceEcho && inForceEcho.caught) || [];
  const inForceFaultLost = provenFault.filter((c) => !inForceFaultKept.includes(c));
  // ── THE BLIND ECHO'S VERDICT — see runBlindEcho ────────────
  // `blindProven` is what a file whose every assertion reads only "was
  // the checker perturbed" scores on the mutation batch. `watched` is
  // the honest remainder: proofs that fake CANNOT reproduce. `wired` is
  // everything else in MUTATION-PROVEN — attached to the rule's
  // definition, never demonstrated to read a board.
  const blindProven = (blindEcho && blindEcho.proven) || [];
  const blindCaught = (blindEcho && blindEcho.caught) || [];
  // ⚠ WATCHED IS A PROPERTY OF THE ROUTE, NOT A SET SUBTRACTION, and
  // the first cut of this got it wrong in a way worth leaving written
  // down. `mutationProven \ blindProven` scored 10, because a code with
  // a GOOD fault proof AND an ordinary clause-mutation proof gets
  // claimed by the blind file through the second one and the difference
  // throws the first one away. A bad proof standing beside a good one
  // does not cancel the good one. So the blind echo's job is to
  // establish WHICH ROUTE survives it — and it does, decisively:
  //   mutation batch  blindProven  86 of 96  (fooled almost completely)
  //   fault roster    blindCaught   0        (not fooled at all)
  // WATCHED is therefore the fault-roster set, minus any code the blind
  // run somehow caught anyway — which would mean the roster row is
  // reddening on the mutation and not on the board.
  const watched = mutate ? provenFault.filter((c) => !blindCaught.includes(c)) : [];
  const wired = mutate ? mutationProven.filter((c) => !watched.includes(c)) : [];
  // ── WHAT `wired` ACTUALLY COUNTS, MEASURED AND SPLIT ───────
  // `wired` reads as a tier of evidence and it is not one: it is almost
  // entirely ONE WAIVER applied many times. `runMutations` grants a red
  // honesty unconditionally the moment it carries no clause attribution
  // (`stateFails > 0`), and the set of unattributed codes has always
  // been, element for element, the `wired` set. So the split is computed
  // here and PRINTED AT THE NUMBER — the banner said it, the screen did
  // not, and a number nobody can see the shape of is how this file's
  // coverage claim was destroyed three times.
  //   ATTRIBUTED   a clause guard produced the red AND both of that
  //                guard's branches were demonstrated to be predicates
  //   UNATTRIBUTED no clause name; the waiver. Says the check is
  //                attached to the rule's DEFINITION and nothing more
  //   WATCHED      the simulator broke the BOARD, the checker was clean,
  //                and the check said no. The only measurement here.
  const clauseAttributed = mutations
    ? Array.from(new Set(mutations.flatMap((m) => m.byClause || []))).filter((c) => catalogue.includes(c))
    : [];
  // SCOPED TO `wired`, so the two numbers ADD UP to the tier they
  // describe. A WATCHED code often also has an ordinary clause proof;
  // counting that toward the wired split made 5 + 75 = 74 on the screen,
  // which is the kind of arithmetic that teaches a reader to skip the
  // line. attributed + waived === wired.length, always.
  const attributed = wired.filter((c) => clauseAttributed.includes(c));
  const unattributed = wired.filter((c) => !clauseAttributed.includes(c));
  // The roster is the ONLY instrument a board-blind check cannot pass.
  // If it ever passes one, this file has no instrument left at all.
  const blindBlind = !!mutate && blindCaught.length > 0;
  // A roster row that did not redden what it declared. Either the
  // injection site is dead or the check is asleep; both are defects and
  // neither is allowed to be quiet.
  const faultBlind = (faults || []).filter((f) => f.blind.length > 0);
  // A rule nothing reaches and nobody has explained is the gap this
  // screen exists to make loud. So is a rule an assertion TOUCHED that
  // no mutation could redden — that one is worse, because it renders in
  // green-adjacent gold and looks like coverage. Both are surfaced as
  // their own numbers, and UNDECLARED is the one that grows when
  // somebody adds a check and forgets to add its mutation.
  const unexplained = unproven.filter((c) => !UNPROVABLE_REASONS[c]);
  const undeclared = reachedOnly.filter((c) => !NOT_PROVEN_REASONS[c] && !PARTIAL_ONLY.has(c));

  const counts = {};
  H.violations.forEach((v) => { counts[v.code] = (counts[v.code] || 0) + 1; });

  // R43/R87: there is no draw. An unresolved duel is CALLED.
  const decided = outcomes.filter((o) => o.endRule === "R33").length;
  const calledR93 = outcomes.filter((o) => o.endRule === "R93").length;
  const unresolved = outcomes.length - decided - calledR93;

  return {
    seed, games, deckA, deckB,
    violations: [...deckViolations, ...H.violations],
    invariants: H.invariants,
    log: H.log,
    counts,
    winsA: outcomes.filter((o) => o.winner === 0).length,
    winsB: outcomes.filter((o) => o.winner === 1).length,
    decided, calledR93, unresolved,
    avgHalfTurns: outcomes.length ? (outcomes.reduce((n, o) => n + o.turns, 0) / outcomes.length).toFixed(1) : "0",
    coverage: {
      catalogue, reached, reachedOnly, mutationProven, provenClause, provenFault,
      vacuous, demoted, partial, unproven, unexplained, undeclared, fired, fakes,
      census, echoLost, echoBlind,
      miscensus, watching, inForceLost, inForceBlind, faultBlind,
      inForceFaultKept, inForceFaultLost,
      blindProven, blindCaught, blindBlind, watched, wired,
      attributed, unattributed, clauseAttributed,
    },
    mutations, faults,
  };
}


// ══════════════════════════════════════════════════════════════
// @@TERROR_DEBUGGER_HEADLESS_CUT@@
// THE SENTINEL IS ONE TOKEN AND IT APPEARS EXACTLY ONCE. It used to be
// the prose string `HEADLESS CUT`, which also appeared in the recipe up
// in the banner that TELLS you to cut at it — so the first `indexOf`
// landed on line 121 and produced a 121-line stub whose only symptom
// was a runner that imported nothing and exported less. A marker a
// document cannot mention without becoming the marker is a bug in the
// marker. Split it in a doc if you must quote it; do not weaken it.
//
// Everything above this line is plain ESM and imports React for
// nothing. Everything below is the screen. Do not move this line
// without updating RUNNING IT HEADLESS in the banner.
// ══════════════════════════════════════════════════════════════
export default function Debugger({ onBack }) {
  const [deckA, setDeckA] = useState(STARTER_DECKS[0]);
  const [deckB, setDeckB] = useState(STARTER_DECKS[1]);
  const [numGames, setNumGames] = useState(10);
  const [seed, setSeed] = useState(1337);
  const [verbose, setVerbose] = useState(false);
  const [mutate, setMutate] = useState(true);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState("summary");
  const [filterCat, setFilterCat] = useState("All");

  const run = () => {
    setRunning(true);
    setResults(null);
    setTimeout(() => {
      let r;
      try {
        r = runAll({ deckA, deckB, games: numGames, seed: Number(seed) | 0, verbose, mutate });
      } catch (e) {
        r = { fatal: String((e && e.stack) || e), seed, games: numGames, violations: [], invariants: [], log: [], counts: {}, coverage: { catalogue: [], reached: [], reachedOnly: [], mutationProven: [], provenClause: [], provenFault: [], vacuous: [], demoted: [], partial: [], unproven: [], unexplained: [], undeclared: [], fired: [], fakes: [], census: [], echoLost: [], echoBlind: false, miscensus: [], watching: [], inForceLost: [], inForceBlind: false, faultBlind: [], inForceFaultKept: [], inForceFaultLost: [], blindProven: [], blindCaught: [], blindBlind: false, watched: [], wired: [], attributed: [], unattributed: [], clauseAttributed: [] } };
      }
      setResults(r);
      setRunning(false);
      setActiveTab(r.fatal ? "summary" : (r.violations.length ? "violations" : "coverage"));
    }, 10);
  };

  const cats = useMemo(() => ["All", ...Array.from(new Set(ALL_RULES.map((r) => r.cat)))], []);
  const ruleOf = (code) => ALL_RULES.find((r) => r.code === code);

  const S = {
    container: { position: "fixed", inset: 0, background: SD.void0, fontFamily: SD.fontDisplay, display: "flex", flexDirection: "column", overflow: "hidden" },
    header: { display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", background: SD.void1, borderBottom: `1px solid ${SD.void4}`, flexShrink: 0, flexWrap: "wrap" },
    body: { flex: 1, overflow: "auto", padding: "14px" },
    card: { background: `linear-gradient(135deg,${SD.void2},${SD.void1})`, border: `1px solid ${SD.void4}`, borderRadius: 12, padding: "14px 18px", marginBottom: 10 },
    label: { fontSize: 9, color: SD.purpleLight, letterSpacing: 3, fontWeight: 900, marginBottom: 6 },
    select: { padding: "8px 12px", background: SD.void2, border: `1px solid ${SD.void4}`, borderRadius: 8, color: SD.starSilver, fontFamily: SD.fontDisplay, fontSize: 12, outline: "none" },
    btn: { padding: "10px 22px", background: `linear-gradient(135deg,${SD.purpleDim},${SD.void3})`, border: `1px solid ${SD.purpleLight}`, borderRadius: 8, color: SD.goldBright, fontSize: 13, fontWeight: 900, fontStyle: "italic", cursor: "pointer", fontFamily: SD.fontDisplay },
    tab: (a) => ({ padding: "7px 14px", borderRadius: 6, border: `1px solid ${a ? SD.purpleLight : SD.void4}`, background: a ? `${SD.purpleLight}22` : "transparent", color: a ? SD.purpleLight : SD.starDim, cursor: "pointer", fontSize: 11, fontFamily: SD.fontDisplay, whiteSpace: "nowrap" }),
    pill: (c) => ({ background: `${c}18`, border: `1px solid ${c}44`, borderRadius: 6, padding: "3px 9px", fontSize: 10, color: c, fontWeight: 900, display: "inline-block", margin: "2px 3px 2px 0" }),
  };
  const logColor = (t) => ({ violation: "#f87171", win: "#22c55e", attack: SD.red, turn: SD.gold, rule: SD.purpleLight, action: SD.starSilver })[t] || SD.starDim;

  const cov = results?.coverage;
  const nTotal = cov?.catalogue.length || 0;
  const pctReached = nTotal ? Math.round((cov.reached.length / nTotal) * 100) : 0;
  const pctProven = nTotal ? Math.round((cov.mutationProven.length / nTotal) * 100) : 0;
  const pctWatched = nTotal ? Math.round(((cov.watched || []).length / nTotal) * 100) : 0;
  const filtered = (results?.violations || []).filter((v) =>
    filterCat === "All" || ruleOf(v.code)?.cat === filterCat);

  const Checkbox = ({ on, set, text }) => (
    <div onClick={() => set((x) => !x)} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
      <div style={{ width: 16, height: 16, borderRadius: 3, border: `1px solid ${on ? SD.gold : SD.void4}`, background: on ? `${SD.gold}33` : "transparent", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: SD.gold }}>{on ? "✓" : ""}</div>
      <span style={{ fontSize: 10, color: SD.starDim }}>{text}</span>
    </div>
  );

  return (
    <div style={S.container}>
      <div style={S.header}>
        <button onClick={onBack} style={{ background: "transparent", border: `1px solid ${SD.void4}`, color: SD.starDim, padding: "4px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer" }}>← Back</button>
        <div style={{ fontSize: 15, fontWeight: 900, color: SD.gold, fontStyle: "italic" }}>◆ Terror Debugger</div>
        {/* Every number here is READ from rules.js. Nothing is typed. */}
        <div style={{ fontSize: 9, color: SD.starDim, letterSpacing: 2 }}>
          {ALL_RULES.length} RULES · SIM + ADVERSARIAL + MUTATION
        </div>
        <div style={{ flex: 1 }} />
        {results && !results.fatal && (
          <div style={{ fontSize: 10, color: SD.starDim }}>
            <span style={{ color: pctReached >= 80 ? "#4ade80" : SD.gold }}>{cov.reached.length}/{nTotal} reached</span>
            {" · "}
            <span style={{ color: "#4ade80" }}>{(cov.watched || []).length} WATCHED</span>
            <span style={{ color: SD.gold }}>{(cov.wired || []).length} wired-only</span>
            {" · seed "}{results.seed}
          </div>
        )}
      </div>

      <div style={S.body}>
        <div style={S.card}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ flex: 1, minWidth: 150 }}>
              <div style={S.label}>DECK A</div>
              <select style={{ ...S.select, width: "100%" }} value={deckA.id} onChange={(e) => setDeckA(STARTER_DECKS.find((d) => d.id === e.target.value) || STARTER_DECKS[0])}>
                {STARTER_DECKS.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 150 }}>
              <div style={S.label}>DECK B</div>
              <select style={{ ...S.select, width: "100%" }} value={deckB.id} onChange={(e) => setDeckB(STARTER_DECKS.find((d) => d.id === e.target.value) || STARTER_DECKS[1])}>
                {STARTER_DECKS.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <div style={S.label}>GAMES</div>
              <select style={S.select} value={numGames} onChange={(e) => setNumGames(Number(e.target.value))}>
                {[1, 5, 10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              {/* A failure nobody can reproduce is a rumour. */}
              <div style={S.label}>SEED</div>
              <input style={{ ...S.select, width: 90 }} value={seed} onChange={(e) => setSeed(e.target.value.replace(/[^0-9-]/g, "") || 0)} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, paddingBottom: 4 }}>
              <Checkbox on={verbose} set={setVerbose} text="Verbose" />
              <Checkbox on={mutate} set={setMutate} text="Mutation self-test" />
              <button onClick={run} disabled={running} style={{ ...S.btn, opacity: running ? 0.5 : 1 }}>
                {running ? "◆ Running..." : "▶ Run"}
              </button>
            </div>
          </div>
        </div>

        {results?.fatal && (
          <div style={{ ...S.card, border: `1px solid ${SD.red}` }}>
            <div style={{ color: SD.red, fontWeight: 900, marginBottom: 6 }}>THE HARNESS ITSELF THREW</div>
            <pre style={{ fontSize: 10, color: "#f87171", whiteSpace: "pre-wrap" }}>{results.fatal}</pre>
          </div>
        )}

        {results && !results.fatal && (
          <>
            <div style={{ display: "flex", gap: 6, marginBottom: 10, overflowX: "auto", paddingBottom: 2 }}>
              {[
                { id: "summary", label: "Summary" },
                { id: "violations", label: `Violations ${results.violations.length ? `(${results.violations.length})` : "✓"}` },
                { id: "coverage", label: `Coverage (${cov.reached.length}r / ${(cov.watched || []).length}w)` },
                { id: "mutation", label: `Mutation ${results.mutations ? (cov.fakes.length ? `(${cov.fakes.length} FAKE)` : "✓") : "(off)"}` },
                { id: "faults", label: `Faults ${results.faults ? (cov.faultBlind.length ? `(${cov.faultBlind.length} BLIND)` : "✓") : "(off)"}` },
                { id: "invariants", label: `Invariants ${results.invariants.length ? `(${results.invariants.length})` : "✓"}` },
                { id: "log", label: "Game 1 Log" },
                { id: "rules", label: `Catalogue (${ALL_RULES.length})` },
              ].map((t) => (
                <button key={t.id} style={S.tab(activeTab === t.id)} onClick={() => setActiveTab(t.id)}>{t.label}</button>
              ))}
            </div>

            {activeTab === "summary" && (
              <div style={S.card}>
                <div style={{ fontSize: 10, color: SD.gold, letterSpacing: 3, marginBottom: 14 }}>
                  ◆ {results.games} DUELS · {deckA.name} vs {deckB.name} · SEED {results.seed}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))", gap: 10, marginBottom: 16 }}>
                  {[
                    { label: "A WINS", val: results.winsA, color: SD.purpleLight },
                    { label: "B WINS", val: results.winsB, color: SD.tealLight },
                    // NOT "draws". Terror has no draw (R43/R87) — R93 CALLS it.
                    { label: "CALLED · R93", val: results.calledR93, color: SD.gold },
                    { label: "UNRESOLVED", val: results.unresolved, color: SD.starDim },
                    { label: "VIOLATIONS", val: results.violations.length, color: results.violations.length ? SD.red : "#22c55e" },
                    { label: "AVG HALF-TURNS", val: results.avgHalfTurns, color: SD.starSilver },
                  ].map(({ label, val, color }) => (
                    <div key={label} style={{ textAlign: "center", background: SD.void2, borderRadius: 8, padding: "12px 6px", border: `1px solid ${color}33` }}>
                      <div style={{ fontSize: 24, fontWeight: 900, color }}>{val}</div>
                      <div style={{ fontSize: 8, color: SD.starDim, letterSpacing: 2, marginTop: 2 }}>{label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 10, color: SD.starDim, lineHeight: 1.8, marginBottom: 12 }}>
                  <strong style={{ color: SD.gold }}>THERE IS NO DRAW.</strong> R43 removed deck-out as a loss and
                  R87/R93 CALL an unresolved duel on the evidence already on the board — Anima, then beings
                  standing, then Grimoire depth, then seat. A duel that reaches TURN_CLOCK ({TURN_CLOCK} half-turns)
                  is awarded, never drawn. "UNRESOLVED" above counts duels that hit the harness backstop
                  (SIM_TURN_CAP = {SIM_TURN_CAP}) without the clock calling them; that is a HARNESS number, not a rule outcome.
                </div>
                {Object.keys(results.counts).length > 0 && (
                  <div>
                    <div style={{ fontSize: 9, color: SD.red, letterSpacing: 3, marginBottom: 8 }}>⚠ VIOLATION BREAKDOWN</div>
                    {Object.entries(results.counts).sort((a, b) => b[1] - a[1]).map(([code, n]) => (
                      <span key={code} style={S.pill(CAT_COLORS[ruleOf(code)?.cat] || SD.red)}>{code} ×{n}</span>
                    ))}
                  </div>
                )}
                <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 8, background: results.violations.length ? `${SD.red}11` : "#22c55e11", border: `1px solid ${results.violations.length ? `${SD.red}44` : "#22c55e44"}` }}>
                  <div style={{ fontSize: 12, color: results.violations.length ? SD.red : "#22c55e", fontWeight: 900 }}>
                    {results.violations.length
                      ? `⚠ ${results.violations.length} violation(s) across ${results.games} duels + the adversarial suite`
                      : `✓ ${results.games} duels and the adversarial suite passed — ${cov.reached.length} of ${nTotal} rules reached, ${cov.mutationProven.length} able to fail, and ${(cov.watched || []).length} of those demonstrated on a deliberately BROKEN board`}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "violations" && (
              <div>
                <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                  <select style={S.select} value={filterCat} onChange={(e) => setFilterCat(e.target.value)}>
                    {cats.map((c) => <option key={c}>{c}</option>)}
                  </select>
                  <span style={{ fontSize: 10, color: SD.starDim, alignSelf: "center" }}>{filtered.length} shown</span>
                </div>
                <div style={S.card}>
                  {filtered.length === 0 ? (
                    <div style={{ color: "#22c55e", fontSize: 13, textAlign: "center", padding: "20px 0" }}>✓ No violations</div>
                  ) : filtered.map((v, i) => {
                    const rule = ruleOf(v.code);
                    const color = CAT_COLORS[rule?.cat] || SD.red;
                    return (
                      <div key={i} style={{ padding: "10px 12px", marginBottom: 8, background: `${SD.red}0a`, border: `1px solid ${SD.red}33`, borderRadius: 8 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 9, color, fontWeight: 900, background: `${color}22`, padding: "2px 8px", borderRadius: 10 }}>{v.code}</span>
                          <span style={{ fontSize: 9, color: SD.starDim }}>{v.phase}</span>
                          {v.game && <span style={{ fontSize: 9, color: SD.starDim }}>game {v.game}</span>}
                          <span style={{ fontSize: 9, color: SD.starDim }}>{rule?.cat}</span>
                        </div>
                        <div style={{ fontSize: 11, color: "#f87171", lineHeight: 1.7, marginBottom: 4 }}>{v.detail}</div>
                        {rule && <div style={{ fontSize: 10, color: SD.starDim, fontStyle: "italic" }}>Rule: {rule.desc}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === "coverage" && (
              <div style={S.card}>
                {/* THE HONEST HALF. The PROVEN set is observed at runtime —
                    every code that reached H.assert during this run. There
                    is no hand-maintained list to drift. */}
                {/* ── THE ALARM BAR ────────────────────────────────
                    Four controls, four ways this file can be lying, and
                    every one of them is a number on the screen rather
                    than a comment somebody has to go and read. The two
                    ECHOES are the important pair: the first degrades
                    BOTH branches of every clause guard and the second
                    degrades the IN-FORCE branch alone, which is the
                    one-line attack that made this whole report
                    byte-identical while the checks watched nothing. */}
                {results.mutations && (() => {
                  const alarms = [
                    cov.fakes.length ? `${cov.fakes.length} FAKE — a mutation row stayed green` : null,
                    cov.faultBlind.length ? `${cov.faultBlind.length} FAULT-BLIND — a named engine bug was committed and the checks that are supposed to catch it did not: ${cov.faultBlind.map((f) => `${f.fault}→${f.blind.join(",")}`).join(" · ")}` : null,
                    cov.echoBlind ? "ECHO-BLIND — degrading every clause guard to a bare echo of the switch cost this report NOTHING. The number cannot tell watching the board from watching itself." : null,
                    cov.inForceBlind ? "IN-FORCE-BLIND — forcing `inForce = true` inside ruleIs cost this report NOTHING, or nothing was watching to begin with. THE IN-FORCE BRANCH IS THE ONLY BRANCH A REAL DUEL EVALUATES." : null,
                    (results.mutations && cov.inForceFaultLost.length === 0) ? "IN-FORCE-FAULT-BLIND — with every clause guard's in-force branch forced true, the FAULT ROSTER still caught every bug it catches normally. Nothing on the roster is being watched by a clause guard that reads the board." : null,
                    cov.blindBlind ? `BLIND-ECHO-BLIND — a file whose every assertion reads only "was the checker perturbed" scored on the FAULT ROSTER (${cov.blindCaught.join(" ")}). The roster was the last instrument a board-blind check could not pass.` : null,
                  ].filter(Boolean);
                  return alarms.length ? (
                    <div style={{ border: `1px solid ${SD.red}`, background: `${SD.red}14`, borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
                      <div style={{ fontSize: 10, color: SD.red, fontWeight: 900, letterSpacing: 2, marginBottom: 6 }}>⚠ THE CONTROLS FAILED</div>
                      {alarms.map((a, i) => (
                        <div key={i} style={{ fontSize: 10, color: "#f87171", lineHeight: 1.7 }}>{a}</div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ border: `1px solid #4ade8044`, background: "#4ade800e", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 10, color: "#4ade80", lineHeight: 1.7 }}>
                      ✓ ALL FIVE CONTROLS BIT. No FAKE row; every fault on the roster reddened what it declared;
                      the echo control cost {cov.echoLost.length} clause proof(s); the IN-FORCE echo cost
                      all {cov.inForceLost.length} of the {cov.watching.length} clause guards that watch the board,
                      and cost the roster {cov.inForceFaultLost.length} of its {cov.provenFault.length} caught bugs;
                      the BLIND ECHO — every assertion in the file replaced by "was the checker perturbed" —
                      scored {cov.blindProven.length} on the mutation batch and {cov.blindCaught.length} on the fault roster.
                    </div>
                  );
                })()}
                <div style={{ fontSize: 11, color: SD.gold, fontWeight: 900, letterSpacing: 1.5, marginBottom: 8 }}>
                  COVERAGE — WATCHED IS THE NUMBER. WIRED IS NOT.
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginBottom: 14 }}>
                  {[
                    { label: "REACHED", val: `${cov.reached.length}/${nTotal}`, sub: `${pctReached}% — an assertion ran`, color: pctReached >= 80 ? "#4ade80" : SD.gold },
                    { label: "WATCHED", val: `${cov.watched.length}/${nTotal}`, sub: `${pctWatched}% — the sim broke the BOARD and the check said no`, color: "#4ade80" },
                    // ⚠ `WIRED` IS A ROSTER, NOT A MEASUREMENT, and the tile
                    // now says what it is made of instead of leaving that in
                    // a banner. Almost all of it is ONE WAIVER: a red with no
                    // clause attribution is granted honesty unconditionally
                    // by `runMutations`, and the unattributed set has always
                    // been the wired set element for element. The ATTRIBUTED
                    // count beside it is the part that went through a clause
                    // guard whose BOTH branches were demonstrated.
                    { label: "WIRED ONLY", val: `${cov.wired.length}  (${cov.attributed.length} attributed · ${cov.unattributed.length} waived)`,
                      sub: "reddens when the rule is withdrawn from the CHECKER'S OWN COPY. 'Waived' = no clause attribution, honesty granted by one line. NEITHER was shown to read a board.", color: SD.gold },
                    { label: "MUTATION-PROVEN", val: `${cov.mutationProven.length}/${nTotal}`, sub: `${pctProven}% — WATCHED + WIRED, and only WATCHED is a measurement. Read them apart.`, color: SD.gold },
                    { label: "BLIND ECHO", val: `${cov.blindProven.length} / ${cov.blindCaught.length}`,
                      sub: `what a file that reads NOTHING scores: ${cov.blindProven.length} of ${cov.mutationProven.length} on the mutation batch, ${cov.blindCaught.length} on the fault roster. That first number is why MUTATION-PROVEN is not the headline.`,
                      color: cov.blindCaught.length ? SD.red : SD.purpleLight },
                    { label: "· VIA CLAUSE", val: cov.provenClause.length, sub: "both branches demonstrated predicates", color: SD.purpleLight },
                    { label: "FAKE", val: cov.fakes.length, sub: cov.fakes.length ? "stayed green under mutation" : "none", color: cov.fakes.length ? SD.red : "#4ade80" },
                    { label: "DEMOTED", val: cov.demoted.length, sub: cov.demoted.length ? `in-force branch watches nothing: ${cov.demoted.join(" ")}` : "no clause guard is a constant", color: cov.demoted.length ? SD.red : "#4ade80" },
                    { label: "VACUOUS", val: cov.vacuous.length, sub: cov.vacuous.length ? `counter-rule true of no board: ${cov.vacuous.join(" ")}` : "none", color: cov.vacuous.length ? SD.gold : "#4ade80" },
                    { label: "UNDECLARED", val: cov.unexplained.length + cov.undeclared.length, sub: (cov.unexplained.length + cov.undeclared.length) ? "a gap with no reason filed" : "every gap has a reason", color: (cov.unexplained.length + cov.undeclared.length) ? SD.red : "#4ade80" },
                  ].map(({ label, val, sub, color }) => (
                    <div key={label} style={{ background: SD.void2, borderRadius: 8, padding: "10px 12px", border: `1px solid ${color}33` }}>
                      <div style={{ fontSize: 20, fontWeight: 900, color }}>{val}</div>
                      <div style={{ fontSize: 8, color: SD.starDim, letterSpacing: 2, marginTop: 2 }}>{label}</div>
                      <div style={{ fontSize: 9, color: SD.starDim, marginTop: 2 }}>{sub}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 10, color: SD.starDim, lineHeight: 1.8, marginBottom: 14 }}>
                  <strong style={{ color: SD.starSilver }}>REACHED</strong> means an assertion for that rule executed —
                  the rule was being <em>watched</em>. It does not by itself mean the check could have failed.
                  {" "}<strong style={{ color: SD.starSilver }}>MUTATION-PROVEN</strong> means a mutation run actually
                  turned that code red, so the check has a demonstrated failing state. Reporting one number for both
                  is how "44% covered" once meant "13% proven".
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 9, color: "#4ade80", letterSpacing: 2, marginBottom: 4 }}>
                    MUTATION-PROVEN ({cov.mutationProven.length}) — {(cov.watched || []).length} WATCHED, {(cov.wired || []).length} wired-only — these have been watched going red
                  </div>
                  {cov.mutationProven.map((c) => (
                    <span key={c} style={S.pill(PARTIAL_ONLY.has(c) ? SD.purpleLight : "#4ade80")}>
                      {c}{PARTIAL_ONLY.has(c) ? "◐" : "✦"}
                    </span>
                  ))}
                  <div style={{ fontSize: 9, color: SD.starDim, marginTop: 4 }}>
                    ✦ = watched going red on a played board · ◐ = watched going red at a hand-built board or at
                    the legality predicate, never inside a duel. Both are proofs; they are not the same proof.
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 9, color: SD.gold, letterSpacing: 2, marginBottom: 4 }}>
                    REACHED ONLY ({cov.reachedOnly.length}) — asserted, but no mutation has proven them failable
                  </div>
                  {cov.reachedOnly.length === 0 && (
                    <div style={{ fontSize: 11, color: "#4ade80" }}>
                      ✓ every rule this run reached has also been watched going red.
                    </div>
                  )}
                  {cov.reachedOnly.map((c) => (
                    <div key={c} style={{ display: "flex", gap: 8, marginBottom: 5, alignItems: "flex-start" }}>
                      <span style={{ ...S.pill(NOT_PROVEN_REASONS[c] ? SD.gold : SD.red), flexShrink: 0 }}>{c}</span>
                      <span style={{ fontSize: 10, color: NOT_PROVEN_REASONS[c] ? SD.starDim : SD.red, lineHeight: 1.6 }}>
                        {NOT_PROVEN_REASONS[c]
                          || `NO REASON FILED. An assertion for ${c} ran and no mutation could turn it red. That is the most dangerous row on this screen: it renders as coverage and watches nothing. Either write the mutation that reddens it, or write down here why none exists.`}
                      </span>
                    </div>
                  ))}
                </div>
                <div>
                  <div style={{ fontSize: 9, color: "#f87171", letterSpacing: 2, marginBottom: 4 }}>UNREACHED ({cov.unproven.length})</div>
                  {cov.unproven.map((c) => (
                    <div key={c} style={{ display: "flex", gap: 8, marginBottom: 5, alignItems: "flex-start" }}>
                      <span style={{ ...S.pill(UNPROVABLE_REASONS[c] ? "#f87171" : SD.red), flexShrink: 0 }}>{c}</span>
                      <span style={{ fontSize: 10, color: UNPROVABLE_REASONS[c] ? SD.starDim : SD.red, lineHeight: 1.6 }}>
                        {UNPROVABLE_REASONS[c]
                          || `NO ASSERTION RAN THIS RUN. ${results.games} duel(s) is not always enough to reach every state — combat, descent and Soul rules need beings to actually die — so raise GAMES and look again. If it stays unreached at 50, nothing in this harness references ${c} and nobody has written down why: that is a gap in the checker, not a property of the rule.`}
                      </span>
                    </div>
                  ))}
                  {cov.unproven.length === 0 && <div style={{ fontSize: 11, color: "#4ade80" }}>✓ every catalogued rule was asserted at least once.</div>}
                </div>
                {/* Partial proofs are stated where they are counted, not in a
                    comment the screen cannot render. */}
                {cov.partial.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 9, color: SD.purpleLight, letterSpacing: 2, marginBottom: 4 }}>
                      PARTIAL PROOFS ({cov.partial.length}) — mutation-proven at a HAND-BUILT board or at the
                      legality predicate, never watched happening inside a played duel
                    </div>
                    {cov.partial.map((c) => (
                      <div key={c} style={{ display: "flex", gap: 8, marginBottom: 5, alignItems: "flex-start" }}>
                        <span style={{ ...S.pill(SD.purpleLight), flexShrink: 0 }}>{c}</span>
                        <span style={{ fontSize: 10, color: SD.starDim, lineHeight: 1.6 }}>{PARTIAL_REASONS[c]}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: 10, color: SD.starDim, marginTop: 12, lineHeight: 1.7, opacity: 0.9 }}>
                  UNPROVABLE is not UNCHECKED. Deck construction is also enforced by <code>RulesEnforcer</code>,
                  and Souls, Supreme Beings and the Anima-zero invariant are watched live by{" "}
                  <code>tools/balance.mjs</code>, which drives the real NPC brain through the real engine.
                  Run both — a bug can hide from either alone.
                </div>
              </div>
            )}

            {activeTab === "mutation" && (
              <div style={S.card}>
                <div style={{ fontSize: 11, color: SD.gold, fontWeight: 900, letterSpacing: 1.5, marginBottom: 6 }}>
                  MUTATION SELF-TEST — CAN THESE CHECKS GO RED?
                </div>
                <div style={{ fontSize: 10, color: SD.starDim, lineHeight: 1.7, marginBottom: 12 }}>
                  Each row perturbs ONE constant in the checker's own copy while the simulator keeps
                  acting on the true value. A check that reads the rule must fail. A check that stays
                  green cannot be failed by anything, which is the definition of a fake check.
                </div>
                {!results.mutations && <div style={{ fontSize: 11, color: SD.starDim }}>Mutation self-test was disabled for this run.</div>}
                {results.mutations && results.mutations.map((m) => (
                  <div key={m.key} style={{ display: "flex", gap: 10, alignItems: "center", padding: "6px 8px", borderRadius: 6, marginBottom: 4, background: m.fake.length ? `${SD.red}12` : "transparent", border: `1px solid ${m.fake.length ? `${SD.red}44` : "transparent"}` }}>
                    <span style={{ fontSize: 10, color: SD.starSilver, width: 180, flexShrink: 0 }}>{m.key}</span>
                    <span style={{ fontSize: 10, color: m.fake.length ? SD.red : "#4ade80", fontWeight: 900, width: 66, flexShrink: 0 }}>
                      {m.fake.length ? "FAKE" : "GOES RED"}
                    </span>
                    <span style={{ fontSize: 10, color: SD.starDim }}>
                      expected {m.expect.join(" ")}
                      {m.red.length ? ` · fired ${m.red.join(" ")}` : ""}
                      {m.fake.length ? ` · STAYED GREEN: ${m.fake.join(" ")}` : ""}
                      {m.error ? ` · harness threw: ${m.error}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "faults" && (
              <div style={S.card}>
                <div style={{ fontSize: 11, color: SD.gold, fontWeight: 900, letterSpacing: 1.5, marginBottom: 6 }}>
                  THE FAULT ROSTER — DID THE CHECKS CATCH A BROKEN BOARD?
                </div>
                <div style={{ fontSize: 10, color: SD.starDim, lineHeight: 1.7, marginBottom: 12 }}>
                  The MUTATION table withdraws a rule from the CHECKER and asks whether the assertions
                  notice. This table does the opposite and it is the stronger question: the CHECKER is
                  left clean and the SIMULATOR commits a named engine bug — the specific error that rule
                  forbids — and the assertions have to catch it. It exists because a closing review added
                  one line (<code>inForce = true</code>) to <code>ruleIs</code>, made every clause-guarded
                  check in this file watch nothing while the rules stand, and got a BYTE-IDENTICAL report
                  out of it. Nothing here was evaluating the branch a real duel runs.
                  {" "}<strong style={{ color: SD.starSilver }}>COLLATERAL is counted and not spent:</strong>{" "}
                  a broken board reddens neighbours, and crediting those to the fault would be the same
                  overstatement in a new colour. Only declared-and-observed reds are proofs.
                </div>
                {!results.faults && <div style={{ fontSize: 11, color: SD.starDim }}>Mutation self-test was disabled for this run, and the roster runs with it.</div>}
                {results.faults && results.faults.map((f) => (
                  <div key={f.key} style={{ display: "flex", gap: 10, alignItems: "center", padding: "6px 8px", borderRadius: 6, marginBottom: 4, background: f.blind.length ? `${SD.red}12` : "transparent", border: `1px solid ${f.blind.length ? `${SD.red}44` : "transparent"}` }}>
                    <span style={{ fontSize: 10, color: SD.starSilver, width: 200, flexShrink: 0 }}>{f.key}</span>
                    <span style={{ fontSize: 10, color: f.blind.length ? SD.red : "#4ade80", fontWeight: 900, width: 66, flexShrink: 0 }}>
                      {f.blind.length ? "BLIND" : "CAUGHT"}
                    </span>
                    <span style={{ fontSize: 10, color: SD.starDim }}>
                      declared {f.expect.join(" ")}
                      {f.red.length ? ` · caught by ${f.red.join(" ")}` : ""}
                      {f.blind.length ? ` · NOBODY NOTICED: ${f.blind.join(" ")}` : ""}
                      {f.collateral.length ? ` · collateral (not counted): ${f.collateral.join(" ")}` : ""}
                      {f.error ? ` · the fault CRASHED the simulator, which proves nothing about any check: ${f.error}` : ""}
                    </span>
                  </div>
                ))}
                {results.faults && (
                  <div style={{ marginTop: 14, fontSize: 10, color: SD.starDim, lineHeight: 1.8 }}>
                    <div style={{ fontSize: 9, color: SD.purpleLight, letterSpacing: 2, marginBottom: 4 }}>
                      THE IN-FORCE CENSUS ({cov.watching.length} clause guards watch the board)
                    </div>
                    {cov.miscensus.map((m) => (
                      <span key={m.clause} style={S.pill(m.watches ? "#4ade80" : SD.red)}>
                        {m.clause} {m.inForceFalse}/{m.n}
                      </span>
                    ))}
                    <div style={{ marginTop: 6 }}>
                      Each pill is <em>how many times this guard's IN-FORCE reading said NO</em> over how many
                      times it was asked, across the roster. A zero on the left is a branch that is true of every
                      board a broken simulator can build — which is a constant wearing a predicate's clothes, and
                      is exactly what Attack A and Attack B produced. A clause needs a non-zero here AND a
                      contentful counter before a red earned through it is counted.
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "invariants" && (
              <div style={S.card}>
                <div style={{ fontSize: 11, color: SD.gold, fontWeight: 900, letterSpacing: 1.5, marginBottom: 6 }}>
                  STATE INVARIANTS — SWEPT AFTER EVERY ACTION
                </div>
                <div style={{ fontSize: 10, color: SD.starDim, lineHeight: 1.7, marginBottom: 12 }}>
                  Card conservation (hand + Grimoire + Luxmoire + field + Souls + Necropolis constant),
                  unique instance ids, no card resident in two zones, non-negative Anima and Khao.
                  These carry NO R-code and are deliberately NOT counted as rule coverage — reporting
                  engine invariants as rules is the inflation this rebuild exists to remove.
                </div>
                {results.invariants.length === 0
                  ? <div style={{ color: "#22c55e", fontSize: 12 }}>✓ every sweep held.</div>
                  : results.invariants.map((v, i) => (
                    <div key={i} style={{ fontSize: 11, color: "#f87171", lineHeight: 1.8 }}>
                      <strong>{v.name}</strong> — {v.detail}{v.game ? ` (game ${v.game})` : ""}
                    </div>
                  ))}
              </div>
            )}

            {activeTab === "log" && (
              <div style={{ ...S.card, maxHeight: "65vh", overflowY: "auto" }}>
                <div style={{ fontSize: 9, color: SD.starDim, letterSpacing: 2, marginBottom: 8 }}>
                  GAME 1 · SEED {results.seed} {!verbose && "(enable Verbose and rerun for the full play-by-play)"}
                </div>
                {results.log.length === 0
                  ? <div style={{ color: SD.starDim, fontSize: 12 }}>Enable Verbose and rerun.</div>
                  : results.log.map((e, i) => (
                    <div key={i} style={{ fontSize: 11, color: logColor(e.type), lineHeight: 1.8, borderBottom: e.type === "turn" ? `1px solid ${SD.void4}` : "none", paddingTop: e.type === "turn" ? 6 : 0 }}>
                      {e.msg}
                    </div>
                  ))}
              </div>
            )}

            {activeTab === "rules" && (
              <div style={S.card}>
                <div style={{ fontSize: 9, color: SD.gold, letterSpacing: 3, marginBottom: 12 }}>
                  ◆ THE GRIMOIRE OF TERROR — {ALL_RULES.length} RULES, READ FROM rules.js
                </div>
                {cats.filter((c) => c !== "All").map((cat) => {
                  const color = CAT_COLORS[cat] || SD.starDim;
                  return (
                    <div key={cat} style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 9, color, letterSpacing: 3, fontWeight: 900, marginBottom: 6 }}>{cat.toUpperCase()}</div>
                      {ALL_RULES.filter((r) => r.cat === cat).map((r) => {
                        const violated = results.counts[r.code];
                        const provenHere = cov.mutationProven.includes(r.code);
                        const watched = cov.reached.includes(r.code);
                        return (
                          <div key={r.code} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 4, padding: "5px 8px", borderRadius: 6, background: violated ? `${SD.red}0a` : "transparent", border: `1px solid ${violated ? `${SD.red}22` : "transparent"}` }}>
                            <span style={{ fontSize: 9, color, fontWeight: 900, flexShrink: 0, marginTop: 1, width: 32 }}>{r.code}</span>
                            <span style={{ fontSize: 9, flexShrink: 0, marginTop: 1, width: 14, color: provenHere ? "#4ade80" : watched ? SD.gold : "#f87171" }}>{provenHere ? "◆" : watched ? "◇" : "○"}</span>
                            <span style={{ fontSize: 11, color: violated ? "#f87171" : SD.starSilver, lineHeight: 1.6 }}>{r.desc}</span>
                            {violated && <span style={{ fontSize: 9, color: SD.red, flexShrink: 0 }}>×{violated}</span>}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                <div style={{ fontSize: 9, color: SD.starDim, marginTop: 8 }}>◆ mutation-proven · ◇ reached (watched, not proven failable) · ○ unreached</div>
              </div>
            )}
          </>
        )}

        {/* ── THE WORLD GRAPH ─────────────────────────────────────
            A duel simulator that says nothing about whether the story can
            be FINISHED is a QA screen with a hole in it. This panel is a
            pure walk of NODES and PATHS with no profile in it, so THE
            SURVEYOR cannot fool it. */}
        <div style={{ ...S.card, marginTop: 12 }}>
          <div style={{ fontSize: 11, color: SD.gold, fontWeight: 900, letterSpacing: 1.5, marginBottom: 6 }}>
            THE WORLD GRAPH — CAN THE STORY BE FINISHED?
          </div>
          {(() => {
            let r;
            try { r = auditWorldReachability(); }
            catch (e) { return <div style={{ fontSize: 11, color: "#f87171" }}>audit threw: {String(e.message)}</div>; }
            return (
              <>
                <div style={{ fontSize: 11, color: SD.starSilver, lineHeight: 1.7 }}>
                  <strong style={{ color: r.ok && r.throneReachable ? "#4ade80" : "#f87171" }}>{r.reachable} of {r.total}</strong>{" "}
                  nodes are reachable by a perfect player starting cold. The throne is{" "}
                  <strong style={{ color: r.throneReachable ? "#4ade80" : "#f87171" }}>
                    {r.throneReachable ? "reachable" : "UNREACHABLE — the story has no ending"}
                  </strong>. Sol Shards earnable: <strong>{r.shardsEarnable}</strong> of {r.shardsTotal}.
                </div>
                {r.unreachable.length > 0 && (
                  <div style={{ fontSize: 10, color: SD.starDim, lineHeight: 1.8, marginTop: 8 }}>
                    <span style={{ color: "#f87171", fontWeight: 900 }}>ORPHANED:</span> {r.unreachable.map((n) => n.id).join(" ")}
                    {r.orphanRegions.length > 0 && (
                      <div style={{ marginTop: 4 }}>
                        <span style={{ color: "#f87171", fontWeight: 900 }}>WHOLE REGIONS WITH NO WAY IN:</span> {r.orphanRegions.join(", ")}
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {!results && !running && (
          <div style={{ ...S.card, textAlign: "center", padding: "24px" }}>
            <div style={{ fontSize: 28, color: SD.gold, marginBottom: 8 }}>◆</div>
            <div style={{ fontSize: 13, color: SD.starSilver, marginBottom: 6 }}>Pick two decks, pick a seed, and run.</div>
            <div style={{ fontSize: 11, color: SD.starDim, lineHeight: 1.8 }}>
              Three passes over the {ALL_RULES.length}-rule catalogue: a seeded SIMULATION, an
              ADVERSARIAL suite that attempts illegal actions and demands rejection, and a
              MUTATION self-test that proves each check can actually go red.
              <br />Coverage is measured, never declared — no rule is claimed unless an assertion for it ran.
            </div>
            <div style={{ fontSize: 10, color: SD.starDim, marginTop: 10, opacity: 0.8 }}>
              For the other half of the picture — the real NPC brain, the real engine —
              run <code>node tools/balance.mjs</code>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
